import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import axios from 'axios';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import * as XLSX from 'xlsx';
import FormBuilderModal from '../components/admin/FormBuilderModal';
import CitiesManager from '../components/admin/CitiesManager';
import { formatDateInParis, formatDateTimeInParis, formatMonthYearInParis, toDateInputValueInParis } from '../utils/dateTime';
import { EMAIL_INPUT_PATTERN, EMAIL_INPUT_TITLE, isValidEmailAddress } from '../utils/emailValidation';
import { cities as catalogCities, parisResidencesBySubRegion, residencesByCity as catalogResidencesByCity } from '../utils/residenceCatalog';
import {
  DEFAULT_REVIEW_REGION,
  DEPARTMENT_TO_CITIES,
  PARIS_MERGED_DEPARTMENTS,
  getDepartmentDisplayCities,
  getReviewCityDisplayName,
  normalizeName,
  resolveRegionName,
  toRegionKey,
} from '../utils/testimonialsMapData';
import './AdminDashboard.css';

const apiBaseUrl = axios.defaults.baseURL || '';
const resolveMediaUrl = (path) => {
  if (!path) return null;
  const raw = String(path).trim();
  if (!raw) return null;
  if (/^https?:\/\//i.test(raw)) return raw;
  const normalized = raw.startsWith('/') ? raw : `/${raw}`;
  if (!normalized.startsWith('/uploads/')) return normalized;
  const base = String(apiBaseUrl || '').replace(/\/$/, '');
  return base ? `${base}${normalized}` : normalized;
};

const parseFooterLogoUrl = (imageUrlRaw) => {
  const value = String(imageUrlRaw || '').trim();
  if (!value) return { src: '', fitMode: 'contain' };

  const [srcPart, hashPart] = value.split('#');
  const rawMode = String(hashPart || '').trim().toLowerCase();
  const fitMode = ['contain', 'cover', 'fill'].includes(rawMode) ? rawMode : 'contain';

  return {
    src: resolveMediaUrl(srcPart || value) || (srcPart || value),
    fitMode,
  };
};

const todayParisDate = toDateInputValueInParis();

const validateAdminMoveDates = (moveInDate, moveOutDate) => {
  const moveIn = String(moveInDate || '').trim();
  const moveOut = String(moveOutDate || '').trim();

  if (!moveIn) {
    return 'Veuillez renseigner la date d\'entrée.';
  }

  if (moveIn < todayParisDate) {
    return 'La date d\'entrée ne peut pas être dans le passé.';
  }

  if (moveOut && moveOut < moveIn) {
    return 'La date de sortie ne peut pas être antérieure à la date d\'entrée.';
  }

  return '';
};

const ADMIN_ROLE_NAMES = Object.freeze({
  USER_MANAGEMENT_ADMIN: 'User Management Admin',
  WEBSITE_MANAGEMENT_ADMIN: 'Website Management Admin',
  ADMINISTRATIVE_ADMIN: 'Administrative Admin',
  SUPER_ADMIN: 'Super Admin',
});

const ADMIN_ROLE_OPTIONS = Object.freeze([
  ADMIN_ROLE_NAMES.USER_MANAGEMENT_ADMIN,
  ADMIN_ROLE_NAMES.WEBSITE_MANAGEMENT_ADMIN,
  ADMIN_ROLE_NAMES.ADMINISTRATIVE_ADMIN,
  ADMIN_ROLE_NAMES.SUPER_ADMIN,
]);

const DEFAULT_TEAM_ROLE_OPTIONS = Object.freeze(
  ADMIN_ROLE_OPTIONS.filter((roleName) => roleName !== ADMIN_ROLE_NAMES.SUPER_ADMIN)
);

const TAB_KEYS = Object.freeze({
  USERS: 'users',
  TEAM: 'team',
  EVENTS: 'events',
  CAROUSEL: 'carousel',
  FOOTER: 'footer',
  FORM_MANAGEMENT: 'form-management',
  FORM_REQUESTS: 'form-requests',
  EVENT_REQUESTS: 'event-requests',
  CONTACT_MESSAGES: 'contact-messages',
  SIGNUP: 'signup',
  DOCUMENT_REQUESTS: 'document-requests',
  HOUSE_MANAGEMENT_CHANGE: 'house-management-change',
  HOUSE_MANAGEMENT_LEAVE: 'house-management-leave',
  USERS_NOTIFICATIONS: 'notifications',
  ADMIN_NOTIFICATIONS_INBOX: 'notifications-admin-inbox',
  ADMIN_NOTIFICATIONS_OUTBOX: 'notifications-admin-outbox',
  NOTIFICATION_MANAGEMENT: 'notification-management',
  TESTIMONIALS_MANAGEMENT: 'testimonials-management',
  PAYMENTS: 'payments',
  INVOICES: 'invoices',
  EMAIL_TEMPLATES: 'email-templates',
});

const ASSIGNABLE_ADMIN_ROLE_TAB_OPTIONS = Object.freeze([
  { key: TAB_KEYS.USERS, label: 'Utilisateurs', group: 'Gestion utilisateurs' },
  { key: TAB_KEYS.EVENTS, label: 'Evenements', group: 'Gestion site web' },
  { key: TAB_KEYS.CAROUSEL, label: 'Carrousel', group: 'Gestion site web' },
  { key: TAB_KEYS.FOOTER, label: 'Footer', group: 'Gestion site web' },
  { key: TAB_KEYS.TESTIMONIALS_MANAGEMENT, label: 'Systeme de temoignage', group: 'Gestion site web' },
  { key: TAB_KEYS.FORM_MANAGEMENT, label: 'Gestion des formulaires', group: 'Gestion site web' },
  { key: TAB_KEYS.FORM_REQUESTS, label: 'Traitement des formulaires', group: 'Gestion utilisateurs' },
  { key: TAB_KEYS.SIGNUP, label: 'Demandes utilisateurs', group: 'Gestion utilisateurs' },
  { key: TAB_KEYS.EVENT_REQUESTS, label: 'Demandes evenements', group: 'Gestion utilisateurs' },
  { key: TAB_KEYS.CONTACT_MESSAGES, label: 'Messages de contact', group: 'Gestion utilisateurs' },
  { key: TAB_KEYS.DOCUMENT_REQUESTS, label: 'Demandes de documents', group: 'Gestion utilisateurs' },
  { key: TAB_KEYS.HOUSE_MANAGEMENT_CHANGE, label: 'Demandes changement de residence', group: 'Gestion site web' },
  { key: TAB_KEYS.HOUSE_MANAGEMENT_LEAVE, label: 'Demandes depart de residence', group: 'Gestion site web' },
  { key: TAB_KEYS.USERS_NOTIFICATIONS, label: 'Notifications utilisateurs', group: 'Gestion utilisateurs' },
  { key: TAB_KEYS.ADMIN_NOTIFICATIONS_INBOX, label: 'Notifications admin (recues)', group: 'Gestion utilisateurs' },
  { key: TAB_KEYS.ADMIN_NOTIFICATIONS_OUTBOX, label: 'Notifications admin (envoyees)', group: 'Gestion utilisateurs' },
  { key: TAB_KEYS.PAYMENTS, label: 'Paiements', group: 'Gestion utilisateurs' },
  { key: TAB_KEYS.INVOICES, label: 'Factures', group: 'Gestion utilisateurs' },
]);

const normalizeFormRequestTabLabel = (value) => String(value || '')
  .trim()
  .replace(/[\s_]+/g, ' ')
  .replace(/\b\w/g, (letter) => letter.toUpperCase());

const normalizeFormRequestTabKey = (value) => String(value || '')
  .trim()
  .toLowerCase()
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .replace(/[^a-z0-9]+/g, '-')
  .replace(/^-+|-+$/g, '');

const FORM_REQUESTS_ALL_KEY = 'all';

const FORM_REQUESTS_EXCLUDED_CHILD_KEYS = new Set([
  normalizeFormRequestTabKey('all'),
  normalizeFormRequestTabKey('registration-form'),
]);

const ADMIN_TAB_PRIORITY = [
  TAB_KEYS.USERS,
  TAB_KEYS.TEAM,
  TAB_KEYS.EVENTS,
  TAB_KEYS.CAROUSEL,
  TAB_KEYS.FOOTER,
  TAB_KEYS.TESTIMONIALS_MANAGEMENT,
  TAB_KEYS.FORM_MANAGEMENT,
  TAB_KEYS.FORM_REQUESTS,
  TAB_KEYS.EVENT_REQUESTS,
  TAB_KEYS.CONTACT_MESSAGES,
  TAB_KEYS.SIGNUP,
  TAB_KEYS.DOCUMENT_REQUESTS,
  TAB_KEYS.HOUSE_MANAGEMENT_CHANGE,
  TAB_KEYS.HOUSE_MANAGEMENT_LEAVE,
  TAB_KEYS.USERS_NOTIFICATIONS,
  TAB_KEYS.ADMIN_NOTIFICATIONS_INBOX,
  TAB_KEYS.ADMIN_NOTIFICATIONS_OUTBOX,
  TAB_KEYS.NOTIFICATION_MANAGEMENT,
  TAB_KEYS.EMAIL_TEMPLATES,
  TAB_KEYS.PAYMENTS,
  TAB_KEYS.INVOICES,
];

const getCanonicalAdminRoleName = (roleName) => {
  const normalized = String(roleName || '').trim().toLowerCase();
  return Object.values(ADMIN_ROLE_NAMES).find((value) => String(value || '').trim().toLowerCase() === normalized) || null;
};

const getAllowedAdminTabs = (roles = [], explicitAllowedTabs = []) => {
  const allowed = new Set(
    Array.isArray(explicitAllowedTabs)
      ? explicitAllowedTabs.filter(Boolean)
      : []
  );

  const roleNames = Array.isArray(roles) ? roles : [];
  roleNames.forEach((roleName) => {
    const canonicalRole = getCanonicalAdminRoleName(roleName);
    if (!canonicalRole) return;

    if (canonicalRole === ADMIN_ROLE_NAMES.SUPER_ADMIN || canonicalRole === ADMIN_ROLE_NAMES.ADMINISTRATIVE_ADMIN) {
      ADMIN_TAB_PRIORITY.forEach((tabKey) => allowed.add(tabKey));
      return;
    }

    if (canonicalRole === ADMIN_ROLE_NAMES.USER_MANAGEMENT_ADMIN) {
      [
        TAB_KEYS.USERS,
        TAB_KEYS.FORM_REQUESTS,
        TAB_KEYS.SIGNUP,
        TAB_KEYS.EVENT_REQUESTS,
        TAB_KEYS.CONTACT_MESSAGES,
        TAB_KEYS.DOCUMENT_REQUESTS,
        TAB_KEYS.USERS_NOTIFICATIONS,
        TAB_KEYS.ADMIN_NOTIFICATIONS_INBOX,
        TAB_KEYS.ADMIN_NOTIFICATIONS_OUTBOX,
        TAB_KEYS.PAYMENTS,
        TAB_KEYS.INVOICES,
        TAB_KEYS.EMAIL_TEMPLATES,
      ].forEach((tabKey) => allowed.add(tabKey));
      return;
    }

    if (canonicalRole === ADMIN_ROLE_NAMES.WEBSITE_MANAGEMENT_ADMIN) {
      [
        TAB_KEYS.EVENTS,
        TAB_KEYS.CAROUSEL,
        TAB_KEYS.FOOTER,
        TAB_KEYS.TESTIMONIALS_MANAGEMENT,
        TAB_KEYS.FORM_MANAGEMENT,
        TAB_KEYS.HOUSE_MANAGEMENT_CHANGE,
        TAB_KEYS.HOUSE_MANAGEMENT_LEAVE,
        TAB_KEYS.ADMIN_NOTIFICATIONS_INBOX,
        TAB_KEYS.ADMIN_NOTIFICATIONS_OUTBOX,
      ].forEach((tabKey) => allowed.add(tabKey));
    }
  });

  return new Set(ADMIN_TAB_PRIORITY.filter((tabKey) => allowed.has(tabKey)));
};

const isSignupRequestSeen = (request) => Number(request?.is_seen ?? request?.admin_seen ?? 0) === 1;

const getSignupRequestNeedsDecisionAttention = (request) => {
  const status = String(request?.status || '').trim().toLowerCase();
  if (!status) return !isSignupRequestSeen(request);
  if (['resolved', 'closed', 'rejected', 'accepted', 'disapproved'].includes(status)) return false;
  return !isSignupRequestSeen(request);
};

const mapSubmissionResponsesToLabels = (responses, schema) => {
  const normalizedResponses = expandSubmissionResponseAliases(responses);
  const ordered = [];
  const seen = new Set();

  flattenFormSchemaFields(schema).forEach(({ key, label, type }) => {
    if (!Object.prototype.hasOwnProperty.call(normalizedResponses, key)) return;
    ordered.push({ key, label, value: normalizedResponses[key], type });
    seen.add(key);
  });

  Object.entries(normalizedResponses).forEach(([key, value]) => {
    if (seen.has(key)) return;
    ordered.push({ key, label: key, value, type: 'text' });
  });

  return ordered;
};

const flattenFormSchemaFields = (schema) => {
  const fields = [];

  const visit = (node) => {
    if (!node) return;
    if (Array.isArray(node)) {
      node.forEach(visit);
      return;
    }
    if (typeof node !== 'object') return;

    if (node.key) {
      fields.push({
        key: String(node.key),
        label: String(node.label || node.title || node.name || node.key),
        type: String(node.type || 'text'),
      });
    }

    visit(node.fields);
    visit(node.children);
    visit(node.rows);
    visit(node.pages);
    visit(node.sections);
    visit(node.elements);
  };

  visit(schema);
  return fields;
};

const EXCEL_MIME_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
const IMPORT_REGISTRATION_FORM_SLUG = 'import-users-registration';
const IMPORT_REGISTRATION_FORM_NAME = 'Inscription temporaire (import)';
const IMPORT_REGISTRATION_REQUEST_TAB = 'Importation utilisateurs';

const normalizeImportLabel = (value) => String(value || '')
  .trim()
  .toLowerCase()
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '');

const IMPORT_REGISTRATION_MATCHERS = new Set(
  [
    IMPORT_REGISTRATION_FORM_SLUG,
    IMPORT_REGISTRATION_FORM_NAME,
    IMPORT_REGISTRATION_REQUEST_TAB,
  ].map(normalizeImportLabel)
);

const createAdditionalDataDraft = () => ({
  contract_type: '',
  lot_number: '',
  city_of_residence: '',
  residence_name: '',
  residence_address: '',
  studio_number: '',
  area_sqm: '',
  move_in_date: '',
  move_out_date: '',
  payment_amount: '',
  ch: '',
});

const createInvoiceDraftFromRequest = (request) => ({
  invoice_type: request?.invoice_type || 'first_time',
  invoice_period_from: toDateInputValueInParis(request?.invoice_period_from || ''),
  invoice_period_to: toDateInputValueInParis(request?.invoice_period_to || ''),
  invoice_rent_charges: request?.invoice_rent_charges ?? '',
  invoice_deposit: request?.invoice_deposit ?? '',
  invoice_dossier_fee: request?.invoice_dossier_fee ?? '',
  invoice_insurance: request?.invoice_insurance ?? '',
  residence_name: request?.residence_name || '',
  city_of_residence: request?.city_of_residence || '',
});

const getInvoiceStatusMeta = (statusRaw) => {
  const status = String(statusRaw || 'not_sent').trim().toLowerCase();
  if (status === 'paid') {
    return { label: 'Payee', className: 'invoice-status-paid' };
  }
  if (status === 'sent') {
    return { label: 'Envoyee', className: 'invoice-status-sent' };
  }
  return { label: 'Non envoyee', className: 'invoice-status-not-sent' };
};

const resolveHousingRequestId = (request) => {
  const candidate = request?.id
    ?? request?.ticket_id
    ?? request?.ticketId
    ?? request?.request_id
    ?? request?.application_id;
  const parsed = Number(candidate);
  return Number.isFinite(parsed) ? parsed : null;
};

const isImportRegistrationSubmission = (submission) => {
  const candidates = [
    submission?.form_slug,
    submission?.slug,
    submission?.form_name,
    submission?.request_tab_name,
  ];

  return candidates.some((value) => IMPORT_REGISTRATION_MATCHERS.has(normalizeImportLabel(value)));
};

const isHousingFormSubmission = (submission) => {
  const candidates = [
    submission?.request_tab_name,
    submission?.form_name,
    submission?.form_slug,
    submission?.slug,
  ];

  return candidates.some((value) => FORM_REQUESTS_EXCLUDED_CHILD_KEYS.has(normalizeFormRequestTabKey(value)));
};

const expandSubmissionResponseAliases = (responses) => {
  const source = responses && typeof responses === 'object' ? responses : {};
  const expanded = {};

  Object.entries(source).forEach(([rawKey, value]) => {
    const key = String(rawKey || '').trim();
    if (!key) return;
    if (!Object.prototype.hasOwnProperty.call(expanded, key)) expanded[key] = value;

    const underscoreKey = key.replace(/-/g, '_');
    if (!Object.prototype.hasOwnProperty.call(expanded, underscoreKey)) expanded[underscoreKey] = value;

    const hyphenKey = key.replace(/_/g, '-');
    if (!Object.prototype.hasOwnProperty.call(expanded, hyphenKey)) expanded[hyphenKey] = value;
  });

  return expanded;
};

const pickSubmissionResponseValue = (responses, keys = []) => {
  for (const key of keys) {
    if (!key) continue;
    if (Object.prototype.hasOwnProperty.call(responses, key)) {
      const value = responses[key];
      if (value !== null && value !== undefined && String(value).trim() !== '') return value;
    }
  }
  return null;
};

const normalizeSubmissionKey = (value) => String(value || '').toLowerCase().replace(/[^a-z0-9]+/g, '');

const pickSubmissionResponseValueByPattern = (responses, { mustInclude = [], anyOf = [] } = {}) => {
  const entries = Object.entries(responses || {});
  for (const [key, value] of entries) {
    const normalizedKey = normalizeSubmissionKey(key);
    if (!normalizedKey) continue;
    const matchesMustInclude = mustInclude.every((part) => normalizedKey.includes(normalizeSubmissionKey(part)));
    const matchesAnyOf = anyOf.length === 0 || anyOf.some((part) => normalizedKey.includes(normalizeSubmissionKey(part)));
    if (matchesMustInclude && matchesAnyOf && value !== null && value !== undefined && String(value).trim() !== '') {
      return value;
    }
  }
  return null;
};

const parsePossibleJsonArray = (value) => {
  if (Array.isArray(value)) return value;
  if (typeof value !== 'string') return value;

  const trimmed = value.trim();
  if (!trimmed.startsWith('[')) return value;

  try {
    const parsed = JSON.parse(trimmed);
    return Array.isArray(parsed) ? parsed : value;
  } catch (error) {
    return value;
  }
};

const getSubmissionResponses = (submission) => {
  if (submission?.responses && typeof submission.responses === 'object') {
    return expandSubmissionResponseAliases(submission.responses);
  }
  if (typeof submission?.responses_json === 'string') {
    try {
      const parsed = JSON.parse(submission.responses_json);
      if (parsed && typeof parsed === 'object') return expandSubmissionResponseAliases(parsed);
    } catch (error) {
      return {};
    }
  }
  return {};
};

const getSubmissionFileRows = (responses, schema) => {
  const fileFields = flattenFormSchemaFields(schema).filter((field) => field.type === 'file');
  const rows = [];

  fileFields.forEach((field) => {
    const raw = responses?.[field.key];
    const values = Array.isArray(raw) ? raw : raw ? [raw] : [];
    values
      .map((value) => String(value || '').trim())
      .filter(Boolean)
      .forEach((url, index) => {
        rows.push({
          id: `${field.key}-${index}`,
          label: field.label || field.key,
          url,
        });
      });
  });

  return rows;
};

const buildImportHousingRequestFromSubmission = (submission) => {
  const responses = getSubmissionResponses(submission);
  const firstName = String(pickSubmissionResponseValue(responses, ['first_name', 'first-name', 'firstname', 'firstName']) || '').trim();
  const lastName = String(pickSubmissionResponseValue(responses, ['last_name', 'last-name', 'lastname', 'lastName']) || '').trim();
  const applicantEmail = String(
    pickSubmissionResponseValue(responses, ['email', 'applicant_email', 'applicant-email'])
    || submission?.applicant_email
    || ''
  ).trim();

  return {
    __source: 'form-submission',
    id: submission?.id ? `form-submission-${submission.id}` : null,
    form_submission_id: submission?.id || null,
    request_tab_name: submission?.request_tab_name || submission?.form_slug || null,
    // Applicant / student fields
    first_name: firstName || null,
    last_name: lastName || null,
    email: applicantEmail || null,
    date_of_birth: pickSubmissionResponseValue(responses, ['date_of_birth', 'dob', 'date-of-birth', 'birth_date']) || null,
    passport_number: pickSubmissionResponseValue(responses, ['passport', 'passport_number', 'passport-number']) || null,
    student_address: pickSubmissionResponseValue(responses, ['student_address', 'address', 'student-address']) || null,
    student_city: pickSubmissionResponseValue(responses, ['student_city', 'city', 'student-city']) || null,
    start_date: pickSubmissionResponseValue(responses, ['start_date', 'start-date', 'move_in_date', 'move-in-date']) || null,
    contract_duration: pickSubmissionResponseValue(responses, ['contract_duration', 'contract-duration', 'duration']) || null,
    desired_city: pickSubmissionResponseValue(responses, ['desired_city', 'desired-city', 'ville_souhaitee']) || null,
    max_budget: pickSubmissionResponseValue(responses, ['max_budget', 'budget_max', 'budget']) || null,
    desired_residences: pickSubmissionResponseValue(responses, ['desired_residences', 'desired-residences', 'residences_souhaitees']) || null,
    studio_number: pickSubmissionResponseValue(responses, ['studio_number', 'studio-number', 'room']) || null,
    area_sqm: pickSubmissionResponseValue(responses, ['area_sqm', 'area', 'area-sqm']) || null,
    // Guardian fields
    guardian_first_name: pickSubmissionResponseValue(responses, ['guardian_first_name', 'guardian-first-name', 'parent_first_name', 'parent-first-name', 'parent_first', 'first_name_parent']) || null,
    guardian_last_name: pickSubmissionResponseValue(responses, ['guardian_last_name', 'guardian-last-name', 'parent_last_name', 'parent-last-name', 'parent_last', 'last_name_parent']) || null,
    guardian_phone: pickSubmissionResponseValue(responses, ['parent_phone', 'parent-phone', 'guardian_phone', 'guardian-phone', 'guardian_phone_number', 'phone_parent', 'parent_phone_number'])
      || pickSubmissionResponseValueByPattern(responses, { mustInclude: ['phone'], anyOf: ['parent', 'guardian'] })
      || null,
    guardian_email: pickSubmissionResponseValue(responses, ['guardian_email', 'guardian-email', 'parent_email', 'parent-email', 'email_parent', 'parent_mail', 'guardian_mail'])
      || pickSubmissionResponseValueByPattern(responses, { mustInclude: ['email'], anyOf: ['parent', 'guardian'] })
      || null,
    guardian_datebirth: pickSubmissionResponseValue(responses, ['parent_datebirth', 'parent-datebirth', 'guardian_datebirth', 'guardian-datebirth', 'parent_dob', 'guardian_dob', 'datebirth_parent'])
      || pickSubmissionResponseValueByPattern(responses, { mustInclude: ['birth'], anyOf: ['parent', 'guardian'] })
      || pickSubmissionResponseValueByPattern(responses, { mustInclude: ['dob'], anyOf: ['parent', 'guardian'] })
      || null,
    guardian_job: pickSubmissionResponseValue(responses, ['parent_job', 'parent-job', 'guardian_job', 'guardian-job', 'parent_profession', 'guardian_profession']) || null,
    guardian_company: pickSubmissionResponseValue(responses, ['parent_company', 'parent-company', 'guardian_company', 'guardian-company', 'parent_societe', 'guardian_societe']) || null,
    guardian_address: pickSubmissionResponseValue(responses, ['parent_address', 'parent-address', 'guardian_address', 'guardian-address', 'address_parent']) || null,
    guardian_city: pickSubmissionResponseValue(responses, ['parent_city', 'parent-city', 'guardian_city', 'guardian-city', 'city_parent']) || null,
    reference_number: submission?.reference_number || null,
    status: submission?.status || submission?.ticket_status || 'pending',
    created_at: submission?.created_at || submission?.ticket_created_at || null,
    is_seen: submission?.is_seen,
  };
};

const sanitizeExportFilenamePart = (value, fallback = 'export') => {
  const normalized = String(value || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9_-]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');

  return normalized || fallback;
};

const formatExportTimestamp = (date = new Date()) => {
  const pad = (value) => String(value).padStart(2, '0');
  return `${date.getFullYear()}${pad(date.getMonth() + 1)}${pad(date.getDate())}_${pad(date.getHours())}${pad(date.getMinutes())}${pad(date.getSeconds())}`;
};

const sanitizeExcelSheetName = (value, fallback = 'Export') => {
  const normalized = String(value || '')
    .split('')
    .map((char) => ('\\/?*[]:'.includes(char) ? ' ' : char))
    .join('')
    .trim()
    .slice(0, 31);
  return normalized || fallback;
};

const normalizeExcelCellValue = (value) => {
  if (value === null || value === undefined) return '';
  if (value instanceof Date) return value.toISOString();
  if (Array.isArray(value)) {
    return value.map((item) => {
      if (item && typeof item === 'object') return JSON.stringify(item);
      return String(item ?? '');
    }).join(', ');
  }
  if (typeof value === 'object') {
    try {
      return JSON.stringify(value);
    } catch (error) {
      return String(value);
    }
  }
  return String(value);
};

const createExcelBlobFromRows = (rows, sheetName) => {
  const safeRows = Array.isArray(rows) ? rows : [];
  const allKeys = [];
  const keySeen = new Set();

  safeRows.forEach((row) => {
    if (!row || typeof row !== 'object' || Array.isArray(row)) return;
    Object.keys(row).forEach((key) => {
      if (keySeen.has(key)) return;
      keySeen.add(key);
      allKeys.push(key);
    });
  });

  const normalizedRows = safeRows.map((row) => {
    const source = row && typeof row === 'object' && !Array.isArray(row) ? row : { value: row };

    if (!allKeys.length) {
      return { value: normalizeExcelCellValue(source.value) };
    }

    const normalized = {};
    allKeys.forEach((key) => {
      normalized[key] = normalizeExcelCellValue(source[key]);
    });
    return normalized;
  });

  const worksheet = normalizedRows.length
    ? XLSX.utils.json_to_sheet(normalizedRows)
    : XLSX.utils.aoa_to_sheet([['No rows to export']]);

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, sanitizeExcelSheetName(sheetName, 'Export'));
  const buffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
  return new Blob([buffer], { type: EXCEL_MIME_TYPE });
};

const downloadExcelBlobFallback = (blob, fileName) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => window.URL.revokeObjectURL(url), 0);
};

const saveExcelBlobWithPicker = async (blob, fileName) => {
  if (typeof window === 'undefined') {
    return { saved: false, method: 'unavailable' };
  }

  if (typeof window.showDirectoryPicker === 'function') {
    try {
      const directoryHandle = await window.showDirectoryPicker({ mode: 'readwrite' });
      const fileHandle = await directoryHandle.getFileHandle(fileName, { create: true });
      const writable = await fileHandle.createWritable();
      await writable.write(blob);
      await writable.close();
      return { saved: true, method: 'directory' };
    } catch (error) {
      if (error?.name === 'AbortError') return { saved: false, method: 'directory', aborted: true };
      console.error('Failed to save Excel file with directory picker:', error);
    }
  }

  if (typeof window.showSaveFilePicker === 'function') {
    try {
      const fileHandle = await window.showSaveFilePicker({
        suggestedName: fileName,
        types: [
          {
            description: 'Excel workbook',
            accept: {
              [EXCEL_MIME_TYPE]: ['.xlsx'],
            },
          },
        ],
      });
      const writable = await fileHandle.createWritable();
      await writable.write(blob);
      await writable.close();
      return { saved: true, method: 'save-file' };
    } catch (error) {
      if (error?.name === 'AbortError') return { saved: false, method: 'save-file', aborted: true };
      console.error('Failed to save Excel file with save picker:', error);
    }
  }

  downloadExcelBlobFallback(blob, fileName);
  return { saved: true, method: 'download' };
};

const AdminDashboard = () => {
  const { user, logout } = useAuth();
  const [activeTab, setActiveTab] = useState('users');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const explicitAllowedTabs = useMemo(() => {
    if (Array.isArray(user?.allowed_tabs)) return user.allowed_tabs;
    if (Array.isArray(user?.allowedTabs)) return user.allowedTabs;
    return null;
  }, [user?.allowed_tabs, user?.allowedTabs]);
  const allowedTabs = useMemo(
    () => getAllowedAdminTabs(user?.roles, explicitAllowedTabs),
    [user?.roles, explicitAllowedTabs]
  );
  const canonicalAdminRoles = useMemo(() => {
    const roles = new Set();
    (Array.isArray(user?.roles) ? user.roles : []).forEach((roleName) => {
      const canonicalRole = getCanonicalAdminRoleName(roleName);
      if (canonicalRole) roles.add(canonicalRole);
    });
    return roles;
  }, [user?.roles]);
  const canAccessTab = useCallback((tabKey) => allowedTabs.has(tabKey), [allowedTabs]);
  const useIndexedSidebar =
    canonicalAdminRoles.has(ADMIN_ROLE_NAMES.ADMINISTRATIVE_ADMIN) ||
    canonicalAdminRoles.has(ADMIN_ROLE_NAMES.SUPER_ADMIN);
  const hasUserManagementSidebarItems =
    canAccessTab(TAB_KEYS.USERS) ||
    canAccessTab(TAB_KEYS.SIGNUP) ||
    canAccessTab(TAB_KEYS.FORM_REQUESTS) ||
    canAccessTab(TAB_KEYS.EVENT_REQUESTS) ||
    canAccessTab(TAB_KEYS.CONTACT_MESSAGES) ||
    canAccessTab(TAB_KEYS.DOCUMENT_REQUESTS) ||
    canAccessTab(TAB_KEYS.USERS_NOTIFICATIONS) ||
    canAccessTab(TAB_KEYS.ADMIN_NOTIFICATIONS_INBOX) ||
    canAccessTab(TAB_KEYS.ADMIN_NOTIFICATIONS_OUTBOX) ||
    canAccessTab(TAB_KEYS.NOTIFICATION_MANAGEMENT) ||
    canAccessTab(TAB_KEYS.PAYMENTS) ||
    canAccessTab(TAB_KEYS.INVOICES);
  const hasWebsiteManagementSidebarItems =
    canAccessTab(TAB_KEYS.CAROUSEL) ||
    canAccessTab(TAB_KEYS.EVENTS) ||
    canAccessTab(TAB_KEYS.FOOTER) ||
    canAccessTab(TAB_KEYS.TESTIMONIALS_MANAGEMENT) ||
    canAccessTab(TAB_KEYS.FORM_MANAGEMENT) ||
    canAccessTab(TAB_KEYS.HOUSE_MANAGEMENT_CHANGE) ||
    canAccessTab(TAB_KEYS.HOUSE_MANAGEMENT_LEAVE);
  const defaultAccessibleTab = useMemo(
    () => ADMIN_TAB_PRIORITY.find((tabKey) => allowedTabs.has(tabKey)) || 'settings',
    [allowedTabs]
  );

  const [adminNotificationsNavExpanded, setAdminNotificationsNavExpanded] = useState(false);
  const [houseManagementNavExpanded, setHouseManagementNavExpanded] = useState(false);
  const [formRequestsNavExpanded, setFormRequestsNavExpanded] = useState(false);
  const [activeFormRequestTabName, setActiveFormRequestTabName] = useState(FORM_REQUESTS_ALL_KEY);

  const prevTabRef = useRef(activeTab);
  const adminDocReqFileInputsRef = useRef({});

  const nodeRefsByKeyRef = useRef(new Map());
  const tabNodeRef = useMemo(() => {
    const existing = nodeRefsByKeyRef.current.get(activeTab);
    if (existing) return existing;
    const created = React.createRef();
    nodeRefsByKeyRef.current.set(activeTab, created);
    return created;
  }, [activeTab]);

  const handleTabChange = (nextTab) => {
    if (nextTab !== 'settings' && !allowedTabs.has(nextTab)) {
      return;
    }

    // Prevent animation if clicking already active tab
    if (nextTab === activeTab) {
      return;
    }

    setMobileMenuOpen(false);
    
    if (nextTab === 'users') {
      setUserManagementExpandedUserId(null);
    }
    if (nextTab !== 'events') {
      setEventsExpandedEventId(null);
      setShowCategoryManager(false);
    }
    if (nextTab !== 'signup') {
      setShowSignupRequestModal(false);
      setSelectedRequest(null);
      setAttachments([]);
          setMandateLatest(null);
    }
    if (nextTab !== TAB_KEYS.SIGNUP && nextTab !== TAB_KEYS.FORM_REQUESTS) {
      setShowImportSubmissionModal(false);
      setImportSubmission(null);
      setImportSubmissionAttachments([]);
    }
    if (nextTab !== 'document-requests') {
      setShowAdminDocumentRequestModal(false);
      setAdminDocumentRequestSelected(null);
    }
    if (nextTab !== TAB_KEYS.HOUSE_MANAGEMENT_CHANGE && nextTab !== TAB_KEYS.HOUSE_MANAGEMENT_LEAVE) {
      setHouseManagementExpandedRequestId(null);
    }
    if (nextTab !== TAB_KEYS.CONTACT_MESSAGES) {
      setContactMessagesExpandedId(null);
    }
    if (nextTab !== TAB_KEYS.CAROUSEL) {
      setShowCarouselSlideForm(false);
      setCarouselExpandedSlideId(null);
      resetCarouselForm();
    }
    if (nextTab !== TAB_KEYS.FOOTER) {
      setShowFooterTextEditor(false);
      setShowFooterContactEditor(false);
      setFooterContactEditingId(null);
      setFooterContactForm({ label: '', href: '', image_url: '' });
      setFooterContactImageFile(null);
    }
    if (nextTab !== TAB_KEYS.TEAM) {
      setShowTeamCreatePanel(false);
    }
    if (nextTab !== TAB_KEYS.FORM_MANAGEMENT) {
      setAdminFormsExpandedId(null);
      setShowFormBuilderModal(false);
      setFormBuilderInitialValue(null);
    }
    if (nextTab !== TAB_KEYS.FORM_REQUESTS) {
      setAdminFormSubmissionExpandedId(null);
    }
    if (nextTab !== TAB_KEYS.NOTIFICATION_MANAGEMENT) {
      setNotificationManagementModalCategory(null);
      setNotificationManagementRecipients([]);
      setNotificationManagementRecipientsLoading(false);
      setNotificationManagementToggleSavingByUserId({});
    }
    if (nextTab !== TAB_KEYS.TESTIMONIALS_MANAGEMENT) {
      setShowTestimonialsRegionInfoModal(false);
      setShowReviewFormConfigModal(false);
      setReviewFormConfigEditingField(null);
    }
    if (nextTab !== TAB_KEYS.INVOICES) {
      setShowInvoiceTemplateModal(false);
    }
    prevTabRef.current = nextTab;
    setActiveTab(nextTab);
  };

  // Admin settings: password change
  const [adminPasswordOtp, setAdminPasswordOtp] = useState('');
  const [adminPasswordResetToken, setAdminPasswordResetToken] = useState('');
  const [adminPasswordNewValue, setAdminPasswordNewValue] = useState('');
  const [adminPasswordConfirmValue, setAdminPasswordConfirmValue] = useState('');
  const [adminPasswordCooldown, setAdminPasswordCooldown] = useState(0);
  const [adminPasswordBusyState, setAdminPasswordBusyState] = useState('');
  const [adminPasswordMessage, setAdminPasswordMessage] = useState('');
  const [adminPasswordError, setAdminPasswordError] = useState('');
  const adminPasswordCooldownTimerRef = useRef(null);

  // Admin password change functions
  const PASSWORD_OTP_COOLDOWN_SECONDS = 30;

  useEffect(() => {
    if (adminPasswordCooldown <= 0) {
      if (adminPasswordCooldownTimerRef.current) {
        clearInterval(adminPasswordCooldownTimerRef.current);
        adminPasswordCooldownTimerRef.current = null;
      }
      return;
    }
    adminPasswordCooldownTimerRef.current = setInterval(() => {
      setAdminPasswordCooldown((prev) => {
        if (prev <= 1) {
          clearInterval(adminPasswordCooldownTimerRef.current);
          adminPasswordCooldownTimerRef.current = null;
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => {
      if (adminPasswordCooldownTimerRef.current) {
        clearInterval(adminPasswordCooldownTimerRef.current);
        adminPasswordCooldownTimerRef.current = null;
      }
    };
  }, [adminPasswordCooldown]);

  const sendAdminPasswordResetOtp = useCallback(async () => {
    const email = String(user?.email || '').trim();
    if (!email) {
      setAdminPasswordError('Adresse email introuvable pour envoyer le code.');
      return;
    }
    if (adminPasswordCooldown > 0) return;
    try {
      setAdminPasswordBusyState('sending');
      setAdminPasswordMessage('');
      setAdminPasswordError('');
      await api.auth.requestPasswordReset(email);
      setAdminPasswordMessage('Code envoye. Verifiez votre email puis saisissez le code ci-dessous.');
      setAdminPasswordCooldown(PASSWORD_OTP_COOLDOWN_SECONDS);
      setAdminPasswordResetToken('');
      setAdminPasswordNewValue('');
      setAdminPasswordConfirmValue('');
    } catch (error) {
      setAdminPasswordError(error?.response?.data?.error || "Impossible d'envoyer le code pour le moment.");
    } finally {
      setAdminPasswordBusyState('');
    }
  }, [adminPasswordCooldown, user?.email]);

  const verifyAdminPasswordResetCode = useCallback(async () => {
    const email = String(user?.email || '').trim();
    const code = String(adminPasswordOtp || '').trim();
    if (!email) {
      setAdminPasswordError('Adresse email introuvable.');
      return;
    }
    if (!/^\d{6}$/.test(code)) {
      setAdminPasswordError('Le code doit contenir 6 chiffres.');
      return;
    }
    try {
      setAdminPasswordBusyState('verifying');
      setAdminPasswordMessage('');
      setAdminPasswordError('');
      const response = await api.auth.verifyPasswordResetOtp(email, code);
      const token = String(response?.data?.resetToken || '').trim();
      if (!token) {
        setAdminPasswordError('Code invalide. Veuillez reessayer.');
        return;
      }
      setAdminPasswordResetToken(token);
      setAdminPasswordMessage('Code confirme. Saisissez maintenant votre nouveau mot de passe.');
    } catch (error) {
      setAdminPasswordError(error?.response?.data?.error || 'Code invalide. Veuillez reessayer.');
    } finally {
      setAdminPasswordBusyState('');
    }
  }, [adminPasswordOtp, user?.email]);

  const submitAdminNewPassword = useCallback(async () => {
    const email = String(user?.email || '').trim();
    const nextPassword = String(adminPasswordNewValue || '');
    const confirmPassword = String(adminPasswordConfirmValue || '');
    if (!email) {
      setAdminPasswordError('Adresse email introuvable.');
      return;
    }
    if (!adminPasswordResetToken) {
      setAdminPasswordError("Veuillez d'abord verifier le code envoye par email.");
      return;
    }
    if (nextPassword.length < 8) {
      setAdminPasswordError('Le mot de passe doit contenir au moins 8 caracteres.');
      return;
    }
    if (nextPassword !== confirmPassword) {
      setAdminPasswordError('Les mots de passe ne correspondent pas.');
      return;
    }
    try {
      setAdminPasswordBusyState('saving');
      setAdminPasswordMessage('');
      setAdminPasswordError('');
      await api.auth.resetPassword(email, adminPasswordResetToken, nextPassword);
      setAdminPasswordMessage('Mot de passe mis a jour avec succes.');
      setAdminPasswordOtp('');
      setAdminPasswordResetToken('');
      setAdminPasswordNewValue('');
      setAdminPasswordConfirmValue('');
    } catch (error) {
      setAdminPasswordError(error?.response?.data?.error || 'Impossible de mettre a jour le mot de passe.');
    } finally {
      setAdminPasswordBusyState('');
    }
  }, [adminPasswordConfirmValue, adminPasswordNewValue, adminPasswordResetToken, user?.email]);

  const cancelAdminPasswordPanel = useCallback(() => {
    setAdminPasswordOtp('');
    setAdminPasswordResetToken('');
    setAdminPasswordNewValue('');
    setAdminPasswordConfirmValue('');
    setAdminPasswordMessage('');
    setAdminPasswordError('');
    setAdminPasswordBusyState('');
  }, []);

  useEffect(() => {
    if (activeTab === 'settings') return;
    if (allowedTabs.has(activeTab)) return;
    setActiveTab(defaultAccessibleTab);
  }, [activeTab, allowedTabs, defaultAccessibleTab]);

  useEffect(() => {
    const hasAdminNotificationTabs =
      allowedTabs.has(TAB_KEYS.ADMIN_NOTIFICATIONS_INBOX) ||
      allowedTabs.has(TAB_KEYS.ADMIN_NOTIFICATIONS_OUTBOX);
    if (!hasAdminNotificationTabs && adminNotificationsNavExpanded) {
      setAdminNotificationsNavExpanded(false);
    }
  }, [allowedTabs, adminNotificationsNavExpanded]);

  useEffect(() => {
    const hasHouseManagementTabs =
      allowedTabs.has(TAB_KEYS.HOUSE_MANAGEMENT_CHANGE) ||
      allowedTabs.has(TAB_KEYS.HOUSE_MANAGEMENT_LEAVE);
    if (!hasHouseManagementTabs && houseManagementNavExpanded) {
      setHouseManagementNavExpanded(false);
    }
  }, [allowedTabs, houseManagementNavExpanded]);

  useEffect(() => {
    if (!allowedTabs.has(TAB_KEYS.FORM_REQUESTS) && formRequestsNavExpanded) {
      setFormRequestsNavExpanded(false);
    }
  }, [allowedTabs, formRequestsNavExpanded]);

  const [events, setEvents] = useState([]);
  const [eventsPage, setEventsPage] = useState(1);
  const [eventsPageSize] = useState(7);
  const [eventsExpandedEventId, setEventsExpandedEventId] = useState(null);
  const [showCreateEvent, setShowCreateEvent] = useState(false);
  const [eventsEditMode, setEventsEditMode] = useState(false);
  const [eventsEditData, setEventsEditData] = useState(null);
  const [eventsSaving, setEventsSaving] = useState(false);
  const [eventCategories, setEventCategories] = useState([]);
  const [eventCategoriesLoading, setEventCategoriesLoading] = useState(false);
  const [showCategoryManager, setShowCategoryManager] = useState(false);
  const [newEventCategory, setNewEventCategory] = useState({ label: '', slug: '' });
  const [creatingEventCategory, setCreatingEventCategory] = useState(false);
  const [eventCategoryDraftById, setEventCategoryDraftById] = useState({});
  const [eventCategorySavingId, setEventCategorySavingId] = useState(null);
  const [eventCategoryDeletingId, setEventCategoryDeletingId] = useState(null);
  const EVENT_TITLE_MAX = 60;
  const EVENT_BODY_MAX = 260;
  const [newEvent, setNewEvent] = useState({
    headline: '',
    body: '',
    date: '',
    category: '',
    event_type: 'normal',
    premium_payment_required: false,
    social_url: '',
    registration_link: '',
    image: null,
  });
  const [creatingEvent, setCreatingEvent] = useState(false);
  const [carouselSlidesAdmin, setCarouselSlidesAdmin] = useState([]);
  const [carouselSlidesLoading, setCarouselSlidesLoading] = useState(false);
  const [carouselSlidesPage, setCarouselSlidesPage] = useState(1);
  const [carouselSlidesPageSize] = useState(4);
  const [carouselSlideSaving, setCarouselSlideSaving] = useState(false);
  const [carouselSlideDeletingId, setCarouselSlideDeletingId] = useState(null);
  const [carouselSlideReorderingId, setCarouselSlideReorderingId] = useState(null);
  const [carouselSlideEditingId, setCarouselSlideEditingId] = useState(null);
  const [carouselExpandedSlideId, setCarouselExpandedSlideId] = useState(null);
  const [showCarouselSlideForm, setShowCarouselSlideForm] = useState(false);
  const [carouselForm, setCarouselForm] = useState({
    title: '',
    body: '',
    image: null,
  });
  const [footerLoading, setFooterLoading] = useState(false);
  const [footerSavingText, setFooterSavingText] = useState(false);
  const [footerTextDraft, setFooterTextDraft] = useState('');
  const [footerContacts, setFooterContacts] = useState([]);
  const [showFooterTextEditor, setShowFooterTextEditor] = useState(false);
  const [showFooterContactEditor, setShowFooterContactEditor] = useState(false);
  const [footerContactForm, setFooterContactForm] = useState({ label: '', href: '', image_url: '' });
  const [footerContactImageFile, setFooterContactImageFile] = useState(null);
  const [footerContactEditingId, setFooterContactEditingId] = useState(null);
  const [footerContactSaving, setFooterContactSaving] = useState(false);
  const [footerContactPublishingId, setFooterContactPublishingId] = useState(null);
  const [footerContactDeletingId, setFooterContactDeletingId] = useState(null);
  const [signupRequests, setSignupRequests] = useState([]);
  const [signupPage, setSignupPage] = useState(1);
  const [signupPageSize] = useState(7);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showSignupRequestModal, setShowSignupRequestModal] = useState(false);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [invoiceDraft, setInvoiceDraft] = useState({
    invoice_type: 'first_time',
    invoice_period_from: '',
    invoice_period_to: '',
    invoice_rent_charges: '',
    invoice_deposit: '',
    invoice_dossier_fee: '',
    invoice_insurance: '',
  });
  const [invoiceSending, setInvoiceSending] = useState(false);
  const [invoiceMarkingPaid, setInvoiceMarkingPaid] = useState(false);
  const [showInvoiceTemplateModal, setShowInvoiceTemplateModal] = useState(false);
  const [invoiceTemplateDrafts, setInvoiceTemplateDrafts] = useState([]);
  const [invoiceTemplateLoading, setInvoiceTemplateLoading] = useState(false);
  const [invoiceTemplateSaving, setInvoiceTemplateSaving] = useState(false);
  const [invoiceTemplateError, setInvoiceTemplateError] = useState('');
  const [emailTemplateCategories, setEmailTemplateCategories] = useState([]);
  const [emailTemplateDrafts, setEmailTemplateDrafts] = useState([]);
  const [emailTemplateLoading, setEmailTemplateLoading] = useState(false);
  const [emailTemplateSaving, setEmailTemplateSaving] = useState(false);
  const [emailTemplateError, setEmailTemplateError] = useState('');
  const [emailTemplateModalTemplate, setEmailTemplateModalTemplate] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [mandateLatest, setMandateLatest] = useState(null);
  const [mandateLoading, setMandateLoading] = useState(false);
  const [showBroadcastModal, setShowBroadcastModal] = useState(false);
  const [broadcastTitle, setBroadcastTitle] = useState('');
  const [broadcastBody, setBroadcastBody] = useState('');
  const [broadcastRecipientScope, setBroadcastRecipientScope] = useState('all');
  const [broadcastTargetUsers, setBroadcastTargetUsers] = useState([]);
  const [broadcastSelectedUserIds, setBroadcastSelectedUserIds] = useState([]);
  const [broadcastSearchQuery, setBroadcastSearchQuery] = useState('');
  const [broadcastUsersLoading, setBroadcastUsersLoading] = useState(false);
  const [broadcastSending, setBroadcastSending] = useState(false);

  const [sentNotificationCampaigns, setSentNotificationCampaigns] = useState([]);
  const [sentNotificationsLoading, setSentNotificationsLoading] = useState(false);
  const [notificationsExpandedCampaignId, setNotificationsExpandedCampaignId] = useState(null);
  const [sentNotificationsPage, setSentNotificationsPage] = useState(1);
  const [sentNotificationsPageSize] = useState(7);

  const [adminInboxNotifications, setAdminInboxNotifications] = useState([]);
  const [adminInboxLoading, setAdminInboxLoading] = useState(false);
  const [adminInboxExpandedNotificationId, setAdminInboxExpandedNotificationId] = useState(null);
  const [adminInboxClearingSeen, setAdminInboxClearingSeen] = useState(false);
  const [adminInboxPage, setAdminInboxPage] = useState(1);
  const [adminInboxPageSize] = useState(7);
  const adminInboxUnreadCount = useMemo(
    () => (adminInboxNotifications || []).reduce((count, row) => count + (Number(row?.is_read) === 1 ? 0 : 1), 0),
    [adminInboxNotifications]
  );

  const [adminOutboxCampaigns, setAdminOutboxCampaigns] = useState([]);
  const [adminOutboxLoading, setAdminOutboxLoading] = useState(false);
  const [adminOutboxExpandedCampaignId, setAdminOutboxExpandedCampaignId] = useState(null);
  const [adminOutboxPage, setAdminOutboxPage] = useState(1);
  const [adminOutboxPageSize] = useState(7);

  const [adminNotificationCategories, setAdminNotificationCategories] = useState([]);
  const [adminNotificationCategoriesLoading, setAdminNotificationCategoriesLoading] = useState(false);
  const [notificationManagementModalCategory, setNotificationManagementModalCategory] = useState(null);
  const [notificationManagementRecipients, setNotificationManagementRecipients] = useState([]);
  const [notificationManagementRecipientsLoading, setNotificationManagementRecipientsLoading] = useState(false);
  const [notificationManagementToggleSavingByUserId, setNotificationManagementToggleSavingByUserId] = useState({});
  const [notificationManagementCustomEmails, setNotificationManagementCustomEmails] = useState([]);
  const [notificationManagementCustomEmailsLoading, setNotificationManagementCustomEmailsLoading] = useState(false);
  const [notificationManagementToggleSavingByCustomEmailId, setNotificationManagementToggleSavingByCustomEmailId] = useState({});

  // Custom notification emails
  const [customNotificationEmails, setCustomNotificationEmails] = useState([]);
  const [customEmailsLoading, setCustomEmailsLoading] = useState(false);
  const [showCustomEmailModal, setShowCustomEmailModal] = useState(false);
  const [customEmailInput, setCustomEmailInput] = useState('');
  const [customEmailAdding, setCustomEmailAdding] = useState(false);
  const [customEmailError, setCustomEmailError] = useState('');
  const [customEmailDeleting, setCustomEmailDeleting] = useState({});

  const [houseManagementRequests, setHouseManagementRequests] = useState([]);
  const [houseManagementLoading, setHouseManagementLoading] = useState(false);
  const [houseManagementExpandedRequestId, setHouseManagementExpandedRequestId] = useState(null);
  const [houseManagementPage, setHouseManagementPage] = useState(1);
  const [houseManagementPageSize] = useState(7);
  const [houseChangeUnreadCount, setHouseChangeUnreadCount] = useState(0);
  const [houseLeaveUnreadCount, setHouseLeaveUnreadCount] = useState(0);

  const [adminInvoices, setAdminInvoices] = useState([]);
  const [adminInvoicesLoading, setAdminInvoicesLoading] = useState(false);
  const [adminInvoicesExpandedId, setAdminInvoicesExpandedId] = useState(null);
  const [adminInvoiceDetailsById, setAdminInvoiceDetailsById] = useState({});
  const [adminInvoicesPage, setAdminInvoicesPage] = useState(1);
  const [adminInvoicesPageSize] = useState(7);

  // Document requests (admin)
  const [adminDocumentRequests, setAdminDocumentRequests] = useState([]);
  const [adminDocumentRequestsLoading, setAdminDocumentRequestsLoading] = useState(false);
  const [adminDocumentRequestsPage, setAdminDocumentRequestsPage] = useState(1);
  const [adminDocumentRequestsPageSize] = useState(7);
  const [adminDocumentRequestSelected, setAdminDocumentRequestSelected] = useState(null);
  const [showAdminDocumentRequestModal, setShowAdminDocumentRequestModal] = useState(false);
  const [adminDocReqUploading, setAdminDocReqUploading] = useState({});
  const [adminDocReqConfirming, setAdminDocReqConfirming] = useState(false);

  const [showAdminBroadcastModal, setShowAdminBroadcastModal] = useState(false);
  const [adminBroadcastTitle, setAdminBroadcastTitle] = useState('');
  const [adminBroadcastBody, setAdminBroadcastBody] = useState('');
  const [adminBroadcastRecipientScope, setAdminBroadcastRecipientScope] = useState('all');
  const [adminRecipientUsers, setAdminRecipientUsers] = useState([]);
  const [adminBroadcastSelectedUserIds, setAdminBroadcastSelectedUserIds] = useState([]);
  const [adminBroadcastSearchQuery, setAdminBroadcastSearchQuery] = useState('');
  const [adminDestinatairesLoading, setAdminDestinatairesLoading] = useState(false);
  const [adminBroadcastSending, setAdminBroadcastSending] = useState(false);
  const [userManagementRows, setUserManagementRows] = useState([]);
  const [userManagementMeta, setUserManagementMeta] = useState({
    q: '',
    page: 1,
    pageSize: 7,
    total: 0,
    totalPages: 1,
    sortBy: 'name',
    sortDir: 'asc',
  });
  const [userManagementRechercher, setUserManagementRechercher] = useState('');
  const [userManagementSort, setUserManagementSort] = useState({ sortBy: 'name', sortDir: 'asc' });
  const [userManagementPage, setUserManagementPage] = useState(1);
  const [userManagementPageSize] = useState(7);
  const [userManagementExpandedUserId, setUserManagementExpandedUserId] = useState(null);
  const [userManagementLoading, setUserManagementLoading] = useState(false);
  const [adminPaiementsRows, setAdminPaiementsRows] = useState([]);
  const [adminPaiementsMeta, setAdminPaiementsMeta] = useState({ page: 1, pageSize: 7, total: 0, totalPages: 1, status: 'pending' });
  const [adminPaiementsPage, setAdminPaiementsPage] = useState(1);
  const [adminPaiementsPageSize] = useState(7);
  const [adminPaiementsLoading, setAdminPaiementsLoading] = useState(false);
  const [docusignGuardianEmail, setDocusignGuardianEmail] = useState('');
  const [docusignAdminEmail, setDocusignAdminEmail] = useState('');
  const [sendingDocusign, setSendingDocusign] = useState(false);
  const [docusignLatest, setDocusignLatest] = useState(null);
  const [docusignLatestLoading, setDocusignLatestLoading] = useState(false);
  const [mandateLatestLoading, setMandateLatestLoading] = useState(false);
  const [importMandateLatest, setImportMandateLatest] = useState(null);
  const [importMandateLoading, setImportMandateLoading] = useState(false);
  const [importMandateLatestLoading, setImportMandateLatestLoading] = useState(false);
  // Renewals modal state
  const [showRenewalsModal, setShowRenewalsModal] = useState(false);
  const [renewalsLoading, setRenewalsLoading] = useState(false);
  const [renewalsRows, setRenewalsRows] = useState([]);
  const [renewalInputs, setRenewalInputs] = useState({}); // { [userId]: { ch, payment_amount, guardianEmail, adminEmail, sending } }
  
  // Event registrations state
  const [eventRegistrations, setEventRegistrations] = useState([]);
  const [eventRegistrationsPage, setEventRegistrationsPage] = useState(1);
  const [eventRegistrationsPageSize] = useState(7);
  const [eventRegistrationsExpandedId, setEventRegistrationsExpandedId] = useState(null);
  const [eventRegistrationsLoading, setEventRegistrationsLoading] = useState(false);
  const [eventRegistrationProcessing, setEventRegistrationProcessing] = useState(false);

  // Dynamic forms (admin)
  const [adminForms, setAdminForms] = useState([]);
  const [adminFormsLoading, setAdminFormsLoading] = useState(false);
  const [adminFormsPage, setAdminFormsPage] = useState(1);
  const [adminFormsPageSize] = useState(7);
  const [adminFormsExpandedId, setAdminFormsExpandedId] = useState(null);
  const [showFormBuilderModal, setShowFormBuilderModal] = useState(false);
  const [formBuilderMode, setFormBuilderMode] = useState('create');
  const [formBuilderInitialValue, setFormBuilderInitialValue] = useState(null);
  const [formBuilderSaving, setFormBuilderSaving] = useState(false);
  const [formPublishingId, setFormPublishingId] = useState(null);
  const [formDeletingId, setFormDeletingId] = useState(null);
  const [showCitiesManager, setShowCitiesManager] = useState(false);

  // Dynamic form submissions (admin)
  const [adminFormSubmissions, setAdminFormSubmissions] = useState([]);
  const [adminFormSubmissionsLoading, setAdminFormSubmissionsLoading] = useState(false);
  const [adminFormSubmissionsPage, setAdminFormSubmissionsPage] = useState(1);
  const [adminFormSubmissionsPageSize] = useState(7);
  const [adminFormSubmissionExpandedId, setAdminFormSubmissionExpandedId] = useState(null);
  const [adminFormSubmissionReviewingId, setAdminFormSubmissionReviewingId] = useState(null);
  const [adminFormSubmissionNotesById, setAdminFormSubmissionNotesById] = useState({});
  const [signupExporting, setSignupExporting] = useState(false);
  const [formSubmissionsExporting, setFormSubmissionsExporting] = useState(false);
  const adminFormSubmissionsUnreadCount = useMemo(
    () => (adminFormSubmissions || []).reduce((count, row) => count + (Number(row?.is_seen) === 1 ? 0 : 1), 0),
    [adminFormSubmissions]
  );

  const formRequestChildTabs = useMemo(() => {
    const unique = new Set();
    const result = [];

    const register = (value) => {
      const label = normalizeFormRequestTabLabel(value);
      if (!label) return;
      const key = normalizeFormRequestTabKey(label);
      if (FORM_REQUESTS_EXCLUDED_CHILD_KEYS.has(key)) return;
      if (unique.has(key)) return;
      unique.add(key);
      result.push(label);
    };

    (adminForms || []).forEach((row) => register(row?.request_tab_name));
    (adminFormSubmissions || []).forEach((row) => register(row?.request_tab_name));

    const hasImportTab = (adminForms || []).some((row) => isImportRegistrationSubmission(row))
      || (adminFormSubmissions || []).some((row) => isImportRegistrationSubmission(row));
    if (hasImportTab) register(IMPORT_REGISTRATION_REQUEST_TAB);

    return result;
  }, [adminForms, adminFormSubmissions]);

  const adminFormUnreadByTab = useMemo(() => {
    const counts = {};
    (adminFormSubmissions || []).forEach((row) => {
      if (Number(row?.is_seen) === 1) return;
      const key = normalizeFormRequestTabKey(row?.request_tab_name);
      if (!key) return;
      counts[key] = (counts[key] || 0) + 1;
    });
    return counts;
  }, [adminFormSubmissions]);

  const isImportFormTabSelected = useMemo(
    () => normalizeImportLabel(activeFormRequestTabName) === normalizeImportLabel(IMPORT_REGISTRATION_REQUEST_TAB),
    [activeFormRequestTabName]
  );

  const filteredAdminFormSubmissions = useMemo(() => {
    if (activeFormRequestTabName === FORM_REQUESTS_ALL_KEY) {
      return adminFormSubmissions || [];
    }

    if (isImportFormTabSelected) {
      return (adminFormSubmissions || []).filter((row) => isImportRegistrationSubmission(row));
    }

    const selectedKey = normalizeFormRequestTabKey(activeFormRequestTabName);
    return (adminFormSubmissions || []).filter((row) => normalizeFormRequestTabKey(row?.request_tab_name) === selectedKey);
  }, [activeFormRequestTabName, adminFormSubmissions, isImportFormTabSelected]);

  const selectedFormRequestTabLabel = useMemo(
    () => (activeFormRequestTabName === FORM_REQUESTS_ALL_KEY ? 'Tous les formulaires' : normalizeFormRequestTabLabel(activeFormRequestTabName)),
    [activeFormRequestTabName]
  );

  const importFormSubmissions = useMemo(
    () => (adminFormSubmissions || []).filter((row) => isImportRegistrationSubmission(row)),
    [adminFormSubmissions]
  );

  const housingFormSubmissions = useMemo(
    () => (adminFormSubmissions || []).filter((row) => isHousingFormSubmission(row)),
    [adminFormSubmissions]
  );

  const housingFormRequests = useMemo(
    () => housingFormSubmissions.map((row) => buildImportHousingRequestFromSubmission(row)),
    [housingFormSubmissions]
  );

  const housingTableRequests = useMemo(() => {
    const merged = [...(signupRequests || []), ...housingFormRequests];
    const filtered = merged.filter((row) => {
      const status = String(row?.status || '').trim().toLowerCase();
      return !['accepted', 'approved', 'resolved'].includes(status);
    });
    return filtered.sort((a, b) => {
      const aTime = Date.parse(a?.created_at || a?.ticket_created_at || a?.updated_at || 0) || 0;
      const bTime = Date.parse(b?.created_at || b?.ticket_created_at || b?.updated_at || 0) || 0;
      return bTime - aTime;
    });
  }, [housingFormRequests, signupRequests]);

  const isHousingRowSeen = (request) => {
    if (request?.__source === 'form-submission') {
      return Number(request?.is_seen) === 1;
    }
    return isSignupRequestSeen(request);
  };

  const signupUnreadCount = useMemo(
    () => (housingTableRequests || []).reduce((count, request) => count + (isHousingRowSeen(request) ? 0 : 1), 0),
    [housingTableRequests]
  );

  const exportRowsToExcel = useCallback(async ({ rows, fileName, sheetName, emptyMessage }) => {
    if (!Array.isArray(rows) || rows.length === 0) {
      window.alert(emptyMessage || 'No rows found to export.');
      return;
    }

    const blob = createExcelBlobFromRows(rows, sheetName);
    const result = await saveExcelBlobWithPicker(blob, fileName);
    if (result?.saved) {
      if (result.method === 'directory') {
        window.alert(`Excel export complete: ${fileName}`);
      } else if (result.method === 'save-file') {
        window.alert(`Excel export complete: ${fileName}`);
      } else {
        window.alert(`Excel export downloaded: ${fileName}`);
      }
    }
  }, []);

  const exportSignupRequestsToExcel = useCallback(async () => {
    try {
      setSignupExporting(true);

      const response = await api.signup.getRequests();
      const rows = Array.isArray(response?.data) ? response.data : [];

      const exportRows = rows.map((request) => {
        const fullName = `${request?.first_name || ''} ${request?.last_name || ''}`.trim() || '(Sans nom)';
        const needsAttention = getSignupRequestNeedsDecisionAttention(request);
        const seen = isSignupRequestSeen(request);

        // attachments may be returned as JSON (from DB) or null
        let attachmentsList = [];
        try {
          if (request?.attachments) {
            if (typeof request.attachments === 'string') {
              attachmentsList = JSON.parse(request.attachments);
            } else if (Array.isArray(request.attachments)) {
              attachmentsList = request.attachments;
            }
          }
        } catch (err) {
          // fallback: empty
          attachmentsList = [];
        }

        const attachmentUrls = (attachmentsList || []).map((a) => resolveMediaUrl(a.file_url)).filter(Boolean);

        return {
          ...request,
          applicant_name: fullName,
          seen_status: needsAttention ? 'Action requise' : (seen ? 'Vu' : 'Non lu'),
          needs_final_decision: needsAttention ? 'Yes' : 'No',
          submitted_at_local: formatDateTimeInParis(request?.created_at, { fallback: '-' }),
          attachments: attachmentUrls.join(' | '),
        };
      });

      const fileName = `demandes_utilisateurs_${formatExportTimestamp()}.xlsx`;
      await exportRowsToExcel({
        rows: exportRows,
        fileName,
        sheetName: 'Demandes utilisateurs',
        emptyMessage: 'Aucune demande utilisateur a exporter.',
      });
    } catch (error) {
      console.error('Failed to export user requests:', error);
      window.alert(error?.response?.data?.error || 'Export failed.');
    } finally {
      setSignupExporting(false);
    }
  }, [exportRowsToExcel]);

  const exportFormSubmissionsToExcel = useCallback(async () => {
    try {
      setFormSubmissionsExporting(true);

      const response = await api.forms.listAdminSubmissions({ limit: 5000 });
      const allRows = Array.isArray(response?.data?.data) ? response.data.data : [];
      const selectedKey = normalizeFormRequestTabKey(activeFormRequestTabName);

      const filteredRows = activeFormRequestTabName === FORM_REQUESTS_ALL_KEY
        ? allRows
        : allRows.filter((row) => normalizeFormRequestTabKey(row?.request_tab_name) === selectedKey);

      const exportRows = filteredRows.map((submission) => ({
        ...submission,
        seen_status: Number(submission?.is_seen) === 1 ? 'Seen' : 'Unseen',
        submitted_at_local: formatDateTimeInParis(submission?.created_at, { fallback: '-' }),
      }));

      const fileLabel = activeFormRequestTabName === FORM_REQUESTS_ALL_KEY
        ? 'all_forms'
        : sanitizeExportFilenamePart(activeFormRequestTabName, 'form_requests');

      const fileName = `form_requests_${fileLabel}_${formatExportTimestamp()}.xlsx`;
      const sheetName = activeFormRequestTabName === FORM_REQUESTS_ALL_KEY
        ? 'All form requests'
        : selectedFormRequestTabLabel;

      await exportRowsToExcel({
        rows: exportRows,
        fileName,
        sheetName,
        emptyMessage: 'No submissions found to export for this form tab.',
      });
    } catch (error) {
      console.error('Failed to export form submissions:', error);
      window.alert(error?.response?.data?.error || 'Export failed.');
    } finally {
      setFormSubmissionsExporting(false);
    }
  }, [activeFormRequestTabName, exportRowsToExcel, selectedFormRequestTabLabel]);

  // Contact messages state
  const [contactMessages, setContactMessages] = useState([]);
  const [contactMessagesLoading, setContactMessagesLoading] = useState(false);
  const [contactMessagesPage, setContactMessagesPage] = useState(1);
  const [contactMessagesPageSize] = useState(7);
  const [contactMessagesExpandedId, setContactMessagesExpandedId] = useState(null);
  const [contactMessagesRespondingId, setContactMessagesRespondingId] = useState(null);
  const [contactResponseDrafts, setContactResponseDrafts] = useState({});
  const [contactMessagesUnreadCount, setContactMessagesUnreadCount] = useState(0);
  
  // Gestion de equipe state
  const [teamEmployees, setTeamEmployees] = useState([]);
  const [teamPage, setTeamPage] = useState(1);
  const [teamPageSize] = useState(7);
  const [newEmployee, setNewEmployee] = useState({
    first_name: '',
    last_name: '',
    email: '',
    role: DEFAULT_TEAM_ROLE_OPTIONS[0],
  });
  const [teamRoleOptions, setTeamRoleOptions] = useState([...DEFAULT_TEAM_ROLE_OPTIONS]);
  const [teamSaving, setTeamSaving] = useState(false);
  const [teamDeletingId, setTeamDeletingId] = useState(null);
  const [showTeamCreatePanel, setShowTeamCreatePanel] = useState(false);
  const [teamRoleTypes, setTeamRoleTypes] = useState([]);
  const [teamRoleTabCatalog, setTeamRoleTabCatalog] = useState([...ASSIGNABLE_ADMIN_ROLE_TAB_OPTIONS]);
  const [teamRoleTypeLoading, setTeamRoleTypeLoading] = useState(false);
  const [teamRoleTypeSaving, setTeamRoleTypeSaving] = useState(false);
  const [showTeamRoleTypePanel, setShowTeamRoleTypePanel] = useState(false);
  const [editingTeamRoleTypeId, setEditingTeamRoleTypeId] = useState(null);
  const [teamRoleTypeForm, setTeamRoleTypeForm] = useState({
    name: '',
    tabs: [],
  });
  const [additionalData, setAdditionalData] = useState(createAdditionalDataDraft);
  const [importSubmission, setImportSubmission] = useState(null);
  const [showImportSubmissionModal, setShowImportSubmissionModal] = useState(false);
  const [importAdditionalData, setImportAdditionalData] = useState(createAdditionalDataDraft);
  const [importSubmissionAttachments, setImportSubmissionAttachments] = useState([]);
  const [testimonialsRows, setTestimonialsRows] = useState([]);
  const [testimonialsLoading, setTestimonialsLoading] = useState(false);
  const [testimonialsQuery, setTestimonialsQuery] = useState('');
  const [testimonialsDeleteId, setTestimonialsDeleteId] = useState(null);
  const [selectedReviewDetail, setSelectedReviewDetail] = useState(null);
  const [showTestimonialsRegionInfoModal, setShowTestimonialsRegionInfoModal] = useState(false);
  const [testimonialsRegionInfoMap, setTestimonialsRegionInfoMap] = useState({});
  const [testimonialsRegionInfoDrafts, setTestimonialsRegionInfoDrafts] = useState({});
  const [testimonialsRegionInfoSavingKey, setTestimonialsRegionInfoSavingKey] = useState('');
  const [testimonialsRegionInfoLoading, setTestimonialsRegionInfoLoading] = useState(false);
  const [showReviewFormConfigModal, setShowReviewFormConfigModal] = useState(false);
  const [reviewFormFields, setReviewFormFields] = useState([]);
  const [reviewFormConfigLoading, setReviewFormConfigLoading] = useState(false);
  const [reviewFormConfigSaving, setReviewFormConfigSaving] = useState(false);
  const [reviewFormConfigEditingField, setReviewFormConfigEditingField] = useState(null);
  const [reviewFormConfigDraft, setReviewFormConfigDraft] = useState({ id: '', type: 'text', label: '', placeholder: '', required: false, options: [] });
  const [draftOptionInput, setDraftOptionInput] = useState('');
  const [residencesCatalog, setResidencesCatalog] = useState([]);
  const [residencesCatalogLoading, setResidencesCatalogLoading] = useState(false);
  const [citiesCatalog, setCitiesCatalog] = useState([]);
  const [citiesCatalogLoading, setCitiesCatalogLoading] = useState(false);

  const testimonialsRegionGroups = useMemo(
    () => {
      const grouped = new Map();

      Object.entries(DEPARTMENT_TO_CITIES).forEach(([department]) => {
        const resolvedDepartment = resolveRegionName(department);
        const isParisMergedRegion = PARIS_MERGED_DEPARTMENTS.has(normalizeName(resolvedDepartment));
        const displayCity = isParisMergedRegion ? 'Paris' : (getDepartmentDisplayCities(resolvedDepartment)[0] || resolvedDepartment);
        const key = toRegionKey(displayCity || resolvedDepartment);

        if (!grouped.has(key)) {
          grouped.set(key, {
            department: displayCity || resolvedDepartment,
            key,
            cities: [displayCity || resolvedDepartment],
          });
        }
      });

      return [...grouped.values()]
        .sort((a, b) => a.department.localeCompare(b.department, 'fr'));
    },
    []
  );

  const residencesByCity = useMemo(() => {
    const grouped = new Map();

    const addResidenceOption = (city, name) => {
      const cityName = String(city || '').trim();
      const residenceName = String(name || '').trim();
      if (!cityName || !residenceName) return;

      if (!grouped.has(cityName)) {
        grouped.set(cityName, new Set());
      }

      grouped.get(cityName).add(residenceName);
    };

    Object.entries(catalogResidencesByCity || {}).forEach(([city, names]) => {
      (Array.isArray(names) ? names : []).forEach((name) => addResidenceOption(city, name));
    });

    Object.values(parisResidencesBySubRegion || {}).forEach((names) => {
      (Array.isArray(names) ? names : []).forEach((name) => addResidenceOption('Paris', name));
    });

    for (const row of residencesCatalog || []) {
      addResidenceOption(row?.city, row?.name);
    }

    return Object.fromEntries(
      [...grouped.entries()]
        .sort(([cityA], [cityB]) => cityA.localeCompare(cityB, 'fr'))
        .map(([city, names]) => [city, [...names].sort((nameA, nameB) => nameA.localeCompare(nameB, 'fr'))])
    );
  }, [residencesCatalog]);

  const residenceCities = useMemo(() => {
    const fromCatalog = Object.keys(residencesByCity || {});
    const fromSharedCatalog = Array.isArray(catalogCities) ? catalogCities : [];
    const fromApi = Array.isArray(citiesCatalog) ? citiesCatalog : [];
    const combined = Array.from(new Set([...fromCatalog, ...fromSharedCatalog, ...fromApi]));
    return combined.sort((a, b) => String(a).localeCompare(b, 'fr'));
  }, [residencesByCity, citiesCatalog]);
  const additionalResidenceOptions = useMemo(
    () => residencesByCity[additionalData.city_of_residence] || [],
    [additionalData.city_of_residence, residencesByCity]
  );
  const importResidenceOptions = useMemo(
    () => residencesByCity[importAdditionalData.city_of_residence] || [],
    [importAdditionalData.city_of_residence, residencesByCity]
  );
  const invoiceResidenceOptions = useMemo(
    () => residencesByCity[invoiceDraft?.city_of_residence] || [],
    [invoiceDraft?.city_of_residence, residencesByCity]
  );

  useEffect(() => {
    let cancelled = false;

    const loadResidencesCatalog = async () => {
      try {
        setResidencesCatalogLoading(true);
        const response = await api.residences.getResidences();
        const rows = Array.isArray(response?.data?.data) ? response.data.data : [];
        if (!cancelled) {
          setResidencesCatalog(rows.filter((row) => Number(row?.active ?? 1) !== 0));
        }
      } catch (error) {
        if (!cancelled) {
          console.error('Failed to load residences catalog:', error);
          setResidencesCatalog([]);
        }
      } finally {
        if (!cancelled) {
          setResidencesCatalogLoading(false);
        }
      }
    };

    void loadResidencesCatalog();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    const loadCities = async () => {
      try {
        setCitiesCatalogLoading(true);
        const resp = await api.residences.getCities();
        const rows = Array.isArray(resp?.data?.data) ? resp.data.data : Array.isArray(resp?.data) ? resp.data : [];
        if (!cancelled) {
          setCitiesCatalog((rows || []).filter(Boolean));
        }
      } catch (error) {
        if (!cancelled) {
          console.error('Failed to load residence cities list:', error);
          setCitiesCatalog([]);
        }
      } finally {
        if (!cancelled) setCitiesCatalogLoading(false);
      }
    };

    void loadCities();

    return () => { cancelled = true; };
  }, []);

  const fetchAdminPaiements = useCallback(async (signal) => {
    try {
      setAdminPaiementsLoading(true);
      const response = await api.gocardless.getAdminPaiements({
        page: adminPaiementsPage,
        pageSize: adminPaiementsPageSize,
        status: 'pending',
      }, { signal });
      const payload = response?.data || {};
      setAdminPaiementsRows(payload.data || []);
      setAdminPaiementsMeta(payload.meta || { page: adminPaiementsPage, pageSize: adminPaiementsPageSize, total: (payload.data || []).length, totalPages: 1, status: 'pending' });
    } catch (e) {
      if (e?.name === 'CanceledError' || e?.code === 'ERR_CANCELED') return;
      console.error('Failed to fetch admin payments:', e);
      // Keep existing rows to avoid distracting UI flicker.
      setAdminPaiementsMeta((prev) => ({ ...prev, total: prev.total ?? 0, totalPages: prev.totalPages ?? 1 }));
    } finally {
      setAdminPaiementsLoading(false);
    }
  }, [adminPaiementsPage, adminPaiementsPageSize]);

  useEffect(() => {
    if (activeTab !== 'payments' || !allowedTabs.has(TAB_KEYS.PAYMENTS)) return;
    const controller = new AbortController();
    fetchAdminPaiements(controller.signal);
    return () => controller.abort();
  }, [activeTab, allowedTabs, fetchAdminPaiements]);

  useEffect(() => {
    if (activeTab !== 'payments' || !allowedTabs.has(TAB_KEYS.PAYMENTS)) return;
    const refreshMs = 5 * 60 * 1000;
    const interval = setInterval(() => {
      fetchAdminPaiements();
    }, refreshMs);
    return () => clearInterval(interval);
  }, [activeTab, allowedTabs, fetchAdminPaiements]);

  const formatCurrencyEUR = (amount) => {
    const n = Number(amount);
    if (!Number.isFinite(n)) return '-';
    return `EUR${n.toFixed(2)}`;
  };

  const formatMoney = (amount, currency) => {
    const n = Number(amount);
    if (!Number.isFinite(n)) return '-';
    const cur = String(currency || '').toUpperCase();
    const symbol = cur === 'EUR' ? 'EUR' : cur === 'GBP' ? 'GBP' : `${cur} `;
    return `${symbol}${n.toFixed(2)}`;
  };

  const buildInvoiceImprimerHtml = (invoice) => {
    if (!invoice) return '';
    const esc = (v) => String(v ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    const items = Array.isArray(invoice.items) ? invoice.items : [];
    const rows = items
      .map((it) => {
        const label = esc(it.label);
        const amt = esc(formatMoney(it.amount, invoice.currency));
        return `<tr><td>${label}</td><td style="text-align:right">${amt}</td></tr>`;
      })
      .join('');

    const tenantName = esc(`${invoice.first_name || ''} ${invoice.last_name || ''}`.trim() || invoice.email || '');
    const tenantAddress = esc([invoice.residence_address, invoice.city_of_residence].filter(Boolean).join(', '));
    const invoiceNo = esc(invoice.invoice_number);
    const issuedAt = esc(formatDate(invoice.issued_at));
    const paidAt = esc(formatDate(invoice.paid_at));
    const total = esc(formatMoney(invoice.total_amount, invoice.currency));

    return `<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <title>${invoiceNo}</title>
    <style>
      * { box-sizing: border-box; }
      body { font-family: Arial, sans-serif; color: #111; margin: 24px; }
      .header { display:flex; justify-content:space-between; gap:16px; }
      .brand { font-weight: 800; font-size: 18px; }
      .muted { color: #555; font-size: 12px; }
      .meta { margin-top: 18px; display:flex; justify-content:space-between; gap:16px; }
      .box { border: 1px solid #ddd; border-radius: 10px; padding: 14px; }
      h1 { font-size: 20px; margin: 0; }
      table { width:100%; border-collapse: collapse; margin-top: 16px; }
      th, td { padding: 10px 8px; border-bottom: 1px solid #eee; }
      th { text-align:left; font-size: 12px; color:#555; }
      .totalRow td { border-top: 2px solid #111; border-bottom: none; font-weight: 800; }
      @media print { body { margin: 0; } }
    </style>
  </head>
  <body>
    <div class="header">
      <div>
        <div class="brand">ADEEF Portal</div>
        <div class="muted">Facture generee a partir du paiement</div>
      </div>
      <div style="text-align:right">
        <h1>Facture</h1>
        <div class="muted">${invoiceNo}</div>
      </div>
    </div>

    <div class="meta">
      <div class="box" style="flex:1">
        <div class="muted">Facture a</div>
        <div style="font-weight:700; margin-top:6px">${tenantName}</div>
        <div class="muted" style="margin-top:4px">${tenantAddress}</div>
      </div>
      <div class="box" style="width: 260px">
        <div class="muted">Emise le</div>
        <div style="font-weight:700; margin-top:6px">${issuedAt}</div>
        <div class="muted" style="margin-top:10px">Payee le</div>
        <div style="font-weight:700; margin-top:6px">${paidAt}</div>
      </div>
    </div>

    <table>
      <thead>
        <tr>
          <th>Description</th>
          <th style="text-align:right">Montant</th>
        </tr>
      </thead>
      <tbody>
        ${rows}
        <tr class="totalRow">
          <td>Total</td>
          <td style="text-align:right">${total}</td>
        </tr>
      </tbody>
    </table>

    <div class="muted" style="margin-top: 18px">Reference de paiement: ${esc(invoice.gocardless_payment_id || '')}</div>
  </body>
</html>`;
  };

  const formatDate = (value) => {
    return formatDateInParis(value, { fallback: '-' });
  };

  const formatDateTime = (value) => {
    return formatDateTimeInParis(value, { fallback: '-' });
  };

  const formatMonthLabel = (value) => {
    const raw = String(value || '').trim();
    const match = raw.match(/^(\d{4})-(\d{2})(?:-\d{2})?/);
    if (!match) return '';
    const year = Number(match[1]);
    const month = Number(match[2]);
    if (!Number.isFinite(year) || !Number.isFinite(month) || month < 1 || month > 12) return '';
    return formatMonthYearInParis(year, month, 'fr-FR');
  };

  const formatCafPeriodLabel = (fromMonth, tillMonth) => {
    const fromLabel = formatMonthLabel(fromMonth);
    const tillLabel = formatMonthLabel(tillMonth);
    if (!fromLabel || !tillLabel) return '';
    if (fromLabel === tillLabel) return fromLabel;
    return `${fromLabel} -> ${tillLabel}`;
  };

  const getAdminDocumentRequestSummary = (request) => {
    const labels = [];
    if (Number(request?.requested_attestation_de_domicile) === 1) labels.push('Attestation de domicile');
    if (Number(request?.requested_assurance_habitation) === 1) labels.push('Assurance habitation');
    if (Number(request?.requested_quittance_de_loyer) === 1) {
      const period = formatCafPeriodLabel(request?.caf_period_from_month, request?.caf_period_till_month);
      labels.push(period ? `Quittance de loyer (${period})` : 'Quittance de loyer');
    }
    if (Number(request?.requested_attestation_de_loyer_caf) === 1) labels.push('Attestation de loyer CAF');
    if (!labels.length) return 'Aucun document selectionne';
    return labels.join(' • ');
  };

  const formatUserDisplayName = (u) => {
    if (!u) return '-';
    const fullName = `${u.first_name || ''} ${u.last_name || ''}`.trim();
    return fullName || u.email || '(Sans nom)';
  };

  const closeBroadcastModal = useCallback(() => {
    setShowBroadcastModal(false);
    setBroadcastSearchQuery('');
  }, []);

  const openBroadcastModal = useCallback(() => {
    setShowBroadcastModal(true);
    setBroadcastSearchQuery('');
  }, []);

  const closeAdminBroadcastModal = useCallback(() => {
    setShowAdminBroadcastModal(false);
    setAdminBroadcastSearchQuery('');
  }, []);

  const openAdminBroadcastModal = useCallback(() => {
    setShowAdminBroadcastModal(true);
    setAdminBroadcastSearchQuery('');
  }, []);

  const broadcastSelectedUsers = useMemo(() => {
    if (!broadcastSelectedUserIds.length || !broadcastTargetUsers.length) return [];
    const byId = new Map(broadcastTargetUsers.map((u) => [Number(u.id), u]));
    return broadcastSelectedUserIds
      .map((id) => byId.get(Number(id)))
      .filter(Boolean);
  }, [broadcastSelectedUserIds, broadcastTargetUsers]);

  const broadcastSearchResults = useMemo(() => {
    if (broadcastRecipientScope !== 'selected') return [];
    const query = String(broadcastSearchQuery || '').trim().toLowerCase();
    if (!query) return [];
    const selected = new Set(broadcastSelectedUserIds.map((id) => Number(id)));

    return broadcastTargetUsers
      .filter((u) => {
        const id = Number(u.id);
        if (selected.has(id)) return false;

        const fullName = `${u.first_name || ''} ${u.last_name || ''}`.trim().toLowerCase();
        const email = String(u.email || '').toLowerCase();
        return fullName.includes(query) || email.includes(query);
      })
      .slice(0, 8);
  }, [broadcastRecipientScope, broadcastSearchQuery, broadcastSelectedUserIds, broadcastTargetUsers]);

  const adminBroadcastSelectedUsers = useMemo(() => {
    if (!adminBroadcastSelectedUserIds.length || !adminRecipientUsers.length) return [];
    const byId = new Map(adminRecipientUsers.map((u) => [Number(u.id), u]));
    return adminBroadcastSelectedUserIds
      .map((id) => byId.get(Number(id)))
      .filter(Boolean);
  }, [adminBroadcastSelectedUserIds, adminRecipientUsers]);

  const adminBroadcastSearchResults = useMemo(() => {
    if (adminBroadcastRecipientScope !== 'selected') return [];
    const query = String(adminBroadcastSearchQuery || '').trim().toLowerCase();
    if (!query) return [];
    const selected = new Set(adminBroadcastSelectedUserIds.map((id) => Number(id)));

    return adminRecipientUsers
      .filter((u) => {
        const id = Number(u.id);
        if (selected.has(id)) return false;

        const fullName = `${u.first_name || ''} ${u.last_name || ''}`.trim().toLowerCase();
        const email = String(u.email || '').toLowerCase();
        return fullName.includes(query) || email.includes(query);
      })
      .slice(0, 8);
  }, [adminBroadcastRecipientScope, adminBroadcastSearchQuery, adminBroadcastSelectedUserIds, adminRecipientUsers]);

  const addBroadcastRecipient = useCallback((userId) => {
    const nextId = Number(userId);
    if (!Number.isFinite(nextId)) return;
    setBroadcastSelectedUserIds((prev) => (prev.includes(nextId) ? prev : [...prev, nextId]));
    setBroadcastSearchQuery('');
  }, []);

  const removeBroadcastRecipient = useCallback((userId) => {
    const nextId = Number(userId);
    if (!Number.isFinite(nextId)) return;
    setBroadcastSelectedUserIds((prev) => prev.filter((id) => Number(id) !== nextId));
  }, []);

  const addAdminBroadcastRecipient = useCallback((userId) => {
    const nextId = Number(userId);
    if (!Number.isFinite(nextId)) return;
    setAdminBroadcastSelectedUserIds((prev) => (prev.includes(nextId) ? prev : [...prev, nextId]));
    setAdminBroadcastSearchQuery('');
  }, []);

  const removeAdminBroadcastRecipient = useCallback((userId) => {
    const nextId = Number(userId);
    if (!Number.isFinite(nextId)) return;
    setAdminBroadcastSelectedUserIds((prev) => prev.filter((id) => Number(id) !== nextId));
  }, []);

  const getContactStatusMeta = (statusRaw) => {
    const status = String(statusRaw || '').toLowerCase();
    if (status === 'responded') return { label: 'Responded', dotClass: 'read' };
    if (status === 'seen') return { label: 'Seen', dotClass: 'read' };
    return { label: 'New', dotClass: 'unread' };
  };

  const fetchContactMessagesUnreadCount = useCallback(async () => {
    try {
      const response = await api.contactMessages.getAdminUnreadCount();
      const payload = response?.data;
      const total = Number(payload?.data?.total || 0);
      setContactMessagesUnreadCount(Number.isFinite(total) ? total : 0);
    } catch (e) {
      console.error('Failed to fetch contact message unread count:', e);
    }
  }, []);

  const fetchContactMessages = useCallback(async (signal) => {
    try {
      setContactMessagesLoading(true);
      const response = await api.contactMessages.listAdmin({ limit: 500 }, signal ? { signal } : {});
      const payload = response?.data;
      const rows = payload?.ok ? (payload.data || []) : (payload?.data || []);
      setContactMessages(rows);
      setContactMessagesUnreadCount(rows.filter((row) => String(row.status || '').toLowerCase() === 'new').length);
    } catch (e) {
      if (e?.name === 'CanceledError' || e?.code === 'ERR_CANCELED') return;
      console.error('Failed to fetch contact messages:', e);
      setContactMessages([]);
    } finally {
      setContactMessagesLoading(false);
    }
  }, []);

  const applyContactMessageUpdate = useCallback((updatedRow) => {
    if (!updatedRow?.id) return;
    setContactMessages((prev) => {
      const next = prev.map((row) => (row.id === updatedRow.id ? { ...row, ...updatedRow } : row));
      const unread = next.filter((row) => String(row.status || '').toLowerCase() === 'new').length;
      setContactMessagesUnreadCount(unread);
      return next;
    });
  }, []);

  const fetchSentNotifications = useCallback(async (signal) => {
    try {
      setSentNotificationsLoading(true);
      const response = await api.notifications.getSentNotifications({ limit: 200 }, { signal });
      // axios get helper above does' accept config; fallback to direct call if needed.
      const payload = response?.data;
      if (payload?.ok) {
        setSentNotificationCampaigns(payload.data || []);
      } else {
        setSentNotificationCampaigns(payload?.data || []);
      }
    } catch (e) {
      if (e?.name === 'CanceledError' || e?.code === 'ERR_CANCELED') return;
      console.error('Failed to fetch sent notifications:', e);
      setSentNotificationCampaigns([]);
    } finally {
      setSentNotificationsLoading(false);
    }
  }, []);

  const fetchBroadcastTargetUsers = useCallback(async () => {
    try {
      setBroadcastUsersLoading(true);
      const response = await api.users.getAllUsers();
      const rows = response?.data?.data || [];
      const sorted = [...rows].sort((a, b) => {
        const aLast = String(a.last_name || '').toLowerCase();
        const bLast = String(b.last_name || '').toLowerCase();
        const aFirst = String(a.first_name || '').toLowerCase();
        const bFirst = String(b.first_name || '').toLowerCase();
        return aLast.localeCompare(bLast) || aFirst.localeCompare(bFirst) || (a.id - b.id);
      });
      setBroadcastTargetUsers(sorted);
    } catch (e) {
      console.error('Failed to fetch users for notification broadcast:', e);
      setBroadcastTargetUsers([]);
    } finally {
      setBroadcastUsersLoading(false);
    }
  }, []);

  const fetchAdminInboxNotifications = useCallback(async (signal) => {
    try {
      setAdminInboxLoading(true);
      const response = await api.notifications.getNotifications(signal ? { signal } : {});
      const payload = response?.data;
      if (payload?.ok) {
        setAdminInboxNotifications(payload.data || []);
      } else {
        setAdminInboxNotifications(payload?.data || []);
      }
    } catch (e) {
      if (e?.name === 'CanceledError' || e?.code === 'ERR_CANCELED') return;
      console.error('Failed to fetch admin inbox notifications:', e);
      setAdminInboxNotifications([]);
    } finally {
      setAdminInboxLoading(false);
    }
  }, []);

  const fetchSentAdminNotifications = useCallback(async (signal) => {
    try {
      setAdminOutboxLoading(true);
      const response = await api.notifications.getSentAdminNotifications({ limit: 200 }, signal ? { signal } : {});
      const payload = response?.data;
      if (payload?.ok) {
        setAdminOutboxCampaigns(payload.data || []);
      } else {
        setAdminOutboxCampaigns(payload?.data || []);
      }
    } catch (e) {
      if (e?.name === 'CanceledError' || e?.code === 'ERR_CANCELED') return;
      console.error('Failed to fetch admin sent notifications:', e);
      setAdminOutboxCampaigns([]);
    } finally {
      setAdminOutboxLoading(false);
    }
  }, []);

  const fetchAdminNotificationCategories = useCallback(async (signal) => {
    try {
      setAdminNotificationCategoriesLoading(true);
      const response = await api.notifications.getAdminNotificationCategories(signal ? { signal } : {});
      const payload = response?.data;
      const rows = payload?.ok ? (payload.data || []) : (payload?.data || []);
      setAdminNotificationCategories(Array.isArray(rows) ? rows : []);
    } catch (e) {
      if (e?.name === 'CanceledError' || e?.code === 'ERR_CANCELED') return;
      console.error('Failed to fetch admin notification categories:', e);
      setAdminNotificationCategories([]);
    } finally {
      setAdminNotificationCategoriesLoading(false);
    }
  }, []);

  const fetchAdminNotificationCategoryRecipients = useCallback(async (categoryKey, signal) => {
    const safeCategoryKey = String(categoryKey || '').trim();
    if (!safeCategoryKey) return [];

    try {
      setNotificationManagementRecipientsLoading(true);
      const response = await api.notifications.getAdminCategoryRecipients(
        safeCategoryKey,
        signal ? { signal } : {}
      );
      const payload = response?.data;
      const rows = payload?.ok ? (payload.data || []) : (payload?.data || []);
      const sorted = [...(Array.isArray(rows) ? rows : [])].sort((a, b) => {
        const aLast = String(a?.last_name || '').toLowerCase();
        const bLast = String(b?.last_name || '').toLowerCase();
        const aFirst = String(a?.first_name || '').toLowerCase();
        const bFirst = String(b?.first_name || '').toLowerCase();
        return aLast.localeCompare(bLast) || aFirst.localeCompare(bFirst) || (Number(a?.id) - Number(b?.id));
      });
      setNotificationManagementRecipients(sorted);
      return sorted;
    } catch (e) {
      if (e?.name === 'CanceledError' || e?.code === 'ERR_CANCELED') return [];
      console.error('Failed to fetch notification category recipients:', e);
      setNotificationManagementRecipients([]);
      throw e;
    } finally {
      setNotificationManagementRecipientsLoading(false);
    }
  }, []);

  const fetchNotificationManagementCustomEmails = useCallback(async (categoryKey, signal) => {
    const safeCategoryKey = String(categoryKey || '').trim();
    if (!safeCategoryKey) return [];

    try {
      setNotificationManagementCustomEmailsLoading(true);
      const response = await api.notifications.getCustomEmailsForCategory(
        safeCategoryKey,
        signal ? { signal } : {}
      );
      const payload = response?.data;
      const emails = payload?.ok ? (payload.data || []) : (payload?.data || []);
      setNotificationManagementCustomEmails(Array.isArray(emails) ? emails : []);
      return Array.isArray(emails) ? emails : [];
    } catch (e) {
      if (e?.name === 'CanceledError' || e?.code === 'ERR_CANCELED') return [];
      console.error('Failed to fetch custom emails for category:', e);
      setNotificationManagementCustomEmails([]);
      return [];
    } finally {
      setNotificationManagementCustomEmailsLoading(false);
    }
  }, []);

  const openNotificationManagementCategoryModal = useCallback(async (category) => {
    const safeCategory = category && typeof category === 'object' ? category : null;
    const categoryKey = String(safeCategory?.key || '').trim();
    if (!categoryKey) return;

    setNotificationManagementModalCategory(safeCategory);
    setNotificationManagementRecipients([]);
    setNotificationManagementCustomEmails([]);
    setNotificationManagementToggleSavingByUserId({});
    setNotificationManagementToggleSavingByCustomEmailId({});

    try {
      await fetchAdminNotificationCategoryRecipients(categoryKey);
      await fetchNotificationManagementCustomEmails(categoryKey);
    } catch (e) {
      alert(e?.response?.data?.error || 'Failed to load notification category details.');
    }
  }, [fetchAdminNotificationCategoryRecipients, fetchNotificationManagementCustomEmails]);

  const closeNotificationManagementModal = useCallback(() => {
    setNotificationManagementModalCategory(null);
    setNotificationManagementRecipients([]);
    setNotificationManagementRecipientsLoading(false);
    setNotificationManagementToggleSavingByUserId({});
    setNotificationManagementCustomEmails([]);
    setNotificationManagementCustomEmailsLoading(false);
    setNotificationManagementToggleSavingByCustomEmailId({});
  }, []);

  const toggleNotificationCategoryRecipient = useCallback(async (recipient) => {
    const categoryKey = String(notificationManagementModalCategory?.key || '').trim();
    const userId = Number(recipient?.id);
    if (!categoryKey || !Number.isFinite(userId)) return;

    const currentlyEnabled = Boolean(recipient?.is_enabled);
    const nextEnabled = !currentlyEnabled;

    setNotificationManagementToggleSavingByUserId((prev) => ({ ...prev, [userId]: true }));

    try {
      const response = await api.notifications.setAdminCategoryRecipientEnabled({
        categoryKey,
        userId,
        enabled: nextEnabled,
      });
      const payload = response?.data;
      const updated = payload?.ok ? payload.data : payload?.data;
      const resolvedEnabled = updated?.is_enabled ?? nextEnabled;

      setNotificationManagementRecipients((prev) => prev.map((row) => (
        Number(row.id) === userId
          ? { ...row, is_enabled: Boolean(resolvedEnabled) }
          : row
      )));

      await fetchAdminNotificationCategories();
    } catch (e) {
      console.error('Failed to toggle notification recipient:', e);
      alert(e?.response?.data?.error || 'Failed to update recipient preference.');
    } finally {
      setNotificationManagementToggleSavingByUserId((prev) => ({ ...prev, [userId]: false }));
    }
  }, [fetchAdminNotificationCategories, notificationManagementModalCategory?.key]);

  // Custom notification email management functions

  const fetchCustomNotificationEmails = useCallback(async (signal) => {
    try {
      setCustomEmailsLoading(true);
      const response = await api.notifications.getCustomEmails(signal ? { signal } : {});
      const payload = response?.data;
      const emails = payload?.ok ? (payload.data || []) : (payload?.data || []);
      setCustomNotificationEmails(Array.isArray(emails) ? emails : []);
    } catch (e) {
      if (e?.name === 'CanceledError' || e?.code === 'ERR_CANCELED') return;
      console.error('Failed to fetch custom notification emails:', e);
      setCustomNotificationEmails([]);
    } finally {
      setCustomEmailsLoading(false);
    }
  }, []);

  const openCustomEmailModal = useCallback(() => {
    setShowCustomEmailModal(true);
    setCustomEmailInput('');
    setCustomEmailError('');
    fetchCustomNotificationEmails();
  }, [fetchCustomNotificationEmails]);

  const closeCustomEmailModal = useCallback(() => {
    setShowCustomEmailModal(false);
    setCustomEmailInput('');
    setCustomEmailError('');
  }, []);

  const addCustomEmail = useCallback(async () => {
    const email = String(customEmailInput || '').trim();
    if (!email) {
      setCustomEmailError('Please enter an email address.');
      return;
    }
    if (!isValidEmailAddress(email)) {
      setCustomEmailError('Please enter a valid email address containing @.');
      return;
    }

    setCustomEmailAdding(true);
    setCustomEmailError('');

    try {
      const response = await api.notifications.addCustomEmail(email);
      const payload = response?.data;
      if (payload?.ok) {
        setCustomNotificationEmails((prev) => [payload.data, ...prev]);
        setCustomEmailInput('');
        setCustomEmailError('');
      } else {
        setCustomEmailError(payload?.error || 'Failed to add email.');
      }
    } catch (e) {
      const errorMsg = e?.response?.data?.error || e?.message || 'Failed to add email.';
      setCustomEmailError(errorMsg);
      console.error('Failed to add custom notification email:', e);
    } finally {
      setCustomEmailAdding(false);
    }
  }, [customEmailInput]);

  const deleteCustomEmail = useCallback(async (customEmailId) => {
    const safeId = Number(customEmailId);
    if (!Number.isFinite(safeId)) return;

    if (!window.confirm('Are you sure you want to delete this custom email?')) {
      return;
    }

    setCustomEmailDeleting((prev) => ({ ...prev, [safeId]: true }));

    try {
      const response = await api.notifications.deleteCustomEmail(safeId);
      const payload = response?.data;
      if (payload?.ok) {
        setCustomNotificationEmails((prev) => prev.filter((email) => Number(email.id) !== safeId));
      } else {
        alert(payload?.error || 'Failed to delete email.');
      }
    } catch (e) {
      alert(e?.response?.data?.error || 'Failed to delete email.');
      console.error('Failed to delete custom notification email:', e);
    } finally {
      setCustomEmailDeleting((prev) => ({ ...prev, [safeId]: false }));
    }
  }, []);

  const toggleNotificationCategoryCustomEmail = useCallback(async (emailRecord) => {
    const categoryKey = String(notificationManagementModalCategory?.key || '').trim();
    const customEmailId = Number(emailRecord?.id);
    if (!categoryKey || !Number.isFinite(customEmailId)) return;

    const currentlyEnabled = Boolean(emailRecord?.is_enabled);
    const nextEnabled = !currentlyEnabled;

    setNotificationManagementToggleSavingByCustomEmailId((prev) => ({ ...prev, [customEmailId]: true }));

    try {
      const response = await api.notifications.setCustomEmailCategoryPreference({
        customEmailId,
        categoryKey,
        enabled: nextEnabled,
      });
      const payload = response?.data;
      const updated = payload?.ok ? payload.data : payload?.data;
      const resolvedEnabled = updated?.is_enabled ?? nextEnabled;

      setNotificationManagementCustomEmails((prev) => prev.map((row) => (
        Number(row.id) === customEmailId
          ? { ...row, is_enabled: Boolean(resolvedEnabled) }
          : row
      )));

      // Refresh the main categories list to update counts
      await fetchAdminNotificationCategories();
    } catch (e) {
      console.error('Failed to toggle custom email preference:', e);
      alert(e?.response?.data?.error || 'Failed to update custom email preference.');
    } finally {
      setNotificationManagementToggleSavingByCustomEmailId((prev) => ({ ...prev, [customEmailId]: false }));
    }
  }, [fetchAdminNotificationCategories, notificationManagementModalCategory?.key]);

  const fetchAdminInvoices = useCallback(async (signal) => {
    try {
      setAdminInvoicesLoading(true);
      const response = await api.invoices.listAdminInvoices({ limit: 500 }, signal ? { signal } : {});
      const payload = response?.data;
      if (payload?.ok) {
        setAdminInvoices(payload.data || []);
      } else {
        setAdminInvoices(payload?.data || []);
      }
    } catch (e) {
      if (e?.name === 'CanceledError' || e?.code === 'ERR_CANCELED') return;
      console.error('Failed to fetch admin invoices:', e);
      setAdminInvoices([]);
    } finally {
      setAdminInvoicesLoading(false);
    }
  }, []);

  const fetchAdminDocumentRequests = useCallback(async (signal) => {
    try {
      setAdminDocumentRequestsLoading(true);
      const response = await api.documentRequests.listAdmin({ limit: 500 }, signal ? { signal } : {});
      const payload = response?.data;
      if (payload?.ok) {
        setAdminDocumentRequests(payload.data || []);
      } else {
        setAdminDocumentRequests(payload?.data || []);
      }
    } catch (e) {
      if (e?.name === 'CanceledError' || e?.code === 'ERR_CANCELED') return;
      console.error('Failed to fetch document requests:', e);
      setAdminDocumentRequests([]);
    } finally {
      setAdminDocumentRequestsLoading(false);
    }
  }, []);

  const fetchCarouselSlidesAdmin = useCallback(async (signal) => {
    try {
      setCarouselSlidesLoading(true);
      const response = await api.carousel.listAdminSlides({ limit: 500 }, signal ? { signal } : {});
      const payload = response?.data;
      const rows = payload?.ok ? (payload.data || []) : (payload?.data || []);
      setCarouselSlidesAdmin(Array.isArray(rows) ? rows : []);
    } catch (error) {
      if (error?.name === 'CanceledError' || error?.code === 'ERR_CANCELED') return;
      console.error('Failed to fetch carousel slides:', error);
      setCarouselSlidesAdmin([]);
    } finally {
      setCarouselSlidesLoading(false);
    }
  }, []);

  const fetchAdminDocumentRequestDetail = useCallback(async (requestId) => {
    const id = Number(requestId);
    if (!Number.isFinite(id)) return null;
    try {
      const response = await api.documentRequests.getAdmin(id);
      const payload = response?.data;
      const row = payload?.ok ? payload?.data : payload?.data;
      return row || null;
    } catch (e) {
      console.error('Failed to fetch document request detail:', e);
      return null;
    }
  }, []);

  const closeAdminDocumentRequestModal = useCallback(() => {
    setShowAdminDocumentRequestModal(false);
    setAdminDocumentRequestSelected(null);
    setAdminDocReqUploading({});
    setAdminDocReqConfirming(false);
  }, []);

  const openAdminDocumentRequestModal = useCallback(async (row) => {
    const detail = await fetchAdminDocumentRequestDetail(row?.id);
    setAdminDocumentRequestSelected(detail || row);
    setShowAdminDocumentRequestModal(true);
    setAdminDocReqUploading({});
    setAdminDocReqConfirming(false);
  }, [fetchAdminDocumentRequestDetail]);

  const uploadAdminDocumentRequestFile = useCallback(async (docType, file) => {
    if (!adminDocumentRequestSelected?.id) return;
    if (!file) return;

    try {
      setAdminDocReqUploading((prev) => ({ ...prev, [docType]: true }));
      const formData = new FormData();
      formData.append('file', file);
      const response = await api.documentRequests.uploadAdminDoc({
        id: adminDocumentRequestSelected.id,
        docType,
        formData,
      });
      const payload = response?.data;
      const updated = payload?.ok ? payload?.data : payload?.data;
      if (updated) {
        setAdminDocumentRequestSelected(updated);
        setAdminDocumentRequests((prev) => prev.map((r) => (r.id === updated.id ? { ...r, ...updated } : r)));
      }
    } catch (e) {
      console.error('Failed to upload document request file:', e);
      alert(e?.response?.data?.error || 'Upload failed');
    } finally {
      setAdminDocReqUploading((prev) => ({ ...prev, [docType]: false }));
    }
  }, [adminDocumentRequestSelected?.id]);

  const confirmAdminDocumentRequest = useCallback(async () => {
    if (!adminDocumentRequestSelected?.id) return;
    try {
      setAdminDocReqConfirming(true);
      const response = await api.documentRequests.confirmAdmin(adminDocumentRequestSelected.id);
      const payload = response?.data;
      const updated = payload?.ok ? payload?.data : payload?.data;
      if (updated) {
        setAdminDocumentRequests((prev) => prev.map((r) => (r.id === updated.id ? { ...r, ...updated } : r)));
        setAdminDocumentRequestSelected(updated);
      }
      closeAdminDocumentRequestModal();
    } catch (e) {
      console.error('Failed to confirm document request:', e);
      alert(e?.response?.data?.error || 'Echec de la confirmation de la demande');
    } finally {
      setAdminDocReqConfirming(false);
    }
  }, [adminDocumentRequestSelected?.id, closeAdminDocumentRequestModal]);

  const fetchAdminInvoiceDetail = useCallback(async (invoiceId) => {
    const id = Number(invoiceId);
    if (!Number.isFinite(id)) return null;
    try {
      const response = await api.invoices.getAdminInvoice(id);
      const payload = response?.data;
      const invoice = payload?.ok ? payload?.data : payload?.data;
      if (invoice) {
        setAdminInvoiceDetailsById((prev) => ({ ...prev, [id]: invoice }));
      }
      return invoice || null;
    } catch (e) {
      console.error('Failed to fetch invoice detail:', e);
      return null;
    }
  }, []);

  const fetchAdminRecipientUsers = useCallback(async () => {
    try {
      setAdminDestinatairesLoading(true);
      const response = await api.notifications.getAdminRecipients();
      const rows = response?.data?.data || [];
      const sorted = [...rows].sort((a, b) => {
        const aLast = String(a.last_name || '').toLowerCase();
        const bLast = String(b.last_name || '').toLowerCase();
        const aFirst = String(a.first_name || '').toLowerCase();
        const bFirst = String(b.first_name || '').toLowerCase();
        return aLast.localeCompare(bLast) || aFirst.localeCompare(bFirst) || (a.id - b.id);
      });
      setAdminRecipientUsers(sorted);
    } catch (e) {
      console.error('Failed to fetch admin recipients:', e);
      setAdminRecipientUsers([]);
    } finally {
      setAdminDestinatairesLoading(false);
    }
  }, []);

  const fetchHouseManagementRequests = useCallback(async ({ requestType, signal } = {}) => {
    try {
      setHouseManagementLoading(true);
      const response = await api.houseManagement.listAdmin(
        {
          type: requestType,
          limit: 500,
        },
        signal ? { signal } : {}
      );
      const rows = response?.data?.data || [];
      setHouseManagementRequests(rows);

      const unreadCount = rows.reduce((count, row) => count + (Number(row?.is_seen) === 1 ? 0 : 1), 0);
      if (requestType === 'change_residence') {
        setHouseChangeUnreadCount(unreadCount);
      } else if (requestType === 'leave_residence') {
        setHouseLeaveUnreadCount(unreadCount);
      }
    } catch (error) {
      if (error?.name === 'CanceledError' || error?.code === 'ERR_CANCELED') return;
      console.error('Failed to fetch house-management requests:', error);
      setHouseManagementRequests([]);
    } finally {
      setHouseManagementLoading(false);
    }
  }, []);

  const fetchHouseManagementUnreadCounts = useCallback(async (signal) => {
    try {
      const [changeResponse, leaveResponse] = await Promise.all([
        api.houseManagement.listAdmin({ type: 'change_residence', limit: 500 }, signal ? { signal } : {}),
        api.houseManagement.listAdmin({ type: 'leave_residence', limit: 500 }, signal ? { signal } : {}),
      ]);

      const changeRows = changeResponse?.data?.data || [];
      const leaveRows = leaveResponse?.data?.data || [];
      const countUnread = (rows) => rows.reduce((count, row) => count + (Number(row?.is_seen) === 1 ? 0 : 1), 0);

      setHouseChangeUnreadCount(countUnread(changeRows));
      setHouseLeaveUnreadCount(countUnread(leaveRows));
    } catch (error) {
      if (error?.name === 'CanceledError' || error?.code === 'ERR_CANCELED') return;
      console.error('Failed to fetch house-management unread counts:', error);
    }
  }, []);

  const openHouseManagementRequestModal = useCallback(async (row) => {
    if (!row?.id) return;
    setHouseManagementExpandedRequestId(row.id);

    if (Number(row.is_seen) === 1) return;

    try {
      const response = await api.houseManagement.markAdminSeen(row.id);
      const updated = response?.data?.data;
      if (!updated) return;

      setHouseManagementRequests((prev) => prev.map((item) => (item.id === updated.id ? updated : item)));
      fetchHouseManagementUnreadCounts();
    } catch (error) {
      console.error('Failed to mark house-management request as seen:', error);
    }
  }, [fetchHouseManagementUnreadCounts]);

  const fetchAdminForms = useCallback(async (signal) => {
    try {
      setAdminFormsLoading(true);
      const response = await api.forms.listAdminForms({ limit: 500 }, signal ? { signal } : {});
      const rows = response?.data?.data || [];
      setAdminForms(Array.isArray(rows) ? rows : []);
    } catch (error) {
      if (error?.name === 'CanceledError' || error?.code === 'ERR_CANCELED') return;
      console.error('Failed to fetch admin forms:', error);
      setAdminForms([]);
    } finally {
      setAdminFormsLoading(false);
    }
  }, []);

  const fetchAdminFormSubmissions = useCallback(async (signal) => {
    try {
      setAdminFormSubmissionsLoading(true);
      const response = await api.forms.listAdminSubmissions({ limit: 500 }, signal ? { signal } : {});
      const payload = response?.data;
      const rows = Array.isArray(payload) ? payload : (payload?.data || []);
      setAdminFormSubmissions(Array.isArray(rows) ? rows : []);
    } catch (error) {
      if (error?.name === 'CanceledError' || error?.code === 'ERR_CANCELED') return;
      console.error('Failed to fetch form submissions:', error);
      setAdminFormSubmissions([]);
    } finally {
      setAdminFormSubmissionsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeFormRequestTabName === FORM_REQUESTS_ALL_KEY) return;

    const selectedKey = normalizeFormRequestTabKey(activeFormRequestTabName);
    const stillExists = formRequestChildTabs.some((name) => normalizeFormRequestTabKey(name) === selectedKey);
    if (!stillExists) {
      setActiveFormRequestTabName(FORM_REQUESTS_ALL_KEY);
    }
  }, [activeFormRequestTabName, formRequestChildTabs]);

  useEffect(() => {
    setAdminFormSubmissionExpandedId(null);
    setAdminFormSubmissionsPage(1);
  }, [activeFormRequestTabName]);

  const openCreateFormBuilder = useCallback(() => {
    setFormBuilderMode('create');
    setFormBuilderInitialValue(null);
    setShowFormBuilderModal(true);
  }, []);

  const openEditFormBuilder = useCallback((form) => {
    setFormBuilderMode('edit');
    setFormBuilderInitialValue(form || null);
    setShowFormBuilderModal(true);
  }, []);

  const closeFormBuilder = useCallback(() => {
    setShowFormBuilderModal(false);
    setFormBuilderInitialValue(null);
  }, []);

  const saveFormBuilder = useCallback(async (payload) => {
    try {
      setFormBuilderSaving(true);
      if (formBuilderMode === 'edit' && formBuilderInitialValue?.id) {
        await api.forms.updateAdminForm(formBuilderInitialValue.id, payload);
      } else {
        await api.forms.createAdminForm(payload);
      }
      await fetchAdminForms();
      closeFormBuilder();
    } catch (error) {
      console.error('Failed to save form definition:', error);
      alert(error?.response?.data?.error || 'Failed to save form definition.');
    } finally {
      setFormBuilderSaving(false);
    }
  }, [closeFormBuilder, fetchAdminForms, formBuilderInitialValue?.id, formBuilderMode]);

  const toggleAdminFormPublish = useCallback(async (form) => {
    if (!form?.id) return;

    try {
      setFormPublishingId(form.id);
      if (Number(form.is_published) === 1 || form.is_published === true) {
        await api.forms.unpublishAdminForm(form.id);
      } else {
        await api.forms.publishAdminForm(form.id);
      }
      await fetchAdminForms();
    } catch (error) {
      console.error('Failed to toggle form publish state:', error);
      alert(error?.response?.data?.error || 'Failed to update publish state.');
    } finally {
      setFormPublishingId(null);
    }
  }, [fetchAdminForms]);

  const deleteAdminFormDefinition = useCallback(async (form) => {
    if (!form?.id) return;
    if (!window.confirm(`Delete the form "${form.form_name || 'form'}"?`)) return;

    try {
      setFormDeletingId(form.id);
      await api.forms.deleteAdminForm(form.id);
      if (Number(adminFormsExpandedId) === Number(form.id)) {
        setAdminFormsExpandedId(null);
      }
      await fetchAdminForms();
    } catch (error) {
      console.error('Failed to delete form definition:', error);
      alert(error?.response?.data?.error || 'Failed to delete form definition.');
    } finally {
      setFormDeletingId(null);
    }
  }, [adminFormsExpandedId, fetchAdminForms]);

  const openImportSubmissionModal = useCallback(async (submission) => {
    if (!submission?.id) return;
    setShowSignupRequestModal(false);
    setSelectedRequest(null);
    setAttachments([]);
    setMandateLatest(null);
    setImportSubmission(submission);
    setShowImportSubmissionModal(true);
    setImportAdditionalData(createAdditionalDataDraft());
    setImportMandateLatest(null);

    const responses = getSubmissionResponses(submission);
    const schema = submission.form_schema && typeof submission.form_schema === 'object'
      ? submission.form_schema
      : null;
    setImportSubmissionAttachments(getSubmissionFileRows(responses, schema));

    if (submission?.ticket_id) {
      fetchLatestImportMandateStatus(submission.ticket_id);
    }

    if (Number(submission.is_seen) === 1) return;

    try {
      const response = await api.forms.markAdminSubmissionSeen(submission.id);
      const updated = response?.data?.data;
      if (!updated) return;

      setAdminFormSubmissions((prev) => prev.map((row) => (
        Number(row.id) === Number(updated.id)
          ? { ...row, ...updated }
          : row
      )));
    } catch (error) {
      console.error('Failed to mark form submission as seen:', error);
    }
  }, []);

  const openAdminFormSubmissionModal = useCallback(async (submission) => {
    if (!submission?.id) return;
    if (isImportRegistrationSubmission(submission)) {
      await openImportSubmissionModal(submission);
      return;
    }
    setAdminFormSubmissionExpandedId(submission.id);
    setAdminFormSubmissionNotesById((prev) => ({
      ...prev,
      [submission.id]: prev[submission.id] ?? (submission.admin_note || ''),
    }));

    if (Number(submission.is_seen) === 1) return;

    try {
      const response = await api.forms.markAdminSubmissionSeen(submission.id);
      const updated = response?.data?.data;
      if (!updated) return;

      setAdminFormSubmissions((prev) => prev.map((row) => (
        Number(row.id) === Number(updated.id)
          ? { ...row, ...updated }
          : row
      )));
    } catch (error) {
      console.error('Failed to mark form submission as seen:', error);
    }
  }, [openImportSubmissionModal]);

  const reviewAdminFormSubmission = useCallback(async (submissionId, action) => {
    const id = Number(submissionId);
    if (!Number.isFinite(id)) return;

    try {
      setAdminFormSubmissionReviewingId(id);
      const adminNote = String(adminFormSubmissionNotesById[id] || '').trim();
      await api.forms.reviewAdminSubmission(id, {
        action,
        admin_note: adminNote || undefined,
      });
      await fetchAdminFormSubmissions();
    } catch (error) {
      console.error('Failed to review form submission:', error);
      alert(error?.response?.data?.error || 'Failed to update request status.');
    } finally {
      setAdminFormSubmissionReviewingId(null);
    }
  }, [adminFormSubmissionNotesById, fetchAdminFormSubmissions]);

  const getPaginationItems = (totalPages, currentPage) => {
    const total = Math.max(1, Number(totalPages) || 1);
    const current = Math.min(total, Math.max(1, Number(currentPage) || 1));
    if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);

    const items = [];
    items.push(1);

    const start = Math.max(2, current - 2);
    const end = Math.min(total - 1, current + 2);

    if (start > 2) items.push('...');
    for (let p = start; p <= end; p += 1) items.push(p);
    if (end < total - 1) items.push('...');

    items.push(total);
    return items;
  };

  const formatShowingMeta = (total, page, pageSize) => {
    const totalCount = Math.max(0, Number(total) || 0);
    if (totalCount === 0) return 'Showing 0 of 0';
    const p = Math.max(1, Number(page) || 1);
    const size = Math.max(1, Number(pageSize) || 1);
    const start = (p - 1) * size + 1;
    const end = Math.min(totalCount, p * size);
    return `Showing ${start}-${end} of ${totalCount}`;
  };

  const sentNotificationsTotalPages = useMemo(
    () => Math.max(1, Math.ceil((sentNotificationCampaigns?.length || 0) / sentNotificationsPageSize)),
    [sentNotificationCampaigns?.length, sentNotificationsPageSize]
  );
  const sentNotificationsPageClamped = useMemo(
    () => Math.min(sentNotificationsTotalPages, Math.max(1, sentNotificationsPage)),
    [sentNotificationsPage, sentNotificationsTotalPages]
  );
  const pagedSentNotificationCampaigns = useMemo(() => {
    const start = (sentNotificationsPageClamped - 1) * sentNotificationsPageSize;
    return (sentNotificationCampaigns || []).slice(start, start + sentNotificationsPageSize);
  }, [sentNotificationCampaigns, sentNotificationsPageClamped, sentNotificationsPageSize]);

  useEffect(() => {
    if (sentNotificationsPage > sentNotificationsTotalPages) {
      setSentNotificationsPage(sentNotificationsTotalPages);
    }
  }, [sentNotificationsPage, sentNotificationsTotalPages]);

  const teamTotalPages = useMemo(
    () => Math.max(1, Math.ceil((teamEmployees?.length || 0) / teamPageSize)),
    [teamEmployees?.length, teamPageSize]
  );
  const teamPageClamped = useMemo(
    () => Math.min(teamTotalPages, Math.max(1, teamPage)),
    [teamPage, teamTotalPages]
  );
  const pagedTeamEmployees = useMemo(() => {
    const start = (teamPageClamped - 1) * teamPageSize;
    return (teamEmployees || []).slice(start, start + teamPageSize);
  }, [teamEmployees, teamPageClamped, teamPageSize]);
  const editingTeamRoleType = useMemo(
    () => teamRoleTypes.find((roleType) => Number(roleType.id) === Number(editingTeamRoleTypeId)) || null,
    [teamRoleTypes, editingTeamRoleTypeId]
  );

  useEffect(() => {
    if (teamPage > teamTotalPages) {
      setTeamPage(teamTotalPages);
    }
  }, [teamPage, teamTotalPages]);

  useEffect(() => {
    setNewEmployee((prev) => {
      if (teamRoleOptions.includes(prev.role)) return prev;
      return {
        ...prev,
        role: teamRoleOptions[0] || DEFAULT_TEAM_ROLE_OPTIONS[0],
      };
    });
  }, [teamRoleOptions]);

  const adminInboxTotalPages = useMemo(
    () => Math.max(1, Math.ceil((adminInboxNotifications?.length || 0) / adminInboxPageSize)),
    [adminInboxNotifications?.length, adminInboxPageSize]
  );
  const adminInboxPageClamped = useMemo(
    () => Math.min(adminInboxTotalPages, Math.max(1, adminInboxPage)),
    [adminInboxPage, adminInboxTotalPages]
  );
  const pagedAdminInboxNotifications = useMemo(() => {
    const start = (adminInboxPageClamped - 1) * adminInboxPageSize;
    return (adminInboxNotifications || []).slice(start, start + adminInboxPageSize);
  }, [adminInboxNotifications, adminInboxPageClamped, adminInboxPageSize]);

  useEffect(() => {
    if (adminInboxPage > adminInboxTotalPages) setAdminInboxPage(adminInboxTotalPages);
  }, [adminInboxPage, adminInboxTotalPages]);

  const adminOutboxTotalPages = useMemo(
    () => Math.max(1, Math.ceil((adminOutboxCampaigns?.length || 0) / adminOutboxPageSize)),
    [adminOutboxCampaigns?.length, adminOutboxPageSize]
  );
  const adminOutboxPageClamped = useMemo(
    () => Math.min(adminOutboxTotalPages, Math.max(1, adminOutboxPage)),
    [adminOutboxPage, adminOutboxTotalPages]
  );
  const pagedAdminOutboxCampaigns = useMemo(() => {
    const start = (adminOutboxPageClamped - 1) * adminOutboxPageSize;
    return (adminOutboxCampaigns || []).slice(start, start + adminOutboxPageSize);
  }, [adminOutboxCampaigns, adminOutboxPageClamped, adminOutboxPageSize]);

  useEffect(() => {
    if (adminOutboxPage > adminOutboxTotalPages) setAdminOutboxPage(adminOutboxTotalPages);
  }, [adminOutboxPage, adminOutboxTotalPages]);

  const houseManagementTotalPages = useMemo(
    () => Math.max(1, Math.ceil((houseManagementRequests?.length || 0) / houseManagementPageSize)),
    [houseManagementRequests?.length, houseManagementPageSize]
  );
  const houseManagementPageClamped = useMemo(
    () => Math.min(houseManagementTotalPages, Math.max(1, houseManagementPage)),
    [houseManagementPage, houseManagementTotalPages]
  );
  const pagedHouseManagementRequests = useMemo(() => {
    const start = (houseManagementPageClamped - 1) * houseManagementPageSize;
    return (houseManagementRequests || []).slice(start, start + houseManagementPageSize);
  }, [houseManagementRequests, houseManagementPageClamped, houseManagementPageSize]);

  useEffect(() => {
    if (houseManagementPage > houseManagementTotalPages) setHouseManagementPage(houseManagementTotalPages);
  }, [houseManagementPage, houseManagementTotalPages]);

  const adminInvoicesTotalPages = useMemo(
    () => Math.max(1, Math.ceil((adminInvoices?.length || 0) / adminInvoicesPageSize)),
    [adminInvoices?.length, adminInvoicesPageSize]
  );
  const adminInvoicesPageClamped = useMemo(
    () => Math.min(adminInvoicesTotalPages, Math.max(1, adminInvoicesPage)),
    [adminInvoicesPage, adminInvoicesTotalPages]
  );
  const pagedAdminInvoices = useMemo(() => {
    const start = (adminInvoicesPageClamped - 1) * adminInvoicesPageSize;
    return (adminInvoices || []).slice(start, start + adminInvoicesPageSize);
  }, [adminInvoices, adminInvoicesPageClamped, adminInvoicesPageSize]);

  useEffect(() => {
    if (adminInvoicesPage > adminInvoicesTotalPages) setAdminInvoicesPage(adminInvoicesTotalPages);
  }, [adminInvoicesPage, adminInvoicesTotalPages]);

  const adminDocumentRequestsTotalPages = useMemo(
    () => Math.max(1, Math.ceil((adminDocumentRequests?.length || 0) / adminDocumentRequestsPageSize)),
    [adminDocumentRequests?.length, adminDocumentRequestsPageSize]
  );
  const adminDocumentRequestsPageClamped = useMemo(
    () => Math.min(adminDocumentRequestsTotalPages, Math.max(1, adminDocumentRequestsPage)),
    [adminDocumentRequestsPage, adminDocumentRequestsTotalPages]
  );
  const pagedAdminDocumentRequests = useMemo(() => {
    const start = (adminDocumentRequestsPageClamped - 1) * adminDocumentRequestsPageSize;
    return (adminDocumentRequests || []).slice(start, start + adminDocumentRequestsPageSize);
  }, [adminDocumentRequests, adminDocumentRequestsPageClamped, adminDocumentRequestsPageSize]);

  useEffect(() => {
    if (adminDocumentRequestsPage > adminDocumentRequestsTotalPages) setAdminDocumentRequestsPage(adminDocumentRequestsTotalPages);
  }, [adminDocumentRequestsPage, adminDocumentRequestsTotalPages]);

  const adminPaiementsTotalPages = useMemo(
    () => Math.max(1, Number(adminPaiementsMeta?.totalPages) || 1),
    [adminPaiementsMeta?.totalPages]
  );
  const adminPaiementsPageClamped = useMemo(
    () => Math.min(adminPaiementsTotalPages, Math.max(1, adminPaiementsPage)),
    [adminPaiementsPage, adminPaiementsTotalPages]
  );

  useEffect(() => {
    if (adminPaiementsPage > adminPaiementsTotalPages) setAdminPaiementsPage(adminPaiementsTotalPages);
  }, [adminPaiementsPage, adminPaiementsTotalPages]);

  const toggleSort = (sortBy) => {
    setUserManagementPage(1);
    setUserManagementSort((prev) => {
      if (prev.sortBy !== sortBy) return { sortBy, sortDir: 'desc' };
      return { sortBy, sortDir: prev.sortDir === 'desc' ? 'asc' : 'desc' };
    });
  };

  const getSortIndicator = (sortBy) => {
    if (userManagementSort.sortBy !== sortBy) return '';
    return userManagementSort.sortDir === 'asc' ? '?' : '?';
  };

  const fetchUserManagementUsers = useCallback(async (signal) => {
    try {
      setUserManagementLoading(true);
      const response = await api.users.listUsers({
        q: userManagementRechercher || undefined,
        page: userManagementPage,
        pageSize: userManagementPageSize,
        sortBy: userManagementSort.sortBy,
        sortDir: userManagementSort.sortDir,
      }, { signal });
      const payload = response.data || {};
      setUserManagementRows(payload.data || []);
      setUserManagementMeta(payload.meta || {
        q: userManagementRechercher,
        page: userManagementPage,
        pageSize: userManagementPageSize,
        total: (payload.data || []).length,
        totalPages: 1,
        sortBy: userManagementSort.sortBy,
        sortDir: userManagementSort.sortDir,
      });
    } catch (error) {
      if (error?.name === 'CanceledError' || error?.code === 'ERR_CANCELED') return;
      console.error('Failed to fetch users:', error);
      setUserManagementRows([]);
      setUserManagementMeta((prev) => ({ ...prev, total: 0, totalPages: 1 }));
    } finally {
      setUserManagementLoading(false);
    }
  }, [userManagementPage, userManagementPageSize, userManagementRechercher, userManagementSort.sortBy, userManagementSort.sortDir]);

  useEffect(() => {
    if (activeTab !== 'users' || !allowedTabs.has(TAB_KEYS.USERS)) return;
    const controller = new AbortController();
    const t = setTimeout(() => {
      fetchUserManagementUsers(controller.signal);
    }, 250);
    return () => {
      clearTimeout(t);
      controller.abort();
    };
  }, [activeTab, allowedTabs, fetchUserManagementUsers]);

  useEffect(() => {
    if (activeTab !== 'users' || !allowedTabs.has(TAB_KEYS.USERS)) return;
    setUserManagementExpandedUserId(null);
  }, [activeTab, allowedTabs, userManagementRechercher, userManagementPage, userManagementSort.sortBy, userManagementSort.sortDir]);

  const fetchRenewalsDue = async (days = 30) => {
    try {
      setRenewalsLoading(true);
      const resp = await api.users.getRenewalsDue(days);
      const rows = resp?.data?.data || [];
      setRenewalsRows(rows);
      // initialize inputs
      const inputs = {};
      (rows || []).forEach((r) => {
        inputs[r.id] = { ch: '', payment_amount: '', sending: false };
      });
      setRenewalInputs(inputs);
    } catch (err) {
      console.error('Failed to fetch renewals due:', err);
      setRenewalsRows([]);
    } finally {
      setRenewalsLoading(false);
    }
  };

  const sendRenewalForUser = async (userId) => {
    const data = renewalInputs[userId] || {};
    if (!data.ch || !data.payment_amount) {
      alert('Veuillez renseigner CH et montant de paiement.');
      return;
    }
    setRenewalInputs((prev) => ({ ...prev, [userId]: { ...(prev[userId] || {}), sending: true } }));
    try {
      const payload = { adminData: { ch: String(data.ch).trim(), payment_amount: String(data.payment_amount).trim() } };
      const resp = await api.users.sendRenewal(userId, payload);
      alert(`Renewal envelope sent: ${resp?.data?.envelopeId || ''}`);
      // refresh list
      await fetchUserManagementUsers();
      await fetchRenewalsDue();
    } catch (err) {
      console.error('Failed to send renewal:', err);
      alert(err?.response?.data?.error || 'Echec de l\'envoi du renouvellement.');
    } finally {
      setRenewalInputs((prev) => ({ ...prev, [userId]: { ...(prev[userId] || {}), sending: false } }));
    }
  };

  const fetchLatestMandateStatus = async (ticketId) => {
    const id = Number(ticketId);
    if (!Number.isFinite(id)) return;

    try {
      setMandateLatestLoading(true);

      // Always pull the freshest ticket so mandate_id changes are reflected.
      const { data } = await api.signup.getRequestById(id);
      const latestTicket = data || null;

      if (!latestTicket) {
        setMandateLatest(null);
        return;
      }

      // If ticket already has mandate_id, treat as active.
      if (latestTicket.mandate_id) {
        setMandateLatest({
          mandate_status: 'active',
          mandate_id: latestTicket.mandate_id,
          message: 'Payment setup completed',
        });
        return;
      }

      // If a user exists, check live mandate status from backend.
      if (latestTicket.user_id) {
        try {
          const { data: statusResp } = await api.gocardless.getMandateStatus(latestTicket.user_id);
          const status = statusResp?.data || statusResp;
          if (status) {
            setMandateLatest({ mandate_status: status.mandate_status || 'unknown', ...status });
            return;
          }
        } catch (error) {
          console.error('Failed to fetch mandate status:', error);
        }
      }

      if (latestTicket.status === 'resolved') {
        setMandateLatest({ mandate_status: 'setup_complete', message: 'Setup completed, waiting for bank authorization' });
      } else {
        setMandateLatest({ mandate_status: 'not_setup', message: 'Payment setup required before approval' });
      }
    } catch (e) {
      console.error('Failed to fetch mandate status:', e);
      // Keep last known status to avoid distracting UI flicker.
    } finally {
      setMandateLatestLoading(false);
    }
  };

  const fetchLatestImportMandateStatus = async (ticketId) => {
    const id = Number(ticketId);
    if (!Number.isFinite(id)) return;

    try {
      setImportMandateLatestLoading(true);
      const { data } = await api.gocardless.getMandateStatusByTicket(id);
      const statusPayload = data?.data || data || null;
      setImportMandateLatest(statusPayload);
    } catch (error) {
      console.error('Failed to fetch import mandate status:', error);
    } finally {
      setImportMandateLatestLoading(false);
    }
  };

  // Auto-refresh mandate status every 10 seconds when a request is selected (same pattern as DocuSign).
  useEffect(() => {
    if (!selectedRequest?.id) return;

    fetchLatestMandateStatus(selectedRequest.id);
    const interval = setInterval(() => {
      fetchLatestMandateStatus(selectedRequest.id);
    }, 10000);

    return () => clearInterval(interval);
  }, [selectedRequest?.id]);

  useEffect(() => {
    if (!importSubmission?.ticket_id) return;

    fetchLatestImportMandateStatus(importSubmission.ticket_id);
    const interval = setInterval(() => {
      fetchLatestImportMandateStatus(importSubmission.ticket_id);
    }, 10000);

    return () => clearInterval(interval);
  }, [importSubmission?.ticket_id]);

  const fetchLatestDocusign = async (ticketId) => {
    if (!ticketId) return;
    try {
      setDocusignLatestLoading(true);
      const { data } = await api.docusign.getLatestEnvelope(ticketId);
      setDocusignLatest(data?.data || null);
    } catch (e) {
      console.error('Failed to fetch DocuSign status:', e);
      // Keep last known status to avoid distracting UI flicker.
    } finally {
      setDocusignLatestLoading(false);
    }
  };

  // Auto-refresh DocuSign status every 10 seconds when a request is selected
  useEffect(() => {
    if (!selectedRequest?.id) return;

    fetchLatestDocusign(selectedRequest.id);
    const interval = setInterval(() => {
      fetchLatestDocusign(selectedRequest.id);
    }, 10000);

    return () => clearInterval(interval);
  }, [selectedRequest?.id]);

  const closeSignupRequestModal = useCallback(() => {
    setShowSignupRequestModal(false);
    setShowInvoiceModal(false);
    setSelectedRequest(null);
    setAttachments([]);
    setMandateLatest(null);
  }, []);

  const openInvoiceModal = useCallback(() => {
    if (!selectedRequest) return;
    setInvoiceDraft(createInvoiceDraftFromRequest(selectedRequest));
    setShowInvoiceModal(true);
  }, [selectedRequest]);

  const closeInvoiceModal = useCallback(() => {
    setShowInvoiceModal(false);
  }, []);

  const loadInvoiceTemplates = useCallback(async () => {
    try {
      setInvoiceTemplateLoading(true);
      setInvoiceTemplateError('');
      const response = await api.invoices.getNotificationTemplates();
      const payload = response?.data;
      const templates = payload?.ok ? payload?.data : payload?.data;
      setInvoiceTemplateDrafts(Array.isArray(templates) ? templates : []);
    } catch (error) {
      console.error('Failed to load invoice templates:', error);
      setInvoiceTemplateDrafts([]);
      setInvoiceTemplateError(error?.response?.data?.error || 'Impossible de charger les templates.');
    } finally {
      setInvoiceTemplateLoading(false);
    }
  }, []);

  const openInvoiceTemplateModal = useCallback(async () => {
    setShowInvoiceTemplateModal(true);
    await loadInvoiceTemplates();
  }, [loadInvoiceTemplates]);

  const closeInvoiceTemplateModal = useCallback(() => {
    setShowInvoiceTemplateModal(false);
    setInvoiceTemplateError('');
  }, []);

  const updateInvoiceTemplateDraft = useCallback((templateKey, field, value) => {
    setInvoiceTemplateDrafts((prev) => prev.map((row) => (
      row.key === templateKey ? { ...row, [field]: value } : row
    )));
  }, []);

  const saveInvoiceTemplates = useCallback(async () => {
    if (invoiceTemplateDrafts.length === 0) return;
    setInvoiceTemplateSaving(true);
    setInvoiceTemplateError('');
    try {
      await Promise.all(
        invoiceTemplateDrafts.map((row) => api.invoices.updateNotificationTemplate(row.key, {
          notification_title: row.notification_title,
          notification_body: row.notification_body,
          email_subject: row.email_subject,
          email_body: row.email_body,
        }))
      );
      await loadInvoiceTemplates();
    } catch (error) {
      console.error('Failed to save invoice templates:', error);
      setInvoiceTemplateError(error?.response?.data?.error || 'Impossible de sauvegarder les templates.');
    } finally {
      setInvoiceTemplateSaving(false);
    }
  }, [invoiceTemplateDrafts, loadInvoiceTemplates]);

  const fetchEmailTemplateCategories = useCallback(async () => {
    try {
      setEmailTemplateLoading(true);
      setEmailTemplateError('');
      const response = await api.emailTemplates.getCategories();
      const payload = response?.data;
      setEmailTemplateCategories(payload?.ok ? (payload?.data || []) : []);
    } catch (error) {
      console.error('Failed to load email template categories:', error);
      setEmailTemplateCategories([]);
      setEmailTemplateError(error?.response?.data?.error || 'Impossible de charger les categories.');
    } finally {
      setEmailTemplateLoading(false);
    }
  }, []);

  const fetchEmailTemplates = useCallback(async () => {
    try {
      setEmailTemplateLoading(true);
      setEmailTemplateError('');
      const response = await api.emailTemplates.listTemplates();
      const payload = response?.data;
      setEmailTemplateDrafts(payload?.ok ? (payload?.data || []) : []);
    } catch (error) {
      console.error('Failed to load email templates:', error);
      setEmailTemplateDrafts([]);
      setEmailTemplateError(error?.response?.data?.error || 'Impossible de charger les templates.');
    } finally {
      setEmailTemplateLoading(false);
    }
  }, []);

  const openEmailTemplateModal = useCallback((template) => {
    setEmailTemplateModalTemplate(template);
  }, []);

  const closeEmailTemplateModal = useCallback(() => {
    setEmailTemplateModalTemplate(null);
    setEmailTemplateError('');
  }, []);

  const updateEmailTemplateDraft = useCallback((templateKey, field, value) => {
    setEmailTemplateDrafts((prev) => prev.map((row) => (
      row.key === templateKey ? { ...row, [field]: value } : row
    )));
  }, []);

  const saveEmailTemplate = useCallback(async (templateKey) => {
    const template = emailTemplateDrafts.find((t) => t.key === templateKey);
    if (!template) return;
    setEmailTemplateSaving(true);
    setEmailTemplateError('');
    try {
      await api.emailTemplates.updateTemplate(templateKey, {
        notification_title: template.notification_title,
        notification_body: template.notification_body,
        email_subject: template.email_subject,
        email_body: template.email_body,
      });
      await fetchEmailTemplates();
    } catch (error) {
      console.error('Failed to save email template:', error);
      setEmailTemplateError(error?.response?.data?.error || 'Impossible de sauvegarder le template.');
    } finally {
      setEmailTemplateSaving(false);
    }
  }, [emailTemplateDrafts, fetchEmailTemplates]);

  const generatePreviewData = useCallback((templateKey) => {
    const previewDataMap = {
      housing_application_received: { first_name: 'Jean', last_name: 'Dupont', email: 'jean.dupont@example.com', reference_number: 'ADEEF-2024-001', status_page_url: 'https://portal.adeef.org/status/ADEEF-2024-001' },
      housing_application_approved: { first_name: 'Jean', last_name: 'Dupont', email: 'jean.dupont@example.com', temporary_password: 'Abc123!' },
      housing_application_rejected: { first_name: 'Jean', last_name: 'Dupont', email: 'jean.dupont@example.com' },
      event_approved_paid: { user_name: 'Marie Curie', event_title: 'Gala ADEEF 2024', registration_link: 'https://portal.adeef.org/events/gala-2024/register' },
      event_approved_free: { user_name: 'Marie Curie', event_title: 'Atelier Numerique', registration_link: 'https://portal.adeef.org/events/atelier/register' },
      event_approved_nolink: { user_name: 'Marie Curie', event_title: 'Conference ADEEF' },
      event_not_approved: { user_name: 'Marie Curie', event_title: 'Gala ADEEF 2024' },
      invoice_first_time: { name: 'Pierre Martin', email: 'pierre.martin@example.com', amount: '1200.00', currency: 'EUR', invoice_number: 'INV-2024-001', payment_link: 'https://portal.adeef.org/pay/inv-001', invoice_link: 'https://portal.adeef.org/invoices/inv-001', period_from: '2024-09-01', period_to: '2025-06-30', residence: 'Cite Universitaire' },
      invoice_post_first_time: { name: 'Pierre Martin', email: 'pierre.martin@example.com', amount: '450.00', currency: 'EUR', invoice_number: 'INV-2024-002', payment_id: 'PAY-002' },
      form_request_received: { applicant_name: 'Sophie Lee', form_name: 'Demande de logement', reference_number: 'ADEEF-2024-002', status_page_url: 'https://portal.adeef.org/status/ADEEF-2024-002' },
      form_request_accepted: { applicant_name: 'Sophie Lee', reference_number: 'ADEEF-2024-002', admin_comment: 'Your request has been reviewed and accepted.' },
      form_request_rejected: { applicant_name: 'Sophie Lee', reference_number: 'ADEEF-2024-002', admin_comment: 'Incomplete documentation provided.' },
      form_request_needs_response: { applicant_name: 'Sophie Lee', reference_number: 'ADEEF-2024-002', admin_comment: 'Please provide additional information.' },
      payment_successful: { first_name: 'Ahmed', amount: '450.00', currency: 'GBP' },
      payment_processing: { first_name: 'Ahmed', amount: '450.00', currency: 'GBP' },
      payment_failed: { first_name: 'Ahmed', amount: '450.00', currency: 'GBP' },
      password_reset: { otp: '482916', expires_minutes: '15' },
      admin_account_created: { display_name: 'Admin User', email: 'admin@adeef.org', temporary_password: 'Xyz789!', role_name: 'Administrative Admin' },
      contact_message_reply: { recipient_name: 'Claire', original_subject: 'Housing inquiry', reply_message: 'Thank you for your message. We have received your inquiry and will respond within 48 hours.', received_date: '15/01/2024' },
    };
    return previewDataMap[templateKey] || {};
  }, []);

  const updateInvoiceDraft = useCallback((field, value) => {
    setInvoiceDraft((prev) => ({ ...prev, [field]: value }));
  }, []);

  const refreshSelectedRequest = useCallback(async (requestId) => {
    if (!requestId) return null;
    try {
      const refreshed = await api.signup.getRequestById(requestId);
      if (refreshed?.data) {
        setSelectedRequest(refreshed.data);
        setInvoiceDraft(createInvoiceDraftFromRequest(refreshed.data));
        setSignupRequests((prev) => prev.map((row) => (
          Number(row.id) === Number(refreshed.data.id)
            ? { ...row, ...refreshed.data }
            : row
        )));
        return refreshed.data;
      }
    } catch (error) {
      console.error('Failed to refresh request after invoice action:', error);
    }
    return null;
  }, []);

  const sendInvoice = useCallback(async () => {
    if (!selectedRequest?.id) return;
    
    // Validate required fields
    if (!invoiceDraft.invoice_period_from?.trim()) {
      alert('Veuillez entrer la date de debut de la periode.');
      return;
    }
    if (!invoiceDraft.invoice_period_to?.trim()) {
      alert('Veuillez entrer la date de fin de la periode.');
      return;
    }
    if (invoiceDraft.invoice_period_from > invoiceDraft.invoice_period_to) {
      alert('La date de debut doit etre avant la date de fin.');
      return;
    }
    if (!invoiceDraft.residence_name?.trim()) {
      alert('Veuillez entrer le nom de la residence.');
      return;
    }
    if (!invoiceDraft.city_of_residence?.trim()) {
      alert('Veuillez entrer la ville de residence.');
      return;
    }
    
    const rentCharges = parseFloat(invoiceDraft.invoice_rent_charges || 0);
    const deposit = parseFloat(invoiceDraft.invoice_deposit || 0);
    const dossierFee = parseFloat(invoiceDraft.invoice_dossier_fee || 0);
    const insurance = parseFloat(invoiceDraft.invoice_insurance || 0);
    
    if (rentCharges < 0 || deposit < 0 || dossierFee < 0 || insurance < 0) {
      alert('Les montants ne peuvent pas etre negatifs.');
      return;
    }
    
    if (rentCharges === 0 && deposit === 0 && dossierFee === 0 && insurance === 0) {
      alert('Le total de la facture doit etre superieur a 0.');
      return;
    }
    
    try {
      setInvoiceSending(true);
      await api.signup.sendInvoice(selectedRequest.id, invoiceDraft);
      await refreshSelectedRequest(selectedRequest.id);
      alert('Facture envoyee.');
    } catch (error) {
      console.error('Failed to send invoice:', error);
      alert(error.response?.data?.error || 'Impossible d\'envoyer la facture.');
    } finally {
      setInvoiceSending(false);
    }
  }, [invoiceDraft, refreshSelectedRequest, selectedRequest?.id]);

  const markInvoicePaid = useCallback(async () => {
    if (!selectedRequest?.id) return;
    try {
      setInvoiceMarkingPaid(true);
      await api.signup.markInvoicePaid(selectedRequest.id);
      await refreshSelectedRequest(selectedRequest.id);
      alert('Facture marquee comme payee.');
    } catch (error) {
      console.error('Failed to mark invoice paid:', error);
      alert(error.response?.data?.error || 'Impossible de marquer la facture comme payee.');
    } finally {
      setInvoiceMarkingPaid(false);
    }
  }, [refreshSelectedRequest, selectedRequest?.id]);

  const closeImportSubmissionModal = useCallback(() => {
    setShowImportSubmissionModal(false);
    setImportSubmission(null);
    setImportSubmissionAttachments([]);
    setImportMandateLatest(null);
  }, []);

  const handleRequestSelect = async (request) => {
    // Some table rows represent imported form-submissions and may not have a
    // normal `id` property. Normalize that case first so we don't call
    // `/signup/admin/:id` with `undefined`.
    const isImportSubmissionRow = request?.__source === 'form-submission'
      || String(request?.id || '').startsWith('form-submission-')
      || (!request?.id && request?.form_submission_id);
    if (isImportSubmissionRow) {
      const submissionId = Number(request.form_submission_id || request.id);
      const submission = adminFormSubmissions.find((row) => Number(row.id) === Number(submissionId));
      if (submission) {
        await openImportSubmissionModal(submission);
      }
      return;
    }
    // Pull the freshest ticket (includes mandate_id updates)
    let latestRequest = request;
    const resolvedRequestId = resolveHousingRequestId(request);
    if (!resolvedRequestId) {
      console.warn('handleRequestSelect called with request missing id:', request);
      setSelectedRequest(request || null);
      setShowSignupRequestModal(true);
      setDocusignGuardianEmail(request?.guardian_email || '');
      setDocusignAdminEmail(user?.email || '');
      setAdditionalData(createAdditionalDataDraft());
      setAttachments([]);
      return;
    }
    try {
      const { data } = await api.signup.getRequestById(resolvedRequestId);
      latestRequest = data || request;
    } catch (error) {
      console.error('Failed to fetch latest request data:', error);
    }

    if (Number(latestRequest?.admin_seen) !== 1) {
      try {
        const response = await api.signup.markAdminSeen(latestRequest.id);
        const updated = response?.data?.data;
        if (updated) {
          latestRequest = { ...latestRequest, ...updated };
          setSignupRequests((prev) => prev.map((row) => (
            Number(row.id) === Number(updated.id)
              ? { ...row, ...updated }
              : row
          )));
        }
      } catch (error) {
        console.error('Failed to mark housing application as seen:', error);
      }
    }

    setSelectedRequest(latestRequest);
    setShowSignupRequestModal(true);

    setDocusignGuardianEmail(latestRequest?.guardian_email || '');
    setDocusignAdminEmail(user?.email || '');
    fetchLatestDocusign(latestRequest?.id);
    
    // Reset additional data for new request
    setAdditionalData(createAdditionalDataDraft());
    
    try {
      const response = await api.signup.getAttachments(resolvedRequestId);
      setAttachments(response.data || []);
    } catch (error) {
      console.error('Failed to fetch attachments:', error);
      setAttachments([]);
    }

    // Kick off mandate status fetch (will also auto-refresh).
    fetchLatestMandateStatus(latestRequest?.id);
  };

  const handleImportSubmissionReview = useCallback(async (action) => {
    if (!importSubmission?.id) return;
    await reviewAdminFormSubmission(importSubmission.id, action);
    closeImportSubmissionModal();
  }, [closeImportSubmissionModal, importSubmission?.id, reviewAdminFormSubmission]);

  const sendDocusignEnvelope = async () => {
    if (!selectedRequest) return;

    const resolvedGuardianEmail = String(docusignGuardianEmail || '').trim() || String(selectedRequest.guardian_email || '').trim();
    if (!resolvedGuardianEmail) {
      alert("Veuillez renseigner l'email du garant.");
      return;
    }

    const ch = String(additionalData.ch || '').replace(/\D/g, '').trim();
    const residenceAddress = String(additionalData.residence_address || '').trim();
    const moveInDate = String(additionalData.move_in_date || '').trim();
    const paymentAmount = String(additionalData.payment_amount || '').trim();
    const moveDateError = validateAdminMoveDates(moveInDate, additionalData.move_out_date);
    if (!ch) {
      alert("Veuillez saisir le CH (chiffres uniquement) avant d'envoyer DocuSign.");
      return;
    }
    if (!residenceAddress || !moveInDate || !paymentAmount) {
      alert("Veuillez remplir l'adresse de residence, la date d'entree et le montant du paiement (EUR) avant d'envoyer DocuSign.");
      return;
    }
    if (moveDateError) {
      alert(moveDateError);
      return;
    }
    try {
      setSendingDocusign(true);
      const { data } = await api.docusign.sendBundle({
        ticketId: selectedRequest.id,
        guardianEmail: resolvedGuardianEmail,
        adminEmail: String(docusignAdminEmail || '').trim() || undefined,
        adminData: {
          ...additionalData,
          ch,
        },
      });
      alert(`Enveloppe DocuSign envoyee. EnvelopeId: ${data.envelopeId || '(unknown)'}`);
      fetchLatestDocusign(selectedRequest.id);
      const refreshed = await api.signup.getRequestById(selectedRequest.id);
      if (refreshed?.data) {
        setSelectedRequest(refreshed.data);
      }
    } catch (e) {
      console.error('Failed to send DocuSign envelope:', e);
      alert(e.response?.data?.error || "Echec de l'envoi de l'enveloppe DocuSign.");
    } finally {
      setSendingDocusign(false);
    }
  };

  // Gestion de equipe functions
  const fetchTeamEmployees = async () => {
    try {
      setLoading(true);
      const response = await api.team.listAdminAccounts();
      const payload = response?.data || {};
      setTeamEmployees(payload.data || []);
      if (Array.isArray(payload.roleOptions) && payload.roleOptions.length > 0) {
        setTeamRoleOptions(payload.roleOptions);
      } else {
        setTeamRoleOptions([...DEFAULT_TEAM_ROLE_OPTIONS]);
      }
      setTeamPage(1);
    } catch (error) {
      console.error('Failed to fetch admin accounts:', error);
      setTeamEmployees([]);
    } finally {
      setLoading(false);
    }
  };

  const resetTeamRoleTypeForm = useCallback(() => {
    setEditingTeamRoleTypeId(null);
    setTeamRoleTypeForm({
      name: '',
      tabs: [],
    });
  }, []);

  const fetchTeamRoleTypes = useCallback(async () => {
    try {
      setTeamRoleTypeLoading(true);
      const response = await api.team.listAdminRoleTypes();
      const payload = response?.data || {};
      const roleTypes = Array.isArray(payload.data) ? payload.data : [];
      const tabCatalog = Array.isArray(payload.tabCatalog) && payload.tabCatalog.length > 0
        ? payload.tabCatalog
        : ASSIGNABLE_ADMIN_ROLE_TAB_OPTIONS;
      setTeamRoleTypes(roleTypes);
      setTeamRoleTabCatalog(tabCatalog);
      return roleTypes;
    } catch (error) {
      console.error('Failed to load admin role types:', error);
      setTeamRoleTypes([]);
      setTeamRoleTabCatalog([...ASSIGNABLE_ADMIN_ROLE_TAB_OPTIONS]);
      return [];
    } finally {
      setTeamRoleTypeLoading(false);
    }
  }, []);

  const openCreateTeamRoleType = useCallback(() => {
    setEditingTeamRoleTypeId(null);
    setTeamRoleTypeForm({ name: '', tabs: [] });
  }, []);

  const openEditTeamRoleType = useCallback((roleType) => {
    if (!roleType) return;
    setEditingTeamRoleTypeId(roleType.id);
    setTeamRoleTypeForm({
      name: String(roleType.name || ''),
      tabs: Array.isArray(roleType.tabs) ? roleType.tabs : [],
    });
  }, []);

  const toggleTeamRoleTypeTab = useCallback((tabKey) => {
    const key = String(tabKey || '').trim();
    if (!key) return;
    setTeamRoleTypeForm((prev) => {
      const currentTabs = Array.isArray(prev.tabs) ? prev.tabs : [];
      const exists = currentTabs.includes(key);
      return {
        ...prev,
        tabs: exists
          ? currentTabs.filter((tab) => tab !== key)
          : [...currentTabs, key],
      };
    });
  }, []);

  const handleSaveTeamRoleType = async (e) => {
    e.preventDefault();

    const payload = {
      name: String(teamRoleTypeForm.name || '').trim(),
      tabs: Array.isArray(teamRoleTypeForm.tabs) ? teamRoleTypeForm.tabs : [],
    };

    if (!payload.name) {
      alert('Veuillez saisir le nom du type d\'admin.');
      return;
    }

    if (payload.tabs.length === 0) {
      alert('Veuillez selectionner au moins un onglet.');
      return;
    }

    try {
      setTeamRoleTypeSaving(true);
      let response;
      if (editingTeamRoleTypeId) {
        response = await api.team.updateAdminRoleType(editingTeamRoleTypeId, payload);
      } else {
        response = await api.team.createAdminRoleType(payload);
      }

      const savedRoleType = response?.data?.data || null;
      await Promise.all([fetchTeamRoleTypes(), fetchTeamEmployees()]);

      if (savedRoleType?.id) {
        openEditTeamRoleType(savedRoleType);
      } else {
        resetTeamRoleTypeForm();
      }

      alert(editingTeamRoleTypeId ? 'Type d\'admin mis a jour.' : 'Type d\'admin cree.');
    } catch (error) {
      console.error('Failed to save admin role type:', error);
      alert(error?.response?.data?.error || 'Echec de l\'enregistrement du type d\'admin.');
    } finally {
      setTeamRoleTypeSaving(false);
    }
  };

  const handleAddEmployee = async (e) => {
    e.preventDefault();
    if (!newEmployee.first_name || !newEmployee.last_name || !newEmployee.email || !newEmployee.role) {
      alert('Veuillez remplir tous les champs requis.');
      return;
    }
    if (!isValidEmailAddress(newEmployee.email)) {
      alert('Veuillez saisir une adresse email valide contenant @.');
      return;
    }

    try {
      setTeamSaving(true);
      await api.team.createAdminAccount({
        first_name: newEmployee.first_name,
        last_name: newEmployee.last_name,
        email: newEmployee.email,
        role: newEmployee.role,
      });
      alert('Compte administrateur cree avec succes. Les identifiants ont ete envoyes par e-mail au nouvel administrateur.');
      setNewEmployee({
        first_name: '',
        last_name: '',
        email: '',
        role: teamRoleOptions[0] || DEFAULT_TEAM_ROLE_OPTIONS[0],
      });
      await fetchTeamEmployees();
      setShowTeamCreatePanel(false);
    } catch (err) {
      console.error('Failed to create admin account:', err);
      alert(err?.response?.data?.error || 'Echec de la creation du compte administrateur.');
    } finally {
      setTeamSaving(false);
    }
  };

  const handleDeleteEmployee = async (id) => {
    if (!window.confirm('Supprimer ce compte administrateur ?')) return;
    try {
      setTeamDeletingId(id);
      await api.team.deleteAdminAccount(id);
      await fetchTeamEmployees();
    } catch (err) {
      console.error('Failed to delete admin account:', err);
      alert(err?.response?.data?.error || 'Echec de la suppression du compte administrateur.');
    } finally {
      setTeamDeletingId(null);
    }
  };

  const resetCarouselForm = useCallback(() => {
    setCarouselForm({ title: '', body: '', image: null });
    setCarouselSlideEditingId(null);
  }, []);

  const openCreateCarouselSlide = useCallback(() => {
    resetCarouselForm();
    setShowCarouselSlideForm(true);
  }, [resetCarouselForm]);

  const closeCarouselSlideForm = useCallback(() => {
    setShowCarouselSlideForm(false);
    resetCarouselForm();
  }, [resetCarouselForm]);

  const handleSubmitCarouselSlide = async (e) => {
    e.preventDefault();
    if (!carouselForm.title.trim() || !carouselForm.body.trim()) {
      alert('Veuillez renseigner le titre et le contenu.');
      return;
    }
    if (!carouselSlideEditingId && !carouselForm.image) {
      alert('Veuillez selectionner une image.');
      return;
    }

    const formData = new FormData();
    formData.append('title', carouselForm.title.trim());
    formData.append('body', carouselForm.body.trim());
    if (carouselForm.image) formData.append('image', carouselForm.image);

    try {
      setCarouselSlideSaving(true);
      if (carouselSlideEditingId) {
        await api.carousel.updateSlide(carouselSlideEditingId, formData);
        alert('Slide du carrousel mise a jour.');
      } else {
        await api.carousel.createSlide(formData);
        alert('Slide du carrousel creee.');
      }
      closeCarouselSlideForm();
      await fetchCarouselSlidesAdmin();
    } catch (error) {
      console.error('Failed to save carousel slide:', error);
      alert(error?.response?.data?.error || "Echec de l'enregistrement de la slide du carrousel.");
    } finally {
      setCarouselSlideSaving(false);
    }
  };

  const handleEditCarouselSlide = (slide) => {
    if (!slide) return;
    setCarouselSlideEditingId(slide.id);
    setCarouselForm({
      title: slide.title || '',
      body: slide.body || '',
      image: null,
    });
    setShowCarouselSlideForm(true);
  };

  const handleDeleteCarouselSlide = async (id) => {
    if (!window.confirm('Supprimer cette slide du carrousel ?')) return;
    try {
      setCarouselSlideDeletingId(id);
      await api.carousel.deleteSlide(id);
      if (Number(carouselExpandedSlideId) === Number(id)) {
        setCarouselExpandedSlideId(null);
      }
      if (Number(carouselSlideEditingId) === Number(id)) {
        closeCarouselSlideForm();
      }
      await fetchCarouselSlidesAdmin();
    } catch (error) {
      console.error('Failed to delete carousel slide:', error);
      alert(error?.response?.data?.error || 'Echec de la suppression de la slide du carrousel.');
    } finally {
      setCarouselSlideDeletingId(null);
    }
  };

  const handleMoveCarouselSlide = async (slideId, direction) => {
    const currentIndex = carouselSlidesAdmin.findIndex((slide) => Number(slide.id) === Number(slideId));
    if (currentIndex < 0) return;

    const targetIndex = currentIndex + direction;
    if (targetIndex < 0 || targetIndex >= carouselSlidesAdmin.length) return;

    const reordered = [...carouselSlidesAdmin];
    const [moved] = reordered.splice(currentIndex, 1);
    reordered.splice(targetIndex, 0, moved);

    try {
      setCarouselSlideReorderingId(slideId);
      const orderedIds = reordered.map((slide) => slide.id);
      const response = await api.carousel.reorderSlides(orderedIds);
      const payload = response?.data;
      const rows = payload?.ok ? (payload.data || []) : (payload?.data || []);
      setCarouselSlidesAdmin(Array.isArray(rows) ? rows : reordered);
    } catch (error) {
      console.error('Failed to reorder carousel slides:', error);
      alert(error?.response?.data?.error || 'Echec du reordonnancement des slides du carrousel.');
    } finally {
      setCarouselSlideReorderingId(null);
    }
  };

  const fetchEvents = async () => {
    try {
      setLoading(true);
      const response = await api.events.getEvents({ limit: 50 });
      const evts = response?.data?.data || [];
      setEvents(evts);
      setEventsPage(1);
    } catch (error) {
      console.error('Failed to fetch events:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchEventCategories = useCallback(async () => {
    try {
      setEventCategoriesLoading(true);
      const response = await api.events.getCategories();
      const rows = response?.data?.data || [];
      setEventCategories(rows);
      setEventCategoryDraftById((prev) => {
        const next = {};
        for (const row of rows) {
          next[row.id] = {
            label: row.label || '',
            slug: row.slug || '',
          };
        }
        return { ...prev, ...next };
      });
    } catch (error) {
      console.error('Failed to fetch event categories:', error);
      setEventCategories([]);
    } finally {
      setEventCategoriesLoading(false);
    }
  }, []);

  const fetchFooterAdmin = useCallback(async () => {
    try {
      setFooterLoading(true);
      const response = await api.footer.getAdmin();
      const data = response?.data?.data || {};
      setFooterTextDraft(data.adeef_text || '');
      const contacts = Array.isArray(data.contacts) ? data.contacts : [];
      setFooterContacts(contacts.map((row) => ({
        ...row,
        is_published: Number(row?.is_published ?? 1) === 1 || row?.is_published === true,
      })));
    } catch (error) {
      console.error('Failed to fetch footer content:', error);
      setFooterContacts([]);
    } finally {
      setFooterLoading(false);
    }
  }, []);

  const saveFooterText = async () => {
    const text = String(footerTextDraft || '').trim();
    if (!text) {
      alert('Le texte ADEEF ne peut pas etre vide.');
      return;
    }

    try {
      setFooterSavingText(true);
      const response = await api.footer.updateText(text);
      const data = response?.data?.data || {};
      setFooterTextDraft(data.adeef_text || text);
      if (Array.isArray(data.contacts)) {
        setFooterContacts(data.contacts.map((row) => ({
          ...row,
          is_published: Number(row?.is_published ?? 1) === 1 || row?.is_published === true,
        })));
      }
      setShowFooterTextEditor(false);
      alert('Texte du footer mis a jour avec succes.');
    } catch (error) {
      console.error('Failed to update footer text:', error);
      alert(error?.response?.data?.error || 'Echec de la mise a jour du texte du footer.');
    } finally {
      setFooterSavingText(false);
    }
  };

  const startEditFooterContact = (contact) => {
    setFooterContactEditingId(contact?.id || null);
    setFooterContactForm({
      label: contact?.label || '',
      href: contact?.href || '',
      image_url: contact?.image_url || '',
    });
    setFooterContactImageFile(null);
    setShowFooterTextEditor(false);
    setShowFooterContactEditor(true);
  };

  const resetFooterContactForm = () => {
    setFooterContactEditingId(null);
    setFooterContactForm({ label: '', href: '', image_url: '' });
    setFooterContactImageFile(null);
    setShowFooterContactEditor(false);
  };

  const uploadFooterContactImage = async (file) => {
    if (!file) return null;
    const response = await api.footer.uploadContactLogo(file);
    const uploadedImageUrl = String(response?.data?.data?.image_url || '').trim();
    if (!uploadedImageUrl) {
      throw new Error('Uploaded footer logo URL is missing');
    }
    return uploadedImageUrl;
  };

  const submitFooterContact = async (e) => {
    e.preventDefault();
    const label = String(footerContactForm.label || '').trim();
    const href = String(footerContactForm.href || '').trim();
    let imageUrl = String(footerContactForm.image_url || '').trim();

    if (!label) {
      alert('Le libelle du contact est requis.');
      return;
    }

    if (href && !/^(https?:\/\/|mailto:|tel:)/i.test(href)) {
      alert('Le lien du contact doit commencer par http://, https://, mailto:, ou tel:');
      return;
    }

    if (!imageUrl && !footerContactImageFile) {
      alert('Veuillez selectionner une image de logo.');
      return;
    }

    if (imageUrl && !/^https?:\/\//i.test(imageUrl) && !/^\//.test(imageUrl)) {
      alert('Le logo doit commencer par http://, https://, ou /');
      return;
    }

    try {
      setFooterContactSaving(true);
      if (footerContactImageFile) {
        imageUrl = await uploadFooterContactImage(footerContactImageFile);
      }

      if (footerContactEditingId) {
        await api.footer.updateContact(footerContactEditingId, {
          label,
          href: href || null,
          image_url: imageUrl || null,
        });
      } else {
        await api.footer.createContact({ label, href: href || null, image_url: imageUrl || null });
      }

      await fetchFooterAdmin();
      resetFooterContactForm();
      setShowFooterContactEditor(false);
    } catch (error) {
      console.error('Failed to save footer contact:', error);
      alert(error?.response?.data?.error || "Echec de l'enregistrement de l'entree de contact.");
    } finally {
      setFooterContactSaving(false);
    }
  };

  const toggleFooterContactPublish = async (contact) => {
    if (!contact?.id) return;
    const nextPublished = !(Number(contact?.is_published) === 1 || contact?.is_published === true);

    try {
      setFooterContactPublishingId(contact.id);
      await api.footer.toggleContactPublish(contact.id, nextPublished);
      await fetchFooterAdmin();
    } catch (error) {
      console.error('Failed to toggle footer contact publish state:', error);
      alert(error?.response?.data?.error || 'Echec de la mise a jour du statut de publication.');
    } finally {
      setFooterContactPublishingId(null);
    }
  };

  const deleteFooterContact = async (contact) => {
    if (!contact?.id) return;
    if (!window.confirm(`Supprimer l'entree de contact "${contact.label || 'element'}" ?`)) return;

    try {
      setFooterContactDeletingId(contact.id);
      await api.footer.deleteContact(contact.id);
      await fetchFooterAdmin();

      if (Number(footerContactEditingId) === Number(contact.id)) {
        resetFooterContactForm();
      }
    } catch (error) {
      console.error('Failed to delete footer contact:', error);
      alert(error?.response?.data?.error || "Echec de la suppression de l'entree de contact.");
    } finally {
      setFooterContactDeletingId(null);
    }
  };

  const createEventCategoryEntry = async () => {
    const label = String(newEventCategory.label || '').trim();
    const slug = String(newEventCategory.slug || '').trim();

    if (!label) {
      alert('Le libelle de la categorie est requis.');
      return;
    }

    try {
      setCreatingEventCategory(true);
      await api.events.createCategory({
        label,
        slug: slug || undefined,
      });
      setNewEventCategory({ label: '', slug: '' });
      await fetchEventCategories();
    } catch (error) {
      console.error('Failed to create event category:', error);
      alert(error?.response?.data?.error || 'Echec de la creation de la categorie.');
    } finally {
      setCreatingEventCategory(false);
    }
  };

  const saveEventCategoryEntry = async (categoryId) => {
    const draft = eventCategoryDraftById[categoryId] || {};
    const label = String(draft.label || '').trim();
    const slug = String(draft.slug || '').trim();

    if (!label || !slug) {
      alert('Le libelle et le slug de la categorie sont requis.');
      return;
    }

    try {
      setEventCategorySavingId(categoryId);
      await api.events.updateCategory(categoryId, { label, slug });
      await Promise.all([fetchEventCategories(), fetchEvents()]);
    } catch (error) {
      console.error('Failed to update category:', error);
      alert(error?.response?.data?.error || 'Echec de la mise a jour de la categorie.');
    } finally {
      setEventCategorySavingId(null);
    }
  };

  const deleteEventCategoryEntry = async (category) => {
    if (!category?.id) return;

    const eventsCount = Number(category.events_count || 0);
    let deleteEvents = false;

    if (eventsCount > 0) {
      const confirmed = window.confirm(
        `Cette categorie contient ${eventsCount} evenement(s). La suppression supprimera aussi ces evenements. Continuer ?`
      );
      if (!confirmed) return;
      deleteEvents = true;
    } else {
      const confirmed = window.confirm(`Supprimer la categorie "${category.label}" ?`);
      if (!confirmed) return;
    }

    try {
      setEventCategoryDeletingId(category.id);
      await api.events.deleteCategory(category.id, { deleteEvents });
      await Promise.all([fetchEventCategories(), fetchEvents()]);
    } catch (error) {
      const serverCode = error?.response?.data?.code;
      const eventsFromMeta = Number(error?.response?.data?.meta?.eventsCount || 0);
      if (serverCode === 'CATEGORY_HAS_EVENTS' && eventsFromMeta > 0) {
        const confirmed = window.confirm(
          `Cette categorie contient ${eventsFromMeta} evenement(s). Supprimer la categorie et ces evenements maintenant ?`
        );
        if (confirmed) {
          await api.events.deleteCategory(category.id, { deleteEvents: true });
          await Promise.all([fetchEventCategories(), fetchEvents()]);
        }
      } else {
        console.error('Failed to delete category:', error);
        alert(error?.response?.data?.error || 'Echec de la suppression de la categorie.');
      }
    } finally {
      setEventCategoryDeletingId(null);
    }
  };

  const fetchSignupRequests = useCallback(async ({ signal, silent = false } = {}) => {
    try {
      if (!silent) setLoading(true);
      const response = await api.signup.getRequests(signal ? { signal } : {});
      const payload = response?.data;
      const rows = Array.isArray(payload) ? payload : (payload?.data || []);
      const normalizedRows = (Array.isArray(rows) ? rows : []).map((row) => {
        if (!row || typeof row !== 'object') return row;
        const resolvedId = resolveHousingRequestId(row);
        return {
          ...row,
          id: resolvedId ?? row.id ?? null,
        };
      });
      setSignupRequests(normalizedRows);
    } catch (error) {
      if (error?.name === 'CanceledError' || error?.code === 'ERR_CANCELED') return;
      console.error('Failed to fetch housing applications:', error);
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  const fetchTestimonialsRows = useCallback(async ({ q } = {}) => {
    try {
      setTestimonialsLoading(true);
      const response = await api.reviews.listAdmin({ q: String((q ?? testimonialsQuery) || '').trim(), limit: 1000 });
      const rows = Array.isArray(response?.data?.data) ? response.data.data : [];
      setTestimonialsRows(rows);
    } catch (error) {
      console.error('Failed to fetch testimonials rows:', error);
      setTestimonialsRows([]);
    } finally {
      setTestimonialsLoading(false);
    }
  }, [testimonialsQuery]);

  const fetchTestimonialsRegionInfo = useCallback(async () => {
    try {
      setTestimonialsRegionInfoLoading(true);
      const response = await api.reviews.listRegionInfoAdmin();
      const rows = Array.isArray(response?.data?.data) ? response.data.data : [];
      const map = {};
      const parisKey = toRegionKey(DEFAULT_REVIEW_REGION);
      const normalizedParis = normalizeName(DEFAULT_REVIEW_REGION);

      rows.forEach((row) => {
        const rawKey = String(row?.region_key || '').trim();
        const normalizedKey = normalizeName(rawKey);
        const key = PARIS_MERGED_DEPARTMENTS.has(normalizedKey)
          ? parisKey
          : toRegionKey(rawKey);
        if (!key) return;
        const value = String(row?.info_text || '').trim();

        if (key === parisKey) {
          if (normalizedKey === normalizedParis || !map[parisKey]) {
            map[parisKey] = value;
          }
          return;
        }

        map[key] = value;
      });
      setTestimonialsRegionInfoMap(map);
      setTestimonialsRegionInfoDrafts(map);
    } catch (error) {
      console.error('Failed to fetch testimonials region info:', error);
      setTestimonialsRegionInfoMap({});
      setTestimonialsRegionInfoDrafts({});
    } finally {
      setTestimonialsRegionInfoLoading(false);
    }
  }, []);

  const deleteTestimonialRow = async (row) => {
    const id = Number(row?.id);
    if (!id) return;
    const reviewer = String(row?.reviewer_display_name || row?.first_name || '').trim() || 'cet avis';
    const confirmed = window.confirm(`Supprimer l'avis de ${reviewer} ?`);
    if (!confirmed) return;

    try {
      setTestimonialsDeleteId(id);
      await api.reviews.deleteAdmin(id);
      await fetchTestimonialsRows({ q: testimonialsQuery });
    } catch (error) {
      console.error('Failed to delete testimonial row:', error);
      alert(error?.response?.data?.error || 'Echec de la suppression de cet avis.');
    } finally {
      setTestimonialsDeleteId(null);
    }
  };

  const saveTestimonialRegionInfo = async (region) => {
    const key = String(region?.key || '').trim();
    if (!key) return;
    const nextText = String(testimonialsRegionInfoDrafts[key] || '').trim();
    if (!nextText) {
      alert('Le texte d\'information ne peut pas etre vide.');
      return;
    }

    try {
      setTestimonialsRegionInfoSavingKey(key);
      await api.reviews.saveRegionInfoAdmin(key, nextText);
      setTestimonialsRegionInfoMap((prev) => ({ ...prev, [key]: nextText }));
    } catch (error) {
      console.error('Failed to save testimonials region info:', error);
      alert(error?.response?.data?.error || 'Echec de la sauvegarde de l\'information.');
    } finally {
      setTestimonialsRegionInfoSavingKey('');
    }
  };

  const fetchReviewFormConfig = useCallback(async () => {
    try {
      setReviewFormConfigLoading(true);
      const response = await api.reviews.getFormConfig();
      const fields = Array.isArray(response?.data?.data) ? response.data.data : [];
      setReviewFormFields(fields);
    } catch (error) {
      console.error('Failed to fetch review form config:', error);
      setReviewFormFields([]);
    } finally {
      setReviewFormConfigLoading(false);
    }
  }, []);

  const saveReviewFormConfig = useCallback(async () => {
    try {
      setReviewFormConfigSaving(true);
      await api.reviews.saveFormConfig(reviewFormFields);
    } catch (error) {
      console.error('Failed to save review form config:', error);
      alert(error?.response?.data?.error || 'Echec de la sauvegarde de la configuration.');
    } finally {
      setReviewFormConfigSaving(false);
    }
  }, [reviewFormFields]);

  const addReviewFormField = () => {
    const draft = { ...reviewFormConfigDraft };
    if (!draft.id || !draft.label) {
      alert('L\'identifiant et le libelle sont requis.');
      return;
    }
    if (reviewFormFields.some(f => f.id === draft.id)) {
      alert('Cet identifiant existe deja.');
      return;
    }
    const hasOptions = draft.type === 'dropdown' || draft.type === 'select' || draft.type === 'checkbox';
    if (hasOptions && (!Array.isArray(draft.options) || draft.options.length === 0)) {
      alert('Vous devez ajouter au moins une option.');
      return;
    }
    const newField = {
      id: draft.id,
      type: draft.type,
      label: draft.label,
      placeholder: draft.placeholder || '',
      required: draft.required,
      order: reviewFormFields.length,
    };
    if (hasOptions) {
      newField.options = [...draft.options];
    }
    setReviewFormFields(prev => [...prev, newField]);
    setReviewFormConfigDraft({ id: '', type: 'text', label: '', placeholder: '', required: false, options: [] });
    setDraftOptionInput('');
  };

  const updateReviewFormField = () => {
    if (!reviewFormConfigEditingField) return;
    const draft = { ...reviewFormConfigDraft };
    if (!draft.label) {
      alert('Le libelle est requis.');
      return;
    }
    const hasOptions = draft.type === 'dropdown' || draft.type === 'select' || draft.type === 'checkbox';
    if (hasOptions && (!Array.isArray(draft.options) || draft.options.length === 0)) {
      alert('Vous devez ajouter au moins une option.');
      return;
    }
    setReviewFormFields(prev => prev.map(f => {
      if (f.id !== reviewFormConfigEditingField) return f;
      const updated = { ...f, label: draft.label, placeholder: draft.placeholder || '', required: draft.required, type: draft.type };
      if (hasOptions) {
        updated.options = [...draft.options];
      } else {
        delete updated.options;
      }
      return updated;
    }));
    setReviewFormConfigEditingField(null);
    setReviewFormConfigDraft({ id: '', type: 'text', label: '', placeholder: '', required: false, options: [] });
    setDraftOptionInput('');
  };

  const deleteReviewFormField = (fieldId) => {
    const field = reviewFormFields.find(f => f.id === fieldId);
    if (field?.locked) {
      alert('Ce champ ne peut pas etre supprime.');
      return;
    }
    if (!window.confirm('Supprimer ce champ du formulaire ?')) return;
    setReviewFormFields(prev => prev.filter(f => f.id !== fieldId));
  };

  const startEditReviewFormField = (field) => {
    setReviewFormConfigEditingField(field.id);
    setReviewFormConfigDraft({
      id: field.id,
      type: field.type || 'text',
      label: field.label || '',
      placeholder: field.placeholder || '',
      required: Boolean(field.required),
      options: Array.isArray(field.options) ? [...field.options] : [],
    });
    setDraftOptionInput('');
  };

  const addDraftOption = () => {
    const val = String(draftOptionInput || '').trim();
    if (!val) return;
    if (reviewFormConfigDraft.options.includes(val)) {
      alert('Cette option existe deja.');
      return;
    }
    setReviewFormConfigDraft(prev => ({ ...prev, options: [...prev.options, val] }));
    setDraftOptionInput('');
  };

  const removeDraftOption = (index) => {
    setReviewFormConfigDraft(prev => ({ ...prev, options: prev.options.filter((_, i) => i !== index) }));
  };

  const editDraftOption = (index, newValue) => {
    const val = String(newValue || '').trim();
    if (!val) return;
    setReviewFormConfigDraft(prev => {
      const next = [...prev.options];
      next[index] = val;
      return { ...prev, options: next };
    });
  };

  const moveDraftOption = (index, direction) => {
    setReviewFormConfigDraft(prev => {
      const next = [...prev.options];
      const target = direction === 'up' ? index - 1 : index + 1;
      if (target < 0 || target >= next.length) return prev;
      [next[index], next[target]] = [next[target], next[index]];
      return { ...prev, options: next };
    });
  };

  const moveReviewFormField = (fieldId, direction) => {
    setReviewFormFields(prev => {
      const idx = prev.findIndex(f => f.id === fieldId);
      if (idx < 0) return prev;
      const newIdx = direction === 'up' ? idx - 1 : idx + 1;
      if (newIdx < 0 || newIdx >= prev.length) return prev;
      const next = [...prev];
      [next[idx], next[newIdx]] = [next[newIdx], next[idx]];
      return next.map((f, i) => ({ ...f, order: i }));
    });
  };

  useEffect(() => {
    if (activeTab !== 'settings' && !allowedTabs.has(activeTab)) return;

    if (activeTab === 'users') {
      // Users tab uses server-side search/sort/pagination.
    } else if (activeTab === 'team') {
      setTeamPage(1);
      setShowTeamCreatePanel(false);
      setShowTeamRoleTypePanel(false);
      fetchTeamEmployees();
      fetchTeamRoleTypes();
    } else if (activeTab === 'events') {
      fetchEventCategories();
      fetchEvents();
    } else if (activeTab === TAB_KEYS.FOOTER) {
      fetchFooterAdmin();
    } else if (activeTab === TAB_KEYS.CAROUSEL) {
      setCarouselSlidesPage(1);
      setCarouselExpandedSlideId(null);
      fetchCarouselSlidesAdmin();
    } else if (activeTab === 'event-requests') {
      setEventRegistrationsPage(1);
      fetchEventRegistrations();
    } else if (activeTab === 'signup') {
      setSignupPage(1);
      setSelectedRequest(null);
      setShowSignupRequestModal(false);
      setAttachments([]);
      setMandateLatest(null);
      fetchSignupRequests();
    } else if (activeTab === 'document-requests') {
      setAdminDocumentRequestsPage(1);
      setShowAdminDocumentRequestModal(false);
      setAdminDocumentRequestSelected(null);
      fetchAdminDocumentRequests();
    } else if (activeTab === 'payments') {
      // Paiements tab uses admin payments list API (server-side pagination).
    } else if (activeTab === TAB_KEYS.TESTIMONIALS_MANAGEMENT) {
      fetchTestimonialsRows({ q: testimonialsQuery });
      fetchReviewFormConfig();
    }
  }, [activeTab, allowedTabs, fetchAdminDocumentRequests, fetchCarouselSlidesAdmin, fetchEventCategories, fetchFooterAdmin, fetchReviewFormConfig, fetchSignupRequests, fetchTeamRoleTypes, fetchTestimonialsRows, testimonialsQuery]);

  useEffect(() => {
    if (!Array.isArray(eventCategories) || eventCategories.length === 0) return;
    const firstSlug = eventCategories[0]?.slug || '';
    if (!firstSlug) return;

    setNewEvent((prev) => {
      const stillValid = eventCategories.some((row) => row.slug === prev.category);
      if (stillValid) return prev;
      return { ...prev, category: firstSlug };
    });

    setEventsEditData((prev) => {
      if (!prev) return prev;
      const stillValid = eventCategories.some((row) => row.slug === prev.category);
      if (stillValid) return prev;
      return { ...prev, category: firstSlug };
    });
  }, [eventCategories]);

  const fetchEventRegistrations = async () => {
    try {
      setEventRegistrationsLoading(true);
      const response = await api.events.getEventRegistrations({ limit: 100 });
      const regs = response?.data?.data || [];
      setEventRegistrations(regs);
      setEventRegistrationsPage(1);
    } catch (error) {
      console.error('Failed to fetch event registrations:', error);
      setEventRegistrations([]);
    } finally {
      setEventRegistrationsLoading(false);
    }
  };

  const eventsTotalPages = useMemo(() => Math.max(1, Math.ceil((events?.length || 0) / eventsPageSize)), [events?.length, eventsPageSize]);
  const eventsPageClamped = useMemo(() => Math.min(eventsTotalPages, Math.max(1, eventsPage)), [eventsPage, eventsTotalPages]);
  const pagedEvents = useMemo(() => {
    const start = (eventsPageClamped - 1) * eventsPageSize;
    return (events || []).slice(start, start + eventsPageSize);
  }, [events, eventsPageClamped, eventsPageSize]);

  useEffect(() => {
    if (eventsPage > eventsTotalPages) setEventsPage(eventsTotalPages);
  }, [eventsPage, eventsTotalPages]);

  const signupTotalPages = useMemo(() => Math.max(1, Math.ceil((housingTableRequests?.length || 0) / signupPageSize)), [housingTableRequests?.length, signupPageSize]);
  const signupPageClamped = useMemo(() => Math.min(signupTotalPages, Math.max(1, signupPage)), [signupPage, signupTotalPages]);
  const pagedSignupRequests = useMemo(() => {
    const start = (signupPageClamped - 1) * signupPageSize;
    return (housingTableRequests || []).slice(start, start + signupPageSize);
  }, [housingTableRequests, signupPageClamped, signupPageSize]);

  useEffect(() => {
    if (signupPage > signupTotalPages) setSignupPage(signupTotalPages);
  }, [signupPage, signupTotalPages]);

  const eventRegistrationsTotalPages = useMemo(() => Math.max(1, Math.ceil((eventRegistrations?.length || 0) / eventRegistrationsPageSize)), [eventRegistrations?.length, eventRegistrationsPageSize]);
  const eventRegistrationsPageClamped = useMemo(() => Math.min(eventRegistrationsTotalPages, Math.max(1, eventRegistrationsPage)), [eventRegistrationsPage, eventRegistrationsTotalPages]);
  const pagedEventRegistrations = useMemo(() => {
    const start = (eventRegistrationsPageClamped - 1) * eventRegistrationsPageSize;
    return (eventRegistrations || []).slice(start, start + eventRegistrationsPageSize);
  }, [eventRegistrations, eventRegistrationsPageClamped, eventRegistrationsPageSize]);

  useEffect(() => {
    if (eventRegistrationsPage > eventRegistrationsTotalPages) setEventRegistrationsPage(eventRegistrationsTotalPages);
  }, [eventRegistrationsPage, eventRegistrationsTotalPages]);

  const adminFormsTotalPages = useMemo(
    () => Math.max(1, Math.ceil((adminForms?.length || 0) / adminFormsPageSize)),
    [adminForms?.length, adminFormsPageSize]
  );
  const adminFormsPageClamped = useMemo(
    () => Math.min(adminFormsTotalPages, Math.max(1, adminFormsPage)),
    [adminFormsPage, adminFormsTotalPages]
  );
  const pagedAdminForms = useMemo(() => {
    const start = (adminFormsPageClamped - 1) * adminFormsPageSize;
    return (adminForms || []).slice(start, start + adminFormsPageSize);
  }, [adminForms, adminFormsPageClamped, adminFormsPageSize]);

  useEffect(() => {
    if (adminFormsPage > adminFormsTotalPages) setAdminFormsPage(adminFormsTotalPages);
  }, [adminFormsPage, adminFormsTotalPages]);

  const adminFormSubmissionsTotalPages = useMemo(
    () => Math.max(1, Math.ceil((filteredAdminFormSubmissions?.length || 0) / adminFormSubmissionsPageSize)),
    [filteredAdminFormSubmissions?.length, adminFormSubmissionsPageSize]
  );
  const adminFormSubmissionsPageClamped = useMemo(
    () => Math.min(adminFormSubmissionsTotalPages, Math.max(1, adminFormSubmissionsPage)),
    [adminFormSubmissionsPage, adminFormSubmissionsTotalPages]
  );
  const pagedAdminFormSubmissions = useMemo(() => {
    const start = (adminFormSubmissionsPageClamped - 1) * adminFormSubmissionsPageSize;
    return (filteredAdminFormSubmissions || []).slice(start, start + adminFormSubmissionsPageSize);
  }, [filteredAdminFormSubmissions, adminFormSubmissionsPageClamped, adminFormSubmissionsPageSize]);

  useEffect(() => {
    if (adminFormSubmissionsPage > adminFormSubmissionsTotalPages) {
      setAdminFormSubmissionsPage(adminFormSubmissionsTotalPages);
    }
  }, [adminFormSubmissionsPage, adminFormSubmissionsTotalPages]);

  const carouselSlidesTotalPages = useMemo(
    () => Math.max(1, Math.ceil((carouselSlidesAdmin?.length || 0) / carouselSlidesPageSize)),
    [carouselSlidesAdmin?.length, carouselSlidesPageSize]
  );
  const carouselSlidesPageClamped = useMemo(
    () => Math.min(carouselSlidesTotalPages, Math.max(1, carouselSlidesPage)),
    [carouselSlidesPage, carouselSlidesTotalPages]
  );
  const pagedCarouselSlides = useMemo(() => {
    const start = (carouselSlidesPageClamped - 1) * carouselSlidesPageSize;
    return (carouselSlidesAdmin || []).slice(start, start + carouselSlidesPageSize);
  }, [carouselSlidesAdmin, carouselSlidesPageClamped, carouselSlidesPageSize]);

  const carouselEditingSlide = useMemo(
    () => (carouselSlideEditingId
      ? (carouselSlidesAdmin || []).find((slide) => Number(slide.id) === Number(carouselSlideEditingId)) || null
      : null),
    [carouselSlideEditingId, carouselSlidesAdmin]
  );

  useEffect(() => {
    if (carouselSlidesPage > carouselSlidesTotalPages) setCarouselSlidesPage(carouselSlidesTotalPages);
  }, [carouselSlidesPage, carouselSlidesTotalPages]);

  const contactMessagesTotalPages = useMemo(
    () => Math.max(1, Math.ceil((contactMessages?.length || 0) / contactMessagesPageSize)),
    [contactMessages?.length, contactMessagesPageSize]
  );
  const contactMessagesPageClamped = useMemo(
    () => Math.min(contactMessagesTotalPages, Math.max(1, contactMessagesPage)),
    [contactMessagesPage, contactMessagesTotalPages]
  );
  const pagedContactMessages = useMemo(() => {
    const start = (contactMessagesPageClamped - 1) * contactMessagesPageSize;
    return (contactMessages || []).slice(start, start + contactMessagesPageSize);
  }, [contactMessages, contactMessagesPageClamped, contactMessagesPageSize]);

  useEffect(() => {
    if (contactMessagesPage > contactMessagesTotalPages) setContactMessagesPage(contactMessagesTotalPages);
  }, [contactMessagesPage, contactMessagesTotalPages]);

  const handleCreateEvent = async (e) => {
    e.preventDefault();

    if (!Array.isArray(eventCategories) || eventCategories.length === 0) {
      alert("Veuillez creer au moins une categorie d'evenement avant de creer un evenement.");
      return;
    }

    const isNormalEvent = newEvent.event_type === 'normal';
    const isPaidPremium = newEvent.event_type === 'premium' && newEvent.premium_payment_required;
    
    if (!newEvent.headline || !newEvent.body || !newEvent.date || !newEvent.category || !newEvent.image) {
      alert('Veuillez remplir les champs requis (titre, description, date, image, categorie).');
      return;
    }
    
    if (isNormalEvent && !newEvent.social_url) {
      alert('Le lien est requis pour les evenements normaux.');
      return;
    }
    
    if (isPaidPremium && !newEvent.registration_link) {
      alert('Le lien de paiement est requis pour les evenements premium payants.');
      return;
    }

    const formData = new FormData();
    formData.append('headline', newEvent.headline);
    formData.append('body', newEvent.body);
    formData.append('date', newEvent.date);
    formData.append('category', newEvent.category);
    formData.append('event_type', newEvent.event_type);
    formData.append('premium_payment_required', newEvent.premium_payment_required);
    if (newEvent.social_url) {
      formData.append('social_url', newEvent.social_url);
    }
    if (newEvent.registration_link) {
      formData.append('registration_link', newEvent.registration_link);
    }
    formData.append('image', newEvent.image);

    try {
      setCreatingEvent(true);
      await api.events.createEvent(formData);
      alert('Evenement cree ! (Brouillon)');
      const fallbackCategory = eventCategories[0]?.slug || '';
      setNewEvent({ headline: '', body: '', date: '', category: fallbackCategory, event_type: 'normal', premium_payment_required: false, social_url: '', registration_link: '', image: null });
      setShowCreateEvent(false);
      fetchEvents();
      fetchEventCategories();
    } catch (err) {
      console.error('Failed to create event:', err);
      // Surface server error message when available
      const serverMsg = err?.response?.data?.error || err?.response?.data?.message || err?.message;
      alert(serverMsg || "Echec de creation de l'evenement.");
    } finally {
      setCreatingEvent(false);
    }
  };

  // (Mandate checker refactored to fetchLatestMandateStatus, mirroring DocuSign pattern)

  const createMandateRequest = async () => {
    if (!selectedRequest) return;
    setMandateLoading(true);
    try {
      const response = await api.gocardless.createMandate(selectedRequest.id, {
        successRedirectUrl: window.location.origin,
      });
      const { redirectUrl } = response.data.data; // Fix: access nested data property

      // Envoyer email to user with the redirect URL
      await api.email.sendMandateEmail(selectedRequest.email, redirectUrl);

      alert("E-mail de configuration du mandat envoye a l'utilisateur avec succes !");
      fetchLatestMandateStatus(selectedRequest.id); // Actualiser status
      const refreshed = await api.signup.getRequestById(selectedRequest.id);
      if (refreshed?.data) {
        setSelectedRequest(refreshed.data);
      }
    } catch (error) {
      console.error('Echec de la creation de la demande de mandat :', error);
      const errorMessage = error.response?.data?.error || 'Echec de la creation de la demande de mandat.';
      alert(`Echec de la creation de la demande de mandat : ${errorMessage}`);
    } finally {
      setMandateLoading(false);
    }
  };

  const createImportMandateRequest = async () => {
    if (!importSubmission?.ticket_id) return;
    const email = String(importSubmission?.applicant_email || importSubmission?.responses?.email || '').trim();
    if (!email) {
      alert("Aucun e-mail de demandeur n'est disponible pour envoyer le mandat.");
      return;
    }
    setImportMandateLoading(true);
    try {
      const response = await api.gocardless.createMandate(importSubmission.ticket_id, {
        successRedirectUrl: window.location.origin,
      });
      const { redirectUrl } = response.data.data;

      await api.email.sendMandateEmail(email, redirectUrl);

      alert("E-mail de configuration du mandat envoye a l'utilisateur avec succes !");
      fetchLatestImportMandateStatus(importSubmission.ticket_id);
    } catch (error) {
      console.error('Echec de la creation de la demande de mandat (import) :', error);
      const errorMessage = error.response?.data?.error || 'Echec de la creation de la demande de mandat.';
      alert(`Echec de la creation de la demande de mandat : ${errorMessage}`);
    } finally {
      setImportMandateLoading(false);
    }
  };

  const handleEventAction = async (eventId, action) => {
    try {
      if (action === 'publish') {
        await api.events.publishEvent(eventId);
      } else if (action === 'unpublish') {
        await api.events.unpublishEvent(eventId);
      } else if (action === 'delete') {
        if (window.confirm('Etes-vous sur de vouloir supprimer cet evenement ? Cette action est irreversible.')) {
          await api.events.deleteEvent(eventId);
        } else {
          return;
        }
      }
      // Actualiser events
      fetchEvents();
      fetchEventCategories();
    } catch (error) {
      console.error('Failed to update event:', error);
    }
  };

  const handleApproveRegistration = async (registrationId) => {
    if (eventRegistrationProcessing) return;
    try {
      setEventRegistrationProcessing(true);
      await api.events.approveRegistration(registrationId);
      alert('Inscription approuvee et utilisateur notifie par e-mail.');
      fetchEventRegistrations();
    } catch (error) {
      console.error('Failed to approve registration:', error);
      alert("Echec de l'approbation de l'inscription. Veuillez reessayer.");
    } finally {
      setEventRegistrationProcessing(false);
    }
  };

  const handleDisapproveRegistration = async (registrationId) => {
    if (eventRegistrationProcessing) return;
    try {
      setEventRegistrationProcessing(true);
      await api.events.disapproveRegistration(registrationId);
      alert('Inscription refusee et utilisateur notifie par e-mail.');
      fetchEventRegistrations();
    } catch (error) {
      console.error('Failed to disapprove registration:', error);
      alert("Echec du refus de l'inscription. Veuillez reessayer.");
    } finally {
      setEventRegistrationProcessing(false);
    }
  };

  const handleDeleteRegistration = async (registrationId) => {
    if (eventRegistrationProcessing) return;
    if (!window.confirm('Etes-vous sur de vouloir supprimer cette inscription ? Cette action est irreversible.')) {
      return;
    }
    try {
      setEventRegistrationProcessing(true);
      await api.events.deleteRegistration(registrationId);
      alert('Inscription supprimee avec succes.');
      setEventRegistrationsExpandedId(null);
      fetchEventRegistrations();
    } catch (error) {
      console.error('Failed to delete registration:', error);
      alert("Echec de la suppression de l'inscription. Veuillez reessayer.");
    } finally {
      setEventRegistrationProcessing(false);
    }
  };

  const markContactMessageSeen = async (messageId) => {
    try {
      const response = await api.contactMessages.markSeen(messageId);
      const payload = response?.data;
      const updated = payload?.ok ? payload.data : payload?.data;
      if (updated) {
        applyContactMessageUpdate(updated);
      }
    } catch (error) {
      console.error('Failed to mark contact message as seen:', error);
    }
  };

  const openContactMessage = async (message) => {
    if (!message?.id) return;
    const expanded = contactMessagesExpandedId === message.id;
    if (expanded) {
      setContactMessagesExpandedId(null);
      return;
    }

    setContactMessagesExpandedId(message.id);
    setContactResponseDrafts((prev) => {
      if (Object.prototype.hasOwnProperty.call(prev, message.id)) return prev;
      return { ...prev, [message.id]: message.admin_response || '' };
    });

    if (String(message.status || '').toLowerCase() === 'new') {
      await markContactMessageSeen(message.id);
    }
  };

  const submitContactResponse = async (messageId) => {
    const row = contactMessages.find((item) => item.id === messageId);
    if (!row) return;

    const responseBody = String(contactResponseDrafts[messageId] || '').trim();
    if (!responseBody) {
      alert("Veuillez saisir une reponse avant l'envoi.");
      return;
    }

    try {
      setContactMessagesRespondingId(messageId);
      const response = await api.contactMessages.respond(messageId, responseBody);
      const payload = response?.data;
      const updated = payload?.ok ? payload.data : payload?.data;
      if (updated) {
        applyContactMessageUpdate(updated);
        setContactResponseDrafts((prev) => ({
          ...prev,
          [messageId]: updated.admin_response || responseBody,
        }));
      }
      alert('Reponse envoyee avec succes.');
    } catch (error) {
      console.error('Failed to send contact response:', error);
      alert(error?.response?.data?.error || "Echec de l'envoi de la reponse.");
    } finally {
      setContactMessagesRespondingId(null);
    }
  };

  const handleEditEvent = (event) => {
    const fallbackCategory = eventCategories[0]?.slug || '';
    setEventsEditMode(true);
    setEventsEditData({
      title: event.title || '',
      description: event.description || '',
      start_at: event.start_at ? event.start_at.split('T')[0] : '',
      category: event.category || fallbackCategory,
      social_url: event.social_url || '',
      banner_url: event.banner_url || '',
      image: null,
    });
  };

  const handleCancelEditEvent = () => {
    setEventsEditMode(false);
    setEventsEditData(null);
  };

  const handleSaveEditEvent = async () => {
    const event = events.find((row) => row.id === eventsExpandedEventId);
    if (!event || !eventsEditData) return;
    if (!eventsEditData.category) {
      alert("Veuillez selectionner une categorie valide avant d'enregistrer.");
      return;
    }

    try {
      setEventsSaving(true);
      const formData = new FormData();
      formData.append('headline', eventsEditData.title);
      formData.append('body', eventsEditData.description);
      formData.append('date', eventsEditData.start_at);
      formData.append('category', eventsEditData.category);
      formData.append('social_url', eventsEditData.social_url);
      if (eventsEditData.image) {
        formData.append('image', eventsEditData.image);
      }

      await api.events.updateEvent(event.slug, formData);
      alert('Evenement mis a jour avec succes !');
      setEventsEditMode(false);
      setEventsEditData(null);
      fetchEvents();
      fetchEventCategories();
    } catch (err) {
      console.error('Failed to update event:', err);
      const serverMsg = err?.response?.data?.error || err?.response?.data?.message || err?.message;
      alert(serverMsg || "Echec de la mise a jour de l'evenement.");
    } finally {
      setEventsSaving(false);
    }
  };

  const deleteUser = async (userId, userName) => {
    if (!window.confirm(`Etes-vous sur de vouloir supprimer l'utilisateur "${userName}" ? Cela supprimera definitivement son compte et tous les paiements associes.`)) {
      return;
    }

    try {
      await api.users.deleteUser(userId);
      alert('Utilisateur supprime avec succes.');
      // Actualiser the user list
      await fetchUserManagementUsers();
      setUserManagementExpandedUserId(null);
    } catch (error) {
      console.error('Failed to delete user:', error);
      const errorMessage = error.response?.data?.error || "Echec de la suppression de l'utilisateur.";
      alert(`Erreur : ${errorMessage}`);
    }
  };

  useEffect(() => {
    if (activeTab !== 'notifications') return;
    const controller = new AbortController();
    fetchSentNotifications(controller.signal);
    setNotificationsExpandedCampaignId(null);
    setSentNotificationsPage(1);
    return () => controller.abort();
  }, [activeTab, fetchSentNotifications]);

  useEffect(() => {
    if (activeTab !== 'notifications-admin-inbox') return;
    const controller = new AbortController();
    fetchAdminInboxNotifications(controller.signal);
    setAdminInboxExpandedNotificationId(null);
    setAdminInboxPage(1);
    return () => controller.abort();
  }, [activeTab, fetchAdminInboxNotifications]);

  useEffect(() => {
    if (activeTab !== 'notifications-admin-outbox') return;
    const controller = new AbortController();
    fetchSentAdminNotifications(controller.signal);
    setAdminOutboxExpandedCampaignId(null);
    setAdminOutboxPage(1);
    return () => controller.abort();
  }, [activeTab, fetchSentAdminNotifications]);

  useEffect(() => {
    if (activeTab !== TAB_KEYS.NOTIFICATION_MANAGEMENT) return;
    const controller = new AbortController();
    fetchAdminNotificationCategories(controller.signal);
    return () => controller.abort();
  }, [activeTab, fetchAdminNotificationCategories]);

  useEffect(() => {
    if (activeTab !== TAB_KEYS.EMAIL_TEMPLATES) return;
    const controller = new AbortController();
    fetchEmailTemplateCategories();
    fetchEmailTemplates();
    return () => controller.abort();
  }, [activeTab, fetchEmailTemplateCategories, fetchEmailTemplates]);

  useEffect(() => {
    if (activeTab !== 'invoices') return;
    const controller = new AbortController();
    fetchAdminInvoices(controller.signal);
    setAdminInvoicesExpandedId(null);
    setAdminInvoicesPage(1);
    return () => controller.abort();
  }, [activeTab, fetchAdminInvoices]);

  useEffect(() => {
    if (activeTab !== 'document-requests') return;
    const controller = new AbortController();
    fetchAdminDocumentRequests(controller.signal);
    setAdminDocumentRequestsPage(1);
    closeAdminDocumentRequestModal();
    return () => controller.abort();
  }, [activeTab, fetchAdminDocumentRequests, closeAdminDocumentRequestModal]);

  useEffect(() => {
    if (activeTab !== TAB_KEYS.FORM_MANAGEMENT) return;
    const controller = new AbortController();
    fetchAdminForms(controller.signal);
    setAdminFormsPage(1);
    setAdminFormsExpandedId(null);
    setShowFormBuilderModal(false);
    return () => controller.abort();
  }, [activeTab, fetchAdminForms]);

  useEffect(() => {
    if (!allowedTabs.has(TAB_KEYS.FORM_REQUESTS)) return;
    if (!allowedTabs.has(TAB_KEYS.FORM_MANAGEMENT)) return;

    const controller = new AbortController();
    fetchAdminForms(controller.signal);
    return () => controller.abort();
  }, [allowedTabs, fetchAdminForms]);

  useEffect(() => {
    if (activeTab !== TAB_KEYS.FORM_REQUESTS) return;
    const controller = new AbortController();
    fetchAdminFormSubmissions(controller.signal);
    setAdminFormSubmissionsPage(1);
    setAdminFormSubmissionExpandedId(null);
    return () => controller.abort();
  }, [activeTab, fetchAdminFormSubmissions]);

  useEffect(() => {
    if (!allowedTabs.has(TAB_KEYS.FORM_REQUESTS)) return;
    if (activeTab === TAB_KEYS.FORM_REQUESTS) return;

    const controller = new AbortController();
    fetchAdminFormSubmissions(controller.signal);

    const interval = setInterval(() => {
      fetchAdminFormSubmissions();
    }, 30000);

    return () => {
      controller.abort();
      clearInterval(interval);
    };
  }, [allowedTabs, activeTab, fetchAdminFormSubmissions]);

  useEffect(() => {
    if (activeTab !== TAB_KEYS.HOUSE_MANAGEMENT_CHANGE && activeTab !== TAB_KEYS.HOUSE_MANAGEMENT_LEAVE) return;
    const controller = new AbortController();
    const requestType = activeTab === TAB_KEYS.HOUSE_MANAGEMENT_CHANGE ? 'change_residence' : 'leave_residence';
    fetchHouseManagementRequests({ requestType, signal: controller.signal });
    setHouseManagementPage(1);
    setHouseManagementExpandedRequestId(null);
    return () => controller.abort();
  }, [activeTab, fetchHouseManagementRequests]);

  useEffect(() => {
    if (activeTab !== TAB_KEYS.CONTACT_MESSAGES) return;
    const controller = new AbortController();
    fetchContactMessages(controller.signal);
    setContactMessagesPage(1);
    setContactMessagesExpandedId(null);
    return () => controller.abort();
  }, [activeTab, fetchContactMessages]);

  useEffect(() => {
    if (!allowedTabs.has(TAB_KEYS.SIGNUP)) return;
    if (activeTab === TAB_KEYS.SIGNUP) return;

    const controller = new AbortController();
    fetchSignupRequests({ signal: controller.signal, silent: true });

    const interval = setInterval(() => {
      fetchSignupRequests({ silent: true });
    }, 30000);

    return () => {
      controller.abort();
      clearInterval(interval);
    };
  }, [allowedTabs, activeTab, fetchSignupRequests]);

  useEffect(() => {
    if (!allowedTabs.has(TAB_KEYS.CONTACT_MESSAGES)) return;
    fetchContactMessagesUnreadCount();

    const interval = setInterval(() => {
      fetchContactMessagesUnreadCount();
    }, 30000);

    return () => clearInterval(interval);
  }, [allowedTabs, fetchContactMessagesUnreadCount]);

  useEffect(() => {
    const hasAdminNotificationTabs =
      allowedTabs.has(TAB_KEYS.ADMIN_NOTIFICATIONS_INBOX) ||
      allowedTabs.has(TAB_KEYS.ADMIN_NOTIFICATIONS_OUTBOX);
    if (!hasAdminNotificationTabs) return;

    const controller = new AbortController();
    fetchAdminInboxNotifications(controller.signal);

    const interval = setInterval(() => {
      fetchAdminInboxNotifications();
    }, 30000);

    return () => {
      controller.abort();
      clearInterval(interval);
    };
  }, [allowedTabs, fetchAdminInboxNotifications]);

  useEffect(() => {
    const hasHouseManagementTabs =
      allowedTabs.has(TAB_KEYS.HOUSE_MANAGEMENT_CHANGE) ||
      allowedTabs.has(TAB_KEYS.HOUSE_MANAGEMENT_LEAVE);
    if (!hasHouseManagementTabs) return;

    const controller = new AbortController();
    fetchHouseManagementUnreadCounts(controller.signal);

    const interval = setInterval(() => {
      fetchHouseManagementUnreadCounts();
    }, 30000);

    return () => {
      controller.abort();
      clearInterval(interval);
    };
  }, [allowedTabs, fetchHouseManagementUnreadCounts]);

  useEffect(() => {
    if (activeTab === 'notifications-admin-inbox' || activeTab === 'notifications-admin-outbox') {
      setAdminNotificationsNavExpanded(true);
    }
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === TAB_KEYS.HOUSE_MANAGEMENT_CHANGE || activeTab === TAB_KEYS.HOUSE_MANAGEMENT_LEAVE) {
      setHouseManagementNavExpanded(true);
    }
  }, [activeTab]);

  useEffect(() => {
    if (!showBroadcastModal) return;
    // Load user list only when needed for the "selected" flow.
    if (broadcastRecipientScope !== 'selected') return;
    if (broadcastTargetUsers.length > 0) return;
    fetchBroadcastTargetUsers();
  }, [showBroadcastModal, broadcastRecipientScope, broadcastTargetUsers.length, fetchBroadcastTargetUsers]);

  useEffect(() => {
    if (!showAdminBroadcastModal) return;
    if (adminBroadcastRecipientScope !== 'selected') return;
    if (adminRecipientUsers.length > 0) return;
    fetchAdminRecipientUsers();
  }, [showAdminBroadcastModal, adminBroadcastRecipientScope, adminRecipientUsers.length, fetchAdminRecipientUsers]);

  const handleSignupAction = async (requestId, action, adminData = {}) => {
    try {
      if (action === 'approve') {
        const response = await api.signup.approve(requestId, adminData);
        if (response.data.password) {
          alert(`Demande approuvee ! Les identifiants de connexion ont ete envoyes a l'utilisateur.\n\nMot de passe temporaire : ${response.data.password}\n\nVeuillez demander a l'utilisateur de changer son mot de passe apres la premiere connexion.`);
        } else {
          alert('Demande approuvee ! Compte utilisateur cree avec succes.');
        }
      } else if (action === 'reject') {
        await api.signup.rejectRequest(requestId);
        alert('Demande rejetee.');
      }
      // Actualiser requests
      fetchSignupRequests();
      setSelectedRequest(null);
    } catch (error) {
      console.error('Failed to process request:', error);
      alert('Echec du traitement de la demande.');
    }
  };

  const broadcastNotification = async () => {
    if (!broadcastTitle.trim() || !broadcastBody.trim()) {
      alert('Veuillez saisir le titre et le message.');
      return;
    }

    if (broadcastRecipientScope === 'selected' && broadcastSelectedUserIds.length === 0) {
      alert('Veuillez selectionner au moins un utilisateur.');
      return;
    }

    try {
      setBroadcastSending(true);
      const broadcastData = {
        title: broadcastTitle.trim(),
        body: broadcastBody.trim(),
        sendEmail: true,
        recipient_scope: broadcastRecipientScope,
        user_ids: broadcastRecipientScope === 'selected' ? broadcastSelectedUserIds : undefined,
      };
      await api.notifications.broadcastNotification(broadcastData);
      alert('Notification envoyee avec succes.');
      closeBroadcastModal();
      setBroadcastTitle('');
      setBroadcastBody('');
      setBroadcastRecipientScope('all');
      setBroadcastSelectedUserIds([]);
      setBroadcastSearchQuery('');
      fetchSentNotifications();
    } catch (error) {
      console.error('Failed to broadcast notification:', error);
      alert("Echec de l'envoi de la notification.");
    } finally {
      setBroadcastSending(false);
    }
  };

  const broadcastAdminNotification = async () => {
    if (!adminBroadcastTitle.trim() || !adminBroadcastBody.trim()) {
      alert('Veuillez saisir le titre et le message.');
      return;
    }

    if (adminBroadcastRecipientScope === 'selected' && adminBroadcastSelectedUserIds.length === 0) {
      alert('Veuillez selectionner au moins un administrateur.');
      return;
    }

    try {
      setAdminBroadcastSending(true);
      const broadcastData = {
        title: adminBroadcastTitle.trim(),
        body: adminBroadcastBody.trim(),
        sendEmail: true,
        recipient_scope: adminBroadcastRecipientScope,
        user_ids: adminBroadcastRecipientScope === 'selected' ? adminBroadcastSelectedUserIds : undefined,
      };
      await api.notifications.broadcastAdminNotification(broadcastData);
      alert('Notification admin envoyee avec succes.');
      closeAdminBroadcastModal();
      setAdminBroadcastTitle('');
      setAdminBroadcastBody('');
      setAdminBroadcastRecipientScope('all');
      setAdminBroadcastSelectedUserIds([]);
      setAdminBroadcastSearchQuery('');
      fetchSentAdminNotifications();
    } catch (error) {
      console.error('Failed to broadcast admin notification:', error);
      alert("Echec de l'envoi de la notification admin.");
    } finally {
      setAdminBroadcastSending(false);
    }
  };

  const clearAdminInboxSeen = async () => {
    try {
      setAdminInboxClearingSeen(true);
      await api.notifications.clearSeenNotifications();
      await fetchAdminInboxNotifications();
    } catch (error) {
      console.error('Failed to clear seen admin inbox notifications:', error);
      alert('Echec de la suppression des notifications lues.');
    } finally {
      setAdminInboxClearingSeen(false);
    }
  };

  const renderAdminNotificationsNav = () => {
    if (!canAccessTab(TAB_KEYS.ADMIN_NOTIFICATIONS_INBOX)) return null;
    const hasAdminInboxUnread = adminInboxUnreadCount > 0;

    return (
      <>
        <button
          type="button"
          className={`admin-side-item ${(activeTab === TAB_KEYS.ADMIN_NOTIFICATIONS_INBOX || activeTab === TAB_KEYS.ADMIN_NOTIFICATIONS_OUTBOX) ? 'active' : ''}`}
          onClick={() => {
            setAdminNotificationsNavExpanded((prev) => {
              const next = !prev;
              if (
                next &&
                activeTab !== TAB_KEYS.ADMIN_NOTIFICATIONS_INBOX &&
                activeTab !== TAB_KEYS.ADMIN_NOTIFICATIONS_OUTBOX
              ) {
                handleTabChange(TAB_KEYS.ADMIN_NOTIFICATIONS_INBOX);
              }
              return next;
            });
          }}
        >
          <span className="admin-side-item-text">Notifications admin</span>
          {hasAdminInboxUnread && (
            <span className="admin-side-alert-dot" aria-label={`${adminInboxUnreadCount} notifications admin non lues`} />
          )}
        </button>

        {adminNotificationsNavExpanded && (
          <div className="admin-side-subnav">
            <button
              type="button"
              className={`admin-side-subitem ${activeTab === TAB_KEYS.ADMIN_NOTIFICATIONS_INBOX ? 'active' : ''}`}
              onClick={() => handleTabChange(TAB_KEYS.ADMIN_NOTIFICATIONS_INBOX)}
            >
              <span className="admin-side-item-text">Inbox</span>
              {hasAdminInboxUnread && (
                <span className="admin-side-alert-dot" aria-label={`${adminInboxUnreadCount} notifications admin non lues`} />
              )}
            </button>
            <button
              type="button"
              className={`admin-side-subitem ${activeTab === TAB_KEYS.ADMIN_NOTIFICATIONS_OUTBOX ? 'active' : ''}`}
              onClick={() => handleTabChange(TAB_KEYS.ADMIN_NOTIFICATIONS_OUTBOX)}
            >
              <span className="admin-side-item-text">Outbox</span>
            </button>
          </div>
        )}
      </>
    );
  };

  const renderFormRequestsNav = () => {
    if (!canAccessTab(TAB_KEYS.FORM_REQUESTS)) return null;

    const canViewHousingRequests = canAccessTab(TAB_KEYS.SIGNUP);
    const hasFormUnread = adminFormSubmissionsUnreadCount > 0;
    const hasHousingUnread = canViewHousingRequests && signupUnreadCount > 0;
    const hasUnread = hasFormUnread || hasHousingUnread;
    const selectedTabKey = normalizeFormRequestTabKey(activeFormRequestTabName);

    const openRequestsTab = (tabName) => {
      setActiveFormRequestTabName(tabName);
      if (activeTab !== TAB_KEYS.FORM_REQUESTS) {
        handleTabChange(TAB_KEYS.FORM_REQUESTS);
      }
    };

    const openHousingRequestsTab = () => {
      if (!canViewHousingRequests) return;
      if (activeTab !== TAB_KEYS.SIGNUP) {
        handleTabChange(TAB_KEYS.SIGNUP);
      }
    };

    return (
      <>
        <button
          type="button"
          className={`admin-side-item ${(activeTab === TAB_KEYS.FORM_REQUESTS || activeTab === TAB_KEYS.SIGNUP) ? 'active' : ''}`}
          onClick={() => {
            setFormRequestsNavExpanded((prev) => {
              const next = !prev;
              if (next && activeTab !== TAB_KEYS.FORM_REQUESTS && activeTab !== TAB_KEYS.SIGNUP) {
                handleTabChange(canViewHousingRequests ? TAB_KEYS.SIGNUP : TAB_KEYS.FORM_REQUESTS);
              }
              return next;
            });
          }}
        >
          <span className="admin-side-item-text">Traitement des formulaires</span>
          {hasUnread && (
            <span
              className="admin-side-alert-dot"
              aria-label={`${adminFormSubmissionsUnreadCount} demandes de formulaire non lues`}
            />
          )}
        </button>

        {formRequestsNavExpanded && (
          <div className="admin-side-subnav">
            {canViewHousingRequests && (
              <button
                type="button"
                className={`admin-side-subitem ${activeTab === TAB_KEYS.SIGNUP ? 'active' : ''}`}
                onClick={openHousingRequestsTab}
              >
                <span className="admin-side-item-text">Demandes utilisateurs</span>
                {signupUnreadCount > 0 && (
                  <span
                    className="admin-side-alert-dot"
                    aria-label={`${signupUnreadCount} demandes utilisateurs non lues`}
                  />
                )}
              </button>
            )}

            <button
              type="button"
              className={`admin-side-subitem ${activeTab === TAB_KEYS.FORM_REQUESTS && activeFormRequestTabName === FORM_REQUESTS_ALL_KEY ? 'active' : ''}`}
              onClick={() => openRequestsTab(FORM_REQUESTS_ALL_KEY)}
            >
              <span className="admin-side-item-text">Tous les formulaires</span>
              {hasUnread && (
                <span
                  className="admin-side-alert-dot"
                  aria-label={`${adminFormSubmissionsUnreadCount} demandes de formulaire non lues`}
                />
              )}
            </button>

            {formRequestChildTabs.map((tabName) => {
              const tabKey = normalizeFormRequestTabKey(tabName);
              const unreadCount = adminFormUnreadByTab[tabKey] || 0;

              return (
                <button
                  key={tabKey}
                  type="button"
                  className={`admin-side-subitem ${activeTab === TAB_KEYS.FORM_REQUESTS && selectedTabKey === tabKey ? 'active' : ''}`}
                  onClick={() => openRequestsTab(tabName)}
                >
                  <span className="admin-side-item-text">{tabName}</span>
                  {unreadCount > 0 && (
                    <span
                      className="admin-side-alert-dot"
                      aria-label={`${unreadCount} demandes non lues: ${tabName}`}
                    />
                  )}
                </button>
              );
            })}
          </div>
        )}
      </>
    );
  };

  const renderHouseManagementNav = () => {
    if (!canAccessTab(TAB_KEYS.HOUSE_MANAGEMENT_CHANGE) && !canAccessTab(TAB_KEYS.HOUSE_MANAGEMENT_LEAVE)) {
      return null;
    }

    const hasChangeUnread = houseChangeUnreadCount > 0;
    const hasLeaveUnread = houseLeaveUnreadCount > 0;
    const hasHouseUnread = hasChangeUnread || hasLeaveUnread;

    return (
      <>
        <button
          type="button"
          className={`admin-side-item ${(activeTab === TAB_KEYS.HOUSE_MANAGEMENT_CHANGE || activeTab === TAB_KEYS.HOUSE_MANAGEMENT_LEAVE) ? 'active' : ''}`}
          onClick={() => {
            setHouseManagementNavExpanded((prev) => {
              const next = !prev;
              if (
                next &&
                activeTab !== TAB_KEYS.HOUSE_MANAGEMENT_CHANGE &&
                activeTab !== TAB_KEYS.HOUSE_MANAGEMENT_LEAVE
              ) {
                handleTabChange(TAB_KEYS.HOUSE_MANAGEMENT_CHANGE);
              }
              return next;
            });
          }}
        >
          <span className="admin-side-item-text">Gestion du logement</span>
          {hasHouseUnread && (
            <span className="admin-side-alert-dot" aria-label="Demandes de gestion du logement non lues" />
          )}
        </button>

        {houseManagementNavExpanded && (
          <div className="admin-side-subnav">
            {canAccessTab(TAB_KEYS.HOUSE_MANAGEMENT_CHANGE) && (
              <button
                type="button"
                className={`admin-side-subitem ${activeTab === TAB_KEYS.HOUSE_MANAGEMENT_CHANGE ? 'active' : ''}`}
                onClick={() => handleTabChange(TAB_KEYS.HOUSE_MANAGEMENT_CHANGE)}
              >
                <span className="admin-side-item-text">Changement de residence</span>
                {hasChangeUnread && <span className="admin-side-alert-dot" aria-label="Demandes non lues: changement de residence" />}
              </button>
            )}
            {canAccessTab(TAB_KEYS.HOUSE_MANAGEMENT_LEAVE) && (
              <button
                type="button"
                className={`admin-side-subitem ${activeTab === TAB_KEYS.HOUSE_MANAGEMENT_LEAVE ? 'active' : ''}`}
                onClick={() => handleTabChange(TAB_KEYS.HOUSE_MANAGEMENT_LEAVE)}
              >
                <span className="admin-side-item-text">Fin de contrat</span>
                {hasLeaveUnread && <span className="admin-side-alert-dot" aria-label="Demandes non lues: fin de contrat" />}
              </button>
            )}
          </div>
        )}
      </>
    );
  };

  return (
    <div className="admin-dashboard admin-shell">
      <div
        className={`admin-mobile-backdrop ${mobileMenuOpen ? 'is-visible' : ''}`}
        onClick={() => setMobileMenuOpen(false)}
      />
      <button
        type="button"
        className="admin-mobile-menu-btn"
        onClick={() => setMobileMenuOpen((prev) => !prev)}
        aria-label={mobileMenuOpen ? 'Fermer le menu' : 'Ouvrir le menu'}
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          {mobileMenuOpen ? (
            <>
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </>
          ) : (
            <>
              <line x1="3" y1="6" x2="21" y2="6" />
              <line x1="3" y1="12" x2="21" y2="12" />
              <line x1="3" y1="18" x2="21" y2="18" />
            </>
          )}
        </svg>
      </button>
      <aside className={`admin-sidebar ${mobileMenuOpen ? 'is-mobile-open' : ''}`}>
        <div className="admin-brand">
          <div className="admin-brand-title">ADEEF Portal</div>
          <div className="admin-brand-subtitle">Admin</div>
        </div>

        <div className="admin-side-nav">
          {useIndexedSidebar ? (
            <>
              {hasUserManagementSidebarItems && (
                <div className="admin-side-section">
                  <div className="admin-side-index">Gestion des utilisateurs</div>

                  {canAccessTab(TAB_KEYS.USERS) && (
                    <button
                      type="button"
                      className={`admin-side-item ${activeTab === TAB_KEYS.USERS ? 'active' : ''}`}
                      onClick={() => handleTabChange(TAB_KEYS.USERS)}
                    >
                      Gestion des utilisateurs
                    </button>
                  )}

                  {renderFormRequestsNav()}

                  {canAccessTab(TAB_KEYS.EVENT_REQUESTS) && (
                    <button
                      type="button"
                      className={`admin-side-item ${activeTab === TAB_KEYS.EVENT_REQUESTS ? 'active' : ''}`}
                      onClick={() => handleTabChange(TAB_KEYS.EVENT_REQUESTS)}
                    >
                      Demandes evenement
                    </button>
                  )}

                  {canAccessTab(TAB_KEYS.CONTACT_MESSAGES) && (
                    <button
                      type="button"
                      className={`admin-side-item ${activeTab === TAB_KEYS.CONTACT_MESSAGES ? 'active' : ''}`}
                      onClick={() => handleTabChange(TAB_KEYS.CONTACT_MESSAGES)}
                    >
                      <span className="admin-side-item-text">Messages de contact</span>
                      {contactMessagesUnreadCount > 0 && <span className="admin-side-alert-dot" aria-label={`${contactMessagesUnreadCount} new contact messages`} />}
                    </button>
                  )}

                  {canAccessTab(TAB_KEYS.DOCUMENT_REQUESTS) && (
                    <button
                      type="button"
                      className={`admin-side-item ${activeTab === TAB_KEYS.DOCUMENT_REQUESTS ? 'active' : ''}`}
                      onClick={() => handleTabChange(TAB_KEYS.DOCUMENT_REQUESTS)}
                    >
                      Demande de documents
                    </button>
                  )}

                  {canAccessTab(TAB_KEYS.PAYMENTS) && (
                    <button
                      type="button"
                      className={`admin-side-item ${activeTab === TAB_KEYS.PAYMENTS ? 'active' : ''}`}
                      onClick={() => handleTabChange(TAB_KEYS.PAYMENTS)}
                    >
                      Paiements
                    </button>
                  )}

                  {canAccessTab(TAB_KEYS.INVOICES) && (
                    <button
                      type="button"
                      className={`admin-side-item ${activeTab === TAB_KEYS.INVOICES ? 'active' : ''}`}
                      onClick={() => handleTabChange(TAB_KEYS.INVOICES)}
                    >
                      Invoices
                    </button>
                  )}

                  {canAccessTab(TAB_KEYS.USERS_NOTIFICATIONS) && (
                    <button
                      type="button"
                      className={`admin-side-item ${activeTab === TAB_KEYS.USERS_NOTIFICATIONS ? 'active' : ''}`}
                      onClick={() => handleTabChange(TAB_KEYS.USERS_NOTIFICATIONS)}
                    >
                      Notifications utilisateurs
                    </button>
                  )}

                  {renderAdminNotificationsNav()}

                  {canAccessTab(TAB_KEYS.NOTIFICATION_MANAGEMENT) && (
                    <button
                      type="button"
                      className={`admin-side-item ${activeTab === TAB_KEYS.NOTIFICATION_MANAGEMENT ? 'active' : ''}`}
                      onClick={() => handleTabChange(TAB_KEYS.NOTIFICATION_MANAGEMENT)}
                    >
                      Notification management
                    </button>
                  )}

                  {canAccessTab(TAB_KEYS.EMAIL_TEMPLATES) && (
                    <button
                      type="button"
                      className={`admin-side-item ${activeTab === TAB_KEYS.EMAIL_TEMPLATES ? 'active' : ''}`}
                      onClick={() => handleTabChange(TAB_KEYS.EMAIL_TEMPLATES)}
                    >
                      Email Templates
                    </button>
                  )}
                </div>
              )}

              {hasWebsiteManagementSidebarItems && (
                <div className="admin-side-section">
                  <div className="admin-side-index">Gestion du site web</div>

                  {canAccessTab(TAB_KEYS.CAROUSEL) && (
                    <button
                      type="button"
                      className={`admin-side-item ${activeTab === TAB_KEYS.CAROUSEL ? 'active' : ''}`}
                      onClick={() => handleTabChange(TAB_KEYS.CAROUSEL)}
                    >
                      Body du carrousel
                    </button>
                  )}

                  {canAccessTab(TAB_KEYS.EVENTS) && (
                    <button
                      type="button"
                      className={`admin-side-item ${activeTab === TAB_KEYS.EVENTS ? 'active' : ''}`}
                      onClick={() => handleTabChange(TAB_KEYS.EVENTS)}
                    >
                      Gestion des evenements
                    </button>
                  )}

                  {canAccessTab(TAB_KEYS.FOOTER) && (
                    <button
                      type="button"
                      className={`admin-side-item ${activeTab === TAB_KEYS.FOOTER ? 'active' : ''}`}
                      onClick={() => handleTabChange(TAB_KEYS.FOOTER)}
                    >
                      Body du footer
                    </button>
                  )}

                  {canAccessTab(TAB_KEYS.TESTIMONIALS_MANAGEMENT) && (
                    <button
                      type="button"
                      className={`admin-side-item ${activeTab === TAB_KEYS.TESTIMONIALS_MANAGEMENT ? 'active' : ''}`}
                      onClick={() => handleTabChange(TAB_KEYS.TESTIMONIALS_MANAGEMENT)}
                    >
                      Systeme de temoignage
                    </button>
                  )}

                  {canAccessTab(TAB_KEYS.FORM_MANAGEMENT) && (
                    <button
                      type="button"
                      className={`admin-side-item ${activeTab === TAB_KEYS.FORM_MANAGEMENT ? 'active' : ''}`}
                      onClick={() => handleTabChange(TAB_KEYS.FORM_MANAGEMENT)}
                    >
                      Form management
                    </button>
                  )}

                  {renderHouseManagementNav()}
                </div>
              )}

              {canAccessTab(TAB_KEYS.TEAM) && (
                <button
                  type="button"
                  className={`admin-side-item admin-side-standalone ${activeTab === TAB_KEYS.TEAM ? 'active' : ''}`}
                  onClick={() => handleTabChange(TAB_KEYS.TEAM)}
                >
                  Gestion de equipe
                </button>
              )}
            </>
          ) : (
            <>
              {canAccessTab(TAB_KEYS.USERS) && (
                <button
                  type="button"
                  className={`admin-side-item ${activeTab === TAB_KEYS.USERS ? 'active' : ''}`}
                  onClick={() => handleTabChange(TAB_KEYS.USERS)}
                >
                  Gestion des utilisateurs
                </button>
              )}

              {canAccessTab(TAB_KEYS.TEAM) && (
                <button
                  type="button"
                  className={`admin-side-item ${activeTab === TAB_KEYS.TEAM ? 'active' : ''}`}
                  onClick={() => handleTabChange(TAB_KEYS.TEAM)}
                >
                  Gestion de equipe
                </button>
              )}

              {canAccessTab(TAB_KEYS.EVENTS) && (
                <button
                  type="button"
                  className={`admin-side-item ${activeTab === TAB_KEYS.EVENTS ? 'active' : ''}`}
                  onClick={() => handleTabChange(TAB_KEYS.EVENTS)}
                >
                  Gestion des evenements
                </button>
              )}

              {canAccessTab(TAB_KEYS.CAROUSEL) && (
                <button
                  type="button"
                  className={`admin-side-item ${activeTab === TAB_KEYS.CAROUSEL ? 'active' : ''}`}
                  onClick={() => handleTabChange(TAB_KEYS.CAROUSEL)}
                >
                  Body du carrousel
                </button>
              )}

              {canAccessTab(TAB_KEYS.FOOTER) && (
                <button
                  type="button"
                  className={`admin-side-item ${activeTab === TAB_KEYS.FOOTER ? 'active' : ''}`}
                  onClick={() => handleTabChange(TAB_KEYS.FOOTER)}
                >
                  Body du footer
                </button>
              )}

              {canAccessTab(TAB_KEYS.TESTIMONIALS_MANAGEMENT) && (
                <button
                  type="button"
                  className={`admin-side-item ${activeTab === TAB_KEYS.TESTIMONIALS_MANAGEMENT ? 'active' : ''}`}
                  onClick={() => handleTabChange(TAB_KEYS.TESTIMONIALS_MANAGEMENT)}
                >
                  Systeme de temoignage
                </button>
              )}

              {canAccessTab(TAB_KEYS.FORM_MANAGEMENT) && (
                <button
                  type="button"
                  className={`admin-side-item ${activeTab === TAB_KEYS.FORM_MANAGEMENT ? 'active' : ''}`}
                  onClick={() => handleTabChange(TAB_KEYS.FORM_MANAGEMENT)}
                >
                  Form management
                </button>
              )}

              {renderHouseManagementNav()}

              {canAccessTab(TAB_KEYS.EVENT_REQUESTS) && (
                <button
                  type="button"
                  className={`admin-side-item ${activeTab === TAB_KEYS.EVENT_REQUESTS ? 'active' : ''}`}
                  onClick={() => handleTabChange(TAB_KEYS.EVENT_REQUESTS)}
                >
                  Demandes evenement
                </button>
              )}

              {canAccessTab(TAB_KEYS.CONTACT_MESSAGES) && (
                <button
                  type="button"
                  className={`admin-side-item ${activeTab === TAB_KEYS.CONTACT_MESSAGES ? 'active' : ''}`}
                  onClick={() => handleTabChange(TAB_KEYS.CONTACT_MESSAGES)}
                >
                  <span className="admin-side-item-text">Messages de contact</span>
                  {contactMessagesUnreadCount > 0 && <span className="admin-side-alert-dot" aria-label={`${contactMessagesUnreadCount} new contact messages`} />}
                </button>
              )}

              {renderFormRequestsNav()}

              {canAccessTab(TAB_KEYS.DOCUMENT_REQUESTS) && (
                <button
                  type="button"
                  className={`admin-side-item ${activeTab === TAB_KEYS.DOCUMENT_REQUESTS ? 'active' : ''}`}
                  onClick={() => handleTabChange(TAB_KEYS.DOCUMENT_REQUESTS)}
                >
                  Demande de documents
                </button>
              )}

              {canAccessTab(TAB_KEYS.USERS_NOTIFICATIONS) && (
                <button
                  type="button"
                  className={`admin-side-item ${activeTab === TAB_KEYS.USERS_NOTIFICATIONS ? 'active' : ''}`}
                  onClick={() => handleTabChange(TAB_KEYS.USERS_NOTIFICATIONS)}
                >
                  Notifications utilisateurs
                </button>
              )}

              {renderAdminNotificationsNav()}

              {canAccessTab(TAB_KEYS.NOTIFICATION_MANAGEMENT) && (
                <button
                  type="button"
                  className={`admin-side-item ${activeTab === TAB_KEYS.NOTIFICATION_MANAGEMENT ? 'active' : ''}`}
                  onClick={() => handleTabChange(TAB_KEYS.NOTIFICATION_MANAGEMENT)}
                >
                  Notification management
                </button>
              )}

              {canAccessTab(TAB_KEYS.EMAIL_TEMPLATES) && (
                <button
                  type="button"
                  className={`admin-side-item ${activeTab === TAB_KEYS.EMAIL_TEMPLATES ? 'active' : ''}`}
                  onClick={() => handleTabChange(TAB_KEYS.EMAIL_TEMPLATES)}
                >
                  Email Templates
                </button>
              )}

              {canAccessTab(TAB_KEYS.PAYMENTS) && (
                <button
                  type="button"
                  className={`admin-side-item ${activeTab === TAB_KEYS.PAYMENTS ? 'active' : ''}`}
                  onClick={() => handleTabChange(TAB_KEYS.PAYMENTS)}
                >
                  Paiements
                </button>
              )}

              {canAccessTab(TAB_KEYS.INVOICES) && (
                <button
                  type="button"
                  className={`admin-side-item ${activeTab === TAB_KEYS.INVOICES ? 'active' : ''}`}
                  onClick={() => handleTabChange(TAB_KEYS.INVOICES)}
                >
                  Invoices
                </button>
              )}
            </>
          )}
        </div>

        <div className="admin-side-footer">
          <button
            type="button"
            className={`admin-side-item ${activeTab === 'settings' ? 'active' : ''}`}
            onClick={() => handleTabChange('settings')}
          >
            Parametres
          </button>
          <button type="button" className="admin-side-item" onClick={logout}>
            Se deconnecter
          </button>
        </div>
      </aside>

      <div className="admin-main">
        <main className="admin-window">
          <div className="admin-tab-viewport">
            <TransitionGroup className="admin-tab-stage">
              <CSSTransition
                key={activeTab}
                nodeRef={tabNodeRef}
                classNames="admin-fade"
                timeout={220}
              >
                <div ref={tabNodeRef} className="admin-tab-page">
                {activeTab === 'users' && (
                  <div className="admin-page">
                    <div className="admin-page-head">
                      <h1 className="admin-page-title">Gestion des utilisateurs</h1>
                      <p className="admin-page-desc">Gerez ici les membres de votre equipe et leurs informations de compte.</p>
                    </div>

                    {showRenewalsModal && (
                      <div className="modal-overlay">
                        <div className="modal-panel">
                          <div className="modal-header">
                            <h3>Renouvellements - Utilisateurs proches de la fin de contrat</h3>
                            <button type="button" onClick={() => setShowRenewalsModal(false)}>Fermer</button>
                          </div>
                          <div className="modal-body">
                            {renewalsLoading ? (
                              <div>Chargement...</div>
                            ) : renewalsRows.length === 0 ? (
                              <div>Aucun renouvellement disponible.</div>
                            ) : (
                              <table className="admin-table">
                                <thead>
                                  <tr><th>Nom</th><th>Email</th><th>Fin actuelle</th><th>CH</th><th>Mensualité</th><th>Actions</th></tr>
                                </thead>
                                <tbody>
                                  {renewalsRows.map((r) => {
                                    const inputs = renewalInputs[r.id] || {};
                                    return (
                                      <tr key={r.id}>
                                        <td>{(r.first_name || '') + ' ' + (r.last_name || '')}</td>
                                        <td>{r.email}</td>
                                        <td>{formatDate(r.effective_end_date)}</td>
                                        <td><input value={inputs.ch || ''} onChange={(e) => setRenewalInputs((prev) => ({ ...prev, [r.id]: { ...(prev[r.id] || {}), ch: e.target.value } }))} /></td>
                                        <td><input value={inputs.payment_amount || ''} onChange={(e) => setRenewalInputs((prev) => ({ ...prev, [r.id]: { ...(prev[r.id] || {}), payment_amount: e.target.value } }))} /></td>
                                        <td>
                                          <button type="button" onClick={() => sendRenewalForUser(r.id)} disabled={inputs.sending}>{inputs.sending ? 'Envoi...' : 'Envoyer'}</button>
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    <div className="admin-page-toolbar">
                      <div className="admin-page-count">
                        Tous les utilisateurs <span className="admin-page-count-number">{userManagementMeta.total || 0}</span>
                      </div>

                      <div className="admin-page-controls admin-user-controls">
                        <input
                          type="search"
                          className="admin-input admin-user-search"
                          placeholder="Rechercher"
                          value={userManagementRechercher}
                          onChange={(e) => {
                            setUserManagementPage(1);
                            setUserManagementRechercher(e.target.value);
                          }}
                        />

                        <div className="admin-renewals-action">
                          <button
                            type="button"
                            className="admin-button admin-renewals-button"
                            onClick={async () => {
                              setShowRenewalsModal(true);
                              await fetchRenewalsDue(30);
                            }}
                            title="Ouvre la liste des utilisateurs dont le contrat arrive à échéance dans les 30 prochains jours"
                          >
                            <span>Renouvellements</span>
                            <span className="admin-renewals-badge">30j</span>
                          </button>
                          <span className="admin-renewals-hint">Contrats proches de la fin de parcours</span>
                        </div>
                      </div>
                    </div>

                    <div className="admin-table-wrap">
                      <table className="admin-table">
                        <thead>
                          <tr>
                            <th>
                              <button type="button" className="admin-sort" onClick={() => toggleSort('name')}
                                aria-label={`Trier par nom ${userManagementSort.sortBy === 'name' ? userManagementSort.sortDir : ''}`}
                              >
                                Nom <span className="admin-sort-indicator">{getSortIndicator('name')}</span>
                              </button>
                            </th>
                            <th>
                              <button type="button" className="admin-sort" onClick={() => toggleSort('phone_number')}
                                aria-label="Numero de telephone"
                                disabled
                              >
                                Numero de telephone
                              </button>
                            </th>
                            <th>
                              <button type="button" className="admin-sort" onClick={() => toggleSort('payment_amount')}
                                aria-label={`Trier par montant du paiement ${userManagementSort.sortBy === 'payment_amount' ? userManagementSort.sortDir : ''}`}
                              >
                                Montant du paiement <span className="admin-sort-indicator">{getSortIndicator('payment_amount')}</span>
                              </button>
                            </th>
                            <th>
                              <button type="button" className="admin-sort" onClick={() => toggleSort('next_payment_date')}
                                aria-label={`Trier par prochain paiement ${userManagementSort.sortBy === 'next_payment_date' ? userManagementSort.sortDir : ''}`}
                              >
                                Prochain paiement <span className="admin-sort-indicator">{getSortIndicator('next_payment_date')}</span>
                              </button>
                            </th>
                            <th></th>
                          </tr>
                        </thead>
                        <tbody>
                          {userManagementLoading && userManagementRows.length === 0 ? (
                            <tr><td colSpan={5} className="admin-table-empty">Chargement...</td></tr>
                          ) : userManagementRows.length === 0 ? (
                            <tr><td colSpan={5} className="admin-table-empty">Aucun utilisateur trouve.</td></tr>
                          ) : (
                            userManagementRows.map((u) => {
                              const expanded = userManagementExpandedUserId === u.id;
                              const fullName = `${u.first_name || ''} ${u.last_name || ''}`.trim() || '(Sans nom)';
                              return (
                                <React.Fragment key={u.id}>
                                  <tr className={expanded ? 'is-expanded' : ''}>
                                    <td>
                                      <div className="admin-user-cell">
                                        <div className="admin-user-name">{fullName}</div>
                                        <div className="admin-user-email">{u.email}</div>
                                      </div>
                                    </td>
                                    <td className="admin-cell-nowrap">{u.phone_number || '-'}</td>
                                    <td className="admin-cell-nowrap">{formatCurrencyEUR(u.payment_amount)}</td>
                                    <td className="admin-cell-nowrap">{formatDate(u.next_payment_date)}</td>
                                    <td className="admin-cell-actions">
                                      <button
                                        type="button"
                                        className="admin-expand"
                                        onClick={() => setUserManagementExpandedUserId(expanded ? null : u.id)}
                                        aria-expanded={expanded}
                                      >
                                        {expanded ? 'Reduire' : 'Developper'}
                                      </button>
                                    </td>
                                  </tr>
                                </React.Fragment>
                              );
                            })
                          )}
                        </tbody>
                      </table>
                    </div>

                    <div className="admin-pagination">
                      <button
                        type="button"
                        className="admin-page-btn"
                        onClick={() => setUserManagementPage((p) => Math.max(1, p - 1))}
                        disabled={userManagementPage <= 1}
                      >
                        Precedent
                      </button>

                      <div className="admin-page-numbers">
                        {getPaginationItems(userManagementMeta.totalPages || 1, userManagementPage)
                          .map((item, idx) => {
                            if (item === '...') {
                              return (
                                <button
                                  key={`dots-${idx}`}
                                  type="button"
                                  className="admin-page-number"
                                  disabled
                                  aria-hidden="true"
                                >
                                  ...
                                </button>
                              );
                            }
                            const p = item;
                            const active = p === userManagementPage;
                            return (
                              <button
                                key={p}
                                type="button"
                                className={`admin-page-number ${active ? 'active' : ''}`}
                                onClick={() => setUserManagementPage(p)}
                              >
                                {p}
                              </button>
                            );
                          })}
                      </div>

                      <button
                        type="button"
                        className="admin-page-btn"
                        onClick={() => setUserManagementPage((p) => Math.min(userManagementMeta.totalPages || 1, p + 1))}
                        disabled={userManagementPage >= (userManagementMeta.totalPages || 1)}
                      >
                        Suivant
                      </button>
                    </div>
                  </div>
                )}

                {activeTab === 'notifications' && (
                  <div className="admin-page">
                    <div className="admin-page-head">
                      <h1 className="admin-page-title">Notifications utilisateurs</h1>
                      <p className="admin-page-desc">Suivez les notifications envoyees et diffusez-en de nouvelles.</p>
                    </div>

                    <div className="admin-page-toolbar">
                      <div className="admin-page-count">
                        Sent
                        <span className="admin-page-count-number">{sentNotificationCampaigns.length}</span>
                      </div>
                      <div className="admin-page-controls">
                        <button
                          type="button"
                          onClick={openBroadcastModal}
                          className="btn btn-secondary"
                        >
                          Broadcast
                        </button>
                      </div>
                    </div>

                    <div className="admin-table-wrap">
                      <table className="admin-table admin-notifications-table">
                        <thead>
                          <tr>
                            <th>Titre</th>
                            <th>Recipient</th>
                            <th>Sent</th>
                            <th></th>
                          </tr>
                        </thead>
                        <tbody>
                          {sentNotificationsLoading && sentNotificationCampaigns.length === 0 ? (
                            <tr><td colSpan={4} className="admin-table-empty">Chargement...</td></tr>
                          ) : sentNotificationCampaigns.length === 0 ? (
                            <tr><td colSpan={4} className="admin-table-empty">Aucune notification envoyee pour le moment.</td></tr>
                          ) : (
                            pagedSentNotificationCampaigns.map((c) => {
                              const expanded = notificationsExpandedCampaignId === c.id;

                              const recipients = Array.isArray(c.recipients) ? c.recipients : [];
                              const recipientSummary =
                                c.recipient_scope === 'all'
                                  ? 'Multiple'
                                  : (recipients.length === 1 ? formatUserDisplayName(recipients[0]) : 'Multiple');

                              return (
                                <React.Fragment key={c.id}>
                                  <tr className={expanded ? 'is-expanded' : ''}>
                                    <td>
                                      <div className="admin-notif-title" title={c.title || ''}>{c.title || '-'}</div>
                                    </td>
                                    <td className="admin-cell-nowrap">{recipientSummary}</td>
                                    <td className="admin-cell-nowrap">{formatDate(c.sent_at)}</td>
                                    <td className="admin-cell-actions">
                                      <button
                                        type="button"
                                        className="admin-expand"
                                        onClick={() => setNotificationsExpandedCampaignId(expanded ? null : c.id)}
                                        aria-expanded={expanded}
                                      >
                                        {expanded ? 'Reduire' : 'Developper'}
                                      </button>
                                    </td>
                                  </tr>
                                </React.Fragment>
                              );
                            })
                          )}
                        </tbody>
                      </table>
                    </div>

                    <div className="admin-pagination-meta">
                      {formatShowingMeta(sentNotificationCampaigns.length, sentNotificationsPageClamped, sentNotificationsPageSize)}
                    </div>

                    <div className="admin-pagination">
                      <button
                        type="button"
                        className="admin-page-btn"
                        onClick={() => {
                          setNotificationsExpandedCampaignId(null);
                          setSentNotificationsPage((p) => Math.max(1, p - 1));
                        }}
                        disabled={sentNotificationsPageClamped <= 1}
                      >
                        Precedent
                      </button>

                      <div className="admin-page-numbers">
                        {getPaginationItems(sentNotificationsTotalPages, sentNotificationsPageClamped).map((item, idx) => {
                          if (item === '...') {
                            return (
                              <button
                                key={`dots-sent-${idx}`}
                                type="button"
                                className="admin-page-number"
                                disabled
                                aria-hidden="true"
                              >
                                ...
                              </button>
                            );
                          }
                          const pageNum = item;
                          const isActive = pageNum === sentNotificationsPageClamped;
                          return (
                            <button
                              key={pageNum}
                              type="button"
                              className={`admin-page-number ${isActive ? 'active' : ''}`}
                              onClick={() => {
                                setNotificationsExpandedCampaignId(null);
                                setSentNotificationsPage(pageNum);
                              }}
                            >
                              {pageNum}
                            </button>
                          );
                        })}
                      </div>

                      <button
                        type="button"
                        className="admin-page-btn"
                        onClick={() => {
                          setNotificationsExpandedCampaignId(null);
                          setSentNotificationsPage((p) => Math.min(sentNotificationsTotalPages, p + 1));
                        }}
                        disabled={sentNotificationsPageClamped >= sentNotificationsTotalPages}
                      >
                        Suivant
                      </button>
                    </div>
                  </div>
                )}

                {activeTab === 'notifications-admin-inbox' && (
                  <div className="admin-page">
                    <div className="admin-page-head">
                      <h1 className="admin-page-title">Notifications admin</h1>
                      <p className="admin-page-desc">Inbox des notifications reeues par votre compte admin.</p>
                    </div>

                    <div className="admin-page-toolbar">
                      <div className="admin-page-count">
                        Inbox
                        <span className="admin-page-count-number">{adminInboxNotifications.length}</span>
                      </div>
                      <div className="admin-page-controls">
                        <button
                          type="button"
                          className="btn btn-outline"
                          onClick={clearAdminInboxSeen}
                          disabled={adminInboxClearingSeen}
                        >
                          {adminInboxClearingSeen ? 'Suppression...' : 'Supprimer les lues'}
                        </button>
                      </div>
                    </div>

                    <div className="admin-table-wrap">
                      <table className="admin-table admin-notifications-table">
                        <thead>
                          <tr>
                            <th>Titre</th>
                            <th>Statut</th>
                            <th>Recu le</th>
                            <th></th>
                          </tr>
                        </thead>
                        <tbody>
                          {adminInboxLoading && adminInboxNotifications.length === 0 ? (
                            <tr><td colSpan={4} className="admin-table-empty">Chargement...</td></tr>
                          ) : adminInboxNotifications.length === 0 ? (
                            <tr><td colSpan={4} className="admin-table-empty">Aucune notification pour le moment.</td></tr>
                          ) : (
                            pagedAdminInboxNotifications.map((n) => {
                              const expanded = adminInboxExpandedNotificationId === n.id;
                              const isRead = Number(n.is_read) === 1;

                              return (
                                <React.Fragment key={n.id}>
                                  <tr className={expanded ? 'is-expanded' : ''}>
                                    <td>
                                      <div className="admin-notif-title" title={n.title || ''}>{n.title || '-'}</div>
                                    </td>
                                    <td className="admin-cell-nowrap">
                                      <div className="admin-notif-status">
                                        <span className={`admin-notif-dot ${isRead ? 'read' : 'unread'}`} aria-hidden="true" />
                                        <span>{isRead ? 'Lu' : 'Recu'}</span>
                                      </div>
                                    </td>
                                    <td className="admin-cell-nowrap">{formatDate(n.created_at)}</td>
                                    <td className="admin-cell-actions">
                                      <button
                                        type="button"
                                        className="admin-expand"
                                        onClick={async () => {
                                          setAdminInboxExpandedNotificationId(expanded ? null : n.id);
                                          if (!expanded && !isRead) {
                                            try {
                                              await api.notifications.markAsRead(n.id);
                                              setAdminInboxNotifications((prev) => prev.map((row) => (row.id === n.id ? { ...row, is_read: 1 } : row)));
                                            } catch (e) {
                                              console.error('Failed to mark notification as read:', e);
                                            }
                                          }
                                        }}
                                        aria-expanded={expanded}
                                      >
                                        {expanded ? 'Reduire' : 'Developper'}
                                      </button>
                                    </td>
                                  </tr>
                                </React.Fragment>
                              );
                            })
                          )}
                        </tbody>
                      </table>
                    </div>

                    <div className="admin-pagination-meta">
                      {formatShowingMeta(adminInboxNotifications.length, adminInboxPageClamped, adminInboxPageSize)}
                    </div>

                    <div className="admin-pagination">
                      <button
                        type="button"
                        className="admin-page-btn"
                        onClick={() => {
                          setAdminInboxExpandedNotificationId(null);
                          setAdminInboxPage((p) => Math.max(1, p - 1));
                        }}
                        disabled={adminInboxPageClamped <= 1}
                      >
                        Precedent
                      </button>

                      <div className="admin-page-numbers">
                        {getPaginationItems(adminInboxTotalPages, adminInboxPageClamped).map((item, idx) => {
                          if (item === '...') {
                            return (
                              <button
                                key={`dots-${idx}`}
                                type="button"
                                className="admin-page-number"
                                disabled
                                aria-hidden="true"
                              >
                                ...
                              </button>
                            );
                          }
                          const p = item;
                          const active = p === adminInboxPageClamped;
                          return (
                            <button
                              key={p}
                              type="button"
                              className={`admin-page-number ${active ? 'active' : ''}`}
                              onClick={() => {
                                setAdminInboxExpandedNotificationId(null);
                                setAdminInboxPage(p);
                              }}
                            >
                              {p}
                            </button>
                          );
                        })}
                      </div>

                      <button
                        type="button"
                        className="admin-page-btn"
                        onClick={() => {
                          setAdminInboxExpandedNotificationId(null);
                          setAdminInboxPage((p) => Math.min(adminInboxTotalPages, p + 1));
                        }}
                        disabled={adminInboxPageClamped >= adminInboxTotalPages}
                      >
                        Suivant
                      </button>
                    </div>
                  </div>
                )}

                {activeTab === 'notifications-admin-outbox' && (
                  <div className="admin-page">
                    <div className="admin-page-head">
                      <h1 className="admin-page-title">Notifications admin</h1>
                      <p className="admin-page-desc">Outbox des diffusions admin (elements delivres/envoyes).</p>
                    </div>

                    <div className="admin-page-toolbar">
                      <div className="admin-page-count">
                        Sent
                        <span className="admin-page-count-number">{adminOutboxCampaigns.length}</span>
                      </div>
                      <div className="admin-page-controls">
                        <button
                          type="button"
                          onClick={openAdminBroadcastModal}
                          className="btn btn-secondary"
                        >
                          Broadcast
                        </button>
                      </div>
                    </div>

                    <div className="admin-table-wrap">
                      <table className="admin-table admin-notifications-table">
                        <thead>
                          <tr>
                            <th>Titre</th>
                            <th>Recipient</th>
                            <th>Sent</th>
                            <th></th>
                          </tr>
                        </thead>
                        <tbody>
                          {adminOutboxLoading && adminOutboxCampaigns.length === 0 ? (
                            <tr><td colSpan={4} className="admin-table-empty">Chargement...</td></tr>
                          ) : adminOutboxCampaigns.length === 0 ? (
                            <tr><td colSpan={4} className="admin-table-empty">Aucune notification envoyee pour le moment.</td></tr>
                          ) : (
                            pagedAdminOutboxCampaigns.map((c) => {
                              const expanded = adminOutboxExpandedCampaignId === c.id;

                              const recipients = Array.isArray(c.recipients) ? c.recipients : [];
                              const recipientSummary =
                                c.recipient_scope === 'all'
                                  ? 'Multiple'
                                  : (recipients.length === 1 ? formatUserDisplayName(recipients[0]) : 'Multiple');

                              return (
                                <React.Fragment key={c.id}>
                                  <tr className={expanded ? 'is-expanded' : ''}>
                                    <td>
                                      <div className="admin-notif-title" title={c.title || ''}>{c.title || '-'}</div>
                                    </td>
                                    <td className="admin-cell-nowrap">{recipientSummary}</td>
                                    <td className="admin-cell-nowrap">{formatDate(c.sent_at)}</td>
                                    <td className="admin-cell-actions">
                                      <button
                                        type="button"
                                        className="admin-expand"
                                        onClick={() => setAdminOutboxExpandedCampaignId(expanded ? null : c.id)}
                                        aria-expanded={expanded}
                                      >
                                        {expanded ? 'Reduire' : 'Developper'}
                                      </button>
                                    </td>
                                  </tr>
                                </React.Fragment>
                              );
                            })
                          )}
                        </tbody>
                      </table>
                    </div>

                    <div className="admin-pagination-meta">
                      {formatShowingMeta(adminOutboxCampaigns.length, adminOutboxPageClamped, adminOutboxPageSize)}
                    </div>

                    <div className="admin-pagination">
                      <button
                        type="button"
                        className="admin-page-btn"
                        onClick={() => {
                          setAdminOutboxExpandedCampaignId(null);
                          setAdminOutboxPage((p) => Math.max(1, p - 1));
                        }}
                        disabled={adminOutboxPageClamped <= 1}
                      >
                        Precedent
                      </button>

                      <div className="admin-page-numbers">
                        {getPaginationItems(adminOutboxTotalPages, adminOutboxPageClamped).map((item, idx) => {
                          if (item === '...') {
                            return (
                              <button
                                key={`dots-${idx}`}
                                type="button"
                                className="admin-page-number"
                                disabled
                                aria-hidden="true"
                              >
                                ...
                              </button>
                            );
                          }
                          const p = item;
                          const active = p === adminOutboxPageClamped;
                          return (
                            <button
                              key={p}
                              type="button"
                              className={`admin-page-number ${active ? 'active' : ''}`}
                              onClick={() => {
                                setAdminOutboxExpandedCampaignId(null);
                                setAdminOutboxPage(p);
                              }}
                            >
                              {p}
                            </button>
                          );
                        })}
                      </div>

                      <button
                        type="button"
                        className="admin-page-btn"
                        onClick={() => {
                          setAdminOutboxExpandedCampaignId(null);
                          setAdminOutboxPage((p) => Math.min(adminOutboxTotalPages, p + 1));
                        }}
                        disabled={adminOutboxPageClamped >= adminOutboxTotalPages}
                      >
                        Suivant
                      </button>
                    </div>
                  </div>
                )}

                {activeTab === TAB_KEYS.NOTIFICATION_MANAGEMENT && (
                  <div className="admin-page">
                    <div className="admin-page-head">
                      <h1 className="admin-page-title">Notification management</h1>
                      <p className="admin-page-desc">Configure which eligible admins receive each notification category.</p>
                    </div>

                    <div className="admin-page-toolbar">
                      <div className="admin-page-count">
                        Categories
                        <span className="admin-page-count-number">{adminNotificationCategories.length}</span>
                      </div>
                      <div className="admin-page-controls">
                        <button
                          type="button"
                          className="btn btn-outline"
                          onClick={() => fetchAdminNotificationCategories()}
                          disabled={adminNotificationCategoriesLoading}
                        >
                          {adminNotificationCategoriesLoading ? 'Actualisation...' : 'Actualiser'}
                        </button>
                        <button
                          type="button"
                          className="btn btn-primary"
                          onClick={() => openCustomEmailModal()}
                        >
                          Add Outside email
                        </button>
                      </div>
                    </div>

                    <div className="admin-table-wrap">
                      <table className="admin-table admin-notification-management-table">
                        <thead>
                          <tr>
                            <th>Category</th>
                            <th>Description</th>
                            <th>Enabled</th>
                            <th>Eligible</th>
                            <th></th>
                          </tr>
                        </thead>
                        <tbody>
                          {adminNotificationCategoriesLoading && adminNotificationCategories.length === 0 ? (
                            <tr><td colSpan={5} className="admin-table-empty">Chargement...</td></tr>
                          ) : adminNotificationCategories.length === 0 ? (
                            <tr><td colSpan={5} className="admin-table-empty">No notification categories were found.</td></tr>
                          ) : (
                            adminNotificationCategories.map((category) => {
                              const enabledCount = Number(category?.enabled_count || 0);
                              const eligibleCount = Number(category?.eligible_count || 0);
                              return (
                                <tr key={category.key}>
                                  <td>
                                    <div className="admin-user-cell">
                                      <div className="admin-user-name">{category.label || category.key}</div>
                                      <div className="admin-user-email">{category.key}</div>
                                    </div>
                                  </td>
                                  <td>{category.description || '-'}</td>
                                  <td className="admin-cell-nowrap">{enabledCount}</td>
                                  <td className="admin-cell-nowrap">{eligibleCount}</td>
                                  <td className="admin-cell-actions">
                                    <button
                                      type="button"
                                      className="admin-expand"
                                      onClick={() => openNotificationManagementCategoryModal(category)}
                                    >
                                      Manage
                                    </button>
                                  </td>
                                </tr>
                              );
                            })
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {activeTab === TAB_KEYS.EMAIL_TEMPLATES && (
                  <div className="admin-page">
                    <div className="admin-page-head">
                      <h1 className="admin-page-title">Email Templates</h1>
                      <p className="admin-page-desc">Configure the content of all emails sent by the portal. Each template can be customized with variables between double curly braces.</p>
                    </div>

                    <div className="admin-page-toolbar">
                      <div className="admin-page-count">
                        Categories
                        <span className="admin-page-count-number">{emailTemplateCategories.length}</span>
                      </div>
                      <div className="admin-page-controls">
                        <button
                          type="button"
                          className="btn btn-outline"
                          onClick={() => { fetchEmailTemplateCategories(); fetchEmailTemplates(); }}
                          disabled={emailTemplateLoading}
                        >
                          {emailTemplateLoading ? 'Actualisation...' : 'Actualiser'}
                        </button>
                      </div>
                    </div>

                    {emailTemplateError && (
                      <p className="admin-form-error" style={{ marginBottom: '12px' }}>{emailTemplateError}</p>
                    )}

                    <div className="admin-table-wrap">
                      <table className="admin-table admin-email-templates-table">
                        <thead>
                          <tr>
                            <th>Category</th>
                            <th>Description</th>
                            <th>Templates</th>
                            <th></th>
                          </tr>
                        </thead>
                        <tbody>
                          {emailTemplateLoading && emailTemplateCategories.length === 0 ? (
                            <tr><td colSpan={4} className="admin-table-empty">Chargement...</td></tr>
                          ) : emailTemplateCategories.length === 0 ? (
                            <tr><td colSpan={4} className="admin-table-empty">No email template categories were found.</td></tr>
                          ) : (
                            emailTemplateCategories.map((category) => {
                              const templateCount = Array.isArray(category.templates) ? category.templates.length : 0;
                              return (
                                <tr key={category.key}>
                                  <td>
                                    <div className="admin-user-cell">
                                      <div className="admin-user-name">{category.label || category.key}</div>
                                      <div className="admin-user-email">{category.key}</div>
                                    </div>
                                  </td>
                                  <td>{category.description || '-'}</td>
                                  <td className="admin-cell-nowrap">{templateCount}</td>
                                  <td className="admin-cell-actions">
                                    {Array.isArray(category.templates) && category.templates.map((tpl) => (
                                      <button
                                        key={tpl.key}
                                        type="button"
                                        className="admin-expand"
                                        style={{ marginRight: '6px', marginBottom: '4px' }}
                                        onClick={() => {
                                          const draft = emailTemplateDrafts.find((d) => d.key === tpl.key);
                                          openEmailTemplateModal(draft || tpl);
                                        }}
                                      >
                                        {tpl.label || tpl.key}
                                      </button>
                                    ))}
                                  </td>
                                </tr>
                              );
                            })
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {activeTab === TAB_KEYS.TESTIMONIALS_MANAGEMENT && (
                  <div className="admin-page">
                    <div className="admin-page-head">
                      <h1 className="admin-page-title">Gestion du systeme de temoignage</h1>
                      <p className="admin-page-desc">Recherchez, moderez et supprimez les avis, puis mettez a jour les informations generales affichees par ville.</p>
                    </div>

                    <div className="admin-page-toolbar">
                      <div className="admin-page-count">
                        Avis <span className="admin-page-count-number">{testimonialsRows.length}</span>
                      </div>
                      <div className="admin-page-controls admin-testimonials-controls">
                        <input
                          type="search"
                          className="admin-input admin-testimonials-search"
                          value={testimonialsQuery}
                          onChange={(e) => setTestimonialsQuery(e.target.value)}
                          placeholder="Rechercher par nom, ville ou texte..."
                        />
                        <button
                          type="button"
                          className="btn btn-outline"
                          onClick={() => fetchTestimonialsRows({ q: testimonialsQuery })}
                          disabled={testimonialsLoading}
                        >
                          {testimonialsLoading ? 'Chargement...' : 'Rechercher'}
                        </button>
                        <button
                          type="button"
                          className="btn btn-primary"
                          onClick={async () => {
                            setShowTestimonialsRegionInfoModal(true);
                            await fetchTestimonialsRegionInfo();
                          }}
                        >
                          Gerer les infos des villes
                        </button>
                        <button
                          type="button"
                          className="btn btn-primary"
                          onClick={async () => {
                            setShowReviewFormConfigModal(true);
                            await fetchReviewFormConfig();
                          }}
                        >
                          Gerer le formulaire
                        </button>
                      </div>
                    </div>

                    <div className="admin-table-wrap">
                      <table className="admin-table">
                        <thead>
                          <tr>
                            <th>Auteur</th>
                            <th>Ville</th>
                            <th>Note</th>
                            <th>Avis</th>
                            <th>Date</th>
                            <th></th>
                          </tr>
                        </thead>
                        <tbody>
                          {testimonialsLoading && testimonialsRows.length === 0 ? (
                            <tr><td colSpan={6} className="admin-table-empty">Chargement...</td></tr>
                          ) : testimonialsRows.length === 0 ? (
                            <tr><td colSpan={6} className="admin-table-empty">Aucun avis trouve.</td></tr>
                          ) : (
                            testimonialsRows.map((row) => (
                              <tr key={row.id}>
                                <td>{String(row.reviewer_display_name || `${row.first_name || ''} ${row.last_name || ''}`).trim() || '-'}</td>
                                <td>{getReviewCityDisplayName(row.city, row.department) || '-'}</td>
                                <td>{Number(row.rating || 0).toFixed(1)} / 5</td>
                                <td>
                                  <div style={{ maxWidth: '360px', whiteSpace: 'pre-wrap' }}>
                                    {row.review_text || '-'}
                                  </div>
                                </td>
                                <td className="admin-cell-nowrap">{formatDateTime(row.created_at)}</td>
                                <td className="admin-cell-actions">
                                  <button
                                    type="button"
                                    className="btn btn-outline btn-sm"
                                    onClick={() => setSelectedReviewDetail(row)}
                                    style={{ marginRight: 4 }}
                                  >
                                    Voir
                                  </button>
                                  <button
                                    type="button"
                                    className="btn btn-danger btn-sm"
                                    disabled={testimonialsDeleteId === row.id}
                                    onClick={() => deleteTestimonialRow(row)}
                                  >
                                    {testimonialsDeleteId === row.id ? 'Suppression...' : 'Supprimer'}
                                  </button>
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {activeTab === 'invoices' && (
                  <div className="admin-page">
                    <div className="admin-page-head">
                      <h1 className="admin-page-title">Invoices</h1>
                      <p className="admin-page-desc">Factures creees automatiquement a partir des paiements reussis.</p>
                    </div>

                    <div className="admin-page-toolbar">
                      <div className="admin-page-count">
                        Toutes les factures <span className="admin-page-count-number">{adminInvoices.length}</span>
                      </div>
                      <div className="admin-page-controls">
                        <button
                          type="button"
                          className="btn btn-primary"
                          onClick={openInvoiceTemplateModal}
                        >
                          Templates notification
                        </button>
                        <button
                          type="button"
                          className="btn btn-outline"
                          onClick={() => fetchAdminInvoices()}
                          disabled={adminInvoicesLoading}
                        >
                          {adminInvoicesLoading ? 'Actualisering...' : 'Actualiser'}
                        </button>
                      </div>
                    </div>

                    <div className="admin-table-wrap">
                      <table className="admin-table admin-invoices-table">
                        <thead>
                          <tr>
                            <th>Facture</th>
                            <th>Locataire</th>
                            <th>Emise le</th>
                            <th>Total</th>
                            <th>Statut</th>
                            <th></th>
                          </tr>
                        </thead>
                        <tbody>
                          {adminInvoicesLoading && adminInvoices.length === 0 ? (
                            <tr><td colSpan={6} className="admin-table-empty">Chargement...</td></tr>
                          ) : adminInvoices.length === 0 ? (
                            <tr><td colSpan={6} className="admin-table-empty">Aucune facture pour le moment.</td></tr>
                          ) : (
                            pagedAdminInvoices.map((inv) => {
                              const expanded = adminInvoicesExpandedId === inv.id;

                              return (
                                <React.Fragment key={inv.id}>
                                  <tr className={expanded ? 'is-expanded' : ''}>
                                    <td className="admin-cell-nowrap">
                                      <div className="admin-notif-title" title={inv.invoice_number || ''}>{inv.invoice_number || `#${inv.id}`}</div>
                                    </td>
                                    <td>
                                      <div className="admin-user-cell">
                                        <div className="admin-user-name">{formatUserDisplayName(inv)}</div>
                                        <div className="admin-user-email">{inv.email || '-'}</div>
                                      </div>
                                    </td>
                                    <td className="admin-cell-nowrap">{formatDate(inv.issued_at)}</td>
                                    <td className="admin-cell-nowrap">{formatMoney(inv.total_amount, inv.currency)}</td>
                                    <td className="admin-cell-nowrap">{String(inv.status || '-')}</td>
                                    <td className="admin-cell-actions">
                                      <button
                                        type="button"
                                        className="admin-expand"
                                        onClick={async () => {
                                          const next = expanded ? null : inv.id;
                                          setAdminInvoicesExpandedId(next);
                                          if (!expanded && !adminInvoiceDetailsById[inv.id]) {
                                            await fetchAdminInvoiceDetail(inv.id);
                                          }
                                        }}
                                        aria-expanded={expanded}
                                      >
                                        {expanded ? 'Reduire' : 'Developper'}
                                      </button>
                                    </td>
                                  </tr>
                                </React.Fragment>
                              );
                            })
                          )}
                        </tbody>
                      </table>
                    </div>

                    <div className="admin-pagination-meta">
                      {formatShowingMeta(adminInvoices.length, adminInvoicesPageClamped, adminInvoicesPageSize)}
                    </div>

                    <div className="admin-pagination">
                      <button
                        type="button"
                        className="admin-page-btn"
                        onClick={() => {
                          setAdminInvoicesExpandedId(null);
                          setAdminInvoicesPage((p) => Math.max(1, p - 1));
                        }}
                        disabled={adminInvoicesPageClamped <= 1}
                      >
                        Precedent
                      </button>

                      <div className="admin-page-numbers">
                        {getPaginationItems(adminInvoicesTotalPages, adminInvoicesPageClamped).map((item, idx) => {
                          if (item === '...') {
                            return (
                              <button
                                key={`dots-inv-${idx}`}
                                type="button"
                                className="admin-page-number"
                                disabled
                                aria-hidden="true"
                              >
                                ...
                              </button>
                            );
                          }
                          const p = item;
                          const active = p === adminInvoicesPageClamped;
                          return (
                            <button
                              key={p}
                              type="button"
                              className={`admin-page-number ${active ? 'active' : ''}`}
                              onClick={() => {
                                setAdminInvoicesExpandedId(null);
                                setAdminInvoicesPage(p);
                              }}
                            >
                              {p}
                            </button>
                          );
                        })}
                      </div>

                      <button
                        type="button"
                        className="admin-page-btn"
                        onClick={() => {
                          setAdminInvoicesExpandedId(null);
                          setAdminInvoicesPage((p) => Math.min(adminInvoicesTotalPages, p + 1));
                        }}
                        disabled={adminInvoicesPageClamped >= adminInvoicesTotalPages}
                      >
                        Suivant
                      </button>
                    </div>
                  </div>
                )}

                {(activeTab === TAB_KEYS.HOUSE_MANAGEMENT_CHANGE || activeTab === TAB_KEYS.HOUSE_MANAGEMENT_LEAVE) && (() => {
                  const isChangeView = activeTab === TAB_KEYS.HOUSE_MANAGEMENT_CHANGE;
                  const pageTitle = isChangeView ? 'Gestion du logement : Changement de residence' : 'Gestion du logement : Fin de contrat';
                  const pageDesc = isChangeView
                    ? 'Demandes de changement de residence soumises par les locataires.'
                    : 'Demandes de fin de contrat / sortie de residence soumises par les locataires.';

                  return (
                    <div className="admin-page">
                      <div className="admin-page-head">
                        <h1 className="admin-page-title">{pageTitle}</h1>
                        <p className="admin-page-desc">{pageDesc}</p>
                      </div>

                      <div className="admin-page-toolbar">
                        <div className="admin-page-count">
                          Demandes <span className="admin-page-count-number">{houseManagementRequests.length}</span>
                        </div>

                        <div className="admin-page-controls">
                          <div className="admin-showing-meta">
                            {formatShowingMeta(houseManagementRequests.length, houseManagementPageClamped, houseManagementPageSize)}
                            {houseManagementLoading ? ' . Chargement...' : ''}
                          </div>
                        </div>
                      </div>

                      <div className="admin-table-wrap">
                        <table className="admin-table">
                          <thead>
                            <tr>
                              <th>Locataire</th>
                              <th>Date souhaitee</th>
                              <th>Soumis le</th>
                              <th>Statut</th>
                              <th>Vu</th>
                              <th></th>
                            </tr>
                          </thead>
                          <tbody>
                            {houseManagementLoading && houseManagementRequests.length === 0 ? (
                              <tr><td colSpan={6} className="admin-table-empty">Chargement...</td></tr>
                            ) : houseManagementRequests.length === 0 ? (
                              <tr><td colSpan={6} className="admin-table-empty">Aucune demande pour le moment.</td></tr>
                            ) : (
                              pagedHouseManagementRequests.map((request) => {
                                const userName = formatUserDisplayName({
                                  first_name: request.user_first_name,
                                  last_name: request.user_last_name,
                                  email: request.user_email,
                                });
                                const expanded = houseManagementExpandedRequestId === request.id;
                                const isSeen = Number(request.is_seen) === 1;
                                const requestedDate = request.request_type === 'change_residence'
                                  ? request.requested_move_date
                                  : request.requested_leave_date;

                                return (
                                  <tr key={request.id} className={expanded ? 'is-expanded' : ''}>
                                    <td>
                                      <div className="admin-user-cell">
                                        <div className="admin-user-name">{userName}</div>
                                        <div className="admin-user-email">{request.user_email || '-'}</div>
                                      </div>
                                    </td>
                                    <td className="admin-cell-nowrap">{formatDate(requestedDate)}</td>
                                    <td className="admin-cell-nowrap">{formatDateTime(request.created_at)}</td>
                                    <td className="admin-cell-nowrap">{String(request.status || 'pending')}</td>
                                    <td className="admin-cell-nowrap">
                                      <div className="admin-notif-status">
                                        <span className={`admin-notif-dot ${isSeen ? 'read' : 'unread'}`} aria-hidden="true" />
                                        <span>{isSeen ? 'Vu' : 'Non lu'}</span>
                                      </div>
                                    </td>
                                    <td className="admin-cell-actions">
                                      <button
                                        type="button"
                                        className="admin-expand"
                                        onClick={() => openHouseManagementRequestModal(request)}
                                        aria-expanded={expanded}
                                      >
                                        {expanded ? 'Reduire' : 'Developper'}
                                      </button>
                                    </td>
                                  </tr>
                                );
                              })
                            )}
                          </tbody>
                        </table>
                      </div>

                      <div className="admin-pagination">
                        <button
                          type="button"
                          className="admin-page-btn"
                          onClick={() => {
                            setHouseManagementExpandedRequestId(null);
                            setHouseManagementPage((p) => Math.max(1, p - 1));
                          }}
                          disabled={houseManagementPageClamped <= 1}
                        >
                          Precedent
                        </button>

                        <div className="admin-page-numbers">
                          {getPaginationItems(houseManagementTotalPages, houseManagementPageClamped).map((item, idx) => {
                            if (item === '...') {
                              return (
                                <button
                                  key={`dots-house-${idx}`}
                                  type="button"
                                  className="admin-page-number"
                                  disabled
                                  aria-hidden="true"
                                >
                                  ...
                                </button>
                              );
                            }
                            const pageNum = item;
                            const isActive = pageNum === houseManagementPageClamped;
                            return (
                              <button
                                key={pageNum}
                                type="button"
                                className={`admin-page-number ${isActive ? 'active' : ''}`}
                                onClick={() => {
                                  setHouseManagementExpandedRequestId(null);
                                  setHouseManagementPage(pageNum);
                                }}
                              >
                                {pageNum}
                              </button>
                            );
                          })}
                        </div>

                        <button
                          type="button"
                          className="admin-page-btn"
                          onClick={() => {
                            setHouseManagementExpandedRequestId(null);
                            setHouseManagementPage((p) => Math.min(houseManagementTotalPages, p + 1));
                          }}
                          disabled={houseManagementPageClamped >= houseManagementTotalPages}
                        >
                          Suivant
                        </button>
                      </div>
                    </div>
                  );
                })()}

                {activeTab === TAB_KEYS.FOOTER && (
                  <div className="admin-page">
                    <div className="admin-page-head">
                      <h1 className="admin-page-title">Body du footer</h1>
                      <p className="admin-page-desc">Modifiez le texte ADEEF et les logos de contact affiches dans le footer public.</p>
                    </div>

                    <div className="admin-page-toolbar">
                      <div className="admin-page-count">
                        Contacts <span className="admin-page-count-number">{footerContacts.length}</span>
                      </div>

                      <div className="admin-page-controls" style={{ gap: '0.55rem', flexWrap: 'wrap' }}>
                        <button
                          type="button"
                          className="btn btn-outline"
                          onClick={fetchFooterAdmin}
                          disabled={footerLoading}
                        >
                          {footerLoading ? 'Actualisation...' : 'Actualiser'}
                        </button>
                        <button
                          type="button"
                          className="btn btn-outline"
                          onClick={() => {
                            setShowFooterContactEditor(false);
                            setShowFooterTextEditor(true);
                          }}
                        >
                          Editer texte colonne gauche
                        </button>
                        <button
                          type="button"
                          className="btn btn-primary"
                          onClick={() => {
                            setFooterContactEditingId(null);
                            setFooterContactForm({ label: '', href: '', image_url: '' });
                            setFooterContactImageFile(null);
                            setShowFooterTextEditor(false);
                            setShowFooterContactEditor(true);
                          }}
                        >
                          Nouveau logo
                        </button>
                      </div>
                    </div>

                    <div className="admin-table-wrap admin-footer-card" style={{ marginTop: '1rem' }}>
                      <table className="admin-table admin-footer-contacts-table">
                        <thead>
                          <tr>
                            <th>Logo</th>
                            <th>Label</th>
                            <th>Lien</th>
                            <th>Statut</th>
                            <th></th>
                          </tr>
                        </thead>
                        <tbody>
                          {footerLoading && footerContacts.length === 0 ? (
                            <tr><td colSpan={5} className="admin-table-empty">Chargement...</td></tr>
                          ) : footerContacts.length === 0 ? (
                            <tr><td colSpan={5} className="admin-table-empty">Aucun logo de contact.</td></tr>
                          ) : (
                            footerContacts.map((contact) => {
                              const isPublished = Number(contact?.is_published) === 1 || contact?.is_published === true;
                              const isPublishing = Number(footerContactPublishingId) === Number(contact.id);
                              const isDeleting = Number(footerContactDeletingId) === Number(contact.id);
                              const { src: logoSrc, fitMode: logoFitMode } = parseFooterLogoUrl(contact?.image_url);
                              return (
                                <tr key={contact.id}>
                                  <td>
                                    {logoSrc ? (
                                      <a
                                        href={logoSrc}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="admin-footer-logo-preview-link"
                                        aria-label={`Apercu logo ${contact.label || 'contact'}`}
                                      >
                                        <img
                                          src={logoSrc}
                                          alt={contact.label || 'Logo contact'}
                                          className="admin-footer-logo-preview"
                                          style={{ objectFit: logoFitMode }}
                                          loading="lazy"
                                        />
                                      </a>
                                    ) : (
                                      <span className="admin-muted">Aucun logo</span>
                                    )}
                                  </td>
                                  <td>{contact.label}</td>
                                  <td>
                                    {contact.href ? (
                                      <a href={contact.href} target="_blank" rel="noreferrer" className="admin-link">{contact.href}</a>
                                    ) : (
                                      <span className="admin-muted">Aucun lien</span>
                                    )}
                                  </td>
                                  <td className="admin-cell-nowrap">
                                    <span className={`admin-footer-status ${isPublished ? 'is-published' : 'is-unpublished'}`}>
                                      {isPublished ? 'Publie' : 'Non publie'}
                                    </span>
                                  </td>
                                  <td className="admin-cell-actions">
                                    <div className="admin-footer-actions-row">
                                      <button
                                        type="button"
                                        className="btn btn-outline"
                                        onClick={() => startEditFooterContact(contact)}
                                      >
                                        Editer
                                      </button>
                                      <button
                                        type="button"
                                        className="btn btn-outline"
                                        onClick={() => toggleFooterContactPublish(contact)}
                                        disabled={isPublishing}
                                      >
                                        {isPublishing ? 'Mise a jour...' : (isPublished ? 'Depublier' : 'Publier')}
                                      </button>
                                      <button
                                        type="button"
                                        className="btn btn-danger"
                                        onClick={() => deleteFooterContact(contact)}
                                        disabled={isDeleting}
                                      >
                                        {isDeleting ? 'Suppression...' : 'Supprimer'}
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              );
                            })
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {activeTab === 'settings' && (
                  <div className="admin-page">
                    <div className="admin-page-head">
                      <h1 className="admin-page-title">Parametres</h1>
                      <p className="admin-page-desc">Gerez vos preferences et votre mot de passe.</p>
                    </div>

                    <div className="user-settings-grid">
                      <section className="user-settings-card">
                        <h2 className="user-settings-title">Informations personnelles</h2>
                        <p className="user-settings-subtitle">Ces champs sont en lecture seule.</p>
                        <div className="user-settings-readonly-grid">
                          <div className="user-settings-readonly-item">
                            <span className="user-settings-readonly-label">Nom</span>
                            <span className="user-settings-readonly-value">{user?.first_name || '-'} {user?.last_name || ''}</span>
                          </div>
                          <div className="user-settings-readonly-item">
                            <span className="user-settings-readonly-label">Email</span>
                            <span className="user-settings-readonly-value">{user?.email || '-'}</span>
                          </div>
                          <div className="user-settings-readonly-item">
                            <span className="user-settings-readonly-label">Role</span>
                            <span className="user-settings-readonly-value">{Array.isArray(user?.roles) ? user.roles.join(', ') : (user?.roles || '-')}</span>
                          </div>
                        </div>
                      </section>

                      <section className="user-settings-card">
                        <div className="user-settings-action-head">
                          <div>
                            <h2 className="user-settings-title">Mot de passe</h2>
                            <p className="user-settings-subtitle">Recevez un code par email puis definissez un nouveau mot de passe.</p>
                          </div>
                        </div>

                        {adminPasswordMessage ? <div className="user-settings-success">{adminPasswordMessage}</div> : null}
                        {adminPasswordError ? <div className="user-settings-error">{adminPasswordError}</div> : null}

                        <div className="user-settings-inline-row">
                          <button
                            type="button"
                            className="btn btn-outline"
                            onClick={sendAdminPasswordResetOtp}
                            disabled={adminPasswordBusyState === 'sending' || adminPasswordCooldown > 0}
                          >
                            {adminPasswordBusyState === 'sending'
                              ? 'Envoi...'
                              : adminPasswordCooldown > 0
                                ? `Renvoyer dans ${adminPasswordCooldown}s`
                                : 'Envoyer le code'}
                          </button>
                        </div>

                        <div className="admin-form-2col">
                          <div className="form-group">
                            <label>Code OTP</label>
                            <input
                              type="text"
                              inputMode="numeric"
                              maxLength={6}
                              value={adminPasswordOtp}
                              onChange={(e) => setAdminPasswordOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                              placeholder="6 chiffres"
                            />
                          </div>
                          <div className="form-group user-settings-otp-action">
                            <label>Verification</label>
                            <button
                              type="button"
                              className="btn btn-outline"
                              onClick={verifyAdminPasswordResetCode}
                              disabled={adminPasswordBusyState === 'verifying' || String(adminPasswordOtp || '').trim().length !== 6}
                            >
                              {adminPasswordBusyState === 'verifying' ? 'Verification...' : 'Confirmer le code'}
                            </button>
                          </div>
                        </div>

                        {adminPasswordResetToken ? (
                          <>
                            <div className="admin-form-2col">
                              <div className="form-group">
                                <label>Nouveau mot de passe</label>
                                <input
                                  type="password"
                                  value={adminPasswordNewValue}
                                  onChange={(e) => setAdminPasswordNewValue(e.target.value)}
                                  autoComplete="new-password"
                                />
                              </div>
                              <div className="form-group">
                                <label>Confirmer le mot de passe</label>
                                <input
                                  type="password"
                                  value={adminPasswordConfirmValue}
                                  onChange={(e) => setAdminPasswordConfirmValue(e.target.value)}
                                  autoComplete="new-password"
                                />
                              </div>
                            </div>

                            <div className="user-settings-actions">
                              <button
                                type="button"
                                className="btn btn-outline"
                                onClick={cancelAdminPasswordPanel}
                                disabled={adminPasswordBusyState === 'saving'}
                              >
                                Annuler
                              </button>
                              <button
                                type="button"
                                className="btn btn-primary"
                                onClick={submitAdminNewPassword}
                                disabled={adminPasswordBusyState === 'saving'}
                              >
                                {adminPasswordBusyState === 'saving' ? 'Mise a jour...' : 'Confirmer le nouveau mot de passe'}
                              </button>
                            </div>
                          </>
                        ) : (
                          <div className="user-settings-actions">
                            <button
                              type="button"
                              className="btn btn-outline"
                              onClick={cancelAdminPasswordPanel}
                              disabled={Boolean(adminPasswordBusyState)}
                            >
                              Annuler
                            </button>
                          </div>
                        )}
                      </section>
                    </div>
                  </div>
                )}

        {activeTab === 'payments' && (
          <div className="admin-page">
            <div className="admin-page-head">
              <h2 className="admin-page-title">Paiements</h2>
              <p className="admin-page-desc">Suit les paiements en attente et se met a jour automatiquement.</p>
            </div>

            <div className="admin-page-toolbar">
              <div className="admin-page-count">
                Paiements en attente <span className="admin-page-count-number">{adminPaiementsMeta.total || 0}</span>
              </div>

              <div className="admin-page-controls">
                <div className="admin-showing-meta">
                  {formatShowingMeta(adminPaiementsMeta.total || 0, adminPaiementsPageClamped, adminPaiementsPageSize)}
                  {adminPaiementsLoading ? ' . Actualisering...' : ''}
                </div>
              </div>
            </div>

            <div className="admin-table-wrap">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Utilisateur</th>
                    <th>Montant</th>
                    <th>Emise le</th>
                    <th>Statut</th>
                  </tr>
                </thead>
                <tbody>
                  {adminPaiementsLoading && adminPaiementsRows.length === 0 ? (
                    <tr><td colSpan={4} className="admin-table-empty">Chargement...</td></tr>
                  ) : adminPaiementsRows.length === 0 ? (
                    <tr><td colSpan={4} className="admin-table-empty">Aucun paiement en attente.</td></tr>
                  ) : (
                    adminPaiementsRows.map((p) => {
                      const fullName = `${p.first_name || ''} ${p.last_name || ''}`.trim() || '(Sans nom)';
                      const issuedAt = p.created_at || p.payment_date || null;
                      return (
                        <tr key={p.id}>
                          <td>
                            <div className="admin-user-cell">
                              <div className="admin-user-name">{fullName}</div>
                              <div className="admin-user-email">{p.email}</div>
                            </div>
                          </td>
                          <td className="admin-cell-nowrap">{formatMoney(p.amount, p.currency)}</td>
                          <td className="admin-cell-nowrap">{formatDate(issuedAt)}</td>
                          <td className="admin-cell-nowrap">{p.status}</td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            <div className="admin-pagination">
              <button
                type="button"
                className="admin-page-btn"
                onClick={() => setAdminPaiementsPage((p) => Math.max(1, p - 1))}
                disabled={adminPaiementsPageClamped <= 1}
              >
                Precedent
              </button>

              <div className="admin-page-numbers">
                {getPaginationItems(adminPaiementsTotalPages, adminPaiementsPageClamped).map((item, idx) => {
                  if (item === '...') {
                    return (
                      <button
                        key={`dots-pay-${idx}`}
                        type="button"
                        className="admin-page-number"
                        disabled
                        aria-hidden="true"
                      >
                        ...
                      </button>
                    );
                  }
                  const pageNum = item;
                  const isActive = pageNum === adminPaiementsPageClamped;
                  return (
                    <button
                      key={pageNum}
                      type="button"
                      className={`admin-page-number ${isActive ? 'active' : ''}`}
                      onClick={() => setAdminPaiementsPage(pageNum)}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>

              <button
                type="button"
                className="admin-page-btn"
                onClick={() => setAdminPaiementsPage((p) => Math.min(adminPaiementsTotalPages, p + 1))}
                disabled={adminPaiementsPageClamped >= adminPaiementsTotalPages}
              >
                Suivant
              </button>
            </div>
          </div>
        )}

        {activeTab === 'events' && (
          <div className="admin-page">
            <div className="admin-page-head">
              <h2 className="admin-page-title">Gestion des evenements</h2>
              <p className="admin-page-desc">Gerez ici les evenements, leur publication et leur suppression.</p>
            </div>

            <div className="admin-page-toolbar">
              <div className="admin-page-count">
                Tous les evenements <span className="admin-page-count-number">{events.length}</span>
              </div>

              <div className="admin-page-controls">
                <button
                  type="button"
                  className="btn btn-outline-primary"
                  onClick={() => setShowCategoryManager((prev) => !prev)}
                >
                  {showCategoryManager ? 'Fermer les categories' : 'Modifier les categories'}
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => setShowCreateEvent(true)}
                >
                  Creer un evenement
                </button>
              </div>
            </div>

            {showCategoryManager && (
              <div className="admin-table-wrap admin-categories-manager">
                <div className="admin-categories-manager-body">
                  <h3>Categories evenement</h3>
                  <p className="admin-page-desc" style={{ marginTop: 0 }}>
                    La modification ou suppression des categories affecte immediatement les formulaires de creation/edition et la page publique des evenements.
                  </p>

                  <table className="admin-table admin-categories-table">
                    <thead>
                      <tr>
                        <th>Label</th>
                        <th>Slug</th>
                        <th>Events</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {eventCategoriesLoading && eventCategories.length === 0 ? (
                        <tr><td colSpan={4} className="admin-table-empty">Chargement des categories...</td></tr>
                      ) : eventCategories.length === 0 ? (
                        <tr><td colSpan={4} className="admin-table-empty">Aucune categorie trouvee.</td></tr>
                      ) : (
                        eventCategories.map((category) => {
                          const draft = eventCategoryDraftById[category.id] || {
                            label: category.label || '',
                            slug: category.slug || '',
                          };
                          const isSaving = Number(eventCategorySavingId) === Number(category.id);
                          const isDeleting = Number(eventCategoryDeletingId) === Number(category.id);
                          return (
                            <tr key={category.id}>
                              <td>
                                <input
                                  type="text"
                                  value={draft.label}
                                  onChange={(e) => {
                                    const value = e.target.value;
                                    setEventCategoryDraftById((prev) => ({
                                      ...prev,
                                      [category.id]: {
                                        ...(prev[category.id] || {}),
                                        label: value,
                                      },
                                    }));
                                  }}
                                />
                              </td>
                              <td>
                                <input
                                  type="text"
                                  value={draft.slug}
                                  onChange={(e) => {
                                    const value = e.target.value;
                                    setEventCategoryDraftById((prev) => ({
                                      ...prev,
                                      [category.id]: {
                                        ...(prev[category.id] || {}),
                                        slug: value,
                                      },
                                    }));
                                  }}
                                />
                              </td>
                              <td className="admin-cell-nowrap">{Number(category.events_count || 0)}</td>
                              <td className="admin-cell-actions">
                                <button
                                  type="button"
                                  className="btn btn-outline-primary"
                                  onClick={() => saveEventCategoryEntry(category.id)}
                                  disabled={isSaving || isDeleting}
                                >
                                  {isSaving ? 'Enregistrement...' : 'Enregistrer'}
                                </button>
                                <button
                                  type="button"
                                  className="btn btn-danger"
                                  style={{ marginLeft: 8 }}
                                  onClick={() => deleteEventCategoryEntry(category)}
                                  disabled={isSaving || isDeleting}
                                >
                                  {isDeleting ? 'Suppression...' : 'Supprimer'}
                                </button>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>

                  <div className="admin-categories-create">
                    <h4>Ajouter une categorie</h4>
                    <div className="admin-form-2col">
                      <div className="form-group">
                        <label>Label</label>
                        <input
                          type="text"
                          value={newEventCategory.label}
                          onChange={(e) => setNewEventCategory((prev) => ({ ...prev, label: e.target.value }))}
                          placeholder="Exemple : Forum emploi"
                        />
                      </div>
                      <div className="form-group">
                        <label>Slug (optionnel)</label>
                        <input
                          type="text"
                          value={newEventCategory.slug}
                          onChange={(e) => setNewEventCategory((prev) => ({ ...prev, slug: e.target.value }))}
                          placeholder="job-fair"
                        />
                      </div>
                    </div>
                    <div className="admin-footer-actions">
                      <button
                        type="button"
                        className="btn btn-success"
                        onClick={createEventCategoryEntry}
                        disabled={creatingEventCategory}
                      >
                        {creatingEventCategory ? 'Creation...' : 'Ajouter une categorie'}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {loading ? (
              <p>Chargement des evenements...</p>
            ) : events.length > 0 ? (
              <>
                <div className="admin-table-wrap">
                  <table className="admin-table">
                    <thead>
                      <tr>
                        <th>Evenement</th>
                        <th>Categorie</th>
                        <th>Publie le</th>
                        <th>Statut</th>
                        <th></th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {pagedEvents.map((event) => {
                        const img = resolveMediaUrl(event.banner_url);
                        const postedAt = event.created_at || event.updated_at || event.start_at;
                        const statusLabel = event.is_published ? 'Publie' : 'Depublie';

                        const expanded = eventsExpandedEventId === event.id;

                        return (
                          <React.Fragment key={event.id}>
                            <tr className={expanded ? 'is-expanded' : ''}>
                              <td>
                                <div className="admin-event-cell">
                                  {img ? (
                                    <a
                                      href={img}
                                      target="_blank"
                                      rel="noreferrer"
                                      className="admin-event-thumbLink"
                                      aria-label="Ouvrir image de evenement dans un nouvel onglet"
                                    >
                                      <img src={img} alt="" className="admin-event-thumb" />
                                    </a>
                                  ) : (
                                    <div className="admin-event-thumbPlaceholder" aria-hidden="true" />
                                  )}
                                  <div className="admin-event-text">
                                    <div className="admin-event-title" title={event.title}>{event.title}</div>
                                  </div>
                                </div>
                              </td>
                              <td className="admin-cell-nowrap">{event.category_label || event.category || '-'}</td>
                              <td className="admin-cell-nowrap">{formatDate(postedAt)}</td>
                              <td className="admin-cell-nowrap">{statusLabel}</td>
                              <td className="admin-cell-actions">
                                {event.is_published ? (
                                  <button
                                    onClick={() => handleEventAction(event.slug, 'unpublish')}
                                    className="btn btn-warning"
                                  >
                                    Depublier
                                  </button>
                                ) : (
                                  <button
                                    onClick={() => handleEventAction(event.slug, 'publish')}
                                    className="btn btn-success"
                                  >
                                    Publier
                                  </button>
                                )}
                                <button
                                  onClick={() => handleEventAction(event.slug, 'delete')}
                                  className="btn btn-danger"
                                  style={{ marginLeft: 8 }}
                                >
                                  Supprimer
                                </button>
                              </td>
                              <td className="admin-cell-actions">
                                <button
                                  type="button"
                                  className="admin-expand"
                                  onClick={() => setEventsExpandedEventId(expanded ? null : event.id)}
                                  aria-expanded={expanded}
                                >
                                  {expanded ? 'Reduire' : 'Developper'}
                                </button>
                              </td>
                            </tr>
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                <div className="admin-pagination">
                  <button
                    type="button"
                    className="admin-page-btn"
                    onClick={() => setEventsPage((p) => Math.max(1, p - 1))}
                    disabled={eventsPageClamped <= 1}
                  >
                    Precedent
                  </button>

                  <div className="admin-page-numbers">
                    {getPaginationItems(eventsTotalPages, eventsPageClamped).map((item, idx) => {
                      if (item === '...') {
                        return (
                          <button
                            key={`dots-${idx}`}
                            type="button"
                            className="admin-page-number"
                            disabled
                            aria-hidden="true"
                          >
                            ...
                          </button>
                        );
                      }
                      const p = item;
                      const active = p === eventsPageClamped;
                      return (
                        <button
                          key={p}
                          type="button"
                          className={`admin-page-number ${active ? 'active' : ''}`}
                          onClick={() => setEventsPage(p)}
                        >
                          {p}
                        </button>
                      );
                    })}
                  </div>

                  <button
                    type="button"
                    className="admin-page-btn"
                    onClick={() => setEventsPage((p) => Math.min(eventsTotalPages, p + 1))}
                    disabled={eventsPageClamped >= eventsTotalPages}
                  >
                    Suivant
                  </button>
                </div>
              </>
            ) : (
              <p>Aucun evenement trouve.</p>
            )}
          </div>
        )}

        {activeTab === TAB_KEYS.CAROUSEL && (
          <div className="admin-page">
            <div className="admin-page-head">
              <h2 className="admin-page-title">Body du carrousel</h2>
              <p className="admin-page-desc">Ajoutez, modifiez, supprimez et ordonnez les slides du carrousel de la page e propos.</p>
            </div>

            <div className="admin-page-toolbar">
              <div className="admin-page-count">
                Slides <span className="admin-page-count-number">{carouselSlidesAdmin.length}</span>
              </div>

              <div className="admin-page-controls">
                <button
                  type="button"
                  className="btn btn-outline"
                  onClick={() => fetchCarouselSlidesAdmin()}
                  disabled={carouselSlidesLoading}
                >
                  {carouselSlidesLoading ? 'Actualisering...' : 'Actualiser'}
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={openCreateCarouselSlide}
                >
                  Creer une slide
                </button>
              </div>
            </div>

            <div className="admin-table-wrap">
              <table className="admin-table admin-carousel-table">
                <thead>
                  <tr>
                    <th>Rank</th>
                    <th>Slide</th>
                    <th>Updated</th>
                    <th>Move</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {carouselSlidesLoading && carouselSlidesAdmin.length === 0 ? (
                    <tr><td colSpan={5} className="admin-table-empty">Chargement...</td></tr>
                  ) : carouselSlidesAdmin.length === 0 ? (
                    <tr><td colSpan={5} className="admin-table-empty">Aucune slide pour le moment.</td></tr>
                  ) : (
                    pagedCarouselSlides.map((slide) => {
                      const globalIndex = carouselSlidesAdmin.findIndex((row) => Number(row.id) === Number(slide.id));
                      const canMoveUp = globalIndex > 0;
                      const canMoveDown = globalIndex >= 0 && globalIndex < carouselSlidesAdmin.length - 1;
                      const imageUrl = resolveMediaUrl(slide.image_url);
                      const expanded = Number(carouselExpandedSlideId) === Number(slide.id);

                      return (
                        <tr key={slide.id} className={expanded ? 'is-expanded' : ''}>
                          <td className="admin-cell-nowrap">#{slide.sort_order}</td>
                          <td>
                            <div className="admin-carousel-slide-cell">
                              {imageUrl ? (
                                <img src={imageUrl} alt={slide.title || 'Carousel slide'} className="admin-carousel-thumb" />
                              ) : (
                                <div className="admin-carousel-thumb admin-carousel-thumb-empty" aria-hidden="true" />
                              )}
                              <div className="admin-carousel-slide-copy">
                                <div className="admin-carousel-slide-title">{slide.title || '-'}</div>
                                <div className="admin-carousel-slide-body">{slide.body || '-'}</div>
                              </div>
                            </div>
                          </td>
                          <td className="admin-cell-nowrap">{formatDateTime(slide.updated_at)}</td>
                          <td className="admin-cell-actions">
                            <div className="admin-carousel-row-actions">
                              <button
                                type="button"
                                className="btn btn-outline btn-sm"
                                onClick={() => handleMoveCarouselSlide(slide.id, -1)}
                                disabled={!canMoveUp || carouselSlideReorderingId === slide.id}
                              >
                                Up
                              </button>
                              <button
                                type="button"
                                className="btn btn-outline btn-sm"
                                onClick={() => handleMoveCarouselSlide(slide.id, 1)}
                                disabled={!canMoveDown || carouselSlideReorderingId === slide.id}
                              >
                                Down
                              </button>
                            </div>
                          </td>
                          <td className="admin-cell-actions">
                            <button
                              type="button"
                              className="admin-expand"
                              onClick={() => setCarouselExpandedSlideId(expanded ? null : slide.id)}
                              aria-expanded={expanded}
                            >
                              {expanded ? 'Reduire' : 'Developper'}
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            <div className="admin-pagination-meta">
              {formatShowingMeta(carouselSlidesAdmin.length, carouselSlidesPageClamped, carouselSlidesPageSize)}
            </div>

            <div className="admin-pagination">
              <button
                type="button"
                className="admin-page-btn"
                onClick={() => setCarouselSlidesPage((p) => Math.max(1, p - 1))}
                disabled={carouselSlidesPageClamped <= 1}
              >
                Precedent
              </button>

              <div className="admin-page-numbers">
                {getPaginationItems(carouselSlidesTotalPages, carouselSlidesPageClamped).map((item, idx) => {
                  if (item === '...') {
                    return (
                      <button
                        key={`dots-carousel-${idx}`}
                        type="button"
                        className="admin-page-number"
                        disabled
                        aria-hidden="true"
                      >
                        ...
                      </button>
                    );
                  }
                  const pageNum = item;
                  const isActive = pageNum === carouselSlidesPageClamped;
                  return (
                    <button
                      key={pageNum}
                      type="button"
                      className={`admin-page-number ${isActive ? 'active' : ''}`}
                      onClick={() => setCarouselSlidesPage(pageNum)}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>

              <button
                type="button"
                className="admin-page-btn"
                onClick={() => setCarouselSlidesPage((p) => Math.min(carouselSlidesTotalPages, p + 1))}
                disabled={carouselSlidesPageClamped >= carouselSlidesTotalPages}
              >
                Suivant
              </button>
            </div>
          </div>
        )}

        {activeTab === TAB_KEYS.FORM_MANAGEMENT && (
          <div className="admin-page">
            <div className="admin-page-head">
              <h2 className="admin-page-title">Form management</h2>
              <p className="admin-page-desc">Create and maintain homepage forms, publish or unpublish them, and edit the full form structure.</p>
            </div>

            <div className="admin-page-toolbar">
              <div className="admin-page-count">
                Forms <span className="admin-page-count-number">{adminForms.length}</span>
              </div>

              <div className="admin-page-controls">
                <button
                  type="button"
                  className="btn btn-outline"
                  onClick={() => setShowCitiesManager(true)}
                >
                  Cities
                </button>

                <button
                  type="button"
                  className="btn btn-outline"
                  onClick={() => fetchAdminForms()}
                  disabled={adminFormsLoading}
                >
                  {adminFormsLoading ? 'Refreshing...' : 'Refresh'}
                </button>

                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={openCreateFormBuilder}
                >
                  Create form
                </button>
              </div>
            </div>

            <div className="admin-table-wrap">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Form</th>
                    <th>Request tab</th>
                    <th>Updated</th>
                    <th>Status</th>
                    <th></th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {adminFormsLoading && adminForms.length === 0 ? (
                    <tr><td colSpan={6} className="admin-table-empty">Loading...</td></tr>
                  ) : adminForms.length === 0 ? (
                    <tr><td colSpan={6} className="admin-table-empty">No forms available yet.</td></tr>
                  ) : (
                    pagedAdminForms.map((form) => {
                      const expanded = Number(adminFormsExpandedId) === Number(form.id);
                      const isPublished = Number(form.is_published) === 1 || form.is_published === true;
                      const isPublishing = Number(formPublishingId) === Number(form.id);
                      const isDeleting = Number(formDeletingId) === Number(form.id);

                      return (
                        <tr key={form.id} className={expanded ? 'is-expanded' : ''}>
                          <td>
                            <div className="admin-user-cell">
                              <div className="admin-user-name">{form.form_name || '-'}</div>
                              <div className="admin-user-email">/{form.slug || '-'}</div>
                            </div>
                          </td>
                          <td>{form.request_tab_name || '-'}</td>
                          <td className="admin-cell-nowrap">{formatDateTime(form.updated_at)}</td>
                          <td className="admin-cell-nowrap">{isPublished ? 'Published' : 'Unpublished'}</td>
                          <td className="admin-cell-actions">
                            <button
                              type="button"
                              className={isPublished ? 'btn btn-warning' : 'btn btn-success'}
                              onClick={() => toggleAdminFormPublish(form)}
                              disabled={isPublishing || isDeleting}
                            >
                              {isPublishing ? 'Updating...' : (isPublished ? 'Unpublish' : 'Publish')}
                            </button>
                            <button
                              type="button"
                              className="btn btn-danger"
                              style={{ marginLeft: 8 }}
                              onClick={() => deleteAdminFormDefinition(form)}
                              disabled={isDeleting || isPublishing}
                            >
                              {isDeleting ? 'Deleting...' : 'Delete'}
                            </button>
                          </td>
                          <td className="admin-cell-actions">
                            <button
                              type="button"
                              className="admin-expand"
                              onClick={() => setAdminFormsExpandedId(expanded ? null : form.id)}
                              aria-expanded={expanded}
                            >
                              {expanded ? 'Reduire' : 'Developper'}
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            <div className="admin-pagination">
              <button
                type="button"
                className="admin-page-btn"
                onClick={() => {
                  setAdminFormsExpandedId(null);
                  setAdminFormsPage((page) => Math.max(1, page - 1));
                }}
                disabled={adminFormsPageClamped <= 1}
              >
                Precedent
              </button>

              <div className="admin-page-numbers">
                {getPaginationItems(adminFormsTotalPages, adminFormsPageClamped).map((item, idx) => {
                  if (item === '...') {
                    return (
                      <button
                        key={`dots-forms-${idx}`}
                        type="button"
                        className="admin-page-number"
                        disabled
                        aria-hidden="true"
                      >
                        ...
                      </button>
                    );
                  }
                  const pageNum = item;
                  const isActive = pageNum === adminFormsPageClamped;
                  return (
                    <button
                      key={pageNum}
                      type="button"
                      className={`admin-page-number ${isActive ? 'active' : ''}`}
                      onClick={() => {
                        setAdminFormsExpandedId(null);
                        setAdminFormsPage(pageNum);
                      }}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>

              <button
                type="button"
                className="admin-page-btn"
                onClick={() => {
                  setAdminFormsExpandedId(null);
                  setAdminFormsPage((page) => Math.min(adminFormsTotalPages, page + 1));
                }}
                disabled={adminFormsPageClamped >= adminFormsTotalPages}
              >
                Suivant
              </button>
            </div>
          </div>
        )}

        {activeTab === TAB_KEYS.FORM_REQUESTS && (
          <div className="admin-page">
            <div className="admin-page-head">
              <h2 className="admin-page-title">Form requests - {selectedFormRequestTabLabel}</h2>
              <p className="admin-page-desc">Review submissions received from all published forms and process them with accept or reject actions.</p>
            </div>

            <div className="admin-page-toolbar">
              <div className="admin-page-count">
                Requests <span className="admin-page-count-number">{filteredAdminFormSubmissions.length}</span>
              </div>

              <div className="admin-page-controls">
                <button
                  type="button"
                  className="btn btn-outline"
                  onClick={() => fetchAdminFormSubmissions()}
                  disabled={adminFormSubmissionsLoading}
                >
                  {adminFormSubmissionsLoading ? 'Refreshing...' : 'Refresh'}
                </button>

                <button
                  type="button"
                  className="btn btn-outline admin-export-btn"
                  onClick={exportFormSubmissionsToExcel}
                  disabled={formSubmissionsExporting || adminFormSubmissionsLoading}
                >
                  {formSubmissionsExporting ? 'Exporting...' : 'Export Excel'}
                </button>
              </div>
            </div>

            <div className="admin-table-wrap">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Applicant</th>
                    <th>Form</th>
                    <th>Reference</th>
                    <th>Submitted</th>
                    <th>Status</th>
                    <th>Seen</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {adminFormSubmissionsLoading && filteredAdminFormSubmissions.length === 0 ? (
                    <tr><td colSpan={7} className="admin-table-empty">Loading...</td></tr>
                  ) : filteredAdminFormSubmissions.length === 0 ? (
                    <tr><td colSpan={7} className="admin-table-empty">No submissions found for this form tab.</td></tr>
                  ) : (
                    pagedAdminFormSubmissions.map((submission) => {
                      const isSeen = Number(submission.is_seen) === 1;

                      return (
                        <tr key={submission.id} className={Number(adminFormSubmissionExpandedId) === Number(submission.id) ? 'is-expanded' : ''}>
                          <td>
                            <div className="admin-user-cell">
                              <div className="admin-user-name">{submission.applicant_name || '-'}</div>
                              <div className="admin-user-email">{submission.applicant_email || '-'}</div>
                            </div>
                          </td>
                          <td>
                            <div>{submission.form_name || '-'}</div>
                            <div className="admin-user-email">{submission.request_tab_name || '-'}</div>
                          </td>
                          <td className="admin-cell-nowrap">{submission.reference_number || '-'}</td>
                          <td className="admin-cell-nowrap">{formatDateTime(submission.created_at)}</td>
                          <td className="admin-cell-nowrap">{submission.status || 'pending'}</td>
                          <td className="admin-cell-nowrap">
                            <div className="admin-notif-status">
                              <span className={`admin-notif-dot ${isSeen ? 'read' : 'unread'}`} aria-hidden="true" />
                              <span>{isSeen ? 'Seen' : 'Unseen'}</span>
                            </div>
                          </td>
                          <td className="admin-cell-actions">
                            <button
                              type="button"
                              className="admin-expand"
                              onClick={() => openAdminFormSubmissionModal(submission)}
                            >
                              Details
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            <div className="admin-pagination">
              <button
                type="button"
                className="admin-page-btn"
                onClick={() => {
                  setAdminFormSubmissionExpandedId(null);
                  setAdminFormSubmissionsPage((page) => Math.max(1, page - 1));
                }}
                disabled={adminFormSubmissionsPageClamped <= 1}
              >
                Precedent
              </button>

              <div className="admin-page-numbers">
                {getPaginationItems(adminFormSubmissionsTotalPages, adminFormSubmissionsPageClamped).map((item, idx) => {
                  if (item === '...') {
                    return (
                      <button
                        key={`dots-form-submissions-${idx}`}
                        type="button"
                        className="admin-page-number"
                        disabled
                        aria-hidden="true"
                      >
                        ...
                      </button>
                    );
                  }
                  const pageNum = item;
                  const isActive = pageNum === adminFormSubmissionsPageClamped;
                  return (
                    <button
                      key={pageNum}
                      type="button"
                      className={`admin-page-number ${isActive ? 'active' : ''}`}
                      onClick={() => {
                        setAdminFormSubmissionExpandedId(null);
                        setAdminFormSubmissionsPage(pageNum);
                      }}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>

              <button
                type="button"
                className="admin-page-btn"
                onClick={() => {
                  setAdminFormSubmissionExpandedId(null);
                  setAdminFormSubmissionsPage((page) => Math.min(adminFormSubmissionsTotalPages, page + 1));
                }}
                disabled={adminFormSubmissionsPageClamped >= adminFormSubmissionsTotalPages}
              >
                Suivant
              </button>
            </div>
          </div>
        )}

        {activeTab === 'event-requests' && (
          <div className="admin-page">
            <div className="admin-page-head">
              <h2 className="admin-page-title">Demandes evenement</h2>
              <p className="admin-page-desc">Examinez et gerez les demandes inscription aux evenements premium.</p>
            </div>

            <div className="admin-page-toolbar">
              <div className="admin-page-count">
                Requests <span className="admin-page-count-number">{eventRegistrations.length}</span>
              </div>

              <div className="admin-page-controls">
                <div className="admin-showing-meta">
                  {formatShowingMeta(eventRegistrations.length, eventRegistrationsPageClamped, eventRegistrationsPageSize)}
                  {eventRegistrationsLoading ? ' . Chargement...' : ''}
                </div>
              </div>
            </div>

            <div className="admin-table-wrap">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Nom</th>
                    <th>E-mail</th>
                    <th>Evenement</th>
                    <th>Date</th>
                    <th>Statut</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {eventRegistrationsLoading && eventRegistrations.length === 0 ? (
                    <tr><td colSpan={6} className="admin-table-empty">Chargement...</td></tr>
                  ) : eventRegistrations.length === 0 ? (
                    <tr><td colSpan={6} className="admin-table-empty">Aucune demande inscription trouvee.</td></tr>
                  ) : (
                    pagedEventRegistrations.map((registration) => {
                      const expanded = eventRegistrationsExpandedId === registration.id;
                      const statusLabel = registration.status || 'pending';
                      return (
                        <React.Fragment key={registration.id}>
                          <tr className={expanded ? 'is-expanded' : ''}>
                            <td>{registration.user_name || '-'}</td>
                            <td>{registration.user_email || '-'}</td>
                            <td>{registration.event_title || '-'}</td>
                            <td className="admin-cell-nowrap">{formatDate(registration.created_at)}</td>
                            <td className="admin-cell-nowrap">{statusLabel}</td>
                            <td className="admin-cell-actions">
                              <button
                                type="button"
                                className="admin-expand"
                                onClick={() => setEventRegistrationsExpandedId(expanded ? null : registration.id)}
                              >
                                {expanded ? 'Reduire' : 'Developper'}
                              </button>
                            </td>
                          </tr>
                        </React.Fragment>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            <div className="admin-pagination">
              <button
                type="button"
                className="admin-page-btn"
                onClick={() => setEventRegistrationsPage((p) => Math.max(1, p - 1))}
                disabled={eventRegistrationsPageClamped <= 1}
              >
                Precedent
              </button>

              <div className="admin-page-numbers">
                {getPaginationItems(eventRegistrationsTotalPages, eventRegistrationsPageClamped).map((item, idx) => {
                  if (item === '...') {
                    return (
                      <button
                        key={`dots-reg-${idx}`}
                        type="button"
                        className="admin-page-number"
                        disabled
                        aria-hidden="true"
                      >
                        ...
                      </button>
                    );
                  }
                  const pageNum = item;
                  const isActive = pageNum === eventRegistrationsPageClamped;
                  return (
                    <button
                      key={pageNum}
                      type="button"
                      className={`admin-page-number ${isActive ? 'active' : ''}`}
                      onClick={() => setEventRegistrationsPage(pageNum)}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>

              <button
                type="button"
                className="admin-page-btn"
                onClick={() => setEventRegistrationsPage((p) => Math.min(eventRegistrationsTotalPages, p + 1))}
                disabled={eventRegistrationsPageClamped >= eventRegistrationsTotalPages}
              >
                Suivant
              </button>
            </div>
          </div>
        )}

        {activeTab === TAB_KEYS.CONTACT_MESSAGES && (
          <div className="admin-page">
            <div className="admin-page-head">
              <h2 className="admin-page-title">Messages de contact</h2>
              <p className="admin-page-desc">Examinez les messages entrants de la page contact et repondez par e-mail.</p>
            </div>

            <div className="admin-page-toolbar">
              <div className="admin-page-count">
                Messages <span className="admin-page-count-number">{contactMessages.length}</span>
              </div>

              <div className="admin-page-controls">
                <div className="admin-showing-meta">
                  {formatShowingMeta(contactMessages.length, contactMessagesPageClamped, contactMessagesPageSize)}
                  {contactMessagesLoading ? ' . Chargement...' : ''}
                </div>
              </div>
            </div>

            <div className="admin-table-wrap">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Nom</th>
                    <th>E-mail</th>
                    <th>Sujet</th>
                    <th>Recu le</th>
                    <th>Statut</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {contactMessagesLoading && contactMessages.length === 0 ? (
                    <tr><td colSpan={6} className="admin-table-empty">Chargement...</td></tr>
                  ) : contactMessages.length === 0 ? (
                    <tr><td colSpan={6} className="admin-table-empty">Aucun message de contact pour le moment.</td></tr>
                  ) : (
                    pagedContactMessages.map((message) => {
                      const expanded = contactMessagesExpandedId === message.id;
                      const statusMeta = getContactStatusMeta(message.status);
                      return (
                        <React.Fragment key={message.id}>
                          <tr className={expanded ? 'is-expanded' : ''}>
                            <td>{message.full_name || '-'}</td>
                            <td>{message.email || '-'}</td>
                            <td>{message.subject || '-'}</td>
                            <td className="admin-cell-nowrap">{formatDateTime(message.created_at)}</td>
                            <td className="admin-cell-nowrap">
                              <div className="admin-notif-status">
                                <span className={`admin-notif-dot ${statusMeta.dotClass}`} aria-hidden="true" />
                                <span>{statusMeta.label}</span>
                              </div>
                            </td>
                            <td className="admin-cell-actions">
                              <button
                                type="button"
                                className="admin-expand"
                                onClick={() => openContactMessage(message)}
                              >
                                {expanded ? 'Reduire' : 'Developper'}
                              </button>
                            </td>
                          </tr>
                        </React.Fragment>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            <div className="admin-pagination">
              <button
                type="button"
                className="admin-page-btn"
                onClick={() => {
                  setContactMessagesExpandedId(null);
                  setContactMessagesPage((p) => Math.max(1, p - 1));
                }}
                disabled={contactMessagesPageClamped <= 1}
              >
                Precedent
              </button>

              <div className="admin-page-numbers">
                {getPaginationItems(contactMessagesTotalPages, contactMessagesPageClamped).map((item, idx) => {
                  if (item === '...') {
                    return (
                      <button
                        key={`dots-contact-${idx}`}
                        type="button"
                        className="admin-page-number"
                        disabled
                        aria-hidden="true"
                      >
                        ...
                      </button>
                    );
                  }
                  const pageNum = item;
                  const isActive = pageNum === contactMessagesPageClamped;
                  return (
                    <button
                      key={pageNum}
                      type="button"
                      className={`admin-page-number ${isActive ? 'active' : ''}`}
                      onClick={() => {
                        setContactMessagesExpandedId(null);
                        setContactMessagesPage(pageNum);
                      }}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>

              <button
                type="button"
                className="admin-page-btn"
                onClick={() => {
                  setContactMessagesExpandedId(null);
                  setContactMessagesPage((p) => Math.min(contactMessagesTotalPages, p + 1));
                }}
                disabled={contactMessagesPageClamped >= contactMessagesTotalPages}
              >
                Suivant
              </button>
            </div>
          </div>
        )}

        {activeTab === 'signup' && (
          <div className="admin-page">
            <div className="admin-page-head">
              <h2 className="admin-page-title">Demandes de logement</h2>
              <p className="admin-page-desc">Examinez et approuvez les demandes de logement.</p>
            </div>

            <div className="admin-page-toolbar">
              <div className="admin-page-count">
                Applications <span className="admin-page-count-number">{housingTableRequests.length}</span>
              </div>

              <div className="admin-page-controls">
                <button
                  type="button"
                  className="btn btn-outline admin-export-btn"
                  onClick={exportSignupRequestsToExcel}
                  disabled={signupExporting || loading}
                >
                  {signupExporting ? 'Exporting...' : 'Export Excel'}
                </button>

                <div className="admin-showing-meta">
                  {formatShowingMeta(housingTableRequests.length, signupPageClamped, signupPageSize)}
                  {loading ? ' . Chargement...' : ''}
                </div>
              </div>
            </div>

            <div className="admin-table-wrap">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Demandeur</th>
                    <th>Reference</th>
                    <th>Statut</th>
                    <th>Vu</th>
                    <th>Soumis le</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {loading && housingTableRequests.length === 0 ? (
                    <tr><td colSpan={6} className="admin-table-empty">Chargement...</td></tr>
                  ) : housingTableRequests.length === 0 ? (
                    <tr><td colSpan={6} className="admin-table-empty">Aucune demande inscription trouvee.</td></tr>
                  ) : (
                    pagedSignupRequests.map((request) => {
                      const isImportSubmission = request.__source === 'form-submission';
                      const expanded = isImportSubmission
                        ? showImportSubmissionModal && importSubmission?.id === request.form_submission_id
                        : showSignupRequestModal && selectedRequest?.id === request.id;
                      const fullName = `${request.first_name || ''} ${request.last_name || ''}`.trim()
                        || request.applicant_name
                        || request.email
                        || '(Sans nom)';
                      const needsDecisionAttention = isImportSubmission ? false : getSignupRequestNeedsDecisionAttention(request);
                      const isSeen = isHousingRowSeen(request);
                      const seenLabel = needsDecisionAttention ? 'Action requise' : (isSeen ? 'Vu' : 'Non lu');
                      return (
                        <React.Fragment key={isImportSubmission ? `import-${request.form_submission_id}` : request.id}>
                          <tr className={expanded ? 'is-expanded' : ''}>
                            <td>
                              <div className="admin-user-cell">
                                <div className="admin-user-name">{fullName}</div>
                                <div className="admin-user-email">{request.email}</div>
                              </div>
                            </td>
                            <td className="admin-cell-nowrap">{request.reference_number || '-'}</td>
                            <td className="admin-cell-nowrap">{request.status || '-'}</td>
                            <td className="admin-cell-nowrap">
                              <div className="admin-notif-status">
                                <span className={`admin-notif-dot ${isSeen ? 'read' : 'unread'}`} aria-hidden="true" />
                                <span>{seenLabel}</span>
                              </div>
                            </td>
                            <td className="admin-cell-nowrap">{formatDate(request.created_at)}</td>
                            <td className="admin-cell-actions">
                              <button
                                type="button"
                                className="admin-expand"
                                onClick={() => handleRequestSelect(request)}
                              >
                                Developper
                              </button>
                            </td>
                          </tr>
                        </React.Fragment>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            <div className="admin-pagination">
              <button
                type="button"
                className="admin-page-btn"
                onClick={() => {
                  closeSignupRequestModal();
                  setSignupPage((p) => Math.max(1, p - 1));
                }}
                disabled={signupPageClamped <= 1}
              >
                Precedent
              </button>

              <div className="admin-page-numbers">
                {getPaginationItems(signupTotalPages, signupPageClamped).map((item, idx) => {
                  if (item === '...') {
                    return (
                      <button
                        key={`dots-signup-${idx}`}
                        type="button"
                        className="admin-page-number"
                        disabled
                        aria-hidden="true"
                      >
                        ...
                      </button>
                    );
                  }
                  const pageNum = item;
                  const isActive = pageNum === signupPageClamped;
                  return (
                    <button
                      key={pageNum}
                      type="button"
                      className={`admin-page-number ${isActive ? 'active' : ''}`}
                      onClick={() => {
                        closeSignupRequestModal();
                        setSignupPage(pageNum);
                      }}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>

              <button
                type="button"
                className="admin-page-btn"
                onClick={() => {
                  closeSignupRequestModal();
                  setSignupPage((p) => Math.min(signupTotalPages, p + 1));
                }}
                disabled={signupPageClamped >= signupTotalPages}
              >
                Suivant
              </button>
            </div>
          </div>
        )}

        {activeTab === 'document-requests' && (
          <div className="admin-page">
            <div className="admin-page-head">
              <h2 className="admin-page-title">Demande de documents</h2>
              <p className="admin-page-desc">Voir et traiter les demandes de documents des utilisateurs.</p>
            </div>

            <div className="admin-page-toolbar">
              <div className="admin-page-count">
                Demandes <span className="admin-page-count-number">{adminDocumentRequests.length}</span>
              </div>

              <div className="admin-page-controls">
                <div className="admin-showing-meta">
                  {formatShowingMeta(adminDocumentRequests.length, adminDocumentRequestsPageClamped, adminDocumentRequestsPageSize)}
                  {adminDocumentRequestsLoading ? ' . Chargement...' : ''}
                </div>
              </div>
            </div>

            <div className="admin-table-wrap">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Utilisateur</th>
                    <th>Recue le</th>
                    <th>Traitee le</th>
                    <th>Statut</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {adminDocumentRequestsLoading && adminDocumentRequests.length === 0 ? (
                    <tr><td colSpan={5} className="admin-table-empty">Chargement...</td></tr>
                  ) : adminDocumentRequests.length === 0 ? (
                    <tr><td colSpan={5} className="admin-table-empty">Aucune demande trouvee.</td></tr>
                  ) : (
                    pagedAdminDocumentRequests.map((r) => {
                      const userName = `${r.user_first_name || ''} ${r.user_last_name || ''}`.trim() || r.user_email || '(Sans nom)';
                      const pending = String(r.status || '') !== 'completed';
                      const expanded = showAdminDocumentRequestModal && adminDocumentRequestSelected?.id === r.id;

                      return (
                        <tr key={r.id} className={expanded ? 'is-expanded' : ''}>
                          <td>
                            <div className="admin-user-cell">
                              <div className="admin-user-name">{userName}</div>
                              <div className="admin-user-email">{r.user_email || '-'}</div>
                              <div className="admin-user-sub">{getAdminDocumentRequestSummary(r)}</div>
                            </div>
                          </td>
                          <td className="admin-cell-nowrap">{formatDateTime(r.requested_at)}</td>
                          <td className="admin-cell-nowrap">{pending ? '--' : formatDateTime(r.completed_at)}</td>
                          <td className="admin-cell-nowrap">
                            <div className="admin-notif-status">
                              <span className={`admin-notif-dot ${pending ? 'unread' : 'read'}`} aria-label={pending ? 'Pending' : 'Completed'} />
                            </div>
                          </td>
                          <td className="admin-cell-actions">
                            <button
                              type="button"
                              className="admin-expand"
                              onClick={() => openAdminDocumentRequestModal(r)}
                            >
                              Details
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            <div className="admin-pagination">
              <button
                type="button"
                className="admin-page-btn"
                onClick={() => setAdminDocumentRequestsPage((p) => Math.max(1, p - 1))}
                disabled={adminDocumentRequestsPageClamped <= 1}
              >
                Precedent
              </button>

              <div className="admin-page-numbers">
                {getPaginationItems(adminDocumentRequestsTotalPages, adminDocumentRequestsPageClamped).map((item, idx) => {
                  if (item === '...') {
                    return (
                      <button
                        key={`dots-docreq-${idx}`}
                        type="button"
                        className="admin-page-number"
                        disabled
                        aria-hidden="true"
                      >
                        ...
                      </button>
                    );
                  }
                  const pageNum = item;
                  const isActive = pageNum === adminDocumentRequestsPageClamped;
                  return (
                    <button
                      key={pageNum}
                      type="button"
                      className={`admin-page-number ${isActive ? 'active' : ''}`}
                      onClick={() => setAdminDocumentRequestsPage(pageNum)}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>

              <button
                type="button"
                className="admin-page-btn"
                onClick={() => setAdminDocumentRequestsPage((p) => Math.min(adminDocumentRequestsTotalPages, p + 1))}
                disabled={adminDocumentRequestsPageClamped >= adminDocumentRequestsTotalPages}
              >
                Suivant
              </button>
            </div>
          </div>
        )}

        {activeTab === 'team' && (
          <div className="admin-page">
            <div className="admin-page-head">
              <h2 className="admin-page-title">Gestion de equipe</h2>
              <p className="admin-page-desc">Creez et supprimez des comptes admin. Cette section est reservee aux Super Admin.</p>
            </div>

            <div className="admin-page-toolbar">
              <div className="admin-page-count">
                Comptes admin <span className="admin-page-count-number">{teamEmployees.length}</span>
              </div>
              <div className="admin-page-controls" style={{ gap: '0.6rem', alignItems: 'center' }}>
                <div className="admin-showing-meta">
                  {formatShowingMeta(teamEmployees.length, teamPageClamped, teamPageSize)}
                  {loading ? ' . Chargement...' : ''}
                </div>
                <button
                  type="button"
                  className="btn btn-outline"
                  onClick={async () => {
                    setShowTeamRoleTypePanel(true);
                    const roleTypes = await fetchTeamRoleTypes();
                    const firstEditableRole = roleTypes.find((roleType) => roleType.is_editable) || null;
                    if (firstEditableRole) {
                      openEditTeamRoleType(firstEditableRole);
                    } else {
                      openCreateTeamRoleType();
                    }
                  }}
                >
                  Types d'admin
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => setShowTeamCreatePanel(true)}
                >
                  Creer un compte admin
                </button>
              </div>
            </div>

            <div className="admin-table-wrap">
              <table className="admin-table employees-table admin-accounts-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Nom</th>
                    <th>E-mail</th>
                    <th>Type d'admin</th>
                    <th>Roles</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {loading && teamEmployees.length === 0 ? (
                    <tr><td colSpan={6} className="admin-table-empty">Chargement...</td></tr>
                  ) : teamEmployees.length === 0 ? (
                    <tr><td colSpan={6} className="admin-table-empty">Aucun compte admin trouve.</td></tr>
                  ) : (
                    pagedTeamEmployees.map((adminUser) => {
                      const fullName = `${adminUser.first_name || ''} ${adminUser.last_name || ''}`.trim() || '(Sans nom)';
                      const roleList = Array.isArray(adminUser.roles) ? adminUser.roles : [];
                      const rolesText = roleList.length ? roleList.join(', ') : (adminUser.role || '-');
                      const isCurrentUser = Number(adminUser.id) === Number(user?.id);

                      return (
                        <tr key={adminUser.id}>
                          <td className="admin-cell-nowrap">#{adminUser.id}</td>
                          <td>{fullName}</td>
                          <td>{adminUser.email || '-'}</td>
                          <td>{adminUser.role || '-'}</td>
                          <td>{rolesText}</td>
                          <td>
                            <button
                              type="button"
                              className="btn btn-danger btn-sm"
                              onClick={() => handleDeleteEmployee(adminUser.id)}
                              disabled={isCurrentUser || teamDeletingId === adminUser.id}
                            >
                              {isCurrentUser ? 'Compte actuel' : (teamDeletingId === adminUser.id ? 'Suppression...' : 'Supprimer')}
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            <div className="admin-pagination">
              <button
                type="button"
                className="admin-page-btn"
                onClick={() => setTeamPage((p) => Math.max(1, p - 1))}
                disabled={teamPageClamped <= 1}
              >
                Precedent
              </button>

              <div className="admin-page-numbers">
                {getPaginationItems(teamTotalPages, teamPageClamped).map((item, idx) => {
                  if (item === '...') {
                    return (
                      <button
                        key={`dots-team-${idx}`}
                        type="button"
                        className="admin-page-number"
                        disabled
                        aria-hidden="true"
                      >
                        ...
                      </button>
                    );
                  }
                  const pageNum = item;
                  const isActive = pageNum === teamPageClamped;
                  return (
                    <button
                      key={pageNum}
                      type="button"
                      className={`admin-page-number ${isActive ? 'active' : ''}`}
                      onClick={() => setTeamPage(pageNum)}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>

              <button
                type="button"
                className="admin-page-btn"
                onClick={() => setTeamPage((p) => Math.min(teamTotalPages, p + 1))}
                disabled={teamPageClamped >= teamTotalPages}
              >
                Suivant
              </button>
            </div>
          </div>
        )}

                </div>
              </CSSTransition>
            </TransitionGroup>
          </div>
        </main>
      </div>

      {showFooterTextEditor && (
        <div className="modal-overlay" onClick={() => setShowFooterTextEditor(false)}>
          <div className="modal-content modal-wide modal-zoom-in" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Editer texte colonne gauche</h3>
              <button className="modal-close" onClick={() => setShowFooterTextEditor(false)}>x</button>
            </div>

            <div className="modal-body">
              <p className="admin-footer-note">Un paragraphe par ligne.</p>
              <textarea
                className="admin-footer-textarea"
                rows={6}
                value={footerTextDraft}
                onChange={(e) => setFooterTextDraft(e.target.value)}
                placeholder="Association Des Etudiants Egyptiens en France"
              />
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-outline"
                onClick={() => setShowFooterTextEditor(false)}
                disabled={footerSavingText}
              >
                Annuler
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={saveFooterText}
                disabled={footerSavingText}
              >
                {footerSavingText ? 'Enregistrement...' : 'Enregistrer le texte'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showFooterContactEditor && (
        <div className="modal-overlay" onClick={resetFooterContactForm}>
          <form className="modal-content modal-wide modal-zoom-in" onSubmit={submitFooterContact} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{footerContactEditingId ? 'Modifier le logo de contact' : 'Ajouter un logo de contact'}</h3>
              <button type="button" className="modal-close" onClick={resetFooterContactForm}>x</button>
            </div>

            <div className="modal-body">
              <div className="admin-form-2col">
                <div className="form-group">
                  <label>Label</label>
                  <input
                    type="text"
                    value={footerContactForm.label}
                    onChange={(e) => setFooterContactForm((prev) => ({ ...prev, label: e.target.value }))}
                    placeholder="Contact ADEEF"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Lien</label>
                  <input
                    type="text"
                    value={footerContactForm.href}
                    onChange={(e) => setFooterContactForm((prev) => ({ ...prev, href: e.target.value }))}
                    placeholder="https://example.com ou mailto:contact@adeef.org"
                  />
                </div>

                <div className="form-group">
                  <label>Image du logo {!footerContactEditingId ? '*' : '(optionnel)'}</label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files && e.target.files[0] ? e.target.files[0] : null;
                      setFooterContactImageFile(file);
                    }}
                    required={!footerContactEditingId && !footerContactForm.image_url}
                  />
                  {footerContactImageFile ? (
                    <small className="admin-muted">Fichier selectionne: {footerContactImageFile.name}</small>
                  ) : (
                    <small className="admin-muted">Selectionnez une image depuis votre appareil pour le logo partenaire.</small>
                  )}

                  {footerContactForm.image_url && (() => {
                    const { src: currentLogoSrc } = parseFooterLogoUrl(footerContactForm.image_url);
                    const currentLogoHref = resolveMediaUrl(currentLogoSrc) || currentLogoSrc;
                    if (!currentLogoHref) return null;
                    return (
                      <div className="admin-carousel-current-image">
                        <span className="admin-k">Logo actuel</span>
                        <a className="admin-v admin-link" href={currentLogoHref} target="_blank" rel="noreferrer">
                          Ouvrir logo actuel
                        </a>
                      </div>
                    );
                  })()}
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-outline"
                onClick={resetFooterContactForm}
                disabled={footerContactSaving}
              >
                Annuler
              </button>
              <button type="submit" className="btn btn-primary" disabled={footerContactSaving}>
                {footerContactSaving ? 'Enregistrement...' : (footerContactEditingId ? 'Mettre a jour le logo' : 'Ajouter le logo')}
              </button>
            </div>
          </form>
        </div>
      )}

      {showTeamRoleTypePanel && (
        <div
          className="modal-overlay"
          onClick={() => {
            setShowTeamRoleTypePanel(false);
            resetTeamRoleTypeForm();
          }}
        >
          <div className="modal-content modal-wide modal-zoom-in team-role-types-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Types d'admin</h3>
              <button
                type="button"
                className="modal-close"
                onClick={() => {
                  setShowTeamRoleTypePanel(false);
                  resetTeamRoleTypeForm();
                }}
              >
                x
              </button>
            </div>

            <div className="modal-body team-role-types-body">
              <aside className="team-role-types-sidebar">
                <button
                  type="button"
                  className="btn btn-outline btn-sm team-role-type-create"
                  onClick={openCreateTeamRoleType}
                >
                  Nouveau type d'admin
                </button>

                {teamRoleTypeLoading ? (
                  <p className="admin-account-note">Chargement des types d'admin...</p>
                ) : (
                  <div className="team-role-types-list">
                    {teamRoleTypes
                      .filter((roleType) => !roleType?.is_super_admin)
                      .map((roleType) => {
                        const isActive = Number(roleType.id) === Number(editingTeamRoleTypeId);
                        return (
                          <button
                            key={roleType.id || roleType.name}
                            type="button"
                            className={`team-role-type-item ${isActive ? 'active' : ''}`}
                            onClick={() => openEditTeamRoleType(roleType)}
                          >
                            <span className="team-role-type-item-name">{roleType.name}</span>
                            <span className="team-role-type-item-meta">
                              {Array.isArray(roleType.tabs) ? roleType.tabs.length : 0} onglets
                            </span>
                          </button>
                        );
                      })}
                  </div>
                )}
              </aside>

              <form className="team-role-types-editor" onSubmit={handleSaveTeamRoleType}>
                <div className="form-group">
                  <label>Nom du type *</label>
                  <input
                    value={teamRoleTypeForm.name}
                    onChange={(e) => setTeamRoleTypeForm((prev) => ({ ...prev, name: e.target.value }))}
                    disabled={Boolean(editingTeamRoleType?.is_core)}
                    required
                  />
                </div>

                {editingTeamRoleType?.is_core && (
                  <p className="admin-account-note">Le nom des types d'admin de base ne peut pas etre modifie.</p>
                )}

                <p className="admin-account-note">
                  Les onglets Gestion de equipe et Notification management restent exclus et reserves aux Super Admin.
                </p>

                <div className="team-role-tabs-grid">
                  {teamRoleTabCatalog.map((tab) => {
                    const tabKey = String(tab?.key || '').trim();
                    if (!tabKey) return null;
                    const checked = Array.isArray(teamRoleTypeForm.tabs) && teamRoleTypeForm.tabs.includes(tabKey);
                    return (
                      <label key={tabKey} className={`team-role-tab-toggle ${checked ? 'active' : ''}`}>
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleTeamRoleTypeTab(tabKey)}
                        />
                        <span>{tab.label || tabKey}</span>
                      </label>
                    );
                  })}
                </div>

                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-outline"
                    onClick={() => {
                      setShowTeamRoleTypePanel(false);
                      resetTeamRoleTypeForm();
                    }}
                    disabled={teamRoleTypeSaving}
                  >
                    Fermer
                  </button>
                  <button type="submit" className="btn btn-primary" disabled={teamRoleTypeSaving}>
                    {teamRoleTypeSaving
                      ? 'Enregistrement...'
                      : (editingTeamRoleTypeId ? 'Mettre a jour le type' : 'Creer le type')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {showTeamCreatePanel && (
        <div
          className="modal-overlay"
          onClick={() => {
            setShowTeamCreatePanel(false);
            setNewEmployee({
              first_name: '',
              last_name: '',
              email: '',
              role: teamRoleOptions[0] || DEFAULT_TEAM_ROLE_OPTIONS[0],
            });
          }}
        >
          <form className="modal-content modal-wide modal-zoom-in" onSubmit={handleAddEmployee} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Creer un compte admin</h3>
              <button
                type="button"
                className="modal-close"
                onClick={() => {
                  setShowTeamCreatePanel(false);
                  setNewEmployee({
                    first_name: '',
                    last_name: '',
                    email: '',
                    role: teamRoleOptions[0] || DEFAULT_TEAM_ROLE_OPTIONS[0],
                  });
                }}
              >
                x
              </button>
            </div>

            <div className="modal-body admin-account-form admin-form-compact">
              <div className="admin-form-2col">
                <div className="form-group">
                  <label>Prenom *</label>
                  <input
                    value={newEmployee.first_name}
                    onChange={(e) => setNewEmployee({ ...newEmployee, first_name: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Nom *</label>
                  <input
                    value={newEmployee.last_name}
                    onChange={(e) => setNewEmployee({ ...newEmployee, last_name: e.target.value })}
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label>E-mail *</label>
                <input
                  type="email"
                  value={newEmployee.email}
                  onChange={(e) => setNewEmployee({ ...newEmployee, email: e.target.value })}
                  required
                    pattern={EMAIL_INPUT_PATTERN}
                    title={EMAIL_INPUT_TITLE}
                />
              </div>

              <p className="admin-account-note">Un mot de passe temporaire securise sera genere automatiquement et envoye a cette adresse e-mail.</p>

              <div className="form-group">
                <label>Type d'admin *</label>
                <select
                  value={newEmployee.role}
                  onChange={(e) => setNewEmployee({ ...newEmployee, role: e.target.value })}
                >
                  {teamRoleOptions.map((roleName) => (
                    <option key={roleName} value={roleName}>{roleName}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-outline"
                onClick={() => {
                  setShowTeamCreatePanel(false);
                  setNewEmployee({
                    first_name: '',
                    last_name: '',
                    email: '',
                    role: teamRoleOptions[0] || DEFAULT_TEAM_ROLE_OPTIONS[0],
                  });
                }}
                disabled={teamSaving}
              >
                Annuler
              </button>
              <button type="submit" className="btn btn-primary" disabled={teamSaving}>
                {teamSaving ? 'Creation...' : 'Creer un compte admin'}
              </button>
            </div>
          </form>
        </div>
      )}

      <FormBuilderModal
        open={showFormBuilderModal}
        mode={formBuilderMode}
        initialValue={formBuilderInitialValue}
        saving={formBuilderSaving}
        onClose={closeFormBuilder}
        onSave={saveFormBuilder}
      />

      <CitiesManager
        open={showCitiesManager}
        onClose={() => setShowCitiesManager(false)}
        onSave={() => fetchAdminForms()}
      />

      {showSignupRequestModal && selectedRequest && (
        <div className="modal-overlay" onClick={closeSignupRequestModal}>
          <div className="modal-content modal-wide modal-zoom-in" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>
                Housing Application -- {`${selectedRequest.first_name || ''} ${selectedRequest.last_name || ''}`.trim() || '(Sans nom)'}
              </h3>
              <button className="modal-close" onClick={closeSignupRequestModal}>x</button>
            </div>

            <div className="modal-body">
              <div className="housing-modal-overview">
                <div className="housing-overview-head">
                  <span className="housing-overview-reference">Reference : {selectedRequest.reference_number || '-'}</span>
                  <span
                    className={`housing-status-chip ${
                      ['resolved', 'approved'].includes(String(selectedRequest.status || '').toLowerCase())
                        ? 'is-success'
                        : ['rejected', 'disapproved', 'closed'].includes(String(selectedRequest.status || '').toLowerCase())
                          ? 'is-danger'
                          : 'is-pending'
                    }`}
                  >
                    {selectedRequest.status || 'open'}
                  </span>
                </div>

                <div className="housing-info-grid">
                  <article className="housing-info-card">
                    <span className="housing-info-label">E-mail</span>
                    <span className="housing-info-value">{selectedRequest.email || '-'}</span>
                  </article>
                  <article className="housing-info-card">
                    <span className="housing-info-label">Soumis le</span>
                    <span className="housing-info-value">{formatDate(selectedRequest.created_at)}</span>
                  </article>
                </div>
              </div>

              <section className="housing-info-section">
                <h4 className="housing-info-section-title">Details du demandeur</h4>
                <div className="housing-info-grid">
                  <article className="housing-info-card">
                    <span className="housing-info-label">Telephone</span>
                    <span className="housing-info-value">{selectedRequest.phone_number || '-'}</span>
                  </article>
                  <article className="housing-info-card">
                    <span className="housing-info-label">DOB</span>
                    <span className="housing-info-value">{formatDate(selectedRequest.date_of_birth)}</span>
                  </article>
                  <article className="housing-info-card">
                    <span className="housing-info-label">Pays de naissance</span>
                    <span className="housing-info-value">{selectedRequest.country_of_birth || '-'}</span>
                  </article>
                  <article className="housing-info-card">
                    <span className="housing-info-label">Ville de naissance</span>
                    <span className="housing-info-value">{selectedRequest.city_of_birth || '-'}</span>
                  </article>
                  <article className="housing-info-card">
                    <span className="housing-info-label">Passport</span>
                    <span className="housing-info-value">{selectedRequest.passport_number || '-'}</span>
                  </article>
                  <article className="housing-info-card">
                    <span className="housing-info-label">Start Date</span>
                    <span className="housing-info-value">{formatDate(selectedRequest.start_date)}</span>
                  </article>
                  <article className="housing-info-card">
                    <span className="housing-info-label">Duree du contrat</span>
                    <span className="housing-info-value">{selectedRequest.contract_duration || '-'}</span>
                  </article>
                  <article className="housing-info-card">
                    <span className="housing-info-label">Ville souhaitee</span>
                    <span className="housing-info-value">{selectedRequest.desired_city || '-'}</span>
                  </article>
                  <article className="housing-info-card">
                    <span className="housing-info-label">Budget max</span>
                    <span className="housing-info-value">{selectedRequest.max_budget || '-'}</span>
                  </article>
                  <article className="housing-info-card housing-info-card-wide">
                    <span className="housing-info-label">Residences souhaitees</span>
                    <span className="housing-info-value">
                      {Array.isArray(selectedRequest.desired_residences)
                        ? selectedRequest.desired_residences.join(', ')
                        : (selectedRequest.desired_residences || 'Non renseigne')}
                    </span>
                  </article>
                </div>
              </section>

              <section className="housing-info-section">
                <h4 className="housing-info-section-title">Details du garant</h4>
                <div className="housing-info-grid">
                  <article className="housing-info-card">
                    <span className="housing-info-label">Nom du garant</span>
                    <span className="housing-info-value">{`${selectedRequest.guardian_first_name || ''} ${selectedRequest.guardian_last_name || ''}`.trim() || 'Non renseigne'}</span>
                  </article>
                  <article className="housing-info-card">
                    <span className="housing-info-label">E-mail du garant</span>
                    <span className="housing-info-value">{selectedRequest.guardian_email || 'Non renseigne'}</span>
                  </article>
                  <article className="housing-info-card">
                    <span className="housing-info-label">Telephone du garant</span>
                    <span className="housing-info-value">{selectedRequest.guardian_phone_number || selectedRequest.guardian_phone || 'Non renseigne'}</span>
                  </article>
                  <article className="housing-info-card">
                    <span className="housing-info-label">Date de naissance du garant</span>
                    <span className="housing-info-value">{selectedRequest.guardian_datebirth ? formatDate(selectedRequest.guardian_datebirth) : 'Non renseigne'}</span>
                  </article>
                  <article className="housing-info-card">
                    <span className="housing-info-label">Profession du garant</span>
                    <span className="housing-info-value">{selectedRequest.guardian_job || 'Non renseigne'}</span>
                  </article>
                  <article className="housing-info-card">
                    <span className="housing-info-label">Entreprise du garant</span>
                    <span className="housing-info-value">{selectedRequest.guardian_company || 'Non renseigne'}</span>
                  </article>
                  <article className="housing-info-card housing-info-card-wide">
                    <span className="housing-info-label">Adresse du garant</span>
                    <span className="housing-info-value">{selectedRequest.guardian_address || 'Non renseigne'}</span>
                  </article>
                  <article className="housing-info-card">
                    <span className="housing-info-label">Ville du garant</span>
                    <span className="housing-info-value">{selectedRequest.guardian_city || 'Non renseigne'}</span>
                  </article>
                </div>
              </section>

              <div className="additional-data">
                <h4>Informations complementaires</h4>
                <div className="form-grid">
                  <div className="form-group">
                    <label>Type de contrat :</label>
                    <input
                      type="text"
                      value={additionalData.contract_type}
                      onChange={(e) => setAdditionalData(prev => ({ ...prev, contract_type: e.target.value }))}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Numero de lot :</label>
                    <input
                      type="text"
                      value={additionalData.lot_number}
                      onChange={(e) => setAdditionalData(prev => ({ ...prev, lot_number: e.target.value }))}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>CH (chiffres uniquement) :</label>
                    <input
                      type="text"
                      inputMode="numeric"
                      value={additionalData.ch}
                      onChange={(e) => setAdditionalData(prev => ({
                        ...prev,
                        ch: String(e.target.value || '').replace(/\D/g, '')
                      }))}
                      placeholder="ex. 12345"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Ville de residence :</label>
                    <select
                      value={additionalData.city_of_residence}
                      onChange={(e) => setAdditionalData((prev) => ({
                        ...prev,
                        city_of_residence: e.target.value,
                        residence_name: '',
                      }))}
                      required
                      disabled={residencesCatalogLoading}
                    >
                      <option value="">{residencesCatalogLoading ? 'Chargement...' : 'Selectionner une ville'}</option>
                      {residenceCities.map((city) => (
                        <option key={city} value={city}>{city}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Nom de la residence :</label>
                    <select
                      value={additionalData.residence_name}
                      onChange={(e) => setAdditionalData((prev) => ({ ...prev, residence_name: e.target.value }))}
                      required
                      disabled={!additionalData.city_of_residence || residencesCatalogLoading}
                    >
                      <option value="">{additionalData.city_of_residence ? 'Selectionner une residence' : 'Choisissez une ville d\'abord'}</option>
                      {additionalResidenceOptions.map((name) => (
                        <option key={name} value={name}>{name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Adresse de la residence :</label>
                    <input
                      type="text"
                      value={additionalData.residence_address}
                      onChange={(e) => setAdditionalData(prev => ({ ...prev, residence_address: e.target.value }))}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Numero de studio :</label>
                    <input
                      type="text"
                      value={additionalData.studio_number}
                      onChange={(e) => setAdditionalData(prev => ({ ...prev, studio_number: e.target.value }))}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Surface (m2) :</label>
                    <input
                      type="number"
                      value={additionalData.area_sqm}
                      onChange={(e) => setAdditionalData(prev => ({ ...prev, area_sqm: e.target.value }))}
                    />
                  </div>
                  <div className="form-group">
                    <label>Date entree :</label>
                    <input
                      type="date"
                      value={additionalData.move_in_date}
                        min={todayParisDate}
                      onChange={(e) => setAdditionalData(prev => {
                        const moveInDate = e.target.value;
                        const next = { ...prev, move_in_date: moveInDate };
                        if (next.move_out_date && moveInDate && next.move_out_date < moveInDate) {
                          next.move_out_date = '';
                        }
                        return next;
                      })}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Date de sortie :</label>
                    <input
                      type="date"
                      value={additionalData.move_out_date}
                        min={additionalData.move_in_date || todayParisDate}
                      onChange={(e) => setAdditionalData(prev => ({ ...prev, move_out_date: e.target.value }))}
                    />
                  </div>
                  <div className="form-group">
                    <label>Montant du paiement (EUR) :</label>
                    <input
                      type="number"
                      step="0.01"
                      value={additionalData.payment_amount}
                      onChange={(e) => setAdditionalData(prev => ({ ...prev, payment_amount: e.target.value }))}
                      placeholder="Montant mensuel du paiement"
                      required
                    />
                  </div>
                </div>

                <div className="mandate-section">
                  <h4>Statut de configuration du paiement</h4>
                  <div className="mandate-status">
                    <p style={{ marginTop: 0 }}>
                      <strong>Statut :</strong>{' '}
                      {(mandateLatest?.mandate_status || (mandateLatestLoading ? 'Chargement...' : 'Non defini'))}
                      {mandateLatestLoading && mandateLatest?.mandate_status ? ' (actualisation...)' : ''}
                      {(mandateLatest?.mandate_id || mandateLatest?.gocardless_mandate_id)
                        ? ` (Mandate: ${mandateLatest?.mandate_id || mandateLatest?.gocardless_mandate_id})`
                        : ''}
                    </p>
                    {mandateLatest?.mandate_created_at && (
                      <p><strong>Cree le :</strong> {formatDate(mandateLatest.mandate_created_at)}</p>
                    )}
                  </div>

                  {(!mandateLatest || mandateLatest.mandate_status === 'not_setup') && (
                    <button
                      onClick={createMandateRequest}
                      className="btn btn-primary"
                      disabled={mandateLoading}
                    >
                      {mandateLoading ? 'Envoi...' : "Envoyer l'email de configuration du mandat"}
                    </button>
                  )}
                </div>

                <div className="mandate-section">
                  <h4>DocuSign</h4>
                  <p style={{ marginTop: 0 }}>
                    <strong>Statut :</strong>{' '}
                    {(docusignLatest?.status || (docusignLatestLoading ? 'Chargement...' : 'Non envoye'))}
                    {docusignLatestLoading && docusignLatest?.status ? ' (actualisation...)' : ''}
                    {docusignLatest?.envelope_id ? ` (Envelope: ${docusignLatest.envelope_id})` : ''}
                  </p>
                  <div className="form-grid">
                    <div className="form-group">
                      <label>E-mail du garant :</label>
                      <input
                        type="email"
                        value={docusignGuardianEmail}
                        onChange={(e) => setDocusignGuardianEmail(e.target.value)}
                        placeholder="guardian@example.com"
                        pattern={EMAIL_INPUT_PATTERN}
                        title={EMAIL_INPUT_TITLE}
                      />
                    </div>
                    <div className="form-group">
                      <label>E-mail admin :</label>
                      <input
                        type="email"
                        value={docusignAdminEmail}
                        onChange={(e) => setDocusignAdminEmail(e.target.value)}
                        placeholder="admin@example.com"
                        pattern={EMAIL_INPUT_PATTERN}
                        title={EMAIL_INPUT_TITLE}
                      />
                    </div>
                  </div>
                  <button
                    onClick={sendDocusignEnvelope}
                    className="btn btn-outline-primary"
                    disabled={sendingDocusign}
                  >
                    {sendingDocusign ? 'Envoi...' : 'Envoyer les e-mails DocuSign'}
                  </button>
                </div>

                <div className="admin-inline-actions">
                  <button
                    onClick={openInvoiceModal}
                    className="btn btn-outline-primary"
                  >
                    Facture
                  </button>
                  <button
                    onClick={() => handleSignupAction(selectedRequest.id, 'approve', additionalData)}
                    className="btn btn-success"
                    disabled={!Object.entries(additionalData).every(([key, value]) => key === 'move_out_date' ? true : value.trim()) || !mandateLatest || mandateLatest.mandate_status !== 'active' || String(selectedRequest?.invoice_status || '').toLowerCase() !== 'paid'}
                  >
                    Approuver la demande
                  </button>
                  <button
                    onClick={() => handleSignupAction(selectedRequest.id, 'reject')}
                    className="btn btn-danger"
                    disabled={loading}
                  >
                    Refuser la demande
                  </button>
                  <button
                    onClick={closeSignupRequestModal}
                    className="btn btn-outline"
                  >
                    Fermer
                  </button>
                </div>
              </div>

              <div className="attachments-section">
                <h4>Documents importes</h4>
                {attachments.length > 0 ? (
                  <div className="attachments-list">
                    {attachments.map(attachment => (
                      <div key={attachment.id} className="attachment-item">
                        <span className="attachment-name">
                          {attachment.file_url.split('/').pop().split('-').slice(1).join('-')}
                        </span>
                        <span className="attachment-size">
                          ({(attachment.file_size / 1024 / 1024).toFixed(2)} MB)
                        </span>
                        <a
                          href={resolveMediaUrl(attachment.file_url) || '#'}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn btn-sm btn-secondary"
                        >
                          Voir/Telecharger
                        </a>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p>Aucun document importe.</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {showInvoiceModal && selectedRequest && (
        <div className="modal-overlay" onClick={closeInvoiceModal}>
          <div className="modal-content modal-wide modal-zoom-in" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Facture - {`${selectedRequest.first_name || ''} ${selectedRequest.last_name || ''}`.trim() || '(Sans nom)'}</h3>
              <button className="modal-close" onClick={closeInvoiceModal}>x</button>
            </div>

            <div className="modal-body">
              <div className="form-grid">
                <div className="form-group">
                  <label>Ville de residence :</label>
                  <select
                    value={invoiceDraft.city_of_residence}
                    onChange={(e) => { updateInvoiceDraft('city_of_residence', (e.target.value || '').trim()); updateInvoiceDraft('residence_name', ''); }}
                    disabled={residencesCatalogLoading}
                  >
                    <option value="">{residencesCatalogLoading ? 'Chargement...' : 'Selectionner une ville'}</option>
                    {residenceCities.map((city) => (
                      <option key={city} value={city}>{city}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Nom de la residence :</label>
                  <select
                    value={invoiceDraft.residence_name}
                    onChange={(e) => updateInvoiceDraft('residence_name', e.target.value)}
                    disabled={!invoiceDraft.city_of_residence || residencesCatalogLoading}
                  >
                    <option value="">{invoiceDraft.city_of_residence ? 'Selectionner une residence' : 'Choisissez une ville d\'abord'}</option>
                    {invoiceResidenceOptions.map((name) => (
                      <option key={name} value={name}>{name}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Periode debut :</label>
                  <input type="date" value={invoiceDraft.invoice_period_from} onChange={(e) => updateInvoiceDraft('invoice_period_from', e.target.value)} />
                </div>
                <div className="form-group">
                  <label>Periode fin :</label>
                  <input type="date" value={invoiceDraft.invoice_period_to} onChange={(e) => updateInvoiceDraft('invoice_period_to', e.target.value)} />
                </div>
                <div className="form-group">
                  <label>Loyer et charges :</label>
                  <input type="number" min="0" step="0.01" value={invoiceDraft.invoice_rent_charges} onChange={(e) => updateInvoiceDraft('invoice_rent_charges', e.target.value)} />
                </div>
                <div className="form-group">
                  <label>Depot de garantie :</label>
                  <input type="number" min="0" step="0.01" value={invoiceDraft.invoice_deposit} onChange={(e) => updateInvoiceDraft('invoice_deposit', e.target.value)} />
                </div>
                <div className="form-group">
                  <label>Frais de dossier :</label>
                  <input type="number" min="0" step="0.01" value={invoiceDraft.invoice_dossier_fee} onChange={(e) => updateInvoiceDraft('invoice_dossier_fee', e.target.value)} />
                </div>
                <div className="form-group">
                  <label>Assurance :</label>
                  <input type="number" min="0" step="0.01" value={invoiceDraft.invoice_insurance} onChange={(e) => updateInvoiceDraft('invoice_insurance', e.target.value)} />
                </div>
                <div className="form-group">
                  <label>Total (calculé automatiquement) :</label>
                  <input type="number" min="0" step="0.01" value={(parseFloat(invoiceDraft.invoice_rent_charges || 0) + parseFloat(invoiceDraft.invoice_deposit || 0) + parseFloat(invoiceDraft.invoice_dossier_fee || 0) + parseFloat(invoiceDraft.invoice_insurance || 0)).toFixed(2)} readOnly style={{backgroundColor: '#f5f5f5', cursor: 'not-allowed'}} />
                </div>
              </div>

              <div className="invoice-modal-footer" style={{ marginTop: 24 }}>
                <div className={`invoice-status-pill ${getInvoiceStatusMeta(selectedRequest.invoice_status).className}`}>
                  <span className="invoice-status-dot" />
                  <span>{getInvoiceStatusMeta(selectedRequest.invoice_status).label}</span>
                </div>

                <div className="admin-inline-actions invoice-modal-actions">
                <button type="button" className="btn btn-primary" onClick={sendInvoice} disabled={invoiceSending || invoiceMarkingPaid}>
                  {invoiceSending ? 'Envoi...' : 'Envoyer la facture'}
                </button>
                <button type="button" className="btn btn-success" onClick={markInvoicePaid} disabled={invoiceSending || invoiceMarkingPaid || String(selectedRequest.invoice_status || '').toLowerCase() === 'paid'}>
                  {invoiceMarkingPaid ? 'Mise a jour...' : 'Marquer comme payee'}
                </button>
                <button type="button" className="btn btn-outline" onClick={closeInvoiceModal}>
                  Fermer
                </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {showInvoiceTemplateModal && (
        <div className="modal-overlay" onClick={closeInvoiceTemplateModal}>
          <div className="modal-content modal-wide modal-zoom-in" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Templates notification facture</h3>
              <button className="modal-close" onClick={closeInvoiceTemplateModal}>x</button>
            </div>
            <div className="modal-body">
              <p className="admin-page-desc">
                {'Modifiez le texte des notifications et des emails pour les factures. Utilisez des variables entre doubles accolades, par exemple {{name}} ou {{amount}}.'}
              </p>
              <div className="invoice-template-hint">
                {'Variables dispo : {{name}}, {{email}}, {{amount}}, {{currency}}, {{invoice_number}}, {{invoice_id}}, {{payment_id}}, {{payment_link}}, {{invoice_link}}, {{period_from}}, {{period_to}}, {{residence}}'}
              </div>

              {invoiceTemplateLoading ? (
                <div className="admin-table-empty">Chargement...</div>
              ) : invoiceTemplateDrafts.length === 0 ? (
                <div className="admin-table-empty">Aucun template disponible.</div>
              ) : (
                invoiceTemplateDrafts.map((template) => (
                  <div key={template.key} className="invoice-template-card">
                    <h4>{template.label || template.key}</h4>
                    <div className="form-grid invoice-template-grid">
                      <div className="form-group">
                        <label>Titre notification</label>
                        <input
                          type="text"
                          value={template.notification_title || ''}
                          onChange={(e) => updateInvoiceTemplateDraft(template.key, 'notification_title', e.target.value)}
                        />
                      </div>
                      <div className="form-group">
                        <label>Texte notification</label>
                        <textarea
                          rows={3}
                          value={template.notification_body || ''}
                          onChange={(e) => updateInvoiceTemplateDraft(template.key, 'notification_body', e.target.value)}
                        />
                      </div>
                      <div className="form-group">
                        <label>Sujet email</label>
                        <input
                          type="text"
                          value={template.email_subject || ''}
                          onChange={(e) => updateInvoiceTemplateDraft(template.key, 'email_subject', e.target.value)}
                        />
                      </div>
                      <div className="form-group">
                        <label>Corps email</label>
                        <textarea
                          rows={5}
                          value={template.email_body || ''}
                          onChange={(e) => updateInvoiceTemplateDraft(template.key, 'email_body', e.target.value)}
                        />
                      </div>
                    </div>
                  </div>
                ))
              )}

              {invoiceTemplateError ? (
                <p className="admin-form-error">{invoiceTemplateError}</p>
              ) : null}
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-primary"
                onClick={saveInvoiceTemplates}
                disabled={invoiceTemplateSaving || invoiceTemplateLoading}
              >
                {invoiceTemplateSaving ? 'Sauvegarde...' : 'Sauvegarder'}
              </button>
              <button className="btn btn-outline" onClick={closeInvoiceTemplateModal}>Fermer</button>
            </div>
          </div>
        </div>
      )}

      {showImportSubmissionModal && importSubmission && (() => {
        const importRequest = buildImportHousingRequestFromSubmission(importSubmission);
        const submissionSchema = importSubmission.form_schema && typeof importSubmission.form_schema === 'object'
          ? importSubmission.form_schema
          : null;
        const fileRows = importSubmissionAttachments.length > 0
          ? importSubmissionAttachments
          : getSubmissionFileRows(getSubmissionResponses(importSubmission), submissionSchema);
        const reviewing = Number(adminFormSubmissionReviewingId) === Number(importSubmission.id);
        const statusValue = String(importRequest.status || 'pending').toLowerCase();

        return (
          <div className="modal-overlay" onClick={closeImportSubmissionModal}>
            <div className="modal-content modal-wide modal-zoom-in" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3>
                  Housing Application -- {`${importRequest.first_name || ''} ${importRequest.last_name || ''}`.trim() || '(Sans nom)'}
                </h3>
                <button className="modal-close" onClick={closeImportSubmissionModal}>x</button>
              </div>

              <div className="modal-body">
                <div className="housing-modal-overview">
                  <div className="housing-overview-head">
                    <span className="housing-overview-reference">Reference : {importRequest.reference_number || '-'}</span>
                    <span
                      className={`housing-status-chip ${
                        ['resolved', 'approved', 'accepted'].includes(statusValue)
                          ? 'is-success'
                          : ['rejected', 'disapproved', 'closed'].includes(statusValue)
                            ? 'is-danger'
                            : 'is-pending'
                      }`}
                    >
                      {importRequest.status || 'open'}
                    </span>
                  </div>

                  <div className="housing-info-grid">
                    <article className="housing-info-card">
                      <span className="housing-info-label">E-mail</span>
                      <span className="housing-info-value">{importRequest.email || '-'}</span>
                    </article>
                    <article className="housing-info-card">
                      <span className="housing-info-label">Soumis le</span>
                      <span className="housing-info-value">{formatDate(importRequest.created_at)}</span>
                    </article>
                  </div>
                </div>

                <section className="housing-info-section">
                  <h4 className="housing-info-section-title">Details du demandeur</h4>
                  <div className="housing-info-grid">
                    <article className="housing-info-card">
                      <span className="housing-info-label">Telephone</span>
                      <span className="housing-info-value">{importRequest.phone_number || '-'}</span>
                    </article>
                    <article className="housing-info-card">
                      <span className="housing-info-label">DOB</span>
                      <span className="housing-info-value">{formatDate(importRequest.date_of_birth)}</span>
                    </article>
                    <article className="housing-info-card">
                      <span className="housing-info-label">Pays de naissance</span>
                      <span className="housing-info-value">{importRequest.country_of_birth || '-'}</span>
                    </article>
                    <article className="housing-info-card">
                      <span className="housing-info-label">Ville de naissance</span>
                      <span className="housing-info-value">{importRequest.city_of_birth || '-'}</span>
                    </article>
                    <article className="housing-info-card">
                      <span className="housing-info-label">Passport</span>
                      <span className="housing-info-value">{importRequest.passport_number || '-'}</span>
                    </article>
                    <article className="housing-info-card">
                      <span className="housing-info-label">Start Date</span>
                      <span className="housing-info-value">{formatDate(importRequest.start_date)}</span>
                    </article>
                    <article className="housing-info-card">
                      <span className="housing-info-label">Duree du contrat</span>
                      <span className="housing-info-value">{importRequest.contract_duration || '-'}</span>
                    </article>
                    <article className="housing-info-card">
                      <span className="housing-info-label">Ville souhaitee</span>
                      <span className="housing-info-value">{importRequest.desired_city || '-'}</span>
                    </article>
                    <article className="housing-info-card">
                      <span className="housing-info-label">Budget max</span>
                      <span className="housing-info-value">{importRequest.max_budget || '-'}</span>
                    </article>
                    <article className="housing-info-card housing-info-card-wide">
                      <span className="housing-info-label">Residences souhaitees</span>
                      <span className="housing-info-value">
                        {Array.isArray(importRequest.desired_residences)
                          ? importRequest.desired_residences.join(', ')
                          : (importRequest.desired_residences || 'Non renseigne')}
                      </span>
                    </article>
                  </div>
                </section>

                <section className="housing-info-section">
                  <h4 className="housing-info-section-title">Details du garant</h4>
                  <div className="housing-info-grid">
                    <article className="housing-info-card">
                      <span className="housing-info-label">Nom du garant</span>
                      <span className="housing-info-value">{`${importRequest.guardian_first_name || ''} ${importRequest.guardian_last_name || ''}`.trim() || 'Non renseigne'}</span>
                    </article>
                    <article className="housing-info-card">
                      <span className="housing-info-label">E-mail du garant</span>
                      <span className="housing-info-value">{importRequest.guardian_email || 'Non renseigne'}</span>
                    </article>
                    <article className="housing-info-card">
                      <span className="housing-info-label">Telephone du garant</span>
                      <span className="housing-info-value">{importRequest.guardian_phone_number || importRequest.guardian_phone || 'Non renseigne'}</span>
                    </article>
                    <article className="housing-info-card">
                      <span className="housing-info-label">Date de naissance du garant</span>
                      <span className="housing-info-value">{importRequest.guardian_datebirth ? formatDate(importRequest.guardian_datebirth) : 'Non renseigne'}</span>
                    </article>
                    <article className="housing-info-card">
                      <span className="housing-info-label">Profession du garant</span>
                      <span className="housing-info-value">{importRequest.guardian_job || 'Non renseigne'}</span>
                    </article>
                    <article className="housing-info-card">
                      <span className="housing-info-label">Entreprise du garant</span>
                      <span className="housing-info-value">{importRequest.guardian_company || 'Non renseigne'}</span>
                    </article>
                    <article className="housing-info-card housing-info-card-wide">
                      <span className="housing-info-label">Adresse du garant</span>
                      <span className="housing-info-value">{importRequest.guardian_address || 'Non renseigne'}</span>
                    </article>
                    <article className="housing-info-card">
                      <span className="housing-info-label">Ville du garant</span>
                      <span className="housing-info-value">{importRequest.guardian_city || 'Non renseigne'}</span>
                    </article>
                  </div>
                </section>

                <div className="additional-data">
                  <h4>Informations complementaires</h4>
                  <div className="form-grid">
                    <div className="form-group">
                      <label>Type de contrat :</label>
                      <input
                        type="text"
                        value={importAdditionalData.contract_type}
                        onChange={(e) => setImportAdditionalData((prev) => ({ ...prev, contract_type: e.target.value }))}
                      />
                    </div>
                    <div className="form-group">
                      <label>Numero de lot :</label>
                      <input
                        type="text"
                        value={importAdditionalData.lot_number}
                        onChange={(e) => setImportAdditionalData((prev) => ({ ...prev, lot_number: e.target.value }))}
                      />
                    </div>
                    <div className="form-group">
                      <label>CH (chiffres uniquement) :</label>
                      <input
                        type="text"
                        inputMode="numeric"
                        value={importAdditionalData.ch}
                        onChange={(e) => setImportAdditionalData((prev) => ({
                          ...prev,
                          ch: String(e.target.value || '').replace(/\D/g, ''),
                        }))}
                        placeholder="ex. 12345"
                      />
                    </div>
                    <div className="form-group">
                      <label>Ville de residence :</label>
                      <select
                        value={importAdditionalData.city_of_residence}
                        onChange={(e) => setImportAdditionalData((prev) => ({
                          ...prev,
                          city_of_residence: e.target.value,
                          residence_name: '',
                        }))}
                        disabled={residencesCatalogLoading}
                      >
                        <option value="">{residencesCatalogLoading ? 'Chargement...' : 'Selectionner une ville'}</option>
                        {residenceCities.map((city) => (
                          <option key={city} value={city}>{city}</option>
                        ))}
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Nom de la residence :</label>
                      <select
                        value={importAdditionalData.residence_name}
                        onChange={(e) => setImportAdditionalData((prev) => ({ ...prev, residence_name: e.target.value }))}
                        disabled={!importAdditionalData.city_of_residence || residencesCatalogLoading}
                      >
                        <option value="">{importAdditionalData.city_of_residence ? 'Selectionner une residence' : 'Choisissez une ville d\'abord'}</option>
                        {importResidenceOptions.map((name) => (
                          <option key={name} value={name}>{name}</option>
                        ))}
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Adresse de la residence :</label>
                      <input
                        type="text"
                        value={importAdditionalData.residence_address}
                        onChange={(e) => setImportAdditionalData((prev) => ({ ...prev, residence_address: e.target.value }))}
                      />
                    </div>
                    <div className="form-group">
                      <label>Numero de studio :</label>
                      <input
                        type="text"
                        value={importAdditionalData.studio_number}
                        onChange={(e) => setImportAdditionalData((prev) => ({ ...prev, studio_number: e.target.value }))}
                      />
                    </div>
                    <div className="form-group">
                      <label>Surface (m2) :</label>
                      <input
                        type="number"
                        value={importAdditionalData.area_sqm}
                        onChange={(e) => setImportAdditionalData((prev) => ({ ...prev, area_sqm: e.target.value }))}
                      />
                    </div>
                    <div className="form-group">
                      <label>Date entree :</label>
                      <input
                        type="date"
                        value={importAdditionalData.move_in_date}
                        min={todayParisDate}
                        onChange={(e) => setImportAdditionalData((prev) => {
                          const moveInDate = e.target.value;
                          const next = { ...prev, move_in_date: moveInDate };
                          if (next.move_out_date && moveInDate && next.move_out_date < moveInDate) {
                            next.move_out_date = '';
                          }
                          return next;
                        })}
                      />
                    </div>
                    <div className="form-group">
                      <label>Date de sortie :</label>
                      <input
                        type="date"
                        value={importAdditionalData.move_out_date}
                        min={importAdditionalData.move_in_date || todayParisDate}
                        onChange={(e) => setImportAdditionalData((prev) => ({ ...prev, move_out_date: e.target.value }))}
                      />
                    </div>
                    <div className="form-group">
                      <label>Montant du paiement (EUR) :</label>
                      <input
                        type="number"
                        step="0.01"
                        value={importAdditionalData.payment_amount}
                        onChange={(e) => setImportAdditionalData((prev) => ({ ...prev, payment_amount: e.target.value }))}
                        placeholder="Montant mensuel du paiement"
                      />
                    </div>
                  </div>

                  <div className="mandate-section">
                    <h4>Statut de configuration du paiement (optionnel)</h4>
                    <div className="mandate-status">
                      <p style={{ marginTop: 0 }}>
                        <strong>Statut :</strong>{' '}
                        {(importMandateLatest?.mandate_status || (importMandateLatestLoading ? 'Chargement...' : 'Non defini'))}
                        {importMandateLatestLoading && importMandateLatest?.mandate_status ? ' (actualisation...)' : ''}
                        {(importMandateLatest?.mandate_id || importMandateLatest?.gocardless_mandate_id)
                          ? ` (Mandate: ${importMandateLatest?.mandate_id || importMandateLatest?.gocardless_mandate_id})`
                          : ''}
                      </p>
                      {importMandateLatest?.mandate_created_at && (
                        <p><strong>Cree le :</strong> {formatDate(importMandateLatest.mandate_created_at)}</p>
                      )}
                    </div>

                    {(!importMandateLatest || importMandateLatest.mandate_status === 'not_setup') && (
                      <button
                        onClick={createImportMandateRequest}
                        className="btn btn-primary"
                        disabled={importMandateLoading}
                      >
                        {importMandateLoading ? 'Envoi...' : "Envoyer l'email de configuration du mandat"}
                      </button>
                    )}
                  </div>

                  <div className="admin-inline-actions">
                    <button
                      onClick={() => handleImportSubmissionReview('accept')}
                      className="btn btn-success"
                      disabled={reviewing}
                    >
                      {reviewing ? 'Traitement...' : 'Approuver la demande'}
                    </button>
                    <button
                      onClick={() => handleImportSubmissionReview('reject')}
                      className="btn btn-danger"
                      disabled={reviewing}
                    >
                      {reviewing ? 'Traitement...' : 'Refuser la demande'}
                    </button>
                    <button
                      onClick={closeImportSubmissionModal}
                      className="btn btn-outline"
                    >
                      Fermer
                    </button>
                  </div>
                </div>

                <div className="attachments-section">
                  <h4>Documents importes</h4>
                  {fileRows.length > 0 ? (
                    <div className="attachments-list">
                      {fileRows.map((attachment) => {
                        const resolved = resolveMediaUrl(attachment.url) || attachment.url;
                        const name = attachment.url.split('/').pop() || attachment.label;
                        return (
                          <div key={`${attachment.id}-${attachment.url}`} className="attachment-item">
                            <span className="attachment-name">{attachment.label}: {name}</span>
                            <a
                              href={resolved}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="btn btn-sm btn-secondary"
                            >
                              Voir/Telecharger
                            </a>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p>Aucun document importe.</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {showBroadcastModal && (
        <div className="modal-overlay" onClick={closeBroadcastModal}>
          <div className="modal-content modal-zoom-in admin-broadcast-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Diffuser une notification</h3>
              <button className="modal-close" onClick={closeBroadcastModal}>x</button>
            </div>
            <div className="modal-body">
              <div className="admin-broadcast-form">
                <section className="admin-broadcast-block">
                  <div className="admin-broadcast-block-head">
                    <h4>Destinataires</h4>
                    {broadcastRecipientScope === 'selected' && (
                      <span className="admin-broadcast-pill">{broadcastSelectedUserIds.length} selectionne(s)</span>
                    )}
                  </div>

                  <div className="admin-broadcast-scope-grid">
                    <label className={`admin-radio-card ${broadcastRecipientScope === 'all' ? 'is-active' : ''}`}>
                      <input
                        type="radio"
                        name="broadcastScope"
                        value="all"
                        checked={broadcastRecipientScope === 'all'}
                        onChange={() => {
                          setBroadcastRecipientScope('all');
                          setBroadcastSelectedUserIds([]);
                          setBroadcastSearchQuery('');
                        }}
                      />
                      <span className="admin-radio-card-copy">
                        <strong>Tous les utilisateurs</strong>
                        <small>Diffusion globale immediate.</small>
                      </span>
                    </label>

                    <label className={`admin-radio-card ${broadcastRecipientScope === 'selected' ? 'is-active' : ''}`}>
                      <input
                        type="radio"
                        name="broadcastScope"
                        value="selected"
                        checked={broadcastRecipientScope === 'selected'}
                        onChange={() => setBroadcastRecipientScope('selected')}
                      />
                      <span className="admin-radio-card-copy">
                        <strong>Utilisateurs cibles</strong>
                        <small>Selectionnez manuellement les destinataires.</small>
                      </span>
                    </label>
                  </div>

                  {broadcastRecipientScope === 'selected' && (
                    <div className="admin-recipient-picker">
                      <label htmlFor="broadcast-recipient-search">Rechercher et ajouter</label>

                      <div className="admin-recipient-search-wrap">
                        <input
                          id="broadcast-recipient-search"
                          type="text"
                          value={broadcastSearchQuery}
                          onChange={(e) => setBroadcastSearchQuery(e.target.value)}
                          placeholder="Tapez un nom ou un email..."
                          autoComplete="off"
                        />

                        {String(broadcastSearchQuery || '').trim().length > 0 && (
                          <div className="admin-recipient-search-dropdown">
                            {broadcastUsersLoading ? (
                              <div className="admin-recipient-search-empty">Chargement...</div>
                            ) : broadcastSearchResults.length === 0 ? (
                              <div className="admin-recipient-search-empty">Aucun resultat.</div>
                            ) : (
                              broadcastSearchResults.map((u) => (
                                <button
                                  key={u.id}
                                  type="button"
                                  className="admin-recipient-search-item"
                                  onClick={() => addBroadcastRecipient(u.id)}
                                >
                                  <span className="admin-recipient-search-name">{formatUserDisplayName(u)}</span>
                                  <span className="admin-recipient-search-email">{u.email || '-'}</span>
                                </button>
                              ))
                            )}
                          </div>
                        )}
                      </div>

                      <div className="admin-recipient-selected">
                        {broadcastSelectedUsers.length === 0 ? (
                          <div className="admin-recipient-selected-empty">Aucun utilisateur selectionne.</div>
                        ) : (
                          broadcastSelectedUsers.map((u) => (
                            <div key={u.id} className="admin-recipient-chip">
                              <span className="admin-recipient-chip-name">{formatUserDisplayName(u)}</span>
                              <button
                                type="button"
                                className="admin-recipient-chip-remove"
                                onClick={() => removeBroadcastRecipient(u.id)}
                                aria-label={`Retirer ${formatUserDisplayName(u)}`}
                              >
                                x
                              </button>
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  )}
                </section>

                <section className="admin-broadcast-block">
                  <div className="form-group">
                    <label>Titre :</label>
                    <input
                      type="text"
                      value={broadcastTitle}
                      onChange={(e) => setBroadcastTitle(e.target.value)}
                      placeholder="Saisir le titre de la notification"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>Message :</label>
                    <textarea
                      value={broadcastBody}
                      onChange={(e) => setBroadcastBody(e.target.value)}
                      placeholder="Saisir le message de la notification"
                      rows="5"
                      required
                    ></textarea>
                  </div>
                </section>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={closeBroadcastModal}>Annuler</button>
              <button className="btn btn-primary" onClick={broadcastNotification} disabled={broadcastSending}>
                {broadcastSending ? 'Envoi...' : 'Envoyer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showAdminDocumentRequestModal && adminDocumentRequestSelected && (() => {
        const userName = `${adminDocumentRequestSelected.user_first_name || ''} ${adminDocumentRequestSelected.user_last_name || ''}`.trim()
          || adminDocumentRequestSelected.user_email
          || '(Sans nom)';
        const pending = String(adminDocumentRequestSelected.status || '') !== 'completed';

        const docConfig = [
          {
            key: 'attestation_de_domicile',
            label: 'Attestation de domicile',
            requestedField: 'requested_attestation_de_domicile',
            fileUrlField: 'attestation_de_domicile_file_url',
          },
          {
            key: 'assurance_habitation',
            label: 'Assurance habitation',
            requestedField: 'requested_assurance_habitation',
            fileUrlField: 'assurance_habitation_file_url',
          },
          {
            key: 'quittance_de_loyer',
            label: 'Quittance de loyer',
            requestedField: 'requested_quittance_de_loyer',
            fileUrlField: 'quittance_de_loyer_file_url',
          },
          {
            key: 'attestation_de_loyer_caf',
            label: 'Attestation de loyer CAF',
            requestedField: 'requested_attestation_de_loyer_caf',
            fileUrlField: 'attestation_de_loyer_caf_file_url',
          },
        ];

        const requestedDocs = docConfig.filter((d) => Boolean(adminDocumentRequestSelected[d.requestedField]));
        const missingDocs = requestedDocs.filter((d) => !adminDocumentRequestSelected[d.fileUrlField]);
        const canConfirm = pending && missingDocs.length === 0;

        return (
          <div className="modal-overlay" onClick={closeAdminDocumentRequestModal}>
            <div className="modal-content modal-wide modal-zoom-in" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3>Demande de documents -- {userName}</h3>
                <button className="modal-close" onClick={closeAdminDocumentRequestModal}>x</button>
              </div>

              <div className="modal-body">
                <div className="admin-expanded-grid">
                  <div><span className="admin-k">Utilisateur</span><span className="admin-v">{userName}</span></div>
                  <div><span className="admin-k">E-mail</span><span className="admin-v">{adminDocumentRequestSelected.user_email || '-'}</span></div>
                  <div><span className="admin-k">Recue le</span><span className="admin-v">{formatDateTime(adminDocumentRequestSelected.requested_at)}</span></div>
                  <div>
                    <span className="admin-k">Statut</span>
                    <span className="admin-v">
                      <span className={`admin-notif-dot ${pending ? 'unread' : 'read'}`} />
                      <span style={{ marginLeft: 8 }}>{pending ? 'En attente' : 'Terminee'}</span>
                    </span>
                  </div>
                </div>

                <div style={{ marginTop: '1rem' }}>
                  <h4>Documents demandes</h4>

                  {requestedDocs.length === 0 ? (
                    <p>Aucun document n'a ete demande.</p>
                  ) : (
                    <div className="admin-table-wrap">
                      <table className="admin-table">
                        <thead>
                          <tr>
                            <th>Document</th>
                            <th>Fichier</th>
                            <th>Importer</th>
                          </tr>
                        </thead>
                        <tbody>
                          {requestedDocs.map((d) => {
                            const currentUrl = resolveMediaUrl(adminDocumentRequestSelected[d.fileUrlField]);
                            const uploading = Boolean(adminDocReqUploading[d.key]);
                            const isQuittance = d.key === 'quittance_de_loyer';
                            const cafPeriod = isQuittance
                              ? formatCafPeriodLabel(adminDocumentRequestSelected?.caf_period_from_month, adminDocumentRequestSelected?.caf_period_till_month)
                              : '';

                            return (
                              <tr key={d.key}>
                                <td>
                                  <div>{d.label}</div>
                                  {cafPeriod ? <div style={{ fontSize: '0.82rem', color: '#666' }}>Periode: {cafPeriod}</div> : null}
                                </td>
                                <td>
                                  {currentUrl ? (
                                    <a href={currentUrl} target="_blank" rel="noreferrer">Voir</a>
                                  ) : (
                                    <span>Manquant</span>
                                  )}
                                </td>
                                <td className="admin-cell-actions">
                                  <input
                                    type="file"
                                    style={{ display: 'none' }}
                                    ref={(el) => {
                                      if (!el) return;
                                      adminDocReqFileInputsRef.current[d.key] = el;
                                    }}
                                    onChange={(e) => {
                                      const file = e.target.files && e.target.files[0] ? e.target.files[0] : null;
                                      if (!file) return;
                                      uploadAdminDocumentRequestFile(d.key, file);
                                      // Allow re-selecting the same file.
                                      e.target.value = '';
                                    }}
                                  />
                                  <button
                                    type="button"
                                    className="btn btn-outline"
                                    onClick={() => {
                                      if (uploading) return;
                                      adminDocReqFileInputsRef.current[d.key]?.click();
                                    }}
                                    disabled={uploading}
                                  >
                                    {uploading ? 'Import...' : 'Importer'}
                                  </button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {pending && missingDocs.length > 0 && (
                    <p style={{ marginTop: '0.75rem' }}>
                      Importez tous les documents demandes avant de confirmer.
                    </p>
                  )}
                </div>
              </div>

              <div className="modal-footer">
                <button className="btn btn-outline" onClick={closeAdminDocumentRequestModal}>Fermer</button>
                <button
                  className="btn btn-primary"
                  onClick={confirmAdminDocumentRequest}
                  disabled={!canConfirm || adminDocReqConfirming}
                >
                  {adminDocReqConfirming ? 'Confirmation...' : 'Confirmer et terminer'}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {(activeTab === TAB_KEYS.HOUSE_MANAGEMENT_CHANGE || activeTab === TAB_KEYS.HOUSE_MANAGEMENT_LEAVE) && houseManagementExpandedRequestId && (() => {
        const request = houseManagementRequests.find((row) => row.id === houseManagementExpandedRequestId);
        if (!request) return null;

        const desiredResidences = Array.isArray(request.desired_residences) ? request.desired_residences : [];
        const isSeen = Number(request.is_seen) === 1;
        const userName = formatUserDisplayName({
          first_name: request.user_first_name,
          last_name: request.user_last_name,
          email: request.user_email,
        });

        return (
          <div className="modal-overlay" onClick={() => setHouseManagementExpandedRequestId(null)}>
            <div className="modal-content modal-wide modal-zoom-in" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3>Demande de gestion du logement</h3>
                <button className="modal-close" onClick={() => setHouseManagementExpandedRequestId(null)}>x</button>
              </div>

              <div className="modal-body">
                <div className="admin-expanded-grid">
                  <div><span className="admin-k">Locataire</span><span className="admin-v">{userName}</span></div>
                  <div><span className="admin-k">E-mail</span><span className="admin-v">{request.user_email || '-'}</span></div>
                  <div><span className="admin-k">Type</span><span className="admin-v">{request.request_type === 'change_residence' ? 'Changement de residence' : 'Fin de contrat'}</span></div>
                  <div>
                    <span className="admin-k">Vu</span>
                    <span className="admin-v">
                      <span className={`admin-notif-dot ${isSeen ? 'read' : 'unread'}`} aria-hidden="true" />
                      <span style={{ marginLeft: 8 }}>{isSeen ? 'Vu' : 'Non lu'}</span>
                    </span>
                  </div>
                  <div><span className="admin-k">Soumis le</span><span className="admin-v">{formatDateTime(request.created_at)}</span></div>
                  <div><span className="admin-k">Statut</span><span className="admin-v">{String(request.status || 'pending')}</span></div>
                  {request.seen_at && (
                    <div><span className="admin-k">Vu le</span><span className="admin-v">{formatDateTime(request.seen_at)}</span></div>
                  )}
                </div>

                {request.request_type === 'change_residence' && (
                  <div style={{ marginTop: '1rem' }}>
                    <h4>Preferences de relogement</h4>
                    <div className="admin-expanded-grid">
                      <div><span className="admin-k">Date de demenagement souhaitee</span><span className="admin-v">{formatDate(request.requested_move_date)}</span></div>
                      <div><span className="admin-k">Ville</span><span className="admin-v">{request.desired_city || '-'}</span></div>
                      <div><span className="admin-k">Region</span><span className="admin-v">{request.desired_region || '-'}</span></div>
                    </div>
                    <div style={{ marginTop: '0.75rem' }}>
                      <span className="admin-k">Residences souhaitees</span>
                      {desiredResidences.length === 0 ? (
                        <div className="admin-v">-</div>
                      ) : (
                        <ul className="admin-house-residence-list">
                          {desiredResidences.map((name) => (
                            <li key={name}>{name}</li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>
                )}

                {request.request_type === 'leave_residence' && (
                  <div style={{ marginTop: '1rem' }}>
                    <h4>Confirmation de fin de contrat</h4>
                    <div className="admin-expanded-grid">
                      <div>
                        <span className="admin-k">Date de depart souhaitee</span>
                        <span className="admin-v">{formatDate(request.requested_leave_date)}</span>
                      </div>
                      <div>
                        <span className="admin-k">Confirmation utilisateur</span>
                        <span className="admin-v">{Number(request.leave_contract_confirmed) === 1 ? 'Confirmee' : 'Non confirmee'}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="modal-footer">
                <button className="btn btn-outline" onClick={() => setHouseManagementExpandedRequestId(null)}>Fermer</button>
              </div>
            </div>
          </div>
        );
      })()}

      {activeTab === 'users' && userManagementExpandedUserId && (() => {
        const u = userManagementRows.find((row) => row.id === userManagementExpandedUserId);
        if (!u) return null;
        const fullName = `${u.first_name || ''} ${u.last_name || ''}`.trim() || '(Sans nom)';
        return (
          <div className="modal-overlay" onClick={() => setUserManagementExpandedUserId(null)}>
            <div className="modal-content modal-wide modal-zoom-in" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3>Utilisateur - {fullName}</h3>
                <button className="modal-close" onClick={() => setUserManagementExpandedUserId(null)}>x</button>
              </div>
              <div className="modal-body">
                <div className="admin-expanded-grid">
                  <div><span className="admin-k">Nom</span><span className="admin-v">{fullName}</span></div>
                  <div><span className="admin-k">E-mail</span><span className="admin-v">{u.email}</span></div>
                  <div><span className="admin-k">Telephone</span><span className="admin-v">{u.phone_number || '-'}</span></div>
                  <div><span className="admin-k">Date de naissance</span><span className="admin-v">{formatDate(u.date_of_birth)}</span></div>
                  <div><span className="admin-k">Ville de naissance</span><span className="admin-v">{u.city_of_birth || '-'}</span></div>
                  <div><span className="admin-k">Pays de naissance</span><span className="admin-v">{u.country_of_birth || '-'}</span></div>
                  <div><span className="admin-k">Passeport</span><span className="admin-v">{u.passport_number || '-'}</span></div>
                  <div><span className="admin-k">Contrat</span><span className="admin-v">{u.contract_type || '-'}</span></div>
                  <div><span className="admin-k">Lot</span><span className="admin-v">{u.lot_number || '-'}</span></div>
                  <div><span className="admin-k">Ville</span><span className="admin-v">{u.city_of_residence || '-'}</span></div>
                  <div><span className="admin-k">Residence</span><span className="admin-v">{u.residence_name || '-'}</span></div>
                  <div><span className="admin-k">Adresse</span><span className="admin-v">{u.residence_address || '-'}</span></div>
                  <div><span className="admin-k">Studio</span><span className="admin-v">{u.studio_number || '-'}</span></div>
                  <div><span className="admin-k">Surface</span><span className="admin-v">{u.area_sqm ? `${u.area_sqm} m2` : '-'}</span></div>
                  <div><span className="admin-k">Entree</span><span className="admin-v">{formatDate(u.move_in_date)}</span></div>
                  <div><span className="admin-k">Sortie</span><span className="admin-v">{formatDate(u.move_out_date)}</span></div>
                  <div><span className="admin-k">Mandat</span><span className="admin-v">{u.mandate_status || '-'}</span></div>
                </div>
                <div className="admin-expanded-actions">
                  <button onClick={() => deleteUser(u.id, fullName)} className="btn btn-danger">Supprimer l'utilisateur</button>
                  <button onClick={() => setUserManagementExpandedUserId(null)} className="btn btn-outline ms-2">Fermer</button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {activeTab === 'notifications' && notificationsExpandedCampaignId && (() => {
        const c = sentNotificationCampaigns.find((row) => row.id === notificationsExpandedCampaignId);
        if (!c) return null;
        const recipients = Array.isArray(c.recipients) ? c.recipients : [];
        return (
          <div className="modal-overlay" onClick={() => setNotificationsExpandedCampaignId(null)}>
            <div className="modal-content modal-wide modal-zoom-in" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3>Notification -- {c.title || '-'}</h3>
                <button className="modal-close" onClick={() => setNotificationsExpandedCampaignId(null)}>x</button>
              </div>
              <div className="modal-body">
                <div className="admin-expanded-grid admin-notif-expanded-grid">
                  <div>
                    <span className="admin-k">Titre</span>
                    <span className="admin-v">{c.title || '-'}</span>
                  </div>
                  <div>
                    <span className="admin-k">Date envoi</span>
                    <span className="admin-v">{formatDateTime(c.sent_at)}</span>
                  </div>
                  <div className="admin-notif-body">
                    <span className="admin-k">Body</span>
                    <span className="admin-v">{c.body || '-'}</span>
                  </div>
                  <div className="admin-notif-recipients">
                    <span className="admin-k">Destinataires</span>
                    {c.recipient_scope === 'all' ? (
                      <span className="admin-v">Tous les utilisateurs</span>
                    ) : (
                      <div className="admin-recipient-scroll">
                        {recipients.length === 0 ? (
                          <div className="admin-recipient-empty">Aucun destinataire enregistre.</div>
                        ) : (
                          recipients.map((u) => (
                            <div key={u.id} className="admin-recipient-item">{formatUserDisplayName(u)}</div>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {activeTab === 'notifications-admin-inbox' && adminInboxExpandedNotificationId && (() => {
        const n = adminInboxNotifications.find((row) => row.id === adminInboxExpandedNotificationId);
        if (!n) return null;
        const isRead = Number(n.is_read) === 1;
        return (
          <div className="modal-overlay" onClick={() => setAdminInboxExpandedNotificationId(null)}>
            <div className="modal-content modal-zoom-in" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3>Admin Notification -- {n.title || '-'}</h3>
                <button className="modal-close" onClick={() => setAdminInboxExpandedNotificationId(null)}>x</button>
              </div>
              <div className="modal-body">
                <div className="admin-expanded-grid admin-notif-expanded-grid">
                  <div>
                    <span className="admin-k">Statut</span>
                    <span className="admin-v">{isRead ? 'Lu' : 'Recu'}</span>
                  </div>
                  <div>
                    <span className="admin-k">Recu le</span>
                    <span className="admin-v">{formatDateTime(n.created_at)}</span>
                  </div>
                  <div className="admin-notif-body">
                    <span className="admin-k">Body</span>
                    <span className="admin-v">{n.body || '-'}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {activeTab === 'notifications-admin-outbox' && adminOutboxExpandedCampaignId && (() => {
        const c = adminOutboxCampaigns.find((row) => row.id === adminOutboxExpandedCampaignId);
        if (!c) return null;
        const recipients = Array.isArray(c.recipients) ? c.recipients : [];
        return (
          <div className="modal-overlay" onClick={() => setAdminOutboxExpandedCampaignId(null)}>
            <div className="modal-content modal-wide modal-zoom-in" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3>Admin Broadcast -- {c.title || '-'}</h3>
                <button className="modal-close" onClick={() => setAdminOutboxExpandedCampaignId(null)}>x</button>
              </div>
              <div className="modal-body">
                <div className="admin-expanded-grid admin-notif-expanded-grid">
                  <div>
                    <span className="admin-k">Titre</span>
                    <span className="admin-v">{c.title || '-'}</span>
                  </div>
                  <div>
                    <span className="admin-k">Date envoi</span>
                    <span className="admin-v">{formatDateTime(c.sent_at)}</span>
                  </div>
                  <div className="admin-notif-body">
                    <span className="admin-k">Body</span>
                    <span className="admin-v">{c.body || '-'}</span>
                  </div>
                  <div className="admin-notif-recipients">
                    <span className="admin-k">Destinataires</span>
                    {c.recipient_scope === 'all' ? (
                      <span className="admin-v">Tous les administrateurs</span>
                    ) : (
                      <div className="admin-recipient-scroll">
                        {recipients.length === 0 ? (
                          <div className="admin-recipient-empty">Aucun destinataire enregistre.</div>
                        ) : (
                          recipients.map((u) => (
                            <div key={u.id} className="admin-recipient-item">{formatUserDisplayName(u)}</div>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {notificationManagementModalCategory && (() => {
        const category = notificationManagementModalCategory;
        const enabledCount = notificationManagementRecipients.reduce(
          (count, recipient) => count + (recipient?.is_enabled ? 1 : 0),
          0
        ) + notificationManagementCustomEmails.reduce(
          (count, email) => count + (email?.is_enabled ? 1 : 0),
          0
        );
        const totalCount = notificationManagementRecipients.length + notificationManagementCustomEmails.length;

        return (
          <div className="modal-overlay" onClick={closeNotificationManagementModal}>
            <div className="modal-content modal-wide modal-zoom-in" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3>Notification category -- {category.label || category.key}</h3>
                <button className="modal-close" onClick={closeNotificationManagementModal}>x</button>
              </div>
              <div className="modal-body">
                <div className="admin-notification-management-meta">
                  <div className="admin-notification-management-meta-key">Category key</div>
                  <div className="admin-notification-management-meta-value">{category.key}</div>
                </div>
                <p className="admin-notification-management-desc">{category.description || 'No description available.'}</p>
                <div className="admin-page-count admin-notification-management-count">
                  Enabled recipients <span className="admin-page-count-number">{enabledCount}</span> / {totalCount}
                </div>

                <div className="admin-table-wrap">
                  <h4 style={{ marginBottom: '10px' }}>Admin Recipients</h4>
                  <table className="admin-table admin-notification-recipients-table">
                    <thead>
                      <tr>
                        <th>Admin</th>
                        <th>Email</th>
                        <th>Roles</th>
                        <th>Receive</th>
                      </tr>
                    </thead>
                    <tbody>
                      {notificationManagementRecipientsLoading ? (
                        <tr><td colSpan={4} className="admin-table-empty">Chargement...</td></tr>
                      ) : notificationManagementRecipients.length === 0 ? (
                        <tr><td colSpan={4} className="admin-table-empty">No eligible admins found for this category.</td></tr>
                      ) : (
                        notificationManagementRecipients.map((recipient) => {
                          const recipientId = Number(recipient.id);
                          const roleList = Array.isArray(recipient.roles) ? recipient.roles : [];
                          const roleLabel = roleList.length ? roleList.join(', ') : '-';
                          const isEnabled = Boolean(recipient.is_enabled);
                          const isSaving = Boolean(notificationManagementToggleSavingByUserId[recipientId]);

                          return (
                            <tr key={recipient.id}>
                              <td>{formatUserDisplayName(recipient)}</td>
                              <td>{recipient.email || '-'}</td>
                              <td>{roleLabel}</td>
                              <td className="admin-cell-nowrap">
                                <button
                                  type="button"
                                  className={`admin-notification-toggle ${isEnabled ? 'is-on' : 'is-off'}`}
                                  onClick={() => toggleNotificationCategoryRecipient(recipient)}
                                  disabled={isSaving}
                                >
                                  {isSaving ? 'Saving...' : (isEnabled ? 'On' : 'Off')}
                                </button>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>

                {notificationManagementCustomEmails.length > 0 && (
                  <div className="admin-table-wrap" style={{ marginTop: '20px' }}>
                    <h4 style={{ marginBottom: '10px' }}>Custom Emails</h4>
                    <table className="admin-table admin-notification-recipients-table">
                      <thead>
                        <tr>
                          <th>Email</th>
                          <th>Added</th>
                          <th>Receive</th>
                        </tr>
                      </thead>
                      <tbody>
                        {notificationManagementCustomEmailsLoading ? (
                          <tr><td colSpan={3} className="admin-table-empty">Chargement...</td></tr>
                        ) : (
                          notificationManagementCustomEmails.map((email) => {
                            const emailId = Number(email.id);
                            const isEnabled = Boolean(email.is_enabled);
                            const isSaving = Boolean(notificationManagementToggleSavingByCustomEmailId[emailId]);

                            return (
                              <tr key={email.id}>
                                <td>{email.email}</td>
                                <td className="admin-cell-nowrap">{new Date(email.created_at).toLocaleDateString()}</td>
                                <td className="admin-cell-nowrap">
                                  <button
                                    type="button"
                                    className={`admin-notification-toggle ${isEnabled ? 'is-on' : 'is-off'}`}
                                    onClick={() => toggleNotificationCategoryCustomEmail(email)}
                                    disabled={isSaving}
                                  >
                                    {isSaving ? 'Saving...' : (isEnabled ? 'On' : 'Off')}
                                  </button>
                                </td>
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button className="btn btn-outline" onClick={closeNotificationManagementModal}>Fermer</button>
              </div>
            </div>
          </div>
        );
      })()}

      {emailTemplateModalTemplate && (() => {
        const template = emailTemplateModalTemplate;
        const previewData = generatePreviewData(template.key);
        const renderPreviewText = (text, data) => {
          if (!text) return '';
          let result = String(text);
          Object.entries(data || {}).forEach(([key, value]) => {
            result = result.replace(new RegExp(`\\{\\{\\s*${key}\\s*\\}\\}`, 'gi'), value == null ? '' : String(value));
          });
          return result;
        };
        const previewSubject = renderPreviewText(template.email_subject, previewData);
        const previewBody = renderPreviewText(template.email_body, previewData);

        return (
          <div className="modal-overlay" onClick={closeEmailTemplateModal}>
            <div className="modal-content modal-wide modal-zoom-in" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3>Email Template -- {template.label || template.key}</h3>
                <button className="modal-close" onClick={closeEmailTemplateModal}>x</button>
              </div>
              <div className="modal-body">
                <div className="admin-notification-management-meta">
                  <div className="admin-notification-management-meta-key">Template key</div>
                  <div className="admin-notification-management-meta-value">{template.key}</div>
                </div>

                <div className="email-template-variable-hint">
                  <strong>Variables disponibles :</strong> {(template.variables || []).map((v) => `{{${v}}}`).join(', ') || 'Aucune'}
                </div>

                <div className="form-grid email-template-grid">
                  <div className="form-group">
                    <label>Titre notification</label>
                    <input
                      type="text"
                      value={template.notification_title || ''}
                      onChange={(e) => updateEmailTemplateDraft(template.key, 'notification_title', e.target.value)}
                    />
                  </div>
                  <div className="form-group">
                    <label>Texte notification</label>
                    <textarea
                      rows={3}
                      value={template.notification_body || ''}
                      onChange={(e) => updateEmailTemplateDraft(template.key, 'notification_body', e.target.value)}
                    />
                  </div>
                  <div className="form-group">
                    <label>Sujet email</label>
                    <input
                      type="text"
                      value={template.email_subject || ''}
                      onChange={(e) => updateEmailTemplateDraft(template.key, 'email_subject', e.target.value)}
                    />
                  </div>
                  <div className="form-group">
                    <label>Corps email</label>
                    <textarea
                      rows={6}
                      value={template.email_body || ''}
                      onChange={(e) => updateEmailTemplateDraft(template.key, 'email_body', e.target.value)}
                    />
                  </div>
                </div>

                <div className="email-template-preview">
                  <h4>Apercu en direct</h4>
                  <div className="email-template-preview-box">
                    <div className="email-template-preview-subject">
                      <strong>Sujet :</strong> {previewSubject || '(vide)'}
                    </div>
                    <div className="email-template-preview-body">
                      {(previewBody || '').split('\n').map((line, idx) => (
                        <span key={idx}>{line}{idx < (previewBody || '').split('\n').length - 1 ? <br /> : null}</span>
                      ))}
                    </div>
                  </div>
                </div>

                {emailTemplateError && (
                  <p className="admin-form-error">{emailTemplateError}</p>
                )}
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => saveEmailTemplate(template.key)}
                  disabled={emailTemplateSaving}
                >
                  {emailTemplateSaving ? 'Sauvegarde...' : 'Sauvegarder'}
                </button>
                <button className="btn btn-outline" onClick={closeEmailTemplateModal}>Fermer</button>
              </div>
            </div>
          </div>
        );
      })()}

      {showTestimonialsRegionInfoModal && (
        <div className="modal-overlay" onClick={() => setShowTestimonialsRegionInfoModal(false)}>
          <div className="modal-content modal-wide modal-zoom-in" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Informations generales des villes de temoignage</h3>
              <button className="modal-close" onClick={() => setShowTestimonialsRegionInfoModal(false)}>x</button>
            </div>
            <div className="modal-body">
              <p className="admin-notification-management-desc">
                Chaque ligne correspond a une ville de la carte. Les villes listees partagent le meme texte d'information generale.
              </p>

              <div className="admin-table-wrap">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Ville</th>
                      <th>Information generale</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {testimonialsRegionInfoLoading ? (
                      <tr><td colSpan={3} className="admin-table-empty">Chargement...</td></tr>
                    ) : testimonialsRegionGroups.map((region) => {
                      const value = testimonialsRegionInfoDrafts[region.key]
                        ?? testimonialsRegionInfoMap[region.key]
                        ?? `Informations generales: ${region.cities.join(', ')} est une ville active pour les retours locataires.`;

                      return (
                        <tr key={region.key}>
                          <td>{region.cities.join(', ')}</td>
                          <td>
                            <textarea
                              rows={3}
                              value={value}
                              onChange={(e) => setTestimonialsRegionInfoDrafts((prev) => ({
                                ...prev,
                                [region.key]: e.target.value,
                              }))}
                            />
                          </td>
                          <td className="admin-cell-actions">
                            <button
                              type="button"
                              className="btn btn-primary btn-sm"
                              onClick={() => saveTestimonialRegionInfo(region)}
                              disabled={testimonialsRegionInfoSavingKey === region.key}
                            >
                              {testimonialsRegionInfoSavingKey === region.key ? 'Sauvegarde...' : 'Sauvegarder'}
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setShowTestimonialsRegionInfoModal(false)}>Fermer</button>
            </div>
          </div>
        </div>
      )}

      {showReviewFormConfigModal && (
        <div className="modal-overlay" onClick={() => { setShowReviewFormConfigModal(false); setReviewFormConfigEditingField(null); }}>
          <div className="modal-content modal-wide modal-zoom-in" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Gerer le formulaire "Laisser une note"</h3>
              <button className="modal-close" onClick={() => { setShowReviewFormConfigModal(false); setReviewFormConfigEditingField(null); }}>x</button>
            </div>
            <div className="modal-body">
              <p className="admin-notification-management-desc">
                Configurez les champs du formulaire de temoignage. Les etoiles (note) sont toujours presentes et ne peuvent pas etre supprimees.
              </p>

              <div className="admin-table-wrap">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Ordre</th>
                      <th>Identifiant</th>
                      <th>Type</th>
                      <th>Libelle</th>
                      <th>Requis</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {reviewFormConfigLoading ? (
                      <tr><td colSpan={6} className="admin-table-empty">Chargement...</td></tr>
                    ) : reviewFormFields.length === 0 ? (
                      <tr><td colSpan={6} className="admin-table-empty">Aucun champ configure.</td></tr>
                    ) : (
                      reviewFormFields.map((field, idx) => (
                        <tr key={field.id} style={reviewFormConfigEditingField === field.id ? { background: '#f0f7ff' } : undefined}>
                          <td>
                            <div style={{ display: 'flex', gap: '4px' }}>
                              <button type="button" className="btn btn-outline btn-sm" onClick={() => moveReviewFormField(field.id, 'up')} disabled={idx === 0} style={{ padding: '2px 6px', fontSize: '0.75rem' }}>▲</button>
                              <button type="button" className="btn btn-outline btn-sm" onClick={() => moveReviewFormField(field.id, 'down')} disabled={idx === reviewFormFields.length - 1} style={{ padding: '2px 6px', fontSize: '0.75rem' }}>▼</button>
                            </div>
                          </td>
                          <td><code>{field.id}</code>{field.locked ? <span style={{ marginLeft: 6, fontSize: '0.7rem', color: '#888' }}>(fixe)</span> : null}</td>
                          <td>
                            {field.type === 'rating' ? 'Note (etoiles)' :
                             field.type === 'text' ? 'Champ texte' :
                             field.type === 'textarea' ? 'Zone de texte' :
                             field.type === 'checkbox' ? (Array.isArray(field.options) && field.options.length > 0 ? 'Groupe de cases' : 'Case a cocher') :
                             field.type === 'dropdown' ? 'Menu deroulant' :
                             field.type === 'select' ? 'Selection' : field.type}
                          </td>
                          <td>{field.label}</td>
                          <td>{field.required ? 'Oui' : 'Non'}</td>
                          <td className="admin-cell-actions">
                            {!field.locked && (
                              <>
                                <button type="button" className="btn btn-outline btn-sm" onClick={() => startEditReviewFormField(field)} style={{ marginRight: 4 }}>Modifier</button>
                                <button type="button" className="btn btn-danger btn-sm" onClick={() => deleteReviewFormField(field.id)}>Supprimer</button>
                              </>
                            )}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              <div style={{ marginTop: '1.5rem', padding: '1rem', border: '1px solid #e0e0e0', borderRadius: '8px', background: '#fafafa' }}>
                <h4 style={{ margin: '0 0 0.75rem 0', fontSize: '0.95rem' }}>
                  {reviewFormConfigEditingField ? 'Modifier le champ' : 'Ajouter un champ'}
                </h4>
                <div className="admin-form-2col">
                  <div className="form-group">
                    <label>Identifiant</label>
                    <input
                      type="text"
                      value={reviewFormConfigDraft.id}
                      onChange={(e) => setReviewFormConfigDraft(prev => ({ ...prev, id: e.target.value }))}
                      placeholder="ex: type_logement"
                      disabled={Boolean(reviewFormConfigEditingField)}
                    />
                  </div>
                  <div className="form-group">
                    <label>Type</label>
                    <select
                      value={reviewFormConfigDraft.type}
                      onChange={(e) => setReviewFormConfigDraft(prev => ({ ...prev, type: e.target.value }))}
                    >
                      <option value="text">Champ texte</option>
                      <option value="textarea">Zone de texte</option>
                      <option value="checkbox">Case a cocher</option>
                      <option value="dropdown">Menu deroulant</option>
                      <option value="select">Selection</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Libelle</label>
                    <input
                      type="text"
                      value={reviewFormConfigDraft.label}
                      onChange={(e) => setReviewFormConfigDraft(prev => ({ ...prev, label: e.target.value }))}
                      placeholder="ex: Type de logement"
                    />
                  </div>
                  <div className="form-group">
                    <label>Texte indicatif (optionnel)</label>
                    <input
                      type="text"
                      value={reviewFormConfigDraft.placeholder}
                      onChange={(e) => setReviewFormConfigDraft(prev => ({ ...prev, placeholder: e.target.value }))}
                      placeholder="ex: Selectionnez..."
                    />
                  </div>
                  {(reviewFormConfigDraft.type === 'dropdown' || reviewFormConfigDraft.type === 'select' || reviewFormConfigDraft.type === 'checkbox') && (
                    <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                      <label>Options</label>
                      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
                        <input
                          type="text"
                          value={draftOptionInput}
                          onChange={(e) => setDraftOptionInput(e.target.value)}
                          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addDraftOption(); } }}
                          placeholder="Saisir une option et appuyer sur Entree"
                          style={{ flex: 1 }}
                        />
                        <button type="button" className="btn btn-primary btn-sm" onClick={addDraftOption}>Ajouter</button>
                      </div>
                      {reviewFormConfigDraft.options.length === 0 ? (
                        <div style={{ color: '#888', fontSize: '0.85rem', fontStyle: 'italic' }}>Aucune option ajoutee.</div>
                      ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                          {reviewFormConfigDraft.options.map((opt, optIdx) => (
                            <div key={optIdx} style={{ display: 'flex', alignItems: 'center', gap: '6px', background: '#fff', border: '1px solid #e0e0e0', borderRadius: '4px', padding: '4px 8px' }}>
                              <span style={{ color: '#888', fontSize: '0.8rem', minWidth: '20px' }}>{optIdx + 1}.</span>
                              <input
                                type="text"
                                value={opt}
                                onChange={(e) => editDraftOption(optIdx, e.target.value)}
                                style={{ flex: 1, border: 'none', outline: 'none', fontSize: '0.9rem', padding: '2px 4px', background: 'transparent' }}
                              />
                              <button type="button" onClick={() => moveDraftOption(optIdx, 'up')} disabled={optIdx === 0} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.75rem', color: '#666', padding: '2px' }}>▲</button>
                              <button type="button" onClick={() => moveDraftOption(optIdx, 'down')} disabled={optIdx === reviewFormConfigDraft.options.length - 1} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.75rem', color: '#666', padding: '2px' }}>▼</button>
                              <button type="button" onClick={() => removeDraftOption(optIdx)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.9rem', color: '#dc3545', padding: '2px 4px' }}>x</button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                  <div className="form-group">
                    <label>
                      <input
                        type="checkbox"
                        checked={reviewFormConfigDraft.required}
                        onChange={(e) => setReviewFormConfigDraft(prev => ({ ...prev, required: e.target.checked }))}
                        style={{ marginRight: 6 }}
                      />
                      Champ requis
                    </label>
                  </div>
                </div>
                <div style={{ marginTop: '0.75rem', display: 'flex', gap: '0.5rem' }}>
                  {reviewFormConfigEditingField ? (
                    <>
                      <button type="button" className="btn btn-primary" onClick={updateReviewFormField}>Mettre a jour</button>
                      <button type="button" className="btn btn-outline" onClick={() => { setReviewFormConfigEditingField(null); setReviewFormConfigDraft({ id: '', type: 'text', label: '', placeholder: '', required: false, options: [] }); setDraftOptionInput(''); }}>Annuler</button>
                    </>
                  ) : (
                    <button type="button" className="btn btn-primary" onClick={addReviewFormField}>Ajouter le champ</button>
                  )}
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => { setShowReviewFormConfigModal(false); setReviewFormConfigEditingField(null); }}>Annuler</button>
              <button className="btn btn-primary" onClick={saveReviewFormConfig} disabled={reviewFormConfigSaving}>
                {reviewFormConfigSaving ? 'Sauvegarde...' : 'Sauvegarder la configuration'}
              </button>
            </div>
          </div>
        </div>
      )}

      {selectedReviewDetail && (
        <div className="modal-overlay" onClick={() => setSelectedReviewDetail(null)}>
          <div className="modal-content modal-wide modal-zoom-in" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Details de l'avis</h3>
              <button className="modal-close" onClick={() => setSelectedReviewDetail(null)}>x</button>
            </div>
            <div className="modal-body">
              <div className="admin-detail-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem 2rem' }}>
                <div className="admin-detail-row">
                  <span className="admin-k" style={{ fontWeight: 600, color: '#555' }}>Auteur</span>
                  <span className="admin-v">{String(selectedReviewDetail.reviewer_display_name || `${selectedReviewDetail.first_name || ''} ${selectedReviewDetail.last_name || ''}`).trim() || '-'}</span>
                </div>
                <div className="admin-detail-row">
                  <span className="admin-k" style={{ fontWeight: 600, color: '#555' }}>Ville</span>
                  <span className="admin-v">{getReviewCityDisplayName(selectedReviewDetail.city, selectedReviewDetail.department) || '-'}</span>
                </div>
                <div className="admin-detail-row">
                  <span className="admin-k" style={{ fontWeight: 600, color: '#555' }}>Note</span>
                  <span className="admin-v">{Number(selectedReviewDetail.rating || 0).toFixed(1)} / 5</span>
                </div>
                <div className="admin-detail-row">
                  <span className="admin-k" style={{ fontWeight: 600, color: '#555' }}>Date</span>
                  <span className="admin-v">{formatDateTime(selectedReviewDetail.created_at)}</span>
                </div>
                <div className="admin-detail-row" style={{ gridColumn: '1 / -1' }}>
                  <span className="admin-k" style={{ fontWeight: 600, color: '#555' }}>Avis</span>
                  <div className="admin-v" style={{ whiteSpace: 'pre-wrap', marginTop: '0.25rem' }}>{selectedReviewDetail.review_text || '-'}</div>
                </div>
                {(() => {
                  let customData = selectedReviewDetail.custom_fields_data;
                  if (customData && typeof customData === 'string') {
                    try { customData = JSON.parse(customData); } catch { customData = null; }
                  }
                  if (!customData || typeof customData !== 'object') return null;
                  const fieldMap = {};
                  reviewFormFields.forEach(f => { fieldMap[f.id] = f; });
                  return Object.entries(customData).map(([key, value]) => {
                    const field = fieldMap[key];
                    const label = field?.label || key;
                    let displayValue = value;
                    if (typeof value === 'boolean') displayValue = value ? 'Oui' : 'Non';
                    else if (Array.isArray(value)) displayValue = value.join(', ');
                    return (
                      <div key={key} className="admin-detail-row" style={{ gridColumn: '1 / -1' }}>
                        <span className="admin-k" style={{ fontWeight: 600, color: '#555' }}>{label}</span>
                        <span className="admin-v">{String(displayValue)}</span>
                      </div>
                    );
                  });
                })()}
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setSelectedReviewDetail(null)}>Fermer</button>
            </div>
          </div>
        </div>
      )}

      {showCustomEmailModal && (() => {
        return (
          <div className="modal-overlay" onClick={closeCustomEmailModal}>
            <div className="modal-content modal-zoom-in admin-custom-email-modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3>Add Outside Email</h3>
                <button className="modal-close" onClick={closeCustomEmailModal}>x</button>
              </div>
              <div className="modal-body">
                <p className="admin-notification-management-desc">
                  Add external email addresses that will receive notifications. These emails will appear in all notification category management screens.
                </p>

                <div className="admin-form-group custom-email-field">
                  <label className="custom-email-label" htmlFor="customEmailInput">Email Address</label>
                  <input
                    id="customEmailInput"
                    type="email"
                    className="form-control custom-email-input"
                    placeholder="example@domain.com"
                    value={customEmailInput}
                    onChange={(e) => setCustomEmailInput(e.target.value)}
                    disabled={customEmailAdding}
                    pattern={EMAIL_INPUT_PATTERN}
                    title={EMAIL_INPUT_TITLE}
                  />
                  {customEmailError && <div className="form-error custom-email-error">{customEmailError}</div>}
                </div>

                <div className="admin-table-wrap custom-email-list">
                  <h4 style={{ marginTop: '20px' }}>Added Emails</h4>
                  {customEmailsLoading ? (
                    <p className="admin-table-empty custom-email-empty" style={{ padding: '20px', textAlign: 'center' }}>Loading...</p>
                  ) : customNotificationEmails.length > 0 ? (
                    <table className="admin-table">
                      <thead>
                        <tr>
                          <th>Email</th>
                          <th>Added</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {customNotificationEmails.map((email) => {
                          const emailId = Number(email.id);
                          const isDeleting = Boolean(customEmailDeleting[emailId]);
                          return (
                            <tr key={email.id}>
                              <td>{email.email}</td>
                              <td className="admin-cell-nowrap">{new Date(email.created_at).toLocaleDateString()}</td>
                              <td className="admin-cell-actions">
                                <button
                                  type="button"
                                  className="btn btn-danger btn-sm"
                                  onClick={() => deleteCustomEmail(emailId)}
                                  disabled={isDeleting}
                                >
                                  {isDeleting ? 'Deleting...' : 'Delete'}
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  ) : (!customEmailAdding && (
                    <p className="admin-table-empty custom-email-empty" style={{ padding: '20px', textAlign: 'center' }}>No custom emails added yet.</p>
                  ))}
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => addCustomEmail()}
                  disabled={customEmailAdding || !customEmailInput.trim()}
                >
                  {customEmailAdding ? 'Adding...' : 'Add Email'}
                </button>
                <button className="btn btn-outline" onClick={closeCustomEmailModal}>Fermer</button>
              </div>
            </div>
          </div>
        );
      })()}

      {activeTab === 'invoices' && adminInvoicesExpandedId && (() => {
        const inv = adminInvoices.find((row) => row.id === adminInvoicesExpandedId);
        const detail = adminInvoiceDetailsById[adminInvoicesExpandedId] || inv;
        if (!detail) return null;
        return (
          <div className="modal-overlay" onClick={() => setAdminInvoicesExpandedId(null)}>
            <div className="modal-content modal-wide modal-zoom-in" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3>Facture - {detail.invoice_number || `#${detail.id}`}</h3>
                <button className="modal-close" onClick={() => setAdminInvoicesExpandedId(null)}>x</button>
              </div>
              <div className="modal-body">
                <div className="admin-invoice-actions">
                  <button
                    type="button"
                    className="btn btn-outline"
                    onClick={async () => {
                      try {
                        const response = await api.invoices.getAdminInvoicePdf(detail.id);
                        const fileUrl = response?.data?.data?.fileUrl;
                        const resolvedUrl = resolveMediaUrl(fileUrl);
                        if (!resolvedUrl) {
                          alert('Impossible de generer le PDF de la facture.');
                          return;
                        }
                        const w = window.open(resolvedUrl, '_blank');
                        if (!w) {
                          alert('Popup bloquee. Veuillez autoriser les popups pour imprimer.');
                        }
                      } catch (e) {
                        console.error('Failed to open invoice PDF:', e);
                        alert(e?.response?.data?.error || 'Echec de l\'ouverture du PDF.');
                      }
                    }}
                  >
                    Imprimer
                  </button>
                </div>
                <div className="admin-expanded-grid admin-invoice-expanded-grid">
                  <div>
                    <span className="admin-k">Facture</span>
                    <span className="admin-v">{detail.invoice_number || '-'}</span>
                  </div>
                  <div>
                    <span className="admin-k">Emise le</span>
                    <span className="admin-v">{formatDateTime(detail.issued_at)}</span>
                  </div>
                  <div>
                    <span className="admin-k">Paid</span>
                    <span className="admin-v">{formatDateTime(detail.paid_at)}</span>
                  </div>
                  <div>
                    <span className="admin-k">Reference de paiement</span>
                    <span className="admin-v">{detail.gocardless_payment_id || '-'}</span>
                  </div>
                  <div className="admin-invoice-lines">
                    <span className="admin-k">Lignes</span>
                    <div className="admin-v">
                      {Array.isArray(detail.items) && detail.items.length ? (
                        <table className="admin-mini-table">
                          <tbody>
                            {detail.items.map((it) => (
                              <tr key={it.id || it.label}>
                                <td>{it.label}</td>
                                <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>{formatMoney(it.amount, detail.currency)}</td>
                              </tr>
                            ))}
                            <tr className="admin-mini-total">
                              <td>Total</td>
                              <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>{formatMoney(detail.total_amount, detail.currency)}</td>
                            </tr>
                          </tbody>
                        </table>
                      ) : (
                        <span>-</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {activeTab === 'events' && eventsExpandedEventId && (() => {
        const event = events.find((row) => row.id === eventsExpandedEventId);
        if (!event) return null;
        const statusLabel = event.is_published ? 'Publie' : 'Depublie';
        return (
          <div className="modal-overlay" onClick={() => { setEventsExpandedEventId(null); setEventsEditMode(false); setEventsEditData(null); }}>
            <div className="modal-content modal-wide modal-zoom-in" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3>Evenement - {event.title || '-'}</h3>
                <button className="modal-close" onClick={() => { setEventsExpandedEventId(null); setEventsEditMode(false); setEventsEditData(null); }}>x</button>
              </div>
              <div className="modal-body">
                {!eventsEditMode ? (
                  <>
                    <div className="admin-expanded-grid admin-event-expanded-grid">
                      <div>
                        <span className="admin-k">Titre</span>
                        <span className="admin-v">{event.title || '-'}</span>
                      </div>
                      <div>
                        <span className="admin-k">Date</span>
                        <span className="admin-v">{formatDateTime(event.start_at)}</span>
                      </div>
                      <div>
                        <span className="admin-k">Categorie</span>
                        <span className="admin-v">{event.category_label || event.category || '-'}</span>
                      </div>
                      <div>
                        <span className="admin-k">Statut</span>
                        <span className="admin-v">{statusLabel}</span>
                      </div>
                      <div className="admin-event-description">
                        <span className="admin-k">Description</span>
                        <span className="admin-v">{event.description || '-'}</span>
                      </div>
                      <div className="admin-event-social">
                        <span className="admin-k">Lien social</span>
                        {event.social_url ? (
                          <a className="admin-v admin-link" href={event.social_url} target="_blank" rel="noreferrer">{event.social_url}</a>
                        ) : (
                          <span className="admin-v">-</span>
                        )}
                      </div>
                    </div>
                    <div className="modal-footer" style={{ marginTop: '1.5rem', display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                      <button type="button" className="btn btn-primary" onClick={() => handleEditEvent(event)}>
                        Edit
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="admin-form">
                      <div className="form-group">
                        <label>Titre (max {EVENT_TITLE_MAX} caracteres)</label>
                        <input
                          type="text"
                          value={eventsEditData?.title || ''}
                          maxLength={EVENT_TITLE_MAX}
                          onChange={(e) => setEventsEditData((p) => ({ ...p, title: e.target.value }))}
                          required
                        />
                        <small>{(eventsEditData?.title || '').length}/{EVENT_TITLE_MAX}</small>
                      </div>

                      <div className="form-group">
                        <label>Description (max {EVENT_BODY_MAX} characters)</label>
                        <textarea
                          value={eventsEditData?.description || ''}
                          maxLength={EVENT_BODY_MAX}
                          onChange={(e) => setEventsEditData((p) => ({ ...p, description: e.target.value }))}
                          required
                          rows={5}
                        />
                        <small>{(eventsEditData?.description || '').length}/{EVENT_BODY_MAX}</small>
                      </div>

                      <div className="admin-form-2col">
                        <div className="form-group">
                          <label>Date</label>
                          <input
                            type="date"
                            value={eventsEditData?.start_at || ''}
                            onChange={(e) => setEventsEditData((p) => ({ ...p, start_at: e.target.value }))}
                            required
                          />
                        </div>

                        <div className="form-group">
                          <label>Categorie</label>
                          <select
                            value={eventsEditData?.category || ''}
                            onChange={(e) => setEventsEditData((p) => ({ ...p, category: e.target.value }))}
                            disabled={eventCategories.length === 0}
                            required
                          >
                            {eventCategories.length === 0 ? (
                              <option value="">Aucune categorie disponible</option>
                            ) : (
                              eventCategories.map((category) => (
                                <option key={category.id} value={category.slug}>{category.label}</option>
                              ))
                            )}
                          </select>
                        </div>
                      </div>

                      <div className="form-group">
                        <label>Lien (reseaux sociaux)</label>
                        <input
                          type="url"
                          value={eventsEditData?.social_url || ''}
                          onChange={(e) => setEventsEditData((p) => ({ ...p, social_url: e.target.value }))}
                          placeholder="https://..."
                          required
                        />
                      </div>

                      <div className="form-group">
                        <label>Photo de l'evenement</label>
                        {eventsEditData?.banner_url ? (
                          <div style={{ marginBottom: '0.75rem' }}>
                            <img
                              src={resolveMediaUrl(eventsEditData.banner_url) || eventsEditData.banner_url}
                              alt={eventsEditData.title || 'Apercu evenement'}
                              style={{ width: '100%', maxWidth: '360px', borderRadius: '14px', objectFit: 'cover', display: 'block' }}
                            />
                          </div>
                        ) : null}
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => setEventsEditData((p) => ({ ...p, image: e.target.files?.[0] || null }))}
                        />
                        <small>Choisissez une nouvelle photo uniquement si vous souhaitez remplacer l'image actuelle.</small>
                      </div>
                    </div>
                    <div className="modal-footer" style={{ marginTop: '1.5rem', display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                      <button type="button" className="btn btn-secondary" onClick={handleCancelEditEvent} disabled={eventsSaving}>
                        Annuler
                      </button>
                      <button type="button" className="btn btn-success" onClick={handleSaveEditEvent} disabled={eventsSaving}>
                        {eventsSaving ? 'Enregistrement...' : 'Enregistrer'}
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {activeTab === TAB_KEYS.FORM_MANAGEMENT && adminFormsExpandedId && (() => {
        const form = adminForms.find((row) => Number(row.id) === Number(adminFormsExpandedId));
        if (!form) return null;

        const schemaPages = Array.isArray(form?.schema?.pages) ? form.schema.pages : [];
        const isPublished = Number(form.is_published) === 1 || form.is_published === true;
        const isPublishing = Number(formPublishingId) === Number(form.id);
        const isDeleting = Number(formDeletingId) === Number(form.id);

        return (
          <div className="modal-overlay" onClick={() => setAdminFormsExpandedId(null)}>
            <div className="modal-content modal-wide modal-zoom-in" onClick={(event) => event.stopPropagation()}>
              <div className="modal-header">
                <h3>Form definition - {form.form_name || '-'}</h3>
                <button className="modal-close" onClick={() => setAdminFormsExpandedId(null)}>x</button>
              </div>

              <div className="modal-body">
                <div className="admin-expanded-grid">
                  <div>
                    <span className="admin-k">Slug</span>
                    <span className="admin-v">/{form.slug || '-'}</span>
                  </div>
                  <div>
                    <span className="admin-k">Request tab</span>
                    <span className="admin-v">{form.request_tab_name || '-'}</span>
                  </div>
                  <div>
                    <span className="admin-k">Status</span>
                    <span className="admin-v">{isPublished ? 'Published' : 'Unpublished'}</span>
                  </div>
                  <div>
                    <span className="admin-k">Submissions</span>
                    <span className="admin-v">{Number(form.submissions_count || 0)}</span>
                  </div>
                  <div>
                    <span className="admin-k">Updated</span>
                    <span className="admin-v">{formatDateTime(form.updated_at)}</span>
                  </div>
                  <div>
                    <span className="admin-k">Created</span>
                    <span className="admin-v">{formatDateTime(form.created_at)}</span>
                  </div>
                  {form.description && (
                    <div className="admin-notif-body">
                      <span className="admin-k">Description</span>
                      <span className="admin-v">{form.description}</span>
                    </div>
                  )}
                </div>

                <div style={{ marginTop: '1rem' }}>
                  <h4>Pages and sections</h4>
                  {schemaPages.length === 0 ? (
                    <p>No page configured.</p>
                  ) : (
                    <div className="admin-form-schema-summary">
                      {schemaPages.map((page, pageIndex) => (
                        <div key={page.id || pageIndex} className="admin-form-schema-page">
                          <div className="admin-form-schema-page-head">
                            <strong>Page {pageIndex + 1}: {page.title || '-'}</strong>
                            <span>{Array.isArray(page.sections) ? page.sections.length : 0} section(s)</span>
                          </div>
                          <ul>
                            {(Array.isArray(page.sections) ? page.sections : []).map((section, sectionIndex) => (
                              <li key={section.id || sectionIndex}>
                                <strong>{section.title || `Section ${sectionIndex + 1}`}</strong>
                                <span> - {Array.isArray(section.fields) ? section.fields.length : 0} field(s)</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="modal-footer" style={{ display: 'flex', gap: '0.5rem', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => {
                      setAdminFormsExpandedId(null);
                      openEditFormBuilder(form);
                    }}
                    disabled={isPublishing || isDeleting}
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    className={isPublished ? 'btn btn-warning' : 'btn btn-success'}
                    onClick={() => toggleAdminFormPublish(form)}
                    disabled={isPublishing || isDeleting}
                  >
                    {isPublishing ? 'Updating...' : (isPublished ? 'Unpublish' : 'Publish')}
                  </button>
                  <button
                    type="button"
                    className="btn btn-danger"
                    onClick={() => deleteAdminFormDefinition(form)}
                    disabled={isDeleting || isPublishing}
                  >
                    {isDeleting ? 'Deleting...' : 'Delete'}
                  </button>
                </div>
                <button type="button" className="btn btn-outline" onClick={() => setAdminFormsExpandedId(null)}>
                  Close
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {activeTab === TAB_KEYS.FORM_REQUESTS && adminFormSubmissionExpandedId && (() => {
        const submission = adminFormSubmissions.find((row) => Number(row.id) === Number(adminFormSubmissionExpandedId));
        if (!submission) return null;

        const responses = submission.responses && typeof submission.responses === 'object'
          ? submission.responses
          : {};
        const linkedForm = adminForms.find((row) => Number(row.id) === Number(submission.form_id));
        const submissionSchema = submission.form_schema && typeof submission.form_schema === 'object'
          ? submission.form_schema
          : linkedForm?.schema;
        const responseRows = mapSubmissionResponsesToLabels(responses, submissionSchema);
        const noteDraft = adminFormSubmissionNotesById[submission.id] ?? (submission.admin_note || '');
        const reviewing = Number(adminFormSubmissionReviewingId) === Number(submission.id);

        return (
          <div className="modal-overlay" onClick={() => setAdminFormSubmissionExpandedId(null)}>
            <div className="modal-content modal-wide modal-zoom-in" onClick={(event) => event.stopPropagation()}>
              <div className="modal-header">
                <h3>Form request - {submission.reference_number || '-'}</h3>
                <button className="modal-close" onClick={() => setAdminFormSubmissionExpandedId(null)}>x</button>
              </div>

              <div className="modal-body">
                <div className="admin-expanded-grid">
                  <div>
                    <span className="admin-k">Applicant</span>
                    <span className="admin-v">{submission.applicant_name || '-'}</span>
                  </div>
                  <div>
                    <span className="admin-k">Email</span>
                    <span className="admin-v">{submission.applicant_email || '-'}</span>
                  </div>
                  <div>
                    <span className="admin-k">Form</span>
                    <span className="admin-v">{submission.form_name || '-'}</span>
                  </div>
                  <div>
                    <span className="admin-k">Request tab</span>
                    <span className="admin-v">{submission.request_tab_name || '-'}</span>
                  </div>
                  <div>
                    <span className="admin-k">Status</span>
                    <span className="admin-v">{submission.status || 'pending'}</span>
                  </div>
                  <div>
                    <span className="admin-k">Seen</span>
                    <span className="admin-v">{Number(submission.is_seen) === 1 ? 'Seen' : 'Unseen'}</span>
                  </div>
                  <div>
                    <span className="admin-k">Submitted</span>
                    <span className="admin-v">{formatDateTime(submission.created_at)}</span>
                  </div>
                  <div>
                    <span className="admin-k">Reviewed at</span>
                    <span className="admin-v">{submission.reviewed_at ? formatDateTime(submission.reviewed_at) : '--'}</span>
                  </div>
                  <div>
                    <span className="admin-k">Seen at</span>
                    <span className="admin-v">{submission.seen_at ? formatDateTime(submission.seen_at) : '--'}</span>
                  </div>
                </div>

                <div style={{ marginTop: '1rem' }}>
                  <h4>Submitted values</h4>
                  {responseRows.length === 0 ? (
                    <p>No submitted values.</p>
                  ) : (
                    <div className="admin-form-responses-list">
                      {responseRows.map((row, index) => (
                        <div key={`${row.key}-${index}`} className="admin-form-response-item">
                          <span className="admin-k">{row.label}</span>
                          {row.type === 'file' ? (() => {
                            const rawValues = Array.isArray(row.value) ? row.value : [row.value];
                            const urls = rawValues
                              .map((item) => String(item || '').trim())
                              .filter(Boolean);

                            if (urls.length === 0) {
                              return <span className="admin-v">-</span>;
                            }

                            return (
                              <span className="admin-v">
                                {urls.map((url, urlIndex) => {
                                  const resolved = resolveMediaUrl(url) || url;
                                  const label = urls.length > 1 ? `Ouvrir fichier ${urlIndex + 1}` : 'Ouvrir fichier';
                                  return (
                                    <a
                                      key={`${row.key}-file-${urlIndex}`}
                                      href={resolved}
                                      target="_blank"
                                      rel="noreferrer"
                                      className="admin-link"
                                      style={{ marginRight: urlIndex < urls.length - 1 ? 12 : 0 }}
                                    >
                                      {label}
                                    </a>
                                  );
                                })}
                              </span>
                            );
                          })() : (
                            <span className="admin-v">{typeof row.value === 'boolean' ? (row.value ? 'Yes' : 'No') : String(row.value || '-')}</span>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="form-group" style={{ marginTop: '1rem' }}>
                  <label>Admin note</label>
                  <textarea
                    rows={4}
                    value={noteDraft}
                    onChange={(event) => {
                      const value = event.target.value;
                      setAdminFormSubmissionNotesById((prev) => ({
                        ...prev,
                        [submission.id]: value,
                      }));
                    }}
                    placeholder="Optional note sent to the applicant"
                  />
                </div>
              </div>

              <div className="modal-footer" style={{ display: 'flex', gap: '0.5rem', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button
                    type="button"
                    className="btn btn-warning"
                    onClick={() => reviewAdminFormSubmission(submission.id, 'awaiting_response_from_user')}
                    disabled={reviewing}
                  >
                    {reviewing ? 'Updating...' : 'Need user response'}
                  </button>
                  <button
                    type="button"
                    className="btn btn-danger"
                    onClick={() => reviewAdminFormSubmission(submission.id, 'reject')}
                    disabled={reviewing}
                  >
                    {reviewing ? 'Updating...' : 'Reject'}
                  </button>
                  <button
                    type="button"
                    className="btn btn-success"
                    onClick={() => reviewAdminFormSubmission(submission.id, 'accept')}
                    disabled={reviewing}
                  >
                    {reviewing ? 'Updating...' : 'Accept'}
                  </button>
                </div>
                <button type="button" className="btn btn-outline" onClick={() => setAdminFormSubmissionExpandedId(null)}>
                  Close
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {activeTab === TAB_KEYS.CAROUSEL && carouselExpandedSlideId && (() => {
        const slide = carouselSlidesAdmin.find((row) => Number(row.id) === Number(carouselExpandedSlideId));
        if (!slide) return null;

        const globalIndex = carouselSlidesAdmin.findIndex((row) => Number(row.id) === Number(slide.id));
        const canMoveUp = globalIndex > 0;
        const canMoveDown = globalIndex >= 0 && globalIndex < carouselSlidesAdmin.length - 1;
        const imageUrl = resolveMediaUrl(slide.image_url);

        return (
          <div className="modal-overlay" onClick={() => setCarouselExpandedSlideId(null)}>
            <div className="modal-content modal-wide modal-zoom-in" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3>Carousel slide -- {slide.title || '-'}</h3>
                <button className="modal-close" onClick={() => setCarouselExpandedSlideId(null)}>x</button>
              </div>

              <div className="modal-body">
                <div className="admin-expanded-grid admin-carousel-expanded-grid">
                  <div>
                    <span className="admin-k">Titre</span>
                    <span className="admin-v">{slide.title || '-'}</span>
                  </div>
                  <div>
                    <span className="admin-k">Rank</span>
                    <span className="admin-v">#{slide.sort_order}</span>
                  </div>
                  <div>
                    <span className="admin-k">Updated</span>
                    <span className="admin-v">{formatDateTime(slide.updated_at)}</span>
                  </div>
                  <div>
                    <span className="admin-k">Cree par</span>
                    <span className="admin-v">{slide.created_by_email || '-'}</span>
                  </div>
                  <div>
                    <span className="admin-k">Derniere mise a jour par</span>
                    <span className="admin-v">{slide.updated_by_email || '-'}</span>
                  </div>
                  <div>
                    <span className="admin-k">Image</span>
                    {imageUrl ? (
                      <a className="admin-v admin-link" href={imageUrl} target="_blank" rel="noreferrer">
                        Ouvrir image
                      </a>
                    ) : (
                      <span className="admin-v">-</span>
                    )}
                  </div>

                  <div className="admin-carousel-expanded-body">
                    <span className="admin-k">Body</span>
                    <p className="admin-v admin-carousel-expanded-body-text">{slide.body || '-'}</p>
                  </div>
                </div>
              </div>

              <div className="modal-footer" style={{ display: 'flex', gap: '0.5rem', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button
                    type="button"
                    className="btn btn-outline"
                    onClick={() => handleMoveCarouselSlide(slide.id, -1)}
                    disabled={!canMoveUp || carouselSlideReorderingId === slide.id}
                  >
                    Up
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline"
                    onClick={() => handleMoveCarouselSlide(slide.id, 1)}
                    disabled={!canMoveDown || carouselSlideReorderingId === slide.id}
                  >
                    Down
                  </button>
                </div>

                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => {
                      setCarouselExpandedSlideId(null);
                      handleEditCarouselSlide(slide);
                    }}
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    className="btn btn-danger"
                    onClick={() => handleDeleteCarouselSlide(slide.id)}
                    disabled={carouselSlideDeletingId === slide.id}
                  >
                    {carouselSlideDeletingId === slide.id ? 'Suppression...' : 'Supprimer'}
                  </button>
                  <button type="button" className="btn btn-outline" onClick={() => setCarouselExpandedSlideId(null)}>
                    Fermer
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {activeTab === 'event-requests' && eventRegistrationsExpandedId && (() => {
        const registration = eventRegistrations.find((r) => r.id === eventRegistrationsExpandedId);
        if (!registration) return null;
        const isPending = registration.status === 'pending';
        const isApproved = registration.status === 'approved';
        const isDisapproved = registration.status === 'disapproved';
        return (
          <div className="modal-overlay" onClick={() => setEventRegistrationsExpandedId(null)}>
            <div className="modal-content modal-wide modal-zoom-in" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3>Demande inscription a un evenement</h3>
                <button className="modal-close" onClick={() => setEventRegistrationsExpandedId(null)}>x</button>
              </div>
              <div className="modal-body">
                <div className="admin-expanded-grid">
                  <div>
                    <span className="admin-k">Nom</span>
                    <span className="admin-v">{registration.user_name || '-'}</span>
                  </div>
                  <div>
                    <span className="admin-k">E-mail</span>
                    <span className="admin-v">{registration.user_email || '-'}</span>
                  </div>
                  <div>
                    <span className="admin-k">Evenement</span>
                    <span className="admin-v">{registration.event_title || '-'}</span>
                  </div>
                  <div>
                    <span className="admin-k">Demandee le</span>
                    <span className="admin-v">{formatDateTime(registration.created_at)}</span>
                  </div>
                  <div>
                    <span className="admin-k">Statut</span>
                    <span className="admin-v" style={{ textTransform: 'capitalize', fontWeight: 'bold', color: isPending ? '#ff9800' : isApproved ? '#28a745' : '#dc3545' }}>
                      {registration.status || 'pending'}
                    </span>
                  </div>
                  {registration.reviewed_at && (
                    <div>
                      <span className="admin-k">Examinee le</span>
                      <span className="admin-v">{formatDateTime(registration.reviewed_at)}</span>
                    </div>
                  )}
                </div>

                <div className="modal-footer" style={{ marginTop: '1.5rem', display: 'flex', gap: '0.5rem', justifyContent: 'space-between' }}>
                  <button
                    type="button"
                    className="btn btn-danger"
                    onClick={() => handleDeleteRegistration(registration.id)}
                    disabled={eventRegistrationProcessing}
                  >
                    Supprimer
                  </button>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    {isPending && (
                      <>
                        <button
                          type="button"
                          className="btn btn-warning"
                          onClick={() => handleDisapproveRegistration(registration.id)}
                          disabled={eventRegistrationProcessing}
                        >
                          {eventRegistrationProcessing ? 'Traitement...' : 'Refuser'}
                        </button>
                        <button
                          type="button"
                          className="btn btn-success"
                          onClick={() => handleApproveRegistration(registration.id)}
                          disabled={eventRegistrationProcessing}
                        >
                          {eventRegistrationProcessing ? 'Traitement...' : 'Approuver'}
                        </button>
                      </>
                    )}
                    {isApproved && (
                      <div style={{ padding: '0.5rem 1rem', background: '#d4edda', color: '#155724', borderRadius: '4px', fontWeight: 'bold' }}>
                        ? Approuvee
                      </div>
                    )}
                    {isDisapproved && (
                      <div style={{ padding: '0.5rem 1rem', background: '#f8d7da', color: '#721c24', borderRadius: '4px', fontWeight: 'bold' }}>
                        ? Refusee
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {activeTab === TAB_KEYS.CONTACT_MESSAGES && contactMessagesExpandedId && (() => {
        const message = contactMessages.find((row) => row.id === contactMessagesExpandedId);
        if (!message) return null;

        const statusMeta = getContactStatusMeta(message.status);
        const responseDraft = contactResponseDrafts[message.id] ?? (message.admin_response || '');
        const isSending = contactMessagesRespondingId === message.id;

        return (
          <div className="modal-overlay" onClick={() => setContactMessagesExpandedId(null)}>
            <div className="modal-content modal-wide modal-zoom-in" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3>Message de contact</h3>
                <button className="modal-close" onClick={() => setContactMessagesExpandedId(null)}>x</button>
              </div>

              <div className="modal-body">
                <div className="admin-expanded-grid admin-contact-expanded-grid">
                  <div>
                    <span className="admin-k">Nom</span>
                    <span className="admin-v">{message.full_name || '-'}</span>
                  </div>
                  <div>
                    <span className="admin-k">E-mail</span>
                    <span className="admin-v">{message.email || '-'}</span>
                  </div>
                  <div>
                    <span className="admin-k">Sujet</span>
                    <span className="admin-v">{message.subject || '-'}</span>
                  </div>
                  <div>
                    <span className="admin-k">Recu le</span>
                    <span className="admin-v">{formatDateTime(message.created_at)}</span>
                  </div>
                  <div>
                    <span className="admin-k">Statut</span>
                    <span className="admin-v">
                      <span className={`admin-notif-dot ${statusMeta.dotClass}`} aria-hidden="true" />
                      <span style={{ marginLeft: 8 }}>{statusMeta.label}</span>
                    </span>
                  </div>
                  <div>
                    <span className="admin-k">Vu le</span>
                    <span className="admin-v">{message.seen_at ? formatDateTime(message.seen_at) : '--'}</span>
                  </div>
                  <div>
                    <span className="admin-k">Repondu le</span>
                    <span className="admin-v">{message.responded_at ? formatDateTime(message.responded_at) : '--'}</span>
                  </div>
                  <div className="admin-contact-message-block">
                    <span className="admin-k">Message utilisateur</span>
                    <p className="admin-v admin-contact-message-body">{message.message || '-'}</p>
                  </div>
                </div>

                <div className="admin-contact-response-block">
                  <label htmlFor="contact-response" className="admin-k">Repondre a utilisateur</label>
                  <textarea
                    id="contact-response"
                    className="admin-contact-response-input"
                    rows="6"
                    value={responseDraft}
                    onChange={(e) => {
                      const value = e.target.value;
                      setContactResponseDrafts((prev) => ({ ...prev, [message.id]: value }));
                    }}
                    placeholder="Saisissez votre reponse ici..."
                  />
                </div>
              </div>

              <div className="modal-footer">
                <button className="btn btn-outline" onClick={() => setContactMessagesExpandedId(null)}>Fermer</button>
                <button
                  className="btn btn-primary"
                  onClick={() => submitContactResponse(message.id)}
                  disabled={isSending}
                >
                  {isSending ? 'Envoi...' : 'Envoyer'}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {showAdminBroadcastModal && (
        <div className="modal-overlay" onClick={closeAdminBroadcastModal}>
          <div className="modal-content modal-zoom-in admin-broadcast-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Diffuser une notification admin</h3>
              <button className="modal-close" onClick={closeAdminBroadcastModal}>x</button>
            </div>
            <div className="modal-body">
              <div className="admin-broadcast-form">
                <section className="admin-broadcast-block">
                  <div className="admin-broadcast-block-head">
                    <h4>Destinataires</h4>
                    {adminBroadcastRecipientScope === 'selected' && (
                      <span className="admin-broadcast-pill">{adminBroadcastSelectedUserIds.length} selectionne(s)</span>
                    )}
                  </div>

                  <div className="admin-broadcast-scope-grid">
                    <label className={`admin-radio-card ${adminBroadcastRecipientScope === 'all' ? 'is-active' : ''}`}>
                      <input
                        type="radio"
                        name="adminBroadcastScope"
                        value="all"
                        checked={adminBroadcastRecipientScope === 'all'}
                        onChange={() => {
                          setAdminBroadcastRecipientScope('all');
                          setAdminBroadcastSelectedUserIds([]);
                          setAdminBroadcastSearchQuery('');
                        }}
                      />
                      <span className="admin-radio-card-copy">
                        <strong>Tous les administrateurs</strong>
                        <small>Diffusion globale immediate.</small>
                      </span>
                    </label>

                    <label className={`admin-radio-card ${adminBroadcastRecipientScope === 'selected' ? 'is-active' : ''}`}>
                      <input
                        type="radio"
                        name="adminBroadcastScope"
                        value="selected"
                        checked={adminBroadcastRecipientScope === 'selected'}
                        onChange={() => setAdminBroadcastRecipientScope('selected')}
                      />
                      <span className="admin-radio-card-copy">
                        <strong>Administrateurs cibles</strong>
                        <small>Selectionnez manuellement les destinataires.</small>
                      </span>
                    </label>
                  </div>

                  {adminBroadcastRecipientScope === 'selected' && (
                    <div className="admin-recipient-picker">
                      <label htmlFor="admin-broadcast-recipient-search">Rechercher et ajouter</label>

                      <div className="admin-recipient-search-wrap">
                        <input
                          id="admin-broadcast-recipient-search"
                          type="text"
                          value={adminBroadcastSearchQuery}
                          onChange={(e) => setAdminBroadcastSearchQuery(e.target.value)}
                          placeholder="Tapez un nom ou un email..."
                          autoComplete="off"
                        />

                        {String(adminBroadcastSearchQuery || '').trim().length > 0 && (
                          <div className="admin-recipient-search-dropdown">
                            {adminDestinatairesLoading ? (
                              <div className="admin-recipient-search-empty">Chargement...</div>
                            ) : adminBroadcastSearchResults.length === 0 ? (
                              <div className="admin-recipient-search-empty">Aucun resultat.</div>
                            ) : (
                              adminBroadcastSearchResults.map((u) => (
                                <button
                                  key={u.id}
                                  type="button"
                                  className="admin-recipient-search-item"
                                  onClick={() => addAdminBroadcastRecipient(u.id)}
                                >
                                  <span className="admin-recipient-search-name">{formatUserDisplayName(u)}</span>
                                  <span className="admin-recipient-search-email">{u.email || '-'}</span>
                                </button>
                              ))
                            )}
                          </div>
                        )}
                      </div>

                      <div className="admin-recipient-selected">
                        {adminBroadcastSelectedUsers.length === 0 ? (
                          <div className="admin-recipient-selected-empty">Aucun administrateur selectionne.</div>
                        ) : (
                          adminBroadcastSelectedUsers.map((u) => (
                            <div key={u.id} className="admin-recipient-chip">
                              <span className="admin-recipient-chip-name">{formatUserDisplayName(u)}</span>
                              <button
                                type="button"
                                className="admin-recipient-chip-remove"
                                onClick={() => removeAdminBroadcastRecipient(u.id)}
                                aria-label={`Retirer ${formatUserDisplayName(u)}`}
                              >
                                x
                              </button>
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  )}
                </section>

                <section className="admin-broadcast-block">
                  <div className="form-group">
                    <label>Titre :</label>
                    <input
                      type="text"
                      value={adminBroadcastTitle}
                      onChange={(e) => setAdminBroadcastTitle(e.target.value)}
                      placeholder="Saisir le titre de la notification"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>Message :</label>
                    <textarea
                      value={adminBroadcastBody}
                      onChange={(e) => setAdminBroadcastBody(e.target.value)}
                      placeholder="Saisir le message de la notification"
                      rows="5"
                      required
                    ></textarea>
                  </div>
                </section>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={closeAdminBroadcastModal}>Annuler</button>
              <button className="btn btn-primary" onClick={broadcastAdminNotification} disabled={adminBroadcastSending}>
                {adminBroadcastSending ? 'Envoi...' : 'Envoyer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showCarouselSlideForm && (
        <div className="modal-overlay" onClick={closeCarouselSlideForm}>
          <div className="modal-content modal-wide modal-zoom-in" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{carouselSlideEditingId ? 'Modifier la slide du carrousel' : 'Creer une slide du carrousel'}</h3>
              <button className="modal-close" onClick={closeCarouselSlideForm}>x</button>
            </div>
            <div className="modal-body">
              <form onSubmit={handleSubmitCarouselSlide} className="admin-form admin-carousel-modal-form">
                <div className="admin-form-2col">
                  <div className="form-group">
                    <label>Titre *</label>
                    <input
                      type="text"
                      value={carouselForm.title}
                      onChange={(e) => setCarouselForm((prev) => ({ ...prev, title: e.target.value }))}
                      maxLength={220}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>Image {carouselSlideEditingId ? '(optional)' : '*'}</label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => {
                        const file = e.target.files && e.target.files[0] ? e.target.files[0] : null;
                        setCarouselForm((prev) => ({ ...prev, image: file }));
                      }}
                      required={!carouselSlideEditingId}
                    />
                    {carouselSlideEditingId && (
                      <small className="admin-carousel-hint">Laissez vide pour conserver image actuelle.</small>
                    )}
                  </div>
                </div>

                <div className="form-group">
                  <label>Body *</label>
                  <textarea
                    value={carouselForm.body}
                    onChange={(e) => setCarouselForm((prev) => ({ ...prev, body: e.target.value }))}
                    rows="7"
                    required
                  ></textarea>
                </div>

                {carouselSlideEditingId && carouselEditingSlide?.image_url && (
                  <div className="admin-carousel-current-image">
                    <span className="admin-k">Image actuelle</span>
                    <a
                      className="admin-v admin-link"
                      href={resolveMediaUrl(carouselEditingSlide.image_url)}
                      target="_blank"
                      rel="noreferrer"
                    >
                      Ouvrir image actuelle
                    </a>
                  </div>
                )}

                <div className="modal-footer" style={{ marginTop: '1.5rem', display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={closeCarouselSlideForm}
                    disabled={carouselSlideSaving}
                  >
                    Annuler
                  </button>
                  <button type="submit" className="btn btn-primary" disabled={carouselSlideSaving}>
                    {carouselSlideSaving ? 'Enregistrement...' : (carouselSlideEditingId ? 'Mettre a jour la slide' : 'Creer une slide')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {showCreateEvent && (
        <div className="modal-overlay" onClick={() => setShowCreateEvent(false)}>
          <div className="modal-content modal-wide modal-zoom-in" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Creer un evenement</h3>
              <button className="modal-close" onClick={() => setShowCreateEvent(false)}>x</button>
            </div>
            <div className="modal-body">
              <form onSubmit={handleCreateEvent} className="admin-form">
                <div className="form-group">
                  <label>Type evenement</label>
                  <div style={{ display: 'flex', gap: '1rem', marginTop: '0.5rem' }}>
                    <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                      <input
                        type="radio"
                        name="event_type"
                        value="normal"
                        checked={newEvent.event_type === 'normal'}
                        onChange={(e) => setNewEvent((p) => ({ ...p, event_type: e.target.value }))}
                        style={{ marginRight: '0.5rem' }}
                      />
                      Normal
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                      <input
                        type="radio"
                        name="event_type"
                        value="premium"
                        checked={newEvent.event_type === 'premium'}
                        onChange={(e) => setNewEvent((p) => ({ ...p, event_type: e.target.value }))}
                        style={{ marginRight: '0.5rem' }}
                      />
                      Premium
                    </label>
                  </div>
                </div>

                {newEvent.event_type === 'premium' && (
                  <div className="form-group">
                    <label>Type evenement premium</label>
                    <div style={{ display: 'flex', gap: '1rem', marginTop: '0.5rem' }}>
                      <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                        <input
                          type="radio"
                          name="premium_payment_required"
                          checked={newEvent.premium_payment_required === true}
                          onChange={() => setNewEvent((p) => ({ ...p, premium_payment_required: true }))}
                          style={{ marginRight: '0.5rem' }}
                        />
                        Paid
                      </label>
                      <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                        <input
                          type="radio"
                          name="premium_payment_required"
                          checked={newEvent.premium_payment_required === false}
                          onChange={() => setNewEvent((p) => ({ ...p, premium_payment_required: false }))}
                          style={{ marginRight: '0.5rem' }}
                        />
                        Non payant
                      </label>
                    </div>
                  </div>
                )}

                <div className="form-group">
                  <label>Titre / Headline (max {EVENT_TITLE_MAX} caracteres)</label>
                  <input
                    type="text"
                    value={newEvent.headline}
                    maxLength={EVENT_TITLE_MAX}
                    onChange={(e) => setNewEvent((p) => ({ ...p, headline: e.target.value }))}
                    onInput={(e) => setNewEvent((p) => ({ ...p, headline: e.target.value }))}
                    placeholder="Ex : soiree de bienvenue ADEEF"
                    required
                  />
                  <small>{(newEvent.headline ?? '').length}/{EVENT_TITLE_MAX}</small>
                </div>

                <div className="form-group">
                  <label>Description / Body (max {EVENT_BODY_MAX} caracteres)</label>
                  <textarea
                    value={newEvent.body}
                    maxLength={EVENT_BODY_MAX}
                    onChange={(e) => setNewEvent((p) => ({ ...p, body: e.target.value }))}
                    onInput={(e) => setNewEvent((p) => ({ ...p, body: e.target.value }))}
                    placeholder="Texte court (doit tenir dans la carte)"
                    required
                    rows={5}
                  />
                  <small>{(newEvent.body ?? '').length}/{EVENT_BODY_MAX}</small>
                </div>

                <div className="admin-form-2col">
                  <div className="form-group">
                    <label>Date</label>
                    <input
                      type="date"
                      value={newEvent.date}
                      onChange={(e) => setNewEvent((p) => ({ ...p, date: e.target.value }))}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>Categorie</label>
                    <select
                      value={newEvent.category}
                      onChange={(e) => setNewEvent((p) => ({ ...p, category: e.target.value }))}
                      disabled={eventCategories.length === 0}
                      required
                    >
                      {eventCategories.length === 0 ? (
                        <option value="">Aucune categorie disponible</option>
                      ) : (
                        eventCategories.map((category) => (
                          <option key={category.id} value={category.slug}>{category.label}</option>
                        ))
                      )}
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label>Image</label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => setNewEvent((p) => ({ ...p, image: e.target.files?.[0] || null }))}
                    required
                  />
                </div>

                {newEvent.event_type === 'normal' && (
                  <div className="form-group">
                    <label>Lien (reseaux sociaux)</label>
                    <input
                      type="url"
                      value={newEvent.social_url}
                      onChange={(e) => setNewEvent((p) => ({ ...p, social_url: e.target.value }))}
                      placeholder="https://..."
                      required
                    />
                  </div>
                )}

                {newEvent.event_type === 'premium' && (
                  <div className="form-group">
                    <label>
                      {newEvent.premium_payment_required ? 'Lien de paiement (obligatoire)' : "Lien d'inscription (optionnel)"}
                    </label>
                    <input
                      type="url"
                      value={newEvent.registration_link}
                      onChange={(e) => setNewEvent((p) => ({ ...p, registration_link: e.target.value }))}
                      placeholder={newEvent.premium_payment_required 
                        ? "https://... (lien de paiement)" 
                        : "https://... (laisser vide si aucune inscription externe n'est necessaire)"}
                      required={newEvent.premium_payment_required}
                    />
                    <small style={{ display: 'block', marginTop: '0.5rem', color: '#666' }}>
                      {newEvent.premium_payment_required 
                        ? 'Saisissez le lien de paiement oA1 les utilisateurs finaliseront leur achat.'
                        : "Pour les evenements gratuits avec inscription externe : saisissez le lien d'inscription. Pour les evenements gratuits sans inscription : laissez vide (l'email fera office de ticket)."}
                    </small>
                  </div>
                )}

                <div className="modal-footer" style={{ marginTop: '1.5rem', display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                  <button type="button" className="btn btn-secondary" onClick={() => setShowCreateEvent(false)} disabled={creatingEvent}>
                    Annuler
                  </button>
                  <button type="submit" className="btn btn-success" disabled={creatingEvent}>
                    {creatingEvent ? 'Creation...' : 'Creer'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;